import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from "react";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useSSE } from "../hooks/useSSE";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../App";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, FileText, Receipt, TrendingUp,
  ChevronDown, ChevronLeft, ChevronRight, ExternalLink,
  CheckCircle, Clock, X, AlertCircle, Search,
	  DollarSign, BarChart3, ShoppingCart, ClipboardList,
	  Edit, Trash2, Copy, Wallet, Printer, Banknote, Save, RotateCcw,
    Shield, Settings, Loader2, Percent, Tag, Calendar, RefreshCw, AlertTriangle, Users, Wrench, Bell, PackageCheck, Truck, User
	} from "lucide-react";
import EntregasPanel from "../components/EntregasPanel";
import MedioCobroSelector, { useProcesadoras } from "../components/MedioCobroSelector";
import EmpresaSwitcher from "../components/EmpresaSwitcher";
import PresupuestoFormModal from "../components/PresupuestoFormModal";
import PresupuestoCostosModal from "../components/PresupuestoCostosModal";
import RecepcionEquipoFormModal from "../components/RecepcionEquipoFormModal";
import RecepcionConfigModal from "../components/RecepcionConfigModal";
import FacturaFormModal from "../components/FacturaFormModal";
import CobroContadoCard from "../components/CobroContadoCard";
import DeliveryConfigCard from "../components/DeliveryConfigCard";
import { imprimirOrdenDelivery } from "../lib/ordenDelivery";
import MoneyInput from "../components/ui/MoneyInput";
import { LogoMarcaArandu, LogoMarcaJar, LogoMarcaAranduJar } from "../components/MarcaLogos";
import { AuditoriaRegistroBtn } from "../components/AuditoriaModal";
import { normalizeLogoTipo, svgMarcaIcon, svgPrintLogoName, svgLogoMarcaRow, svgDocumentHeaderLogoHtml } from "../lib/marcaLogoSvg";
import { resolveLogoForContext } from "../lib/logoUtils";
import DateInput from "../components/ui/DateInput";

// Retención de IVA de ventas — PROPORCIONAL al pago (igual que el backend, utils/retencion.py):
// retención = IVA × % × (monto del pago ÷ total de la factura), sin superar lo que falta retener.
function retencionProporcional(fac, monto) {
  const total = Number(fac?.monto || 0);
  const retTotal = Number(fac?.retencion_info?.monto_retencion_total || 0);
  if (!fac?.retencion_info || total <= 0 || retTotal <= 0 || !(monto > 0)) return 0;
  const ya = (fac.pagos || []).reduce((s, p) => s + (Number(p.monto_retencion) || 0), 0);
  const falta = Math.max(0, retTotal - ya);
  return Math.min(falta, Math.min(monto, monto * retTotal / total));
}
// Monto bruto a registrar para que ingrese `neto` en efectivo/banco (inverso de retencionProporcional).
function brutoParaNeto(fac, neto) {
  const total = Number(fac?.monto || 0);
  const retTotal = Number(fac?.retencion_info?.monto_retencion_total || 0);
  if (!fac?.retencion_info || total <= 0 || retTotal <= 0) return neto;
  const r = retTotal / total;
  let g = r < 1 ? neto / (1 - r) : neto;
  if (retencionProporcional(fac, g) < g * r - 0.005) g = neto + retencionProporcional(fac, g);
  return g;
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Helpers ──────────────────────────────────────────────────
function getMesActual() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}
function getDiaActual() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}
function prevDia(d) { const dt = new Date(d + "T12:00:00"); dt.setDate(dt.getDate() - 1); return dt.toISOString().slice(0, 10); }
function nextDia(d) { const dt = new Date(d + "T12:00:00"); dt.setDate(dt.getDate() + 1); return dt.toISOString().slice(0, 10); }
function diaLabel(d) { if (!d) return ""; return new Date(d + "T12:00:00").toLocaleDateString("es-PY", { weekday: "short", day: "numeric", month: "short" }); }
const LS_VENTAS_FILTRO = "ard_ventas_filtro_tipo";
function prevMes(m) {
  const [y, mo] = m.split("-").map(Number);
  return mo === 1 ? `${y - 1}-12` : `${y}-${String(mo - 1).padStart(2, "0")}`;
}
function nextMes(m) {
  const [y, mo] = m.split("-").map(Number);
  return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, "0")}`;
}
function mesLabel(m) {
  const [y, mo] = m.split("-");
  const meses = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  return `${meses[parseInt(mo, 10) - 1]} ${y}`;
}
function fmtPYG(n) {
  if (n == null || isNaN(n)) return "-";
  return `₲ ${Math.round(n).toLocaleString("es-PY")}`;
}
function fmtMonto(monto, moneda = "PYG") {
  if (monto == null) return "-";
  if (moneda === "PYG") return `₲ ${Math.round(monto).toLocaleString("es-PY")}`;
  if (moneda === "BRL") return `R$ ${Number(monto).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${moneda} ${Number(monto).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

async function readApiError(res, fallback = "Error") {
  const data = await res.json().catch(() => null);
  const detail = data?.detail ?? data?.message ?? data?.error;
  if (Array.isArray(detail)) {
    return detail.map(d => {
      if (typeof d === "string") return d;
      const loc = Array.isArray(d?.loc) ? d.loc.join(".") : "";
      const msg = d?.msg || d?.message || JSON.stringify(d);
      return loc ? `${loc}: ${msg}` : msg;
    }).join("; ") || fallback;
  }
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object") return detail.message || JSON.stringify(detail);
  if (data && typeof data === "object") return data.message || data.error || fallback;
  return fallback;
}

function renderCuentasPorMoneda(cuentas = [], logo = null) {
  const labels = { PYG: "Cuenta bancaria Guaraníes (PYG)", USD: "Cuenta bancaria Dólares (USD)", BRL: "Cuenta bancaria Reales (BRL)", ARS: "Cuenta bancaria Pesos (ARS)" };
  const activas = (cuentas || []).filter(c => c.activa !== false && (!logo || !c.logo_tipo || c.logo_tipo === logo));
  const monedasBase = ["PYG", "USD", "BRL", "ARS"];
  const monedasExtra = Array.from(new Set(activas.map(c => c.moneda || "PYG"))).filter(m => !monedasBase.includes(m));
  return [...monedasBase, ...monedasExtra].map(moneda => {
    const grupo = activas.filter(c => (c.moneda || "PYG") === moneda);
    if (!grupo.length) return null;
    return (
      <optgroup key={moneda} label={labels[moneda] || moneda}>
        {grupo.map(c => (
          <option key={c.id} value={c.id}>
            {c.nombre}{c.banco ? ` · ${c.banco}` : ""} ({c.moneda || "PYG"}){c.es_predeterminada ? " ★" : ""}
          </option>
        ))}
      </optgroup>
    );
  });
}

function convertToDocMoneda(amount, monedaOrigen, tcOverride, docMoneda, docTC) {
  const value = Number(amount) || 0;
  const origen = monedaOrigen || docMoneda || "PYG";
  const destino = docMoneda || "PYG";
  if (origen === destino) return value;
  const tc = Number(tcOverride || docTC || 0);
  if (tc <= 0) return 0;
  if (destino === "PYG" && origen === "USD") return value * tc;
  if (destino === "USD" && origen === "PYG") return value / tc;
  if (destino === "PYG" && origen === "BRL") return value * tc;
  if (destino === "BRL" && origen === "PYG") return value / tc;
  return value;
}

function calcUtilidadPresupuesto(p) {
  const total = Number(p?.total || 0);
  const moneda = p?.moneda || "PYG";
  const isUSD = moneda === "USD";
  if (p?.costos_reales && p.costos_reales.ganancia !== undefined && p.costos_reales.ganancia !== null) {
    const utilidadReal = Number(p.costos_reales.ganancia);
    return {
      utilidad: isUSD ? Math.round(utilidadReal * 100) / 100 : Math.round(utilidadReal),
      fuente: "real",
    };
  }
  if (!Array.isArray(p?.items)) return { utilidad: null, fuente: null };
  const costoEstimado = p.items.reduce((sum, item) => {
    const cantidad = Number(item.cantidad || 1);
    const costoUnit = convertToDocMoneda(
      item.costo || 0,
      item.moneda_item || moneda,
      item.tipo_cambio_item,
      moneda,
      p.tipo_cambio
    );
    return sum + costoUnit * cantidad;
  }, 0);
  const utilidad = total - costoEstimado;
  return {
    utilidad: isUSD ? Math.round(utilidad * 100) / 100 : Math.round(utilidad),
    fuente: "estimada",
  };
}

const LOGO_CHIP = {
  arandujar: "bg-blue-600/20 text-blue-300 border-blue-600/30",
  arandu:    "bg-emerald-600/20 text-emerald-300 border-emerald-600/30",
  jar:       "bg-red-600/20 text-red-300 border-red-600/30",
};
const LOGO_LABEL = { arandujar: "A&JAR", arandu: "Arandu", jar: "JAR" };

const ESTADO_BADGE = {
  borrador:   { cls: "bg-slate-500/20 text-slate-400 border-slate-500/30",     label: "Borrador",   icon: Clock },
  aprobado:   { cls: "bg-blue-500/15 text-blue-300 border-blue-500/30",        label: "Aprobado",   icon: CheckCircle },
  rechazado:  { cls: "bg-red-500/15 text-red-300 border-red-500/30",           label: "Rechazado",  icon: X },
  facturado:  { cls: "bg-orange-500/15 text-orange-300 border-orange-500/30",  label: "Facturado",  icon: Receipt },
  cobrado:    { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",label: "Cobrado",   icon: CheckCircle },
  cancelado:  { cls: "bg-red-500/15 text-red-300 border-red-500/30",           label: "Cancelado",  icon: X },
  pagada:     { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",label: "Pagada",    icon: CheckCircle },
  pendiente:  { cls: "bg-amber-500/15 text-amber-300 border-amber-500/30",     label: "Pendiente",  icon: Clock },
  parcial:    { cls: "bg-blue-500/15 text-blue-300 border-blue-500/30",        label: "Parcial",    icon: Clock },
  anulada:    { cls: "bg-slate-500/20 text-slate-400 border-slate-500/30",     label: "Anulada",    icon: X },
};

// Header de tabla cliqueable para ordenar
function SortTh({ label, sortKey, currentSort, onClick, className = "" }) {
  const isActive = currentSort.key === sortKey;
  return (
    <th className={`px-4 py-2.5 text-slate-400 font-body uppercase text-[11px] tracking-wider cursor-pointer select-none hover:text-white transition-all ${className}`}
        onClick={() => onClick(sortKey)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (
          currentSort.dir === "asc"
            ? <span className="text-emerald-400">▲</span>
            : <span className="text-emerald-400">▼</span>
        ) : (
          <span className="opacity-30">⇅</span>
        )}
      </span>
    </th>
  );
}

function StateBadge({ estado }) {
  const s = ESTADO_BADGE[estado] || ESTADO_BADGE.pendiente;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-body border ${s.cls}`}>
      <Icon className="w-3 h-3" />{s.label}
    </span>
  );
}

// Dropdown editable de estado (para cambiar el estado al hacer click).
// Se renderiza vía portal con position:fixed para que no quede recortado por
// contenedores con overflow (tablas, modales, etc.) ni "se vaya atrás" detrás
// de otros elementos al aprobar/rechazar varios presupuestos seguidos.
function StateDropdown({ estado, options, onChange, disabled = false }) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState({ top: 0, left: 0, openUp: false, width: 130 });
  const ref = React.useRef(null);
  const menuRef = React.useRef(null);

  const computePos = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuH = 80; // altura aproximada para 2-3 opciones
    const menuW = Math.max(130, rect.width);
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuH + 12 && rect.top > menuH + 12;
    setPos({
      top: openUp ? rect.top - 6 : rect.bottom + 6,
      left: rect.left + rect.width / 2,
      openUp,
      width: menuW,
    });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    computePos();
    const onDoc = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => computePos();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, computePos]);

  if (disabled) return <StateBadge estado={estado} />;

  const menu = open ? (
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        transform: pos.openUp ? "translate(-50%, -100%)" : "translate(-50%, 0)",
        minWidth: pos.width,
        zIndex: 9999,
      }}
      className="bg-arandu-dark border border-white/20 rounded-lg shadow-2xl overflow-hidden"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {options.map(op => (
        <button
          key={op}
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(false); if (op !== estado) onChange(op); }}
          className={`w-full text-left px-3 py-1.5 text-xs font-body transition-all ${
            op === estado
              ? "bg-white/10 text-white"
              : "text-slate-300 hover:bg-white/5 hover:text-white"
          }`}
        >
          {ESTADO_BADGE[op]?.label || op}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <span
      className="relative inline-block"
      ref={ref}
      onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
    >
      <span className="cursor-pointer">
        <StateBadge estado={estado} />
      </span>
      {menu && createPortal(menu, document.body)}
    </span>
  );
}

import { DescuentosConfigTab, VendedoresConfigTab } from "./ConfigVentasPage";
import PresupuestoAprobacionModal from "../components/PresupuestoAprobacionModal";

const TABS = [
  { id: "presupuestos", label: "Presupuestos", icon: FileText,      color: "blue" },
  { id: "recepcion",    label: "Recepción de Equipos", icon: Wrench, color: "cyan" },
  { id: "facturas",     label: "Facturas",     icon: Receipt,       color: "emerald" },
  { id: "entregas",     label: "Entregas pendientes", icon: PackageCheck, color: "emerald" },
	  { id: "ingresos",    label: "Ingresos",     icon: TrendingUp,    color: "violet" },
	  { id: "recibos",      label: "Recibos",      icon: Banknote,      color: "amber" },
	  { id: "notas",        label: "Notas crédito", icon: RotateCcw,     color: "rose" },
	  { id: "configuracion", label: "Configuración", icon: Settings,      color: "slate" },
	];
const TAB_PERMISOS = {
  presupuestos: "presupuestos.ver",
  recepcion: "presupuestos.recepcion_ver",
  facturas: "facturas.ver",
  entregas: "entregas.ver",
  ingresos: "ingresos_varios.ver",
  recibos: "recibos.ver",
  notas: "notas_credito.ver",
  configuracion: "configuracion_ventas.gestionar_descuentos",
};

// ─── Cache en memoria (persiste entre remounts mientras la app esté abierta) ──
const _facturasCache = new Map();
const _presupuestosCache = new Map();

// ─── localStorage cache (persiste entre sesiones) ────────────────
const LS_TTL = 24 * 60 * 60 * 1000; // 24h — siempre hay background revalidate
const lsGet = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > LS_TTL) { localStorage.removeItem(key); return null; }
    return data;
  } catch { return null; }
};
const lsSet = (key, data) => {
  try { localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
};
const lsDel = (key) => { try { localStorage.removeItem(key); } catch {} };
const lsDelPrefix = (prefix) => {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(prefix)) localStorage.removeItem(key);
    });
  } catch {}
};

export default function VentasPage() {
  const { token, user, hasPermission, hasModule, empresasPropias, activeEmpresaPropia, loading: authLoading } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";
  const canViewDeleted = (perm) => isAdmin || user?.role === "gerente" || user?.role === "super_admin" || hasPermission(perm);
  const canEditTcDoc = isAdmin || user?.role === "gerente" || user?.role === "super_admin" || hasPermission("cotizaciones.editar_en_ventas");
  const isVentasPrivileged = ["admin", "gerente", "super_admin"].includes(user?.role);
  const [ventasDescuentoModo, setVentasDescuentoModo] = useState(user?.ventas_config?.descuentos?.modo || null);
  const canUseDescuento = isVentasPrivileged || !!ventasDescuentoModo;

  useEffect(() => {
    setVentasDescuentoModo(user?.ventas_config?.descuentos?.modo || null);
  }, [user?.ventas_config?.descuentos?.modo]);

  useEffect(() => {
    if (!token || isVentasPrivileged || user?.ventas_config?.descuentos?.modo) return;
    let alive = true;
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!alive || !data) return;
        setVentasDescuentoModo(data?.ventas_config?.descuentos?.modo || null);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [token, isVentasPrivileged, user?.id, user?.ventas_config?.descuentos?.modo]);

  // Auto-fetch TC del día desde cotizaciones
  const fetchTcDelDia = async (moneda, fecha) => {
    if (!moneda || moneda === "PYG" || !fecha) return null;
    try {
      const logo = activeEmpresaPropia?.slug ? `&logo_tipo=${encodeURIComponent(activeEmpresaPropia.slug)}` : "";
      const res = await fetch(`${API}/admin/cotizaciones/${moneda.toLowerCase()}?fecha=${fecha}${logo}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return null;
      const d = await res.json();
      return d?.venta || d?.tipo_cambio_sugerido || null;
    } catch { return null; }
  };
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab");
	    return t && ["presupuestos", "recepcion", "facturas", "entregas", "ingresos", "recibos", "notas", "configuracion"].includes(t) ? t : "facturas";
  });
  // Filtro temporal: "dia" | "mes" | "anio" | "todos"
  const [filtroTipo, setFiltroTipo] = useState(() => localStorage.getItem(LS_VENTAS_FILTRO) || "dia");
  const [dia, setDia] = useState(getDiaActual);
  const [mes, setMes] = useState(getMesActual());
  const [anio, setAnio] = useState(String(new Date().getFullYear()));
  const setFiltroTipoP = (v) => { setFiltroTipo(v); localStorage.setItem(LS_VENTAS_FILTRO, v); };
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showDescuentosGestor, setShowDescuentosGestor] = useState(false);
  const [showVendedoresGestor, setShowVendedoresGestor] = useState(false);
  const [tiposFac, setTiposFac] = useState([]);
  const [showTiposGestor, setShowTiposGestor] = useState(false);
  const [tipoFormItem, setTipoFormItem] = useState(null);  // null=cerrado, {}=nuevo, {id,...}=editar
  const [tipoFormNombre, setTipoFormNombre] = useState("");
  const [tipoFormColor, setTipoFormColor] = useState("#6366f1");
  const [tipoFormClienteId, setTipoFormClienteId] = useState(""); // Cliente dueño de la Obra (Balancete - Obra)
  const [savingTipo, setSavingTipo] = useState(false);
  const [mostrarEliminadas, setMostrarEliminadas] = useState({
    presupuestos: false,
    facturas: false,
    ingresos: false,
    recibos: false,
    notas: false,
  });
  const newBtnRef = useRef(null);

  // Ordenamiento por tab: { [tab]: { key, dir: "asc"|"desc" } }
  const [sortBy, setSortBy] = useState({
    presupuestos: { key: "fecha", dir: "desc" },
    facturas:     { key: "fecha", dir: "desc" },
	    ingresos:     { key: "fecha", dir: "desc" },
	    recibos:      { key: "fecha_pago", dir: "desc" },
	    notas:        { key: "fecha", dir: "desc" },
	  });
  const toggleSort = (tabName, key) => {
    setSortBy(prev => ({
      ...prev,
      [tabName]: prev[tabName].key === key
        ? { key, dir: prev[tabName].dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    }));
  };
  const sortList = (list, sort) => {
    const { key, dir } = sort;
    const mult = dir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = a[key] ?? "";
      const bv = b[key] ?? "";
      let primary = 0;
      if (typeof av === "number" && typeof bv === "number") primary = (av - bv) * mult;
      else primary = String(av).localeCompare(String(bv), "es", { numeric: true }) * mult;
      if (primary !== 0) return primary;
      return String(b.created_at || b.updated_at || b.id || "").localeCompare(
        String(a.created_at || a.updated_at || a.id || ""),
        "es",
        { numeric: true }
      );
    });
  };

  // Vista previa inline al click en fila (contratos/ingresos)
  const [previewItem, setPreviewItem] = useState(null); // { kind, data }
  const openPreview = (kind, data) => setPreviewItem({ kind, data });
  const closePreview = () => setPreviewItem(null);

  // Presupuesto: documento completo con vista previa y acciones
  const [presDoc, setPresDoc] = useState(null);
  const [presDocLoading, setPresDocLoading] = useState(false);

  // Factura: chips search + visual doc + form modal + pago modals
  const [facChips, setFacChips] = useState([]);
  const [facInput, setFacInput] = useState("");
  const [facDoc, setFacDoc] = useState(null);           // factura en doc-view
  const [facDocLoading, setFacDocLoading] = useState(false);
  const [autoPrintFacDoc, setAutoPrintFacDoc] = useState("no"); // "no"|"a4"|"a4_doble"|"ticket" — dispara impresión automática al abrir (config "guardar e imprimir")
  const [facFormItem, setFacFormItem] = useState(null); // { factura|null } — null = nueva
  const [showFacForm, setShowFacForm] = useState(false);
  const [facSinFactura, setFacSinFactura] = useState(false); // modo boleta
  const [facBoleta, setFacBoleta] = useState("todas"); // "todas" | "con_factura" | "sin_factura"
  // Timbrado config modal
  const [showTimbradoConfig, setShowTimbradoConfig] = useState(false);
  const [timbradoData, setTimbradoData] = useState(null);
  const [timbradoLoading, setTimbradoLoading] = useState(false);
  const [savingTimbrado, setSavingTimbrado] = useState(false);
  const nuevoPuntoVacio = (codigo = "001") => ({
    codigo, descripcion: "",
    modo_numeracion: "manual",
    tipo_emision: "preimpreso",
    nro_timbrado: "", establecimiento: "001",
    fecha_inicio: new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10),
    fecha_vigencia: "",
    numero_desde: "", numero_hasta: "",
    // Rango propio de Nota de Crédito (mismo timbrado del punto, secuencia
    // aparte) — nc_activo=false significa "todavía no configurado para este
    // punto", y Nota de Crédito sigue usando número libre para ese punto.
    nc_activo: false, nc_numero_desde: "", nc_numero_hasta: "",
  });
  const [timbradoForm, setTimbradoForm] = useState({
    punto_default: "001",
    personalizado_por_usuario: false,
    puntos_expedicion: [nuevoPuntoVacio("001")],
    // Global por empresa (no por punto): exigido por DNIT cuando algún punto
    // usa tipo_emision="autoimpresor".
    habilitacion_autoimpresor: "",
  });
  // Huecos de numeración (números liberados por facturas eliminadas, dentro
  // del rango ya usado de un punto) — gateado por permiso granular
  // configuracion_ventas.ver_huecos_timbrado.
  const [huecosPuntoAbierto, setHuecosPuntoAbierto] = useState(null); // código de punto cuyo listado está expandido
  const [huecosLoading, setHuecosLoading] = useState(false);
  const [huecosLista, setHuecosLista] = useState([]); // [{numero, numero_raw, fecha_desde, fecha_hasta}]
  const [huecoParaFactura, setHuecoParaFactura] = useState(null); // hueco elegido para precargar Nueva Factura
  const [timbradoUsuarios, setTimbradoUsuarios] = useState([]); // [{user_id, nombre, email, role, punto_expedicion}]
  const [loadingTimbradoUsuarios, setLoadingTimbradoUsuarios] = useState(false);
  const [savingTimbradoUsuarioId, setSavingTimbradoUsuarioId] = useState(null);
  const [recibosConfig, setRecibosConfig] = useState({ modo_recibos: "automatico" });
  const [savingRecibosConfig, setSavingRecibosConfig] = useState(false);
  const [impresionConfig, setImpresionConfig] = useState({ modo: "no", modo_empresa: "no", personalizado_por_usuario: false, modelos_habilitados: ["a4", "a4_doble", "ticket", "compacta"], max_items: 12, mostrar_observacion: true });
  const [savingImpresionConfig, setSavingImpresionConfig] = useState(false);
  // Borrador del input de "Máximo de ítems" — se guarda recién al salir del campo
  // (blur/Enter), no en cada tecla, para no disparar un PUT por cada dígito.
  const [maxItemsDraft, setMaxItemsDraft] = useState("12");
  React.useEffect(() => { setMaxItemsDraft(String(impresionConfig.max_items ?? 12)); }, [impresionConfig.max_items]);
  const [impresionUsuarios, setImpresionUsuarios] = useState([]); // [{user_id, nombre, email, role, modo}]
  const [loadingImpresionUsuarios, setLoadingImpresionUsuarios] = useState(false);
  const [savingImpresionUsuarioId, setSavingImpresionUsuarioId] = useState(null);
  // Modal de confirmación con motivo para anular factura
  const [anularFacTarget, setAnularFacTarget] = useState(null);
  const [anularFacMotivo, setAnularFacMotivo] = useState("");
  const [anulandoFac, setAnulandoFac] = useState(false);

  // Contrato: chips search + visual doc + form modal
  // Pago modals
  const [pagoFac, setPagoFac] = useState(null);
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [pagoItemsMonto, setPagoItemsMonto] = useState(null); // null = modo normal, number = pago por ítems
  const [pagoMontoRecibido, setPagoMontoRecibido] = useState(null); // monto entregado por el cliente (para vuelto)
  const [pagoVueltoCuenta, setPagoVueltoCuenta] = useState("");     // cuenta de la que se da el vuelto
  const [pagoItemsData, setPagoItemsData] = useState(null); // [{idx, monto}] cuando es pago por ítems
  const [fechaPago, setFechaPago] = useState(new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));
  const [showPagoParcialModal, setShowPagoParcialModal] = useState(false);
  const [pagoRef, setPagoRef] = useState({ referencia: "", nota: "", imagen: "" });
  const [pagoParcialFac, setPagoParcialFac] = useState(null);
  const [pagoParcialCuotas, setPagoParcialCuotas] = useState([]); // cuotas cargadas al abrir PagoParcialModal (desde lista o modal)
  const [pagoParcialCuotaIds, setPagoParcialCuotaIds] = useState(new Set()); // cuotas tildadas en PagoParcialModal — se envían explícitas al backend, no se adivinan por monto
  const [montoParcial, setMontoParcial] = useState("");
  const [medioCobro, setMedioCobro] = useState({ procesadora_id: "", metodo_cobro: "" }); // efectivo/transferencia o procesadora + medio
  const [sinRetencionPago, setSinRetencionPago] = useState(false); // permiso retenciones.omitir_retencion
  const procesadorasCobro = useProcesadoras(API, token, activeEmpresaPropia?.slug);
  const [descuentoPagoParcial, setDescuentoPagoParcial] = useState(""); // descuento otorgado en el pago (p. ej. quitar interés al cancelar todas las cuotas)
  const [fechaPagoParcial, setFechaPagoParcial] = useState(new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));
  const [numeroReciboManual, setNumeroReciboManual] = useState("");
  // Saldo a favor del cliente al momento del pago
  const [saldoFavorFac, setSaldoFavorFac] = useState(null);
  const [aplicarSaldoFavor, setAplicarSaldoFavor] = useState(false);
  const [montoSaldoAplicar, setMontoSaldoAplicar] = useState("");
  // Vale de cliente (crédito cargado por Ingresos) al momento del pago
  const [valeFac, setValeFac] = useState(null);
  const [aplicarVale, setAplicarVale] = useState(false);
  const [montoValeAplicar, setMontoValeAplicar] = useState("");
  // Cuentas bancarias multi-moneda
  const [cuentasDisp, setCuentasDisp] = useState([]);
  const [cuentasPYG, setCuentasPYG] = useState([]);   // seleccionadas PYG
  const [cuentasUSD, setCuentasUSD] = useState([]);   // seleccionadas USD (cuando moneda difiere)
  const [cuentasBRL, setCuentasBRL] = useState([]);   // seleccionadas BRL (cuando moneda difiere)
  const [tcPago, setTcPago] = useState("");           // tipo de cambio para monto en USD/BRL
  const [montoUSD, setMontoUSD] = useState("");       // monto en USD/BRL (para derivar TC)
  // Editar pago individual
  const [editPagoCtx, setEditPagoCtx] = useState(null); // { factura, pago }
  // Pagos múltiples (bulk)
  const [selectedFacIds, setSelectedFacIds] = useState(new Set());
  const [showBulkPagoModal, setShowBulkPagoModal] = useState(false);
  const [bulkFechaPago, setBulkFechaPago] = useState(new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));
  const [bulkCuentaId, setBulkCuentaId] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  // Recibos
  const [recibos, setRecibos] = useState([]);
  const [reciboDoc, setReciboDoc] = useState(null);
  const [reciboChips, setReciboChips] = useState([]);
  const [reciboInput, setReciboInput] = useState("");
  const openPresDoc = async (id) => {
    setPresDocLoading(true);
    try {
      const res = await fetch(`${API}/admin/presupuestos/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setPresDoc(await res.json());
      else toast.error("Error al cargar presupuesto");
    } catch (e) { toast.error("Error de red"); }
    setPresDocLoading(false);
  };

  // Fetch presupuesto completo (con items) antes de abrir el modal de facturación
  const openPresFacturar = async (p) => {
    try {
      const res = await fetch(`${API}/admin/presupuestos/${p.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setPresFacturarItem(res.ok ? await res.json() : p);
    } catch { setPresFacturarItem(p); }
  };

  // Chips de búsqueda para presupuestos
  const [presChips, setPresChips] = useState([]);
  const [presInput, setPresInput] = useState("");

  // Acciones de presupuesto inline
  const deletePresupuesto = async (id) => {
    if (!window.confirm("¿Eliminar este presupuesto?")) return;
    try {
      const res = await fetch(`${API}/admin/presupuestos/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success("Presupuesto eliminado"); fetchPresupuestos(true); if (presDoc?.id === id) setPresDoc(null); }
      else toast.error("Error al eliminar");
    } catch (e) { toast.error("Error de red"); }
  };

  const duplicatePresupuesto = async (p) => {
    try {
      const res = await fetch(`${API}/admin/presupuestos/${p.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error("Error al cargar datos para duplicar"); return; }
      const full = await res.json();
      const { id, numero, ...rest } = full; // eslint-disable-line
      const payload = { ...rest, numero: null, fecha: new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10) };
      const createRes = await fetch(`${API}/admin/presupuestos`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (createRes.ok) { toast.success("Presupuesto duplicado"); fetchPresupuestos(true); }
      else toast.error("Error al duplicar");
    } catch (e) { toast.error("Error de red"); }
  };

  const formatNumber = (num, moneda = "PYG") => {
    if (moneda === "USD") return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
    return new Intl.NumberFormat('es-PY').format(num);
  };
  const getCurrencySymbol = (moneda) => moneda === "USD" ? "US$" : moneda === "BRL" ? "R$" : "₲";

  // logo_tipo activo basado en empresa seleccionada
  const logoFilter = activeEmpresaPropia?.slug || "todas";

  // Data
  const [presupuestos, setPresupuestos] = useState([]);
  const [recepciones, setRecepciones] = useState([]);
  const [showRecepConfig, setShowRecepConfig] = useState(false);
  const [recepFormItem, setRecepFormItem] = useState(null); // null=cerrado, {}=nuevo, {id,...}=editar
	  const [facturas, setFacturas] = useState([]);
	  const [ingresos, setIngresos] = useState([]);
	  const [notasCredito, setNotasCredito] = useState([]);
	  const [loading, setLoading] = useState(false);

  // ── SSE — eventos en tiempo real ─────────────────────────────────────────
  useSSE({
    logoTipo: activeEmpresaPropia?.slug,
    token,
    enabled: !!activeEmpresaPropia?.slug && !!token,
    onEvent: useCallback((event) => {
      if (event.tipo === "factura_actualizada") {
        setFacturas(prev => prev.map(f => f.id === event.data.id ? {
          ...f,
          estado: event.data.estado,
          ...(event.data.monto_pagado !== undefined ? { monto_pagado: event.data.monto_pagado } : {}),
          ...(event.data.fecha_pago !== undefined ? { fecha_pago: event.data.fecha_pago ? event.data.fecha_pago.slice(0, 10) : null } : {}),
        } : f));
      }
      if (event.tipo === "factura_creada") {
        const fac = event.data;
        if (!fac || fac.eliminada) return;
        const processed = {
          ...fac,
          fecha: fac.fecha ? fac.fecha.slice(0, 10) : null,
          fecha_vencimiento: fac.fecha_vencimiento ? fac.fecha_vencimiento.slice(0, 10) : null,
          fecha_pago: fac.fecha_pago ? fac.fecha_pago.slice(0, 10) : null,
        };
        setFacturas(prev => prev.some(f => f.id === fac.id) ? prev : [processed, ...prev]);
      }
    }, []), // eslint-disable-line
  });

  // Cache de tabs cargados — evita re-fetchear al cambiar de tab si los filtros no cambiaron
  const loadedTabsRef = useRef(new Set());
  const lastFiltersRef = useRef(null);
  // Cache en memoria: se usan los Maps a nivel de módulo (_facturasCache, _presupuestosCache)
  // que sobreviven remounts. loadedTabsRef sigue siendo useRef (por instancia de componente).
  const invalidateVentasCache = () => {
    _facturasCache.clear();
    _presupuestosCache.clear();
    lsDelPrefix("ard_facturas_");
    lsDelPrefix("ard_presupuestos_");
    loadedTabsRef.current = new Set();
  };
	  const [showIngForm, setShowIngForm] = useState(false);  // modal nuevo ingreso
	  const [ingFormItem, setIngFormItem] = useState(null);   // ingreso a editar
	  const [showNotaForm, setShowNotaForm] = useState(false);
	  const [notaFormItem, setNotaFormItem] = useState(null);
  const [notaDoc, setNotaDoc] = useState(null); // modal documento nota de crédito

  // Presupuesto form/costos modals
  const [presFormItem, setPresFormItem] = useState(null); // { presupuesto, mode }
  const [presCostosItem, setPresCostosItem] = useState(null); // presupuesto object
  const [presFacturarItem, setPresFacturarItem] = useState(null); // presupuesto for facturar modal
  const [presAprobarId, setPresAprobarId] = useState(null);       // presupuesto abierto en el modal de aprobación (total/parcial)
  const [vincQty, setVincQty] = useState({});                      // cantidades que cubre una factura existente al vincularla (aprobación parcial)

  // Orden de Carga (Transporte) que se está "liquidando al cliente" — la
  // Factura se abre precargada (cliente + concepto "Flete: origen → destino" +
  // monto) y, al guardarse, se vincula de vuelta a la orden.
  const [ordenTransporte, setOrdenTransporte] = useState(null);

  // Pesaje de Báscula (dirección "venta") que se está facturando — la Factura
  // se abre precargada (cliente + concepto con el producto y el peso comercial
  // final en kg) y, al guardarse, se vincula de vuelta al pesaje.
  const [pesajeBascula, setPesajeBascula] = useState(null);

  useEffect(() => {
    const nuevo = searchParams.get("nuevo");
    if (!nuevo) return;
    if (nuevo === "presupuesto" && hasPermission("presupuestos.crear")) {
      setTab("presupuestos");
      setPresFormItem({ presupuesto: null, mode: "new" });
    } else if (nuevo === "factura" && hasPermission("facturas.crear")) {
      setTab("facturas");
      setFacFormItem(null);
      setShowFacForm(true);
    } else if (nuevo === "boleta" && hasPermission("facturas.crear")) {
      setTab("facturas");
      setFacFormItem(null);
      setFacSinFactura(true);
      setShowFacForm(true);
    } else if (nuevo === "ingreso" && hasPermission("ingresos_varios.crear")) {
      setTab("ingresos");
      setIngFormItem(null);
      setShowIngForm(true);
    } else if (nuevo === "nota_credito" && hasPermission("notas_credito.crear")) {
      setTab("notas");
      setNotaFormItem(null);
      setShowNotaForm(true);
    } else if (nuevo === "factura_transporte" && hasPermission("facturas.crear")) {
      const ordenIds = searchParams.get("orden_carga_ids") || searchParams.get("orden_carga_id");
      if (ordenIds) {
        fetch(`${API}/admin/transporte/ordenes/lote?ids=${encodeURIComponent(ordenIds)}`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => res.ok ? res.json() : null)
          .then(lista => {
            if (!lista || !lista.length) { toast.error("No se pudo cargar la orden de carga"); return; }
            setOrdenTransporte({ ...lista[0], ordenes: lista });
            setTab("facturas");
            setFacFormItem(null);
            setShowFacForm(true);
          })
          .catch(() => toast.error("No se pudo cargar la orden de carga"));
      }
    } else if (nuevo === "factura_bascula" && hasPermission("facturas.crear")) {
      const idsParam = searchParams.get("pesaje_ids") || searchParams.get("pesaje_id") || "";
      const ids = idsParam.split(",").map(x => x.trim()).filter(Boolean);
      if (ids.length) {
        Promise.all(ids.map(id => fetch(`${API}/admin/bascula/pesajes/${id}`, { headers: { Authorization: `Bearer ${token}` } }).then(res => res.ok ? res.json() : null)))
          .then(lista => {
            const ok = lista.filter(Boolean);
            if (!ok.length) { toast.error("No se pudo cargar el pesaje"); return; }
            if (ok.length < ids.length) toast.warning("Algunos pesajes no se pudieron cargar");
            setPesajeBascula({ ...ok[0], lista: ok });
            setTab("facturas");
            setFacFormItem(null);
            setShowFacForm(true);
          })
          .catch(() => toast.error("No se pudo cargar el pesaje"));
      }
    }
    const next = new URLSearchParams(searchParams);
    next.delete("nuevo");
    next.delete("orden_carga_id");
    next.delete("orden_carga_ids");
    next.delete("pesaje_id");
    next.delete("pesaje_ids");
    setSearchParams(next, { replace: true });
  }, [searchParams]); // eslint-disable-line

  // Precarga de la Factura cuando viene desde "Liquidación al cliente" de una
  // Orden de Carga (Transporte) — un solo concepto libre con el monto del flete.
  const facturaInicialTransporte = useMemo(() => {
    if (!ordenTransporte) return null;
    const lista = ordenTransporte.ordenes || [ordenTransporte];
    return {
      __source_id: `transporte-${lista.map(o => o.id).join("-")}`,
      moneda: ordenTransporte.moneda || "PYG",
      conceptos: lista.map(o => ({
        descripcion: `Flete ${o.numero || ""}: ${o.origen || ""} → ${o.destino || ""}`,
        cantidad: 1,
        precio_unitario: Number(o.monto_flete || 0),
        subtotal: Number(o.monto_flete || 0),
      })),
    };
  }, [ordenTransporte]);

  // Precarga de la Factura cuando viene desde "Facturar" de un Pesaje de
  // Báscula (dirección venta) — un ítem con el producto pesado y el peso
  // comercial final en kg como cantidad.
  const facturaInicialBascula = useMemo(() => {
    if (!pesajeBascula) return null;
    const lista = pesajeBascula.lista || [pesajeBascula];
    return {
      __source_id: `bascula-${lista.map(x => x.id).join("-")}`,
      conceptos: lista.map(pz => ({
        descripcion: pz.producto_nombre ? `${pz.producto_nombre} — Pesaje ${pz.numero}` : `Pesaje ${pz.numero}`,
        cantidad: Number(pz.peso_neto_final || 0),
        precio_unitario: 0,
        subtotal: 0,
      })),
    };
  }, [pesajeBascula]);

  // Precarga de la Factura cuando viene de "Crear factura" en el listado de
  // huecos de numeración (Configuración de Timbrado) — solo precarga número
  // y fecha sugerida (la del extremo inferior del rango válido); el backend
  // igual valida al guardar que la fecha quede entre la del número anterior
  // y la del siguiente (ver _validar_fecha_coherente_con_numero).
  const facturaInicialHueco = useMemo(() => {
    if (!huecoParaFactura) return null;
    return {
      __source_id: `hueco-${huecoParaFactura.numero}`,
      numero: huecoParaFactura.numero,
      punto_expedicion: huecoParaFactura.punto_expedicion || (huecoParaFactura.numero || "").split("-")[1] || undefined,
      fecha: huecoParaFactura.fecha_desde || huecoParaFactura.fecha_hasta || undefined,
    };
  }, [huecoParaFactura]);
  // Facturar modal state
  const [facturaForm, setFacturaForm] = useState({ numero: "", fecha: new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10), forma_pago: "contado", notas: "" });
  const [facturaMode, setFacturaMode] = useState("existente");
  const [facturaSearch, setFacturaSearch] = useState("");
  const [facturasDisponibles, setFacturasDisponibles] = useState([]);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [savingFactura, setSavingFactura] = useState(false);
  // Compras para vincular desde el modal Facturar/Vincular
  const [comprasDisponibles, setComprasDisponibles] = useState([]);
  const [compraSeleccionada, setCompraSeleccionada] = useState(null);
  const [compraSearch, setCompraSearch] = useState("");
  // Additional data for form
  const [empresas, setEmpresas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [productos, setProductos] = useState([]);
  const [planCuentas, setPlanCuentas] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (newBtnRef.current && !newBtnRef.current.contains(e.target)) setShowNew(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Helper buildQ ─────────────────────────────────────────────
  const buildQ = (params) => {
    const p = Object.entries(params).filter(([,v]) => v != null && v !== "").map(([k,v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    return p ? `?${p}` : "";
  };

  // ── Datos compartidos: empresas, proveedores, productos, cuentas, plan ──
  const fetchShared = async () => {
    try {
      const logoQc = logoFilter !== "todas" ? `?logo_tipo=${logoFilter}` : "";
      const entClientesQs = logoFilter !== "todas" ? `?es_cliente=true&logo_tipo=${logoFilter}` : `?es_cliente=true`;
      const [rEmp, rProv, rProd, rCuentas, rPlan] = await Promise.all([
        fetch(`${API}/admin/entidades${entClientesQs}`, { headers }),
        hasPermission("proveedores.ver") ? fetch(`${API}/admin/entidades?es_proveedor=true&activo=true`, { headers }) : Promise.resolve({ ok: true, json: async () => [] }),
        hasModule?.("productos_stock") ? fetch(`${API}/admin/productos${logoQc}`, { headers }) : Promise.resolve({ ok: true, json: async () => [] }),
        fetch(`${API}/admin/cuentas-bancarias${logoQc}`, { headers }),
        fetch(`${API}/admin/plan-cuentas${logoQc}`, { headers }),
      ]);
      if (rEmp.ok) setEmpresas(await rEmp.json());
      if (rProv.ok) setProveedores(await rProv.json());
      if (rProd.ok) { const d = await rProd.json(); setProductos(Array.isArray(d) ? d : []); }
      if (rCuentas.ok) setCuentasDisp(await rCuentas.json());
      if (rPlan.ok) setPlanCuentas(await rPlan.json());
    } catch (e) {
      console.error("Error al cargar datos compartidos", e);
    }
  };

  // ── Normalizar campos de fecha de una factura ─────────────────
  const processFactura = (f) => ({
    ...f,
    fecha: f.fecha ? f.fecha.slice(0, 10) : null,
    fecha_vencimiento: f.fecha_vencimiento ? f.fecha_vencimiento.slice(0, 10) : null,
    fecha_pago: f.fecha_pago ? f.fecha_pago.slice(0, 10) : null,
  });

  // ── Refrescar una sola factura (para después de pago/edición) ──
  const refreshSingleFactura = async (facId) => {
    try {
      const r = await fetch(`${API}/admin/facturas/${facId}`, { headers });
      if (!r.ok) return;
      const updated = processFactura(await r.json());
      setFacturas(prev => prev.map(f => f.id === facId ? updated : f));
      if (facDoc?.id === facId) setFacDoc(updated);
      invalidateVentasCache();
    } catch {}
  };

  // ── Fetch por tab ──────────────────────────────────────────────
  const fetchPresupuestos = async (invalidate = false) => {
    const mesParam = filtroTipo === "mes" ? mes : "";
    const anioParam = filtroTipo === "anio" ? anio : "";
    const desdeParam = filtroTipo === "dia" ? dia : "";
    const hastaParam = filtroTipo === "dia" ? dia : "";
    const cacheKey = `${logoFilter}|${mesParam}|${anioParam}|${desdeParam}|${mostrarEliminadas.presupuestos}`;
    const lsKey = `ard_presupuestos_${cacheKey}`;

    const doFetch = async () => {
      const r = hasPermission("presupuestos.ver")
        ? await fetch(`${API}/admin/presupuestos${buildQ({ mes: mesParam || null, anio: anioParam || null, desde: desdeParam || null, hasta: hastaParam || null, logo_tipo: logoFilter !== "todas" ? logoFilter : null, incluir_eliminadas: mostrarEliminadas.presupuestos ? "true" : null })}`, { headers })
        : { ok: true, json: async () => [] };
      if (!r.ok) { setPresupuestos([]); return; }
      const data = await r.json();
      const filtered = mostrarEliminadas.presupuestos ? data.filter(x => x.eliminada) : data.filter(x => !x.eliminada);
      _presupuestosCache.set(cacheKey, filtered);
      lsSet(lsKey, filtered);
      setPresupuestos(filtered);
    };

    // 1. In-memory cache (sesión actual)
    if (!invalidate && _presupuestosCache.has(cacheKey)) {
      setPresupuestos(_presupuestosCache.get(cacheKey));
      setLoading(false);
      return;
    }

    // 2. localStorage (visitas de retorno) — display inmediato + revalidate background
    if (!invalidate) {
      const lsData = lsGet(lsKey);
      if (lsData) {
        setPresupuestos(lsData);
        _presupuestosCache.set(cacheKey, lsData);
        setLoading(false);
        doFetch().catch(() => {});
        return;
      }
    } else {
      _presupuestosCache.clear();
      lsDelPrefix("ard_presupuestos_");
      lsDel(lsKey);
    }

    // 3. Full fetch (sin cache)
    setLoading(true);
    try { await doFetch(); }
    catch (e) { toast.error("Error al cargar presupuestos"); setPresupuestos([]); }
    setLoading(false);
  };

  const fetchRecepciones = async () => {
    if (!hasPermission("presupuestos.recepcion_ver")) { setRecepciones([]); return; }
    try {
      const params = new URLSearchParams();
      if (logoFilter !== "todas") params.set("logo_tipo", logoFilter);
      const r = await fetch(`${API}/admin/presupuestos/recepcion?${params.toString()}`, { headers });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "No se pudo cargar la lista de recepciones de equipo");
        setRecepciones([]);
        return;
      }
      setRecepciones(await r.json());
    } catch (e) {
      toast.error("Error al cargar recepciones de equipo");
      setRecepciones([]);
    }
  };

  const fetchTiposFac = async () => {
    if (!hasModule?.("tipos_factura")) return;
    try {
      const logo = activeEmpresaPropia?.slug || "";
      const r = await fetch(`${API}/admin/tipos-factura${logo ? `?logo_tipo=${logo}` : ""}`, { headers });
      if (r.ok) setTiposFac(await r.json());
    } catch {}
  };

  const fetchFacturas = async (invalidate = false) => {
    const mesParam = filtroTipo === "mes" ? mes : "";
    const desdeParam = filtroTipo === "dia" ? dia : "";
    const hastaParam = filtroTipo === "dia" ? dia : "";
    const cacheKey = `${logoFilter}|${mesParam}|${desdeParam}|${mostrarEliminadas.facturas}`;
    const lsKey = `ard_facturas_${cacheKey}`;

    const doFetch = async () => {
      const r = await fetch(`${API}/admin/facturas${buildQ({ mes: mesParam || null, desde: desdeParam || null, hasta: hastaParam || null, logo_tipo: logoFilter !== "todas" ? logoFilter : null, incluir_eliminadas: mostrarEliminadas.facturas ? "true" : null })}`, { headers });
      if (!r.ok) return;
      const data = await r.json();
      const visibles = mostrarEliminadas.facturas ? data.filter(f => f.eliminada) : data.filter(f => !f.eliminada);
      const processed = visibles.map(processFactura);
      _facturasCache.set(cacheKey, processed);
      lsSet(lsKey, processed);
      setFacturas(processed);
    };

    // 1. In-memory cache (sesión actual)
    if (!invalidate && _facturasCache.has(cacheKey)) {
      setFacturas(_facturasCache.get(cacheKey));
      setLoading(false);
      return;
    }

    // 2. localStorage (visitas de retorno) — display inmediato + revalidate background
    if (!invalidate) {
      const lsData = lsGet(lsKey);
      if (lsData) {
        setFacturas(lsData);
        _facturasCache.set(cacheKey, lsData);
        setLoading(false);
        doFetch().catch(() => {});
        return;
      }
    } else {
      _facturasCache.clear();
      lsDelPrefix("ard_facturas_");
      lsDel(lsKey);
    }

    // 3. Full fetch (sin cache)
    setLoading(true);
    try { await doFetch(); }
    catch (e) { toast.error("Error al cargar facturas"); }
    setLoading(false);
  };

  const fetchIngresos = async () => {
    setLoading(true);
    try {
      const mesParam = filtroTipo === "mes" ? mes : "";
      const desdeParam = filtroTipo === "dia" ? dia : "";
      const hastaParam = filtroTipo === "dia" ? dia : "";
      const r = await fetch(`${API}/admin/ingresos-varios${buildQ({ mes: mesParam || null, desde: desdeParam || null, hasta: hastaParam || null, logo_tipo: logoFilter !== "todas" ? logoFilter : null, incluir_eliminadas: mostrarEliminadas.ingresos ? "true" : null })}`, { headers });
      if (r.ok) { const dIng = await r.json(); const arr = Array.isArray(dIng) ? dIng : []; setIngresos(mostrarEliminadas.ingresos ? arr.filter(x => x.eliminada) : arr.filter(x => !x.eliminada)); }
    } catch (e) { toast.error("Error al cargar ingresos"); }
    setLoading(false);
  };

  const fetchRecibos = async () => {
    setLoading(true);
    try {
      const mesParam = filtroTipo === "mes" ? mes : "";
      const desdeParam = filtroTipo === "dia" ? dia : "";
      const hastaParam = filtroTipo === "dia" ? dia : "";
      const r = await fetch(`${API}/admin/recibos${buildQ({ mes: mesParam || null, desde: desdeParam || null, hasta: hastaParam || null, logo_tipo: logoFilter !== "todas" ? logoFilter : null, incluir_eliminadas: mostrarEliminadas.recibos ? "true" : null })}`, { headers });
      if (r.ok) { const data = await r.json(); setRecibos(mostrarEliminadas.recibos ? data.filter(x => x.eliminada) : data.filter(x => !x.eliminada)); }
    } catch (e) { toast.error("Error al cargar recibos"); }
    setLoading(false);
  };

  const fetchNotas = async () => {
    setLoading(true);
    try {
      const mesParam = filtroTipo === "mes" ? mes : "";
      const desdeParam = filtroTipo === "dia" ? dia : "";
      const hastaParam = filtroTipo === "dia" ? dia : "";
      if (!hasPermission("notas_credito.ver")) { setNotasCredito([]); setLoading(false); return; }
      const r = await fetch(`${API}/admin/notas-credito${buildQ({ tipo: "venta", mes: mesParam || null, desde: desdeParam || null, hasta: hastaParam || null, logo_tipo: logoFilter !== "todas" ? logoFilter : null, incluir_eliminadas: mostrarEliminadas.notas ? "true" : null })}`, { headers });
      if (r.ok) { const data = await r.json(); setNotasCredito(mostrarEliminadas.notas ? data.filter(x => x.eliminada) : data.filter(x => !x.eliminada)); }
    } catch (e) { toast.error("Error al cargar notas de crédito"); }
    setLoading(false);
  };

  // ── Helper: recargar el tab activo forzando re-fetch (después de CRUD) ──
  const fetchActiveTab = () => {
    invalidateVentasCache();
    if (tab === "presupuestos") fetchPresupuestos(true);
    else if (tab === "recepcion") fetchRecepciones();
    else if (tab === "facturas") fetchFacturas(true);
    else if (tab === "ingresos") fetchIngresos();
    else if (tab === "recibos") fetchRecibos();
    else if (tab === "notas") fetchNotas();
  };

  const handleRefreshManual = () => {
    fetchActiveTab();
    if (tab === "notas") fetchFacturas(true);
    if (tab === "configuracion") {
      fetchTiposFac();
      fetchRecibosConfig();
      fetchImpresionConfig();
      if (hasPermission("configuracion_ventas.gestionar_timbrado")) fetchTimbradoConfig();
    }
  };

  // ── Cargar datos compartidos al cambiar empresa ────────────────
  useEffect(() => { fetchShared(); }, [activeEmpresaPropia?.slug]); // eslint-disable-line

  // ── Aviso: licencias pendientes de facturar en esta empresa (emisora) ──
  const [licenciasPendientesFacturar, setLicenciasPendientesFacturar] = useState([]);
  const [showAvisoLicencias, setShowAvisoLicencias] = useState(false);
  // Histórico / pendientes de avisos (persistido en servidor)
  const [avisosLic, setAvisosLic] = useState({ avisos: [], pendientes: [], total_pendientes: 0 });
  const [showAvisosHist, setShowAvisosHist] = useState(false);
  const [avisosTab, setAvisosTab] = useState("pendientes");

  const fetchAvisosLic = useCallback(async () => {
    const slug = activeEmpresaPropia?.slug;
    if (!slug || !token || !isVentasPrivileged) return;
    try {
      const r = await fetch(`${API}/admin/licencias/avisos?logo_tipo=${encodeURIComponent(slug)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setAvisosLic(await r.json());
    } catch (e) { /* silencioso */ }
  }, [activeEmpresaPropia?.slug, token, isVentasPrivileged]);

  // El aviso aparece SIEMPRE al entrar a Ventas/Facturación (sin descarte persistente).
  useEffect(() => {
    const slug = activeEmpresaPropia?.slug;
    if (!slug || !token || !isVentasPrivileged) { setLicenciasPendientesFacturar([]); return; }
    let alive = true;
    fetch(`${API}/admin/licencias/alertas-facturacion?logo_tipo=${encodeURIComponent(slug)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { pendientes: [] })
      .then(data => {
        if (!alive) return;
        const pendientes = data.pendientes || [];
        setLicenciasPendientesFacturar(pendientes);
        if (pendientes.length > 0) setShowAvisoLicencias(true);
        fetchAvisosLic(); // el servidor ya registró los avisos
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [activeEmpresaPropia?.slug, token, isVentasPrivileged]); // eslint-disable-line

  const handleDismissAvisoLicencias = () => setShowAvisoLicencias(false);

  const atenderAvisoLic = async (id) => {
    try {
      const r = await fetch(`${API}/admin/licencias/avisos/${id}/atender`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      fetchAvisosLic();
    } catch (e) { toast.error("No se pudo marcar el aviso como atendido"); }
  };

  // ── Cargar tab activo cuando cambia tab/filtros/empresa ───────
  useEffect(() => {
    // Esperar que AuthContext termine de cargar empresas propias
    if (authLoading) return;
    // Clave de filtros activos — si cambia, invalidar caché de todos los tabs
    const filtersKey = `${dia}|${mes}|${filtroTipo}|${anio}|${JSON.stringify(mostrarEliminadas)}|${activeEmpresaPropia?.slug}|${logoFilter}`;
    if (lastFiltersRef.current !== filtersKey) {
      loadedTabsRef.current = new Set();
      lastFiltersRef.current = filtersKey;
    }

    // Si el tab ya fue cargado con estos filtros, no re-fetchear
    if (loadedTabsRef.current.has(tab)) return;
    loadedTabsRef.current.add(tab);

    if (tab === "presupuestos") {
      fetchPresupuestos();
    } else if (tab === "recepcion") {
      fetchRecepciones();
    } else if (tab === "facturas") {
      fetchFacturas();
      // También traemos presupuestos acá (no solo en su propio tab): el listado de
      // facturas muestra un selector con el número de cada presupuesto vinculado,
      // y sin esto quedaba vacío/con el id crudo si nunca se visitó el tab Presupuestos.
      fetchPresupuestos();
    } else if (tab === "ingresos") fetchIngresos();
    else if (tab === "recibos") fetchRecibos();
    else if (tab === "notas") { fetchNotas(); fetchFacturas(); }
    else if (tab === "configuracion") { fetchTiposFac(); fetchRecibosConfig(); fetchImpresionConfig(); if (hasPermission("configuracion_ventas.gestionar_timbrado")) fetchTimbradoConfig(); }
  }, [tab, dia, mes, filtroTipo, anio, mostrarEliminadas, activeEmpresaPropia?.slug, logoFilter, authLoading]); // eslint-disable-line

  // El botón "TIPO" en la vista previa de una factura (FacturaDocModal) depende de
  // tiposFac ya cargado — pero antes solo se cargaba al visitar la pestaña
  // "Configuración". Si se abre una factura por cualquier otro camino sin haber
  // pasado por ahí (ej: desde un ticket de Soporte u otro enlace directo), tiposFac
  // quedaba vacío y el botón directamente no se dibujaba, aunque la empresa sí
  // tuviera tipos configurados. Por eso el bug era intermitente. Lo cargamos acá,
  // independiente de qué pestaña esté activa, apenas se sabe la empresa activa.
  useEffect(() => {
    if (authLoading) return;
    fetchTiposFac();
  }, [activeEmpresaPropia?.slug, authLoading]); // eslint-disable-line

  const visibleTabs = TABS.filter(t => {
    if (t.id === "configuracion") return isAdmin || hasPermission("configuracion_ventas.gestionar_descuentos") || hasPermission("tipos_factura.gestionar") || hasPermission("configuracion_ventas.gestionar_recibos") || hasPermission("configuracion_ventas.gestionar_timbrado") || hasPermission("configuracion_ventas.gestionar_impresion") || hasPermission("configuracion_ventas.gestionar_cobro_contado") || hasPermission("configuracion_ventas.gestionar_delivery") || hasPermission("vendedores.ver");
    return hasPermission(TAB_PERMISOS[t.id]);
  });
  const deletePermByTab = {
    presupuestos: "presupuestos.eliminar",
    facturas: "facturas.eliminar",
    ingresos: "ingresos_varios.eliminar",
    recibos: "recibos.eliminar",
    notas: "notas_credito.eliminar",
  };
  const deletedToggle = canViewDeleted(deletePermByTab[tab]) ? (
    <button
      onClick={() => setMostrarEliminadas(prev => ({ ...prev, [tab]: !prev[tab] }))}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all font-body whitespace-nowrap ${mostrarEliminadas[tab] ? "bg-red-500/20 border-red-500/40 text-red-300" : "border-white/10 text-slate-400 hover:text-white hover:bg-white/5"}`}
    >
      <Trash2 className="w-4 h-4" /> {mostrarEliminadas[tab] ? "Ocultar eliminadas" : "Ver eliminadas"}
    </button>
  ) : null;
  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.some(t => t.id === tab)) {
      setTab(visibleTabs[0].id);
    }
  }, [activeEmpresaPropia?.id, user?.permisos, tab]); // eslint-disable-line



  // ── Al entrar con ?empresa=<id> desde Clientes, pre-llenar el buscador chip ──
  // (ruta vieja era /sistema/presupuestos?empresa=X; ahora es /sistema/ventas?tab=presupuestos&empresa=X
  //  y también /sistema/presupuestos?empresa=X sigue funcionando porque apunta a este mismo componente)
  useEffect(() => {
    const empresaId = searchParams.get("empresa");
    if (!empresaId || empresas.length === 0) return;
    const emp = empresas.find(e => e.id === empresaId);
    if (!emp) return;
    // Colocar el nombre del cliente como chip de búsqueda si no está ya
    setPresChips(prev => (prev.includes(emp.nombre) ? prev : [...prev, emp.nombre]));
    setTab("presupuestos");
    // Limpiar el param para que recargas manuales no lo vuelvan a poner dos veces
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("empresa");
    setSearchParams(nextParams, { replace: true });
    // eslint-disable-next-line
  }, [empresas]);

  // ── Cambiar estado de presupuesto/factura inline ──
  const PRESUP_ESTADOS = ["aprobado", "rechazado"];
  const FACT_ESTADOS   = ["pendiente", "pagada", "anulada"];

  const cambiarEstadoPresupuesto = async (id, estado) => {
    try {
      const res = await fetch(`${API}/admin/presupuestos/${id}/estado?estado=${estado}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (res.ok) {
        toast.success(`Estado actualizado: ${estado}`);
        setPresupuestos(list => list.map(p => p.id === id ? { ...p, estado } : p));
      } else {
        toast.error("No se pudo cambiar el estado");
      }
    } catch (e) { toast.error("Error de red"); }
  };

  const cambiarEstadoFactura = async (id, estado) => {
    try {
      const res = await fetch(`${API}/admin/facturas/${id}/estado?estado=${estado}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (res.ok) {
        toast.success(`Factura ${estado}`);
        setFacturas(list => list.map(f => f.id === id ? { ...f, estado } : f));
      } else {
        toast.error("No se pudo cambiar el estado");
      }
    } catch (e) { toast.error("Error de red"); }
  };

  // Load facturas + compras when facturar modal opens
  useEffect(() => {
    if (presFacturarItem) {
      setFacturaMode("existente");
      setFacturaSearch("");
      setFacturaSeleccionada(null);
      setCompraSeleccionada(null);
      setCompraSearch("");
      setVincQty(Object.fromEntries((presFacturarItem.items || []).map((it, i) => [i, String(it.pendiente_facturar ?? 0)])));
      setFacturaForm({ numero: "", fecha: new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10), forma_pago: "contado", notas: "" });
      const empId = presFacturarItem.empresa_id ? `&empresa_id=${presFacturarItem.empresa_id}` : "";
      fetch(`${API}/admin/facturas?tipo=emitida${empId}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(data => setFacturasDisponibles(data))
        .catch(() => setFacturasDisponibles([]));
      fetch(`${API}/admin/compras`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(data => setComprasDisponibles(Array.isArray(data) ? data : []))
        .catch(() => setComprasDisponibles([]));
    }
  }, [presFacturarItem]); // eslint-disable-line

  const handleFacturar = async () => {
    if (!facturaForm.numero.trim()) { toast.error("El número de factura es requerido"); return; }
    const presupuesto = presFacturarItem;
    setSavingFactura(true);
    try {
      const facturaPayload = {
        logo_tipo: presupuesto.logo_tipo,
        tipo: "emitida",
        forma_pago: facturaForm.forma_pago,
        numero: facturaForm.numero,
        fecha: facturaForm.fecha,
        razon_social: presupuesto.empresa_nombre || "",
        ruc: presupuesto.empresa_ruc || "",
        concepto: presupuesto.nombre_archivo ? `${presupuesto.numero} - ${presupuesto.nombre_archivo}` : `Presupuesto ${presupuesto.numero}`,
        conceptos: (presupuesto.items || []).map(item => ({
          descripcion: item.descripcion || "",
          cantidad: item.cantidad || 1,
          producto_id: item.producto_id || null,
          producto_sku: item.producto_sku || null,
          variante_id: item.variante_id || null,
          variante_texto: item.variante_texto || null,
          precio_unitario: item.precio_unitario || 0,
          descuento_pct: item.descuento_pct || 0,
          descuento_monto: item.descuento_monto || 0,
          subtotal: item.subtotal || ((item.precio_unitario || 0) * (item.cantidad || 1) * (1 - ((item.descuento_pct || 0) / 100))),
          monto: item.subtotal || ((item.precio_unitario || 0) * (item.cantidad || 1) * (1 - ((item.descuento_pct || 0) / 100))),
          iva_tipo: item.iva_tipo || "10",
          observacion: item.observacion || null,
        })),
        monto: presupuesto.total,
        moneda: presupuesto.moneda,
        tipo_cambio: presupuesto.tipo_cambio || null,
        modo: (presupuesto.items || []).some(item => item.producto_id) ? "productos" : (presupuesto.modo || "libre"),
        afecta_stock: (presupuesto.items || []).some(item => item.producto_id),
        estado: "pendiente",
        notas: facturaForm.notas || null,
        presupuesto_ids: [presupuesto.id],
        presupuesto_id: presupuesto.id,
      };
      const resFactura = await fetch(`${API}/admin/facturas`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(facturaPayload)
      });
      if (!resFactura.ok) { toast.error("Error al crear la factura"); setSavingFactura(false); return; }
      const yaFacturado = presupuesto.estado === "facturado" || presupuesto.estado === "cobrado";
      if (!yaFacturado) {
        await fetch(`${API}/admin/presupuestos/${presupuesto.id}/estado?estado=facturado`, {
          method: "PUT", headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success("Factura creada");
      setPresFacturarItem(null);
      fetchActiveTab();
    } catch { toast.error("Error al facturar"); }
    finally { setSavingFactura(false); }
  };

  const handleVincularExistente = async () => {
    if (!facturaSeleccionada) { toast.error("Seleccioná una factura"); return; }
    const presupuesto = presFacturarItem;
    setSavingFactura(true);
    try {
      const idsActuales = facturaSeleccionada.presupuesto_ids?.length ? facturaSeleccionada.presupuesto_ids : (facturaSeleccionada.presupuesto_id ? [facturaSeleccionada.presupuesto_id] : []);
      const nuevosIds = idsActuales.includes(presupuesto.id) ? idsActuales : [...idsActuales, presupuesto.id];
      const res = await fetch(`${API}/admin/facturas/${facturaSeleccionada.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...facturaSeleccionada, presupuesto_ids: nuevosIds, presupuesto_id: nuevosIds.length === 1 ? nuevosIds[0] : facturaSeleccionada.presupuesto_id }),
      });
      if (!res.ok) { toast.error("Error al vincular factura"); setSavingFactura(false); return; }
      if ((presupuesto.aprobaciones || []).length > 0) {
        // aprobación parcial: dejar registrado qué cantidades de lo aprobado cubre esta factura
        const resV = await fetch(`${API}/admin/presupuestos/${presupuesto.id}/vinculo-factura`, {
          method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ factura_id: facturaSeleccionada.id, items: (presupuesto.items || []).map((_, i) => ({ idx: i, cantidad: Math.max(0, Number(vincQty[i]) || 0) })) }),
        });
        if (!resV.ok) toast.error("La factura se vinculó pero no se pudo registrar el detalle por renglón");
      }
      const yaFacturado = presupuesto.estado === "facturado" || presupuesto.estado === "cobrado";
      if (!yaFacturado) {
        await fetch(`${API}/admin/presupuestos/${presupuesto.id}/estado?estado=facturado`, {
          method: "PUT", headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success("Factura vinculada");
      setPresFacturarItem(null);
      fetchActiveTab();
    } catch { toast.error("Error al vincular"); }
    finally { setSavingFactura(false); }
  };

  const handleVincularCompra = async () => {
    if (!compraSeleccionada) { toast.error("Seleccioná una compra"); return; }
    const presupuesto = presFacturarItem;
    setSavingFactura(true);
    try {
      const res = await fetch(`${API}/admin/compras/${compraSeleccionada.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...compraSeleccionada, presupuesto_id: presupuesto.id }),
      });
      if (!res.ok) { toast.error("Error al vincular compra"); setSavingFactura(false); return; }
      toast.success("Compra vinculada al presupuesto");
      setPresFacturarItem(null);
      fetchActiveTab();
    } catch { toast.error("Error al vincular compra"); }
    finally { setSavingFactura(false); }
  };

  const handleDesvincularFactura = async (facturaId) => {
    const presupuesto = presFacturarItem;
    try {
      const res = await fetch(`${API}/admin/presupuestos/${presupuesto.id}/facturas/${facturaId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error("Error al desvincular"); return; }
      toast.success("Factura desvinculada");
      // Refrescar lista de facturas en el modal
      const empId = presupuesto.empresa_id ? `&empresa_id=${presupuesto.empresa_id}` : "";
      const data = await fetch(`${API}/admin/facturas?tipo=emitida${empId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : []);
      setFacturasDisponibles(data);
      fetchActiveTab();
    } catch { toast.error("Error al desvincular"); }
  };

  // ── Abrir factura doc view ────────────────────────────────────
  const openFacDoc = async (id) => {
    // Fetchear documento completo (incluye conceptos para imprimir/editar)
    setFacDocLoading(true);
    try {
      const r = await fetch(`${API}/admin/facturas/${id}`, { headers });
      if (r.ok) {
        const full = await r.json();
        setFacDoc({
          ...full,
          fecha: full.fecha ? full.fecha.slice(0, 10) : null,
          fecha_vencimiento: full.fecha_vencimiento ? full.fecha_vencimiento.slice(0, 10) : null,
          fecha_pago: full.fecha_pago ? full.fecha_pago.slice(0, 10) : null,
        });
      } else {
        toast.error("Factura no encontrada — recargue la página");
      }
    } catch (e) { toast.error("Error al cargar factura"); }
    setFacDocLoading(false);
  };

  // ── Factura: delete ──────────────────────────────────────────
  const handleDeleteFac = async (fac) => {
    if (!window.confirm(`¿Eliminar factura ${fac.numero}?`)) return;
    try {
      const res = await fetch(`${API}/admin/facturas/${fac.id}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error(await readApiError(res, "Error al eliminar"));
      toast.success("Factura eliminada");
      if (facDoc?.id === fac.id) setFacDoc(null);
      setFacturas(prev => prev.filter(f => f.id !== fac.id));
      invalidateVentasCache();
    } catch (e) { toast.error(e.message || "Error al eliminar"); }
  };

  // ── Factura: deshacer pago ───────────────────────────────────
  const handleDeshacerFac = async (fac) => {
    if (!window.confirm("¿Deshacer el pago? Se eliminarán todos los pagos y recibos registrados de esta factura.")) return;
    try {
      const pagos = fac.pagos || [];
      if (pagos.length > 0) {
        // Eliminar cada pago (y su recibo vinculado) uno por uno
        for (const pago of pagos) {
          await fetch(`${API}/admin/facturas/${fac.id}/pago/${pago.id}`, { method: "DELETE", headers });
        }
      } else {
        // Factura sin array pagos (legacy) — solo cambiar estado
        await fetch(`${API}/admin/facturas/${fac.id}/estado?estado=pendiente`, { method: "PATCH", headers });
      }
      toast.success("Pago deshecho");
      await refreshSingleFactura(fac.id);
    } catch { toast.error("Error"); }
  };

  // ── Factura: anular (pide motivo con confirmación explícita) ──
  const handleAnularFac = (fac) => {
    setAnularFacTarget(fac);
    setAnularFacMotivo("");
  };

  const confirmarAnularFac = async () => {
    const fac = anularFacTarget;
    if (!fac) return;
    const motivo = anularFacMotivo.trim();
    if (!motivo) { toast.error("Escribí el motivo de la anulación"); return; }
    setAnulandoFac(true);
    try {
      const res = await fetch(`${API}/admin/facturas/${fac.id}/estado?estado=anulada&motivo=${encodeURIComponent(motivo)}`, { method: "PATCH", headers });
      if (!res.ok) throw new Error(await readApiError(res, "Error al anular"));
      toast.success("Factura anulada");
      await refreshSingleFactura(fac.id);
      setAnularFacTarget(null);
      setAnularFacMotivo("");
    } catch (e) { toast.error(e.message || "Error al anular"); }
    finally { setAnulandoFac(false); }
  };

  // ── Fetch saldo a favor del cliente para el modal de pago ────
  const fetchSaldoFavor = async (fac) => {
    setSaldoFavorFac(null);
    setAplicarSaldoFavor(false);
    setMontoSaldoAplicar("");
    if (!fac?.empresa_id) return;
    try {
      const params = new URLSearchParams({ logo_tipo: fac.logo_tipo || activeEmpresaPropia?.slug || "", moneda: fac.moneda || "PYG" });
      const res = await fetch(`${API}/admin/saldos-favor/empresa/${fac.empresa_id}?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        const hayDisponible = (data.saldos || []).some(s => s.monto_disponible > 0);
        if (hayDisponible) {
          setSaldoFavorFac(data);
          // Pre-llenar con el monto menor entre saldo disponible y saldo pendiente de la factura
          const saldoMoneda = (data.saldos || []).find(s => s.moneda === (fac.moneda || "PYG"));
          if (saldoMoneda) {
            const pendiente = (fac.monto || 0) - (fac.monto_pagado || 0);
            setMontoSaldoAplicar(String(Math.min(saldoMoneda.monto_disponible, pendiente)));
          }
        }
      }
    } catch {}
  };

  // ── Fetch vale(s) de cliente disponibles para el modal de pago ──
  // Sistema separado del saldo a favor (a propósito, ver Ingresos varios).
  const fetchVale = async (fac) => {
    setValeFac(null);
    setAplicarVale(false);
    setMontoValeAplicar("");
    if (!fac?.empresa_id) return;
    try {
      const res = await fetch(`${API}/admin/vales-clientes/empresa/${fac.empresa_id}`, { headers });
      if (res.ok) {
        const data = await res.json();
        const vales = (Array.isArray(data) ? data : []).filter(v => (v.moneda || "PYG") === (fac.moneda || "PYG") && v.monto_disponible > 0);
        if (vales.length) {
          const totalDisponible = vales.reduce((s, v) => s + (v.monto_disponible || 0), 0);
          setValeFac({ vales, totalDisponible });
          const pendiente = (fac.monto || 0) - (fac.monto_pagado || 0);
          setMontoValeAplicar(String(Math.min(totalDisponible, pendiente)));
        }
      }
    } catch {}
  };

  // ── Pago contado: abre modal ─────────────────────────────────
  const openPagoContado = (fac) => {
    setPagoFac(fac);
    setPagoItemsMonto(null);
    setFechaPago(fac.fecha || new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));
    setNumeroReciboManual("");
    // Pre-seleccionar cuenta predeterminada
    const def = cuentasDisp.find(c => c.logo_tipo === fac.logo_tipo && c.moneda === fac.moneda && c.es_predeterminada)
      || cuentasDisp.find(c => c.logo_tipo === fac.logo_tipo && c.moneda === fac.moneda);
    setCuentasPYG(def ? [def.id] : []);
    setCuentasUSD([]);
    setCuentasBRL([]);
    setTcPago("");
    setMontoUSD("");
    // Auto-fill TC desde cotizaciones si la factura es en moneda extranjera
    if (fac.moneda && fac.moneda !== "PYG") {
      const fecha = fac.fecha?.slice(0, 10) || new Date().toISOString().slice(0, 10);
      fetchTcDelDia(fac.moneda, fecha).then(tc => { if (tc) setTcPago(String(tc)); });
    }
    setMedioCobro({ procesadora_id: "", metodo_cobro: "" });
    setSinRetencionPago(false);
    setShowPagoModal(true);
    fetchSaldoFavor(fac);
    fetchVale(fac);
  };

  // ── Pago parcial crédito ─────────────────────────────────────
  const openPagoParcial = (fac, cuotasYaCargadas = null) => {
    setPagoParcialFac(fac);
    setPagoItemsMonto(null);
    setPagoParcialCuotaIds(new Set());
    const pendiente = (fac.monto || 0) - (fac.monto_pagado || 0);
    setMontoParcial(String(pendiente > 0 ? pendiente : fac.monto));
    setFechaPagoParcial(new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));
    setNumeroReciboManual("");
    const def = cuentasDisp.find(c => c.logo_tipo === fac.logo_tipo && c.moneda === fac.moneda && c.es_predeterminada)
      || cuentasDisp.find(c => c.logo_tipo === fac.logo_tipo && c.moneda === fac.moneda);
    setCuentasPYG(def ? [def.id] : []);
    setCuentasUSD([]);
    setCuentasBRL([]);
    setTcPago("");
    setMontoUSD("");
    // Auto-fill TC desde cotizaciones si la factura es en moneda extranjera
    if (fac.moneda && fac.moneda !== "PYG") {
      const fecha = fac.fecha?.slice(0, 10) || new Date().toISOString().slice(0, 10);
      fetchTcDelDia(fac.moneda, fecha).then(tc => { if (tc) setTcPago(String(tc)); });
    }
    // Cargar cuotas: usar las ya cargadas si vienen del modal, o fetchear si viene de la lista
    if (cuotasYaCargadas !== null) {
      setPagoParcialCuotas(cuotasYaCargadas);
    } else if (fac.forma_pago === "credito" || fac.es_credito_cuotas) {
      fetch(`${API}/admin/cuotas?doc_id=${fac.id}&limit=60`, { headers })
        .then(r => r.ok ? r.json() : [])
        .then(data => setPagoParcialCuotas(Array.isArray(data) ? data : (data.items || [])))
        .catch(() => setPagoParcialCuotas([]));
    } else {
      setPagoParcialCuotas([]);
    }
    setDescuentoPagoParcial(""); setMedioCobro({ procesadora_id: "", metodo_cobro: "" }); setSinRetencionPago(false); setShowPagoParcialModal(true);
    fetchSaldoFavor(fac);
    fetchVale(fac);
  };

  // Construir cuenta_id para pago (simplificado: primera cuenta PYG, si hay USD/BRL la consideramos)
  const getCuentaIdPago = (fac) => {
    // Selección única: o hay cuenta en la moneda de la factura (cuentasPYG) o una
    // cuenta de otra moneda (cuentasUSD) — nunca las dos a la vez.
    return cuentasUSD[0] || cuentasPYG[0] || null;
  };

  // Detalle del vuelto para el pago: en qué moneda se recibió/da y desde qué cuenta.
  const detalleVuelto = (tcFinal, cuentaCobroId, montoRecibidoPYG, vueltoPYG) => {
    if (!vueltoPYG || vueltoPYG <= 0) return {};
    const cta = cuentasDisp.find(c => c.id === cuentaCobroId);
    const monedaCobro = tcFinal && cta ? (cta.moneda || "PYG") : "PYG";
    const div = tcFinal && monedaCobro !== "PYG" ? tcFinal : 1;
    return {
      vuelto_cuenta_id: pagoVueltoCuenta || cuentaCobroId || undefined,
      vuelto_moneda: monedaCobro,
      vuelto_monto_moneda: Math.round((vueltoPYG / div) * 100) / 100,
      monto_recibido_moneda: montoRecibidoPYG ? Math.round((montoRecibidoPYG / div) * 100) / 100 : undefined,
    };
  };

  // ── Confirmar pago total (contado) ───────────────────────────
  const handlePagarContado = async () => {
    if (!fechaPago) { toast.error("Fecha requerida"); return; }
    try {
      const montoPendienteTotal = pagoItemsMonto !== null
        ? pagoItemsMonto
        : ((pagoFac.monto || 0) - (pagoFac.monto_pagado || 0));
      // Vale de cliente: si se aplica, reduce lo que hay que cobrar en efectivo/banco
      // (a diferencia del saldo a favor, esto SÍ se descuenta antes de calcular el pago en efectivo).
      const valeAplicarMonto = (aplicarVale && valeFac?.vales?.length && parseFloat(montoValeAplicar) > 0)
        ? Math.min(parseFloat(montoValeAplicar), montoPendienteTotal)
        : 0;
      const montoPendiente = montoPendienteTotal - valeAplicarMonto;
      const tcFinal = tcPago ? parseFloat(tcPago) : null;
      const montoRecibNum = pagoMontoRecibido ? parseFloat(pagoMontoRecibido) : null;
      // Retención de IVA: proporcional al monto de ESTE pago (no a la factura entera).
      const retEnEstePago = sinRetencionPago ? 0 : retencionProporcional(pagoFac, montoPendiente);
      // Lo que realmente debería entrar en efectivo/banco (el cliente se queda con la retención)
      const netoEsperado = montoPendiente - retEnEstePago;
      // Si el monto recibido cubre el neto esperado (o no se especificó) → factura queda saldada
      // completa (el "resto" es la retención, no plata pendiente de cobrar).
      // Si el monto recibido es MENOR al neto esperado → pago parcial real: se registra lo
      // efectivamente cobrado más la retención que ya se descontó de eso.
      const esPagoParcialReal = montoRecibNum != null && montoRecibNum < netoEsperado;
      const montoReal = esPagoParcialReal ? brutoParaNeto(pagoFac, montoRecibNum) : montoPendiente;
      const vueltoNum = montoRecibNum && montoRecibNum > netoEsperado ? montoRecibNum - netoEsperado : null;
      let data = null;
      if (montoReal > 0) {
        const res = await fetch(`${API}/admin/facturas/${pagoFac.id}/pago-parcial`, {
          method: "PATCH",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({
            monto_pagado: montoReal,
            sin_retencion: sinRetencionPago || undefined,
            procesadora_id: medioCobro.procesadora_id || undefined,
            metodo_cobro: medioCobro.metodo_cobro || undefined,
            fecha_pago: fechaPago,
            numero_recibo: numeroReciboManual || null,
            cuenta_id: getCuentaIdPago(pagoFac),
            tipo_cambio: tcFinal,
            monto_recibido: montoRecibNum,
            vuelto: vueltoNum,
            ...detalleVuelto(tcFinal, getCuentaIdPago(pagoFac), montoRecibNum, vueltoNum),
            pago_items: pagoItemsData || undefined,
            referencia_pago: pagoRef.referencia || undefined,
            nota_pago: pagoRef.nota || undefined,
            comprobante_pago: pagoRef.imagen || undefined,
          }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error"); }
        data = await res.json();
      }
      // Aplicar saldo a favor si el operador lo seleccionó
      if (aplicarSaldoFavor && parseFloat(montoSaldoAplicar) > 0 && saldoFavorFac?.detalle?.length) {
        const sfId = saldoFavorFac.detalle[0]?.id;
        if (sfId) {
          await fetch(`${API}/admin/saldos-favor/${sfId}/aplicar`, {
            method: "POST",
            headers: { ...headers, "Content-Type": "application/json" },
            body: JSON.stringify({ factura_id: pagoFac.id, factura_numero: pagoFac.numero, monto: parseFloat(montoSaldoAplicar) }),
          }).catch(() => {});
        }
      }
      // Aplicar vale(s) de cliente — cubre el monto en orden (el más antiguo primero)
      if (valeAplicarMonto > 0) {
        let restante = valeAplicarMonto;
        for (const v of (valeFac?.vales || [])) {
          if (restante <= 0.009) break;
          const aplicar = Math.min(restante, v.monto_disponible);
          if (aplicar <= 0) continue;
          const rv = await fetch(`${API}/admin/vales-clientes/${v.id}/aplicar`, {
            method: "POST",
            headers: { ...headers, "Content-Type": "application/json" },
            body: JSON.stringify({ factura_id: pagoFac.id, monto: aplicar }),
          }).catch(() => null);
          if (rv?.ok) { restante -= aplicar; if (!data) data = await rv.json().catch(() => null); }
        }
      }
      toast.success(esPagoParcialReal
        ? `Pago parcial registrado${data?.recibo?.numero ? ` · Recibo ${data.recibo.numero}` : ""}`
        : `Pagada${data?.recibo?.numero ? ` · Recibo ${data.recibo.numero}` : ""}`);
      setPagoItemsData(null);
      setPagoRef({ referencia: "", nota: "", imagen: "" });
      setShowPagoModal(false);
      setSaldoFavorFac(null); setAplicarSaldoFavor(false); setMontoSaldoAplicar("");
      setValeFac(null); setAplicarVale(false); setMontoValeAplicar("");
      await refreshSingleFactura(pagoFac.id);
    } catch (e) { toast.error(e.message || "Error al registrar pago"); }
  };

  // ── Confirmar pago parcial (crédito) ─────────────────────────
  const handlePagoParcial = async () => {
    const monto = parseFloat(montoParcial);
    if (!monto || monto <= 0) { toast.error("Monto inválido"); return; }
    try {
      const pendienteParcial = (pagoParcialFac.monto || 0) - (pagoParcialFac.monto_pagado || 0);
      const descuentoPago = Math.max(0, parseFloat(descuentoPagoParcial) || 0);
      const montoTotal = Math.min(monto, Math.max(0, pendienteParcial - descuentoPago)); // si excede, paga el saldo exacto
      // Vale de cliente: reduce lo que hay que cobrar en efectivo/banco de este pago
      const valeAplicarMonto = (aplicarVale && valeFac?.vales?.length && parseFloat(montoValeAplicar) > 0)
        ? Math.min(parseFloat(montoValeAplicar), montoTotal)
        : 0;
      const montoReal = montoTotal - valeAplicarMonto;
      const montoRecibidoParcial = (monto + descuentoPago) > pendienteParcial ? monto : null;
      const vueltoParcial = (monto + descuentoPago) > pendienteParcial ? (monto + descuentoPago) - pendienteParcial : null;
      const tcFinal = tcPago ? parseFloat(tcPago) : null;
      let data = null;
      if (montoReal > 0) {
        const res = await fetch(`${API}/admin/facturas/${pagoParcialFac.id}/pago-parcial`, {
          method: "PATCH",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({
            monto_pagado: montoReal,
            sin_retencion: sinRetencionPago || undefined,
            descuento: descuentoPago > 0 ? descuentoPago : undefined,
            procesadora_id: medioCobro.procesadora_id || undefined,
            metodo_cobro: medioCobro.metodo_cobro || undefined,
            fecha_pago: fechaPagoParcial,
            numero_recibo: numeroReciboManual || null,
            cuenta_id: getCuentaIdPago(pagoParcialFac),
            tipo_cambio: tcFinal,
            monto_recibido: montoRecibidoParcial,
            vuelto: vueltoParcial,
            ...detalleVuelto(tcFinal, getCuentaIdPago(pagoParcialFac), montoRecibidoParcial, vueltoParcial),
            pago_items: pagoItemsData || undefined,
            referencia_pago: pagoRef.referencia || undefined,
            nota_pago: pagoRef.nota || undefined,
            comprobante_pago: pagoRef.imagen || undefined,
            // Cuotas tildadas explícitamente en el modal — el backend las marca
            // "pagado" exactas, sin adivinar por coincidencia de monto (evita que
            // una cuota ya cobrada quede disponible para cobrarse de nuevo).
            cuota_ids: pagoParcialCuotaIds.size > 0 ? Array.from(pagoParcialCuotaIds) : undefined,
          }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error"); }
        data = await res.json();
      }
      // Aplicar saldo a favor si el operador lo seleccionó
      if (aplicarSaldoFavor && parseFloat(montoSaldoAplicar) > 0 && saldoFavorFac?.detalle?.length) {
        const sfId = saldoFavorFac.detalle[0]?.id;
        if (sfId) {
          await fetch(`${API}/admin/saldos-favor/${sfId}/aplicar`, {
            method: "POST",
            headers: { ...headers, "Content-Type": "application/json" },
            body: JSON.stringify({ factura_id: pagoParcialFac.id, factura_numero: pagoParcialFac.numero, monto: parseFloat(montoSaldoAplicar) }),
          }).catch(() => {});
        }
      }
      // Aplicar vale(s) de cliente — cubre el monto en orden (el más antiguo primero)
      if (valeAplicarMonto > 0) {
        let restante = valeAplicarMonto;
        for (const v of (valeFac?.vales || [])) {
          if (restante <= 0.009) break;
          const aplicar = Math.min(restante, v.monto_disponible);
          if (aplicar <= 0) continue;
          const rv = await fetch(`${API}/admin/vales-clientes/${v.id}/aplicar`, {
            method: "POST",
            headers: { ...headers, "Content-Type": "application/json" },
            body: JSON.stringify({ factura_id: pagoParcialFac.id, monto: aplicar }),
          }).catch(() => null);
          if (rv?.ok) { restante -= aplicar; if (!data) data = await rv.json().catch(() => null); }
        }
      }
      const pendiente = (pagoParcialFac.monto || 0) - (pagoParcialFac.monto_pagado || 0);
      const msg = (monto + descuentoPago) >= pendiente
        ? `Pagada completamente${data?.recibo?.numero ? ` · Recibo ${data.recibo.numero}` : ""}`
        : `Pago parcial registrado${data?.recibo?.numero ? ` · Recibo ${data.recibo.numero}` : ""}`;
      toast.success(msg);
      setPagoItemsData(null);
      setPagoRef({ referencia: "", nota: "", imagen: "" });
      setShowPagoParcialModal(false);
      setPagoParcialCuotaIds(new Set());
      setDescuentoPagoParcial(""); setMedioCobro({ procesadora_id: "", metodo_cobro: "" });
      setSaldoFavorFac(null); setAplicarSaldoFavor(false); setMontoSaldoAplicar("");
      setValeFac(null); setAplicarVale(false); setMontoValeAplicar("");
      await refreshSingleFactura(pagoParcialFac.id);
    } catch (e) { toast.error(e.message || "Error al registrar pago"); }
  };

  // ── Pago en lote (bulk) — agrupa por cliente, un recibo por cliente ─
  const handleBulkPago = async () => {
    if (!bulkFechaPago) { toast.error("Fecha requerida"); return; }
    const facsPendientes = filteredFac.filter(
      f => selectedFacIds.has(f.id) && (f.estado === "pendiente" || f.estado === "parcial")
    );
    if (facsPendientes.length === 0) { toast.error("No hay facturas pendientes seleccionadas"); return; }
    setBulkLoading(true);
    try {
      const res = await fetch(`${API}/admin/facturas/pago-bulk`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          factura_ids: facsPendientes.map(f => f.id),
          fecha_pago: bulkFechaPago,
          cuenta_id: bulkCuentaId || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al registrar pagos");
      const { ok, errors, recibos } = data;
      if (ok > 0) {
        // Mostrar resumen de recibos generados
        const resumenRecibos = recibos.map(r =>
          `${r.recibo_numero} · ${r.cliente} (${r.facturas_count} fac.)`
        ).join(" | ");
        toast.success(`${ok} factura${ok !== 1 ? "s" : ""} pagada${ok !== 1 ? "s" : ""} · ${recibos.length} recibo${recibos.length !== 1 ? "s" : ""}: ${resumenRecibos}`);
      }
      if (errors > 0) {
        // Limpiar el mensaje: sacar UUIDs y mostrar solo el tipo de error
        let detalle = "";
        if (data.error_details?.length) {
          const raw = data.error_details[0] || "";
          // Quitar el prefijo "Cliente 'UUID':" y mostrar solo el error
          const sinUuid = raw.replace(/Cliente '[^']*':\s*/i, "").replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "").trim();
          detalle = sinUuid ? ` — ${sinUuid}` : "";
        }
        toast.error(`${errors} factura${errors !== 1 ? "s" : ""} no se pudieron registrar${detalle}`);
      }
      if (recibos?.length === 1 && recibos[0].recibo_id) {
        try {
          const rRec = await fetch(`${API}/admin/recibos/${recibos[0].recibo_id}`, { headers });
          if (rRec.ok) setReciboDoc(await rRec.json());
        } catch (_) { /* ignore */ }
      }
    } catch (e) {
      toast.error(e.message || "Error al registrar pagos");
    }
    setBulkLoading(false);
    setShowBulkPagoModal(false);
    setSelectedFacIds(new Set());
    fetchFacturas(true);
  };

  // ── Editar pago individual ────────────────────────────────────
  const handleSaveEditPago = async (factura, pago, cambios) => {
    try {
      const res = await fetch(`${API}/admin/facturas/${factura.id}/pago/${pago.id}`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(cambios),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error"); }
      toast.success("Pago actualizado");
      setEditPagoCtx(null);
      await refreshSingleFactura(factura.id);
    } catch (e) { toast.error(e.message || "Error al editar pago"); }
  };

  // ── Eliminar pago individual ──────────────────────────────────
  const handleDeletePago = async (factura, pago) => {
    if (!window.confirm(`¿Eliminar este pago de ${fmtMonto(pago.monto, factura.moneda)}? Se eliminará también el recibo vinculado.`)) return;
    try {
      const res = await fetch(`${API}/admin/facturas/${factura.id}/pago/${pago.id}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error"); }
      toast.success("Pago eliminado");
      await refreshSingleFactura(factura.id);
    } catch (e) { toast.error(e.message || "Error al eliminar pago"); }
  };

  // Filtrar por año en frontend cuando filtroTipo === "anio"
  const matchesYear = (fechaStr) => {
    if (filtroTipo !== "anio") return true;
    return (fechaStr || "").startsWith(anio);
  };

  // logos accesibles (usado internamente para otros filtros)
  const logosAccesibles = React.useMemo(() => {
    if (user?.role === "admin") return empresasPropias;
    const ids = user?.logos_asignados || [];
    return empresasPropias.filter(ep => ids.includes(ep.id));
  }, [user, empresasPropias]);

  // Empresa de contexto: si los chips de la tab activa coinciden con exactamente una empresa, la pre-llenamos
  const contextEmpresaId = useMemo(() => {
    const activeChips = tab === "presupuestos" ? presChips : tab === "facturas" ? facChips : [];
    if (activeChips.length === 0 || empresas.length === 0) return "";
    const chipsLower = activeChips.map(c => c.toLowerCase());
    const matched = empresas.filter(e => chipsLower.some(chip => (e.nombre || "").toLowerCase().includes(chip)));
    return matched.length === 1 ? matched[0].id : "";
  }, [tab, presChips, facChips, empresas]);

  // ─── Filtros locales ─────────────────────────────────────────
  const q = search.toLowerCase();
  // Presupuestos: filtro por chips (AND lógico)
  const presAllChips = [...presChips, ...(presInput.trim() ? [presInput.trim()] : [])];
  const filteredPres = sortList(presupuestos
    .filter(p => matchesYear(p.fecha))
    .filter(p => {
      if (presAllChips.length === 0) return true;
      const texto = [
        p.numero, p.nombre_archivo, p.empresa_nombre, p.observaciones,
        ...(p.items || []).map(i => i.descripcion)
      ].filter(Boolean).join(" ").toLowerCase();
      return presAllChips.every(chip => texto.includes(chip.toLowerCase()));
    }), sortBy.presupuestos);
  // Facturas: chips search (AND lógico)
  const facAllChips = [...facChips, ...(facInput.trim() ? [facInput.trim()] : [])];
  const filteredFac = sortList(facturas
    .filter(f => matchesYear(f.fecha))
    .filter(f => {
      // filtro boleta
      if (facBoleta === "con_factura" && f.sin_factura) return false;
      if (facBoleta === "sin_factura" && !f.sin_factura) return false;
      if (facAllChips.length === 0) return true;
      const texto = [
        f.numero, f.numero_boleta, f.empresa_nombre, f.razon_social, f.ruc, f.concepto, f.notas,
        ...(f.conceptos || []).map(c => c.descripcion),
        String(f.monto || ""), f.estado, f.forma_pago
      ].filter(Boolean).join(" ").toLowerCase();
      return facAllChips.every(chip => texto.includes(chip.toLowerCase()));
    }), sortBy.facturas);

  // Cargar config de timbrado (por punto de expedición)
  const fetchTimbradoConfig = async () => {
    if (!activeEmpresaPropia?.slug) return;
    setTimbradoLoading(true);
    try {
      const res = await fetch(`${API}/admin/configuracion-timbrado/${activeEmpresaPropia.slug}`, { headers });
      if (res.ok) {
        const cfg = await res.json();
        setTimbradoData(cfg);
        const puntos = cfg.puntos_expedicion?.length
          ? cfg.puntos_expedicion.map(p => {
              const t = p.timbrado_activo || {};
              return {
                codigo: p.codigo, descripcion: p.descripcion || "",
                modo_numeracion: p.modo_numeracion || "manual",
                tipo_emision: t.tipo_emision || "preimpreso",
                nro_timbrado: t.nro_timbrado || "",
                establecimiento: t.establecimiento || "001",
                fecha_inicio: t.fecha_inicio || new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10),
                fecha_vigencia: t.fecha_vigencia || "",
                numero_desde: t.numero_desde != null ? String(t.numero_desde) : "",
                numero_hasta: t.numero_hasta != null ? String(t.numero_hasta) : "",
                nc_activo: !!t.rango_nota_credito,
                nc_numero_desde: t.rango_nota_credito?.numero_desde != null ? String(t.rango_nota_credito.numero_desde) : "",
                nc_numero_hasta: t.rango_nota_credito?.numero_hasta != null ? String(t.rango_nota_credito.numero_hasta) : "",
              };
            })
          : [nuevoPuntoVacio("001")];
        setTimbradoForm({
          punto_default: cfg.punto_default || puntos[0]?.codigo || "001",
          personalizado_por_usuario: !!cfg.personalizado_por_usuario,
          puntos_expedicion: puntos,
          habilitacion_autoimpresor: cfg.habilitacion_autoimpresor || "",
        });
        if (cfg.personalizado_por_usuario && hasPermission("configuracion_ventas.gestionar_timbrado")) fetchTimbradoUsuarios();
      }
    } catch (e) { toast.error("Error al cargar timbrado"); }
    setTimbradoLoading(false);
  };

  // Huecos de numeración (números de factura liberados por eliminación) de
  // un punto puntual — solo visible/usable con el permiso granular
  // configuracion_ventas.ver_huecos_timbrado (ver backend timbrado.py).
  const fetchHuecosNumeracion = async (codigoPunto) => {
    if (!activeEmpresaPropia?.slug) return;
    if (huecosPuntoAbierto === codigoPunto) { setHuecosPuntoAbierto(null); return; } // toggle: cerrar si ya estaba abierto
    setHuecosPuntoAbierto(codigoPunto);
    setHuecosLoading(true);
    setHuecosLista([]);
    try {
      const res = await fetch(`${API}/admin/timbrado/${activeEmpresaPropia.slug}/huecos/${codigoPunto}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setHuecosLista(data.huecos || []);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error al buscar huecos de numeración");
      }
    } catch (e) {
      toast.error("Error al buscar huecos de numeración");
    }
    setHuecosLoading(false);
  };

  const fetchTimbradoUsuarios = async () => {
    if (!activeEmpresaPropia?.slug || !hasPermission("configuracion_ventas.gestionar_timbrado")) return;
    setLoadingTimbradoUsuarios(true);
    try {
      const res = await fetch(`${API}/admin/configuracion-timbrado/${activeEmpresaPropia.slug}/usuarios`, { headers });
      if (res.ok) setTimbradoUsuarios(await res.json());
    } catch (e) {
      toast.error("Error al cargar asignación de puntos por usuario");
    }
    setLoadingTimbradoUsuarios(false);
  };

  const saveTimbradoUsuario = async (userId, puntoExpedicion) => {
    if (!activeEmpresaPropia?.slug || !hasPermission("configuracion_ventas.gestionar_timbrado")) return;
    setSavingTimbradoUsuarioId(userId);
    try {
      const res = await fetch(`${API}/admin/configuracion-timbrado/${activeEmpresaPropia.slug}/usuarios/${userId}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ logo_tipo: activeEmpresaPropia.slug, punto_expedicion: puntoExpedicion || null }),
      });
      if (res.ok) {
        setTimbradoUsuarios(prev => prev.map(u => u.user_id === userId ? { ...u, punto_expedicion: puntoExpedicion || null } : u));
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo guardar la asignación");
      }
    } catch (e) {
      toast.error("Error al guardar la asignación");
    }
    setSavingTimbradoUsuarioId(null);
  };

  const fetchRecibosConfig = async () => {
    if (!activeEmpresaPropia?.slug || !(isAdmin || hasPermission("configuracion_ventas.gestionar_recibos") || hasPermission("recibos.ver"))) return;
    try {
      const res = await fetch(`${API}/admin/configuracion-recibos${buildQ({ logo_tipo: activeEmpresaPropia.slug })}`, { headers });
      if (res.ok) {
        const cfg = await res.json();
        setRecibosConfig({ modo_recibos: cfg.modo_recibos || "automatico" });
      }
    } catch (e) {
      toast.error("Error al cargar configuración de recibos");
    }
  };

  const saveRecibosConfig = async (modo) => {
    if (!activeEmpresaPropia?.slug || !hasPermission("configuracion_ventas.gestionar_recibos")) return;
    setSavingRecibosConfig(true);
    try {
      const res = await fetch(`${API}/admin/configuracion-recibos`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ logo_tipo: activeEmpresaPropia.slug, modo_recibos: modo }),
      });
      if (res.ok) {
        setRecibosConfig({ modo_recibos: modo });
        toast.success("Configuración de recibos guardada");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo guardar configuración de recibos");
      }
    } catch (e) {
      toast.error("Error al guardar configuración de recibos");
    }
    setSavingRecibosConfig(false);
  };

  const fetchImpresionConfig = async () => {
    if (!activeEmpresaPropia?.slug || !(isAdmin || hasPermission("configuracion_ventas.gestionar_impresion") || hasPermission("facturas.ver"))) return;
    try {
      const res = await fetch(`${API}/admin/configuracion-impresion-facturas${buildQ({ logo_tipo: activeEmpresaPropia.slug })}`, { headers });
      if (res.ok) {
        const cfg = await res.json();
        setImpresionConfig({
          modo: cfg.modo || "no",
          modo_empresa: cfg.modo_empresa || "no",
          personalizado_por_usuario: !!cfg.personalizado_por_usuario,
          modelos_habilitados: Array.isArray(cfg.modelos_habilitados) && cfg.modelos_habilitados.length > 0
            ? cfg.modelos_habilitados
            : ["a4", "a4_doble", "ticket", "compacta"],
          max_items: Number.isFinite(cfg.max_items) && cfg.max_items > 0 ? cfg.max_items : 12,
          mostrar_observacion: cfg.mostrar_observacion !== false,
        });
        if (cfg.personalizado_por_usuario && hasPermission("configuracion_ventas.gestionar_impresion")) fetchImpresionUsuarios();
      }
    } catch (e) {
      toast.error("Error al cargar configuración de impresión");
    }
  };

  // Guarda la config a nivel empresa: modo por defecto y/o si se activa personalización por usuario.
  const saveImpresionConfig = async (partial) => {
    if (!activeEmpresaPropia?.slug || !hasPermission("configuracion_ventas.gestionar_impresion")) return;
    const next = { ...impresionConfig, ...partial };
    setSavingImpresionConfig(true);
    try {
      const res = await fetch(`${API}/admin/configuracion-impresion-facturas`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          logo_tipo: activeEmpresaPropia.slug,
          modo: next.modo_empresa,
          personalizado_por_usuario: next.personalizado_por_usuario,
          modelos_habilitados: next.modelos_habilitados,
          max_items: next.max_items,
          mostrar_observacion: next.mostrar_observacion,
        }),
      });
      if (res.ok) {
        setImpresionConfig({ modo: next.modo_empresa, modo_empresa: next.modo_empresa, personalizado_por_usuario: next.personalizado_por_usuario, modelos_habilitados: next.modelos_habilitados, max_items: next.max_items, mostrar_observacion: next.mostrar_observacion });
        toast.success("Configuración de impresión guardada");
        if (next.personalizado_por_usuario) fetchImpresionUsuarios();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo guardar configuración de impresión");
      }
    } catch (e) {
      toast.error("Error al guardar configuración de impresión");
    }
    setSavingImpresionConfig(false);
  };

  const fetchImpresionUsuarios = async () => {
    if (!activeEmpresaPropia?.slug || !hasPermission("configuracion_ventas.gestionar_impresion")) return;
    setLoadingImpresionUsuarios(true);
    try {
      const res = await fetch(`${API}/admin/configuracion-impresion-facturas/usuarios${buildQ({ logo_tipo: activeEmpresaPropia.slug })}`, { headers });
      if (res.ok) setImpresionUsuarios(await res.json());
    } catch (e) {
      toast.error("Error al cargar preferencias por usuario");
    }
    setLoadingImpresionUsuarios(false);
  };

  const saveImpresionUsuario = async (userId, modo) => {
    if (!activeEmpresaPropia?.slug || !hasPermission("configuracion_ventas.gestionar_impresion")) return;
    setSavingImpresionUsuarioId(userId);
    try {
      const res = await fetch(`${API}/admin/configuracion-impresion-facturas/usuarios/${userId}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ logo_tipo: activeEmpresaPropia.slug, modo }),
      });
      if (res.ok) {
        setImpresionUsuarios(prev => prev.map(u => u.user_id === userId ? { ...u, modo } : u));
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo guardar la preferencia");
      }
    } catch (e) {
      toast.error("Error al guardar la preferencia");
    }
    setSavingImpresionUsuarioId(null);
  };
  // Recibos: chips search
  const reciboAllChips = [...reciboChips, ...(reciboInput.trim() ? [reciboInput.trim()] : [])];
  const filteredRecibos = sortList(recibos
    .filter(r => matchesYear(r.fecha_pago))
    .filter(r => {
      if (reciboAllChips.length === 0) return true;
      const texto = [r.numero, r.factura_numero, r.razon_social, r.ruc, r.notas, r.estado, r.pendiente_edicion ? "pendiente edicion" : ""].filter(Boolean).join(" ").toLowerCase();
      return reciboAllChips.every(chip => texto.includes(chip.toLowerCase()));
    }), sortBy.recibos);
	  const filteredIng = sortList((Array.isArray(ingresos) ? ingresos : [])
	    .filter(i => i && matchesYear(i.fecha))
    .filter(i =>
      !q || (i.descripcion || "").toLowerCase().includes(q) ||
      (i.categoria || "").toLowerCase().includes(q) ||
      String(i.monto_pyg || i.monto || "").includes(q)
	    ).filter(i => logoFilter === "todas" || (i.logo_tipo || "arandujar") === logoFilter), sortBy.ingresos || { key: "fecha", dir: "desc" });
	  const filteredNotas = sortList((Array.isArray(notasCredito) ? notasCredito : [])
	    .filter(n => n && matchesYear(n.fecha))
	    .filter(n =>
	      !q || (n.numero || "").toLowerCase().includes(q) ||
	      (n.factura_numero || "").toLowerCase().includes(q) ||
	      (n.razon_social || "").toLowerCase().includes(q) ||
	      (n.motivo || "").toLowerCase().includes(q) ||
	      String(n.monto || "").includes(q)
	    ).filter(n => logoFilter === "todas" || (n.logo_tipo || "arandujar") === logoFilter), sortBy.notas || { key: "fecha", dir: "desc" });
	  // ─── Stats ───────────────────────────────────────────────────
	  const sumMonto = (items, field = "monto") => items.reduce((s, it) => s + (it.monto_pyg || ((it.moneda || "PYG") === "PYG" ? it[field] : 0) || 0), 0);
	  const sumUSD = (items, field = "monto") => items.reduce((s, it) => s + ((it.moneda || "PYG") === "USD" ? (Number(it[field]) || 0) : 0), 0);
	  const sumBRL = (items, field = "monto") => items.reduce((s, it) => s + ((it.moneda || "PYG") === "BRL" ? (Number(it[field]) || 0) : 0), 0);
	  const countMonto = (items, field = "monto") => ({ count: items.length, monto: sumMonto(items, field), monto_usd: sumUSD(items, field), monto_brl: sumBRL(items, field) });
	  const totalCobradoFactura = (f) => (f.pagos || []).reduce((s, p) => s + (p.monto || 0), 0) || f.monto_pagado || (f.estado === "pagada" ? f.monto : 0) || 0;
	  const statCards = (() => {
	    if (tab === "presupuestos") {
	      const aprob = countMonto(filteredPres.filter(p => p.estado === "aprobado"), "total");
	      const rech = countMonto(filteredPres.filter(p => p.estado === "rechazado"), "total");
	      const fact = countMonto(filteredPres.filter(p => p.estado === "facturado" || p.estado === "cobrado" || p.facturas_count > 0), "total");
	      const falta = countMonto(filteredPres.filter(p => p.estado === "aprobado" && !p.facturas_count), "total");
	      return [
	        { label: "Aprobados", ...aprob, color: "text-blue-300" },
	        { label: "Rechazados", ...rech, color: "text-red-300" },
	        { label: "Facturados / cobrados", ...fact, color: "text-emerald-300" },
	        { label: "Faltan facturar/cobrar", ...falta, color: "text-amber-300" },
	      ];
	    }
	    if (tab === "facturas") {
	      const emit = countMonto(filteredFac.filter(f => f.tipo === "emitida" && f.estado !== "anulada"));
	      const pag = countMonto(filteredFac.filter(f => f.estado === "pagada"));
	      const parc = countMonto(filteredFac.filter(f => f.estado === "parcial"));
	      const pend = countMonto(filteredFac.filter(f => f.estado === "pendiente"));
	      return [
	        { label: "Emitidas", ...emit, color: "text-cyan-300" },
	        { label: "Pagadas", ...pag, color: "text-emerald-300" },
	        { label: "Parciales", ...parc, color: "text-blue-300", sub: `Cobrado ${fmtPYG(filteredFac.filter(f => f.estado === "parcial").reduce((s, f) => s + ((f.moneda || "PYG") === "PYG" ? totalCobradoFactura(f) : 0), 0))}${filteredFac.some(f => f.estado === "parcial" && f.moneda === "USD") ? ` / USD ${filteredFac.filter(f => f.estado === "parcial" && f.moneda === "USD").reduce((s, f) => s + totalCobradoFactura(f), 0).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ""}${filteredFac.some(f => f.estado === "parcial" && f.moneda === "BRL") ? ` / R$ ${filteredFac.filter(f => f.estado === "parcial" && f.moneda === "BRL").reduce((s, f) => s + totalCobradoFactura(f), 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ""}` },
	        { label: "Pendientes", ...pend, color: "text-amber-300" },
	      ];
	    }
	    if (tab === "ingresos") {
	      const total = countMonto(filteredIng);
	      const transfer = countMonto(filteredIng.filter(i => (i.categoria || "").toLowerCase().includes("transfer")));
	      const efectivo = countMonto(filteredIng.filter(i => (i.categoria || "").toLowerCase().includes("efectivo")));
	      const otros = countMonto(filteredIng.filter(i => !(i.categoria || "").toLowerCase().includes("transfer") && !(i.categoria || "").toLowerCase().includes("efectivo")));
	      return [
	        { label: "Ingresos", ...total, color: "text-violet-300" },
	        { label: "Transferencias", ...transfer, color: "text-blue-300" },
	        { label: "Efectivo", ...efectivo, color: "text-emerald-300" },
	        { label: "Otros", ...otros, color: "text-slate-300" },
	      ];
	    }
	    if (tab === "recibos") {
	      const total = countMonto(filteredRecibos);
	      const pyg = countMonto(filteredRecibos.filter(r => (r.moneda || "PYG") === "PYG"));
	      const usd = filteredRecibos.filter(r => r.moneda === "USD");
	      const hoy = filteredRecibos.filter(r => r.fecha_pago === new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));
	      return [
	        { label: "Recibos", ...total, color: "text-amber-300" },
	        { label: "En guaraníes", ...pyg, color: "text-emerald-300" },
	        { label: "En USD", count: usd.length, monto: usd.reduce((s, r) => s + (r.monto || 0), 0), color: "text-blue-300", currency: "USD" },
	        { label: "Hoy", ...countMonto(hoy), color: "text-cyan-300" },
	      ];
	    }
	    const emit = countMonto(filteredNotas.filter(n => n.estado !== "anulada"));
	    const anul = countMonto(filteredNotas.filter(n => n.estado === "anulada"));
	    const vinculadas = countMonto(filteredNotas.filter(n => n.factura_id));
	    const sueltas = countMonto(filteredNotas.filter(n => !n.factura_id));
	    return [
	      { label: "Notas emitidas", ...emit, color: "text-rose-300" },
	      { label: "Vinculadas a factura", ...vinculadas, color: "text-amber-300" },
	      { label: "Sin factura vinculada", ...sueltas, color: "text-slate-300" },
	      { label: "Anuladas", ...anul, color: "text-red-300" },
	    ];
	  })();

  const hayModalAbierto = !!(showNew || showDescuentosGestor || showTiposGestor || showFacForm || showTimbradoConfig || showPagoModal || showPagoParcialModal || showBulkPagoModal || showIngForm || showNotaForm || !!previewItem || !!presDoc || !!facDoc || !!editPagoCtx);
  useAutoRefresh(fetchFacturas, 30_000, !hayModalAbierto);

  return (
    <div className="min-h-screen bg-arandu-dark text-white">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/sistema" className="text-slate-400 hover:text-white transition-colors" data-testid="back-btn" title="Volver al Dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="font-heading text-2xl text-white flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-emerald-400" />
              Ventas
            </h1>
            <p className="text-slate-400 text-sm font-body">Presupuestos · Facturas · Ingresos</p>
          </div>
          <EmpresaSwitcher compact />
        </div>
        <div className="flex items-center gap-2">
          {isVentasPrivileged && (
            <button
              onClick={() => { fetchAvisosLic(); setAvisosTab("pendientes"); setShowAvisosHist(true); }}
              className="relative p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-all"
              title="Avisos de licencias (pendientes e histórico)"
              data-testid="avisos-licencias-btn"
            >
              <Bell className="w-4 h-4" />
              {avisosLic.total_pendientes > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-black text-[10px] font-bold flex items-center justify-center">
                  {avisosLic.total_pendientes}
                </span>
              )}
            </button>
          )}
          <button
            onClick={() => { handleRefreshManual(); fetchAvisosLic(); }}
            disabled={loading}
            className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-all disabled:opacity-50"
            title="Actualizar"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Filtro: tipo de rango + mes/año */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex rounded-lg border border-white/10 bg-white/5 p-0.5 text-xs">
            {[
              { v: "dia",   label: "Hoy" },
              { v: "mes",   label: "Por mes" },
              { v: "anio",  label: "Por año" },
              { v: "todos", label: "Todos" },
            ].map(op => (
              <button
                key={op.v}
                onClick={() => setFiltroTipoP(op.v)}
                data-testid={`filtro-${op.v}`}
                className={`px-3 py-1.5 rounded-md font-body transition-all ${
                  filtroTipo === op.v
                    ? "bg-emerald-500/20 text-emerald-200"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                {op.label}
              </button>
            ))}
          </div>
          {filtroTipo === "dia" && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg px-1 py-0.5">
                <button onClick={() => setDia(prevDia(dia))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-slate-300 text-sm font-body min-w-[110px] text-center px-1 capitalize">{diaLabel(dia)}</span>
                <button onClick={() => setDia(nextDia(dia))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              {dia !== getDiaActual() && (
                <button onClick={() => setDia(getDiaActual())}
                  className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                  Hoy
                </button>
              )}
            </div>
          )}
          {filtroTipo === "mes" && (
            <div className="flex items-center gap-1">
              <button onClick={() => setMes(prevMes(mes))}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-slate-300 font-body text-sm px-2 min-w-[90px] text-center">{mesLabel(mes)}</span>
              <button onClick={() => setMes(nextMes(mes))}
                disabled={mes >= getMesActual()}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all disabled:opacity-30">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          {filtroTipo === "anio" && (
            <div className="flex items-center gap-1">
              <button onClick={() => setAnio(String(Number(anio) - 1))}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-slate-300 font-body text-sm px-3 min-w-[60px] text-center">{anio}</span>
              <button onClick={() => setAnio(String(Number(anio) + 1))}
                disabled={Number(anio) >= new Date().getFullYear()}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all disabled:opacity-30">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

	        {/* Estadísticas por pestaña */}
	        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
	          {statCards.map(card => (
	            <div key={card.label} className="bg-white/5 border border-white/10 rounded-xl p-4">
	              <p className="text-slate-400 text-xs font-body mb-1">{card.label}</p>
	              <p className={`${card.color} font-heading font-bold text-lg`}>{card.count}</p>
	              <p className="text-slate-500 text-xs font-body mt-0.5">
	                {card.currency === "USD" ? `USD ${Number(card.monto || 0).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : fmtPYG(card.monto || 0)}
	              </p>
	              {!card.currency && card.monto_usd > 0 && (
	                <p className="text-blue-500 text-xs font-body mt-0.5">
	                  USD {Number(card.monto_usd || 0).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
	                </p>
	              )}
	              {!card.currency && card.monto_brl > 0 && (
	                <p className="text-green-500 text-xs font-body mt-0.5">
	                  R$ {Number(card.monto_brl || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
	                </p>
	              )}
	              {card.sub && <p className="text-slate-600 text-xs font-body mt-0.5">{card.sub}</p>}
	            </div>
	          ))}
	        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/10 pb-0">
	          {visibleTabs.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); setSearch(""); }}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-body font-medium border-b-2 transition-all -mb-px ${
                  active
                    ? `text-${t.color}-300 border-${t.color}-400`
                    : "text-slate-400 border-transparent hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
                {t.id !== "configuracion" && t.id !== "entregas" && (
                  <span className={`text-xs px-1.5 rounded-full ${
                    active ? `bg-${t.color}-500/20 text-${t.color}-300` : "bg-white/10 text-slate-500"
                  }`}>
                    {t.id === "presupuestos" ? filteredPres.length
	                     : t.id === "recepcion" ? recepciones.length
	                     : t.id === "facturas" ? filteredFac.length
	                     : t.id === "recibos" ? filteredRecibos.length
	                     : t.id === "notas" ? filteredNotas.length
	                     : filteredIng.length}
                  </span>
                )}
              </button>
            );
          })}

        </div>

        {/* Search — chips para presupuestos, facturas y recibos; simple para otros */}
        {tab === "entregas" && (
          <EntregasPanel
            tipo="venta" token={token} logoTipo={activeEmpresaPropia?.slug}
            hasPermission={hasPermission} isAdmin={isAdmin || user?.role === "gerente" || user?.role === "super_admin"}
            initialOrigenId={searchParams.get("entrega_factura") || null}
          />
        )}

        {tab !== "configuracion" && tab !== "entregas" && ((tab === "presupuestos" || tab === "facturas" || tab === "recibos") ? (() => {
          const isPresupuestos = tab === "presupuestos";
          const isFacturas = tab === "facturas";
          const chips = isPresupuestos ? presChips : isFacturas ? facChips : reciboChips;
          const setChips = isPresupuestos ? setPresChips : isFacturas ? setFacChips : setReciboChips;
          const inputVal = isPresupuestos ? presInput : isFacturas ? facInput : reciboInput;
          const setInputVal = isPresupuestos ? setPresInput : isFacturas ? setFacInput : setReciboInput;
          const accentColor = isPresupuestos ? "blue" : isFacturas ? "emerald" : "amber";
          return (
            <div className="space-y-2">
              {chips.length > 0 && (
                <div className="flex flex-wrap gap-2 items-center">
                  {chips.map((chip, idx) => (
                    <span key={idx} className={`flex items-center gap-1.5 bg-${accentColor}-500/20 text-${accentColor}-300 border border-${accentColor}-500/40 rounded-full px-3 py-1 text-xs font-body`}>
                      <Search className="w-3 h-3" />
                      {chip}
                      <button onClick={() => setChips(prev => prev.filter((_, i) => i !== idx))} className="ml-1 hover:text-white transition-colors">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <button onClick={() => setChips([])} className="text-xs text-slate-500 hover:text-slate-300 px-2 py-1 rounded-full border border-white/10 hover:border-white/20 transition-all font-body">
                    Limpiar
                  </button>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-lg">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    value={inputVal}
                    onChange={e => setInputVal(e.target.value)}
                    onKeyDown={e => {
                      if ((e.key === "Enter" || e.key === ",") && inputVal.trim()) {
                        e.preventDefault();
                        const term = inputVal.trim().replace(/,$/, "");
                        if (term && !chips.includes(term)) setChips(prev => [...prev, term]);
                        setInputVal("");
                      }
                      if (e.key === "Backspace" && inputVal === "" && chips.length > 0) {
                        setChips(prev => prev.slice(0, -1));
                      }
                    }}
                    placeholder={
                      isPresupuestos ? "Buscar por nº, empresa, descripción… (Enter para agregar filtro)" :
                      isFacturas ? "Buscar por nº, razón social, concepto… (Enter para agregar filtro)" :
                      "Buscar por nº recibo, factura, empresa… (Enter para agregar filtro)"
                    }
                    className={`w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-white text-sm font-body focus:outline-none focus:border-${accentColor}-500`}
                  />
                </div>
                {deletedToggle}
                {isPresupuestos && !mostrarEliminadas.presupuestos && hasPermission("presupuestos.crear") && (
                  <button
                    onClick={() => setPresFormItem({ presupuesto: null, mode: "new" })}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-all font-body whitespace-nowrap"
                  >
                    <Plus className="w-4 h-4" /> Nuevo presupuesto
                  </button>
                )}
                {isFacturas && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Filtro Todas / Con Factura / Sin Factura */}
                    <div className="flex rounded-lg border border-white/10 overflow-hidden text-xs font-body">
                      {[{v:"todas",label:"Todas"},{v:"con_factura",label:"Con factura"},{v:"sin_factura",label:"Boletas"}].map(op => (
                        <button key={op.v} onClick={() => setFacBoleta(op.v)}
                          className={`px-3 py-1.5 transition-all ${facBoleta === op.v ? "bg-amber-600 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
                          {op.label}
                        </button>
                      ))}
                    </div>
                    {!mostrarEliminadas.facturas && hasPermission("facturas.crear") && (
                      <button
                        onClick={() => { setFacFormItem(null); setFacSinFactura(false); setShowFacForm(true); }}
                        className="flex items-center gap-2 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded-lg transition-all font-body whitespace-nowrap"
                      >
                        <Plus className="w-4 h-4" /> Nueva factura
                      </button>
                    )}
                    {!mostrarEliminadas.facturas && hasPermission("facturas.crear") && (
                      <button
                        onClick={() => { setFacFormItem(null); setFacSinFactura(true); setShowFacForm(true); }}
                        className="flex items-center gap-2 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm rounded-lg transition-all font-body whitespace-nowrap"
                      >
                        <Receipt className="w-4 h-4" /> Venta sin factura
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })() : (
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar descripción..."
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-white text-sm font-body focus:outline-none focus:border-violet-500"
              />
            </div>
            {deletedToggle}
	            {tab === "ingresos" && !mostrarEliminadas.ingresos && hasPermission("ingresos_varios.crear") && (
	              <button
                onClick={() => setShowIngForm(true)}
                className="flex items-center gap-2 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm rounded-lg transition-all font-body whitespace-nowrap"
              >
	                <Plus className="w-4 h-4" /> Nuevo ingreso
	              </button>
	            )}
	            {tab === "notas" && !mostrarEliminadas.notas && hasPermission("notas_credito.crear") && (
	              <button
	                onClick={() => { setNotaFormItem(null); setShowNotaForm(true); }}
	                className="flex items-center gap-2 px-3 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm rounded-lg transition-all font-body whitespace-nowrap"
	              >
	                <Plus className="w-4 h-4" /> Nueva nota
	              </button>
	            )}
	            {tab === "recepcion" && (isAdmin || hasPermission("presupuestos.recepcion_configurar")) && (
	              <button
	                onClick={() => setShowRecepConfig(true)}
	                className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/15 text-white text-sm rounded-lg transition-all font-body whitespace-nowrap"
	              >
	                Texto de la boleta
	              </button>
	            )}
	            {tab === "recepcion" && (isAdmin || hasPermission("presupuestos.recepcion_crear")) && (
	              <button
	                onClick={() => setRecepFormItem({})}
	                className="flex items-center gap-2 px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded-lg transition-all font-body whitespace-nowrap"
	              >
	                <Plus className="w-4 h-4" /> Nueva Recepción
	              </button>
	            )}
	          </div>
	        ))}

        {/* ── Tab: Recepción de Equipos ──────────────────────────────── */}
        {tab === "recepcion" && (
          <div className="border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-slate-400 text-xs uppercase font-body">
                <tr>
                  <th className="text-left px-4 py-2.5">N°</th>
                  <th className="text-left px-4 py-2.5">Fecha</th>
                  <th className="text-left px-4 py-2.5">Cliente</th>
                  <th className="text-left px-4 py-2.5">Aparato</th>
                  <th className="text-left px-4 py-2.5">Falla reclamada</th>
                  <th className="text-left px-4 py-2.5">Estado</th>
                  <th className="text-left px-4 py-2.5">Presupuesto</th>
                  <th className="text-right px-4 py-2.5">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {recepciones.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-slate-500 py-8 font-body">Sin recepciones de equipo registradas.</td></tr>
                )}
                {recepciones.map(r => (
                  <tr key={r.id} className="hover:bg-white/5">
                    <td className="px-4 py-2.5 text-white font-mono text-xs">{r.numero}</td>
                    <td className="px-4 py-2.5 text-slate-300">{r.fecha}</td>
                    <td className="px-4 py-2.5 text-slate-300">{r.cliente_nombre}</td>
                    <td className="px-4 py-2.5 text-slate-300">{r.marca_modelo}{r.nro_serie ? ` (N/S ${r.nro_serie})` : ""}</td>
                    <td className="px-4 py-2.5 text-slate-400 max-w-xs truncate">{r.falla_reclamada}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs px-2 py-1 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                        {r.estado}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-400">
                      {r.presupuesto_numero ? (
                        <button
                          onClick={() => { setTab("presupuestos"); setPresChips(prev => prev.includes(r.presupuesto_numero) ? prev : [...prev, r.presupuesto_numero]); }}
                          className="text-blue-400 hover:underline"
                        >
                          {r.presupuesto_numero}
                        </button>
                      ) : "-"}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => setRecepFormItem(r)}
                        className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-all"
                        title={hasPermission("presupuestos.recepcion_editar") ? "Editar diagnóstico/estado" : "Ver"}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Tab: Configuración ─────────────────────────────────── */}
        {tab === "configuracion" && (
          <div className="space-y-4">
            <p className="text-slate-400 text-sm font-body">Configuración del módulo Ventas.</p>

            {/* Card: Descuentos por usuario */}
            {(isAdmin || hasPermission("configuracion_ventas.gestionar_descuentos")) && (
              <div className="border border-white/10 rounded-xl p-5 bg-white/3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                      <Percent className="w-4 h-4 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Descuentos por usuario</p>
                      <p className="text-slate-400 text-xs mt-0.5 max-w-sm">
                        Configurá qué descuento puede aplicar cada usuario al facturar: sin límite, hasta el máximo del producto, o un tope personalizado con excepciones por producto.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDescuentosGestor(v => !v)}
                    className="h-8 text-xs bg-amber-600/80 hover:bg-amber-600 text-white px-3 flex-shrink-0 ml-4 rounded-lg flex items-center gap-1.5 transition-colors"
                  >
                    <Settings className="w-3.5 h-3.5" /> {showDescuentosGestor ? "Cerrar" : "Gestionar"}
                  </button>
                </div>
                {showDescuentosGestor && (
                  <div className="mt-4 border-t border-white/10 pt-4">
                    <DescuentosConfigTab />
                  </div>
                )}
              </div>
            )}

            {/* Card: Vendedores y Comisiones */}
            {(isAdmin || hasPermission("vendedores.ver")) && (
              <div className="border border-white/10 rounded-xl p-5 bg-white/3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                      <Users className="w-4 h-4 text-blue-300" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Vendedores y Comisiones</p>
                      <p className="text-slate-400 text-xs mt-0.5 max-w-sm">
                        Registrá vendedores y su % de comisión sobre venta. Se pueden asignar (opcionalmente) al facturar.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowVendedoresGestor(v => !v)}
                    className="h-8 text-xs bg-blue-600/80 hover:bg-blue-600 text-white px-3 flex-shrink-0 ml-4 rounded-lg flex items-center gap-1.5 transition-colors"
                  >
                    <Settings className="w-3.5 h-3.5" /> {showVendedoresGestor ? "Cerrar" : "Gestionar"}
                  </button>
                </div>
                {showVendedoresGestor && (
                  <div className="mt-4 border-t border-white/10 pt-4">
                    <VendedoresConfigTab />
                  </div>
                )}
              </div>
            )}

            {(isAdmin || hasPermission("configuracion_ventas.gestionar_timbrado")) && (
              <div className="border border-white/10 rounded-xl p-5 bg-white/3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-4 h-4 text-amber-300" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Timbrado fiscal</p>
                      <p className="text-slate-400 text-xs mt-0.5 max-w-xl">
                        Configurá la autorización SET, el tipo de emisión, los puntos de expedición y la numeración manual o automática.
                      </p>
                      {timbradoData?.puntos_expedicion?.length > 0 && (
                        <div className="text-slate-500 text-xs mt-2 space-y-0.5">
                          {timbradoData.puntos_expedicion.map(p => (
                            <p key={p.codigo}>
                              Punto <span className="font-mono text-amber-300">{p.codigo}</span>
                              {p.codigo === timbradoData.punto_default && <span className="ml-1 text-slate-600">(default)</span>}
                              <span className="mx-1">·</span>
                              <span className="font-mono">{p.timbrado_activo?.nro_timbrado || "sin timbrado"}</span>
                              {p.timbrado_activo && (
                                <>
                                  <span className="mx-1">·</span>
                                  {p.timbrado_activo.tipo_emision === "electronico" ? "Electrónico sin vencimiento" : `Vigencia ${p.timbrado_activo.fecha_vigencia || "-"}`}
                                  {!p.vigente && <span className="ml-1 text-red-400">(vencido)</span>}
                                </>
                              )}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowTimbradoConfig(true); fetchTimbradoConfig(); }}
                    className="h-8 text-xs bg-amber-600/80 hover:bg-amber-600 text-white px-3 flex-shrink-0 rounded-lg flex items-center gap-1.5 transition-colors"
                  >
                    <Settings className="w-3.5 h-3.5" /> Gestionar
                  </button>
                </div>
              </div>
            )}

            {(isAdmin || hasPermission("configuracion_ventas.gestionar_recibos")) && (
              <div className="border border-white/10 rounded-xl p-5 bg-white/3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                      <Receipt className="w-4 h-4 text-blue-300" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Recibos de pago</p>
                      <p className="text-slate-400 text-xs mt-0.5 max-w-xl">
                        Definí cómo se numera el recibo cuando se registra un cobro. El modo automático sigue siendo el predeterminado para todas las empresas.
                      </p>
                    </div>
                  </div>
                  {savingRecibosConfig && <Loader2 className="w-4 h-4 text-blue-300 animate-spin" />}
                </div>
                <div className="grid md:grid-cols-3 gap-3 mt-4">
                  {[
                    { v: "automatico", t: "Automático", d: "Genera REC-0001 al registrar el pago." },
                    { v: "manual_obligatorio", t: "Manual obligatorio", d: "Exige escribir el número antes de guardar." },
                    { v: "borrador_pendiente", t: "Borrador pendiente", d: "Crea REC-PEND y queda para edición." },
                  ].map(op => {
                    const active = recibosConfig.modo_recibos === op.v;
                    return (
                      <button
                        key={op.v}
                        disabled={savingRecibosConfig}
                        onClick={() => saveRecibosConfig(op.v)}
                        className={`text-left rounded-xl border px-4 py-3 transition-all ${active ? "bg-blue-500/15 border-blue-400/50 text-blue-100" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"}`}
                      >
                        <span className="block text-sm font-semibold">{op.t}</span>
                        <span className="block text-xs mt-1 text-slate-400">{op.d}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Card: Cobro automático de ventas al contado */}
            {(isAdmin || hasPermission("configuracion_ventas.gestionar_cobro_contado")) && (
              <CobroContadoCard token={token} logoTipo={activeEmpresaPropia?.slug} puedeEditar={isAdmin || hasPermission("configuracion_ventas.gestionar_cobro_contado")} />
            )}

            {/* Card: Delivery */}
            {(isAdmin || hasPermission("configuracion_ventas.gestionar_delivery")) && (
              <DeliveryConfigCard token={token} logoTipo={activeEmpresaPropia?.slug} puedeEditar={isAdmin || hasPermission("configuracion_ventas.gestionar_delivery")} />
            )}

            {/* Card: Impresión automática al guardar */}
            {(isAdmin || hasPermission("configuracion_ventas.gestionar_impresion")) && (
              <div className="border border-white/10 rounded-xl p-5 bg-white/3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                      <Printer className="w-4 h-4 text-blue-300" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Impresión al guardar factura</p>
                      <p className="text-slate-400 text-xs mt-0.5 max-w-xl">
                        Definí si al crear una factura o boleta el botón guarda solamente (como hasta ahora), o guarda e imprime en el mismo paso — y en qué formato.
                      </p>
                    </div>
                  </div>
                  {savingImpresionConfig && <Loader2 className="w-4 h-4 text-blue-300 animate-spin" />}
                </div>

                {/* 1) Guardar solamente / Guardar e imprimir */}
                <div className="grid md:grid-cols-2 gap-3 mt-4">
                  {[
                    { v: "no", t: "Guardar solamente", d: "Comportamiento actual: guardás y después imprimís cuando quieras." },
                    { v: "elegir", t: "Guardar e imprimir", d: "El botón pasa a decir \"Guardar e Imprimir\" y abre la impresión en el formato que elijas abajo." },
                  ].map(op => {
                    const activa = op.v === "no" ? impresionConfig.modo_empresa === "no" : impresionConfig.modo_empresa !== "no";
                    return (
                      <button
                        key={op.v}
                        disabled={savingImpresionConfig}
                        onClick={() => saveImpresionConfig({ modo_empresa: op.v === "no" ? "no" : (impresionConfig.modo_empresa !== "no" ? impresionConfig.modo_empresa : "a4") })}
                        className={`text-left rounded-xl border px-4 py-3 transition-all ${activa ? "bg-blue-500/15 border-blue-400/50 text-blue-100" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"}`}
                      >
                        <span className="block text-sm font-semibold">{op.t}</span>
                        <span className="block text-xs mt-1 text-slate-400">{op.d}</span>
                      </button>
                    );
                  })}
                </div>

                {/* 2) Formato — solo si "Guardar e imprimir" está activo */}
                {impresionConfig.modo_empresa !== "no" && (
                  <div className="mt-3">
                    <p className="text-slate-500 text-xs font-medium mb-2">Formato por defecto de la empresa</p>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { v: "a4", t: "A4" },
                        { v: "a4_doble", t: "A4 doble vía" },
                        { v: "compacta", t: "Compacta" },
                        { v: "ticket", t: "Ticket" },
                      ].map(op => (
                        <button key={op.v} disabled={savingImpresionConfig}
                          onClick={() => saveImpresionConfig({ modo_empresa: op.v })}
                          className={`rounded-lg border px-3 py-2 text-xs font-semibold transition-all ${impresionConfig.modo_empresa === op.v ? "bg-blue-500 border-blue-400 text-white" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"}`}>
                          {op.t}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* 2b) Modelos habilitados para imprimir manualmente — controla qué botones
                    aparecen en "Vista previa" de la factura (Imprimir A4 / Doble vía / Compacta
                    / Ticket). Si no se tilda ninguno, se muestran los 4 (no puede quedar la
                    empresa sin poder imprimir nada). */}
                <div className="mt-4 pt-4 border-t border-white/5">
                  <p className="text-slate-200 text-sm font-medium">Modelos de impresión habilitados</p>
                  <p className="text-slate-500 text-xs mt-0.5 max-w-md mb-2">
                    Elegí qué formatos aparecen como botones al imprimir una factura. Si querés todos, dejalos todos tildados.
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { v: "a4", t: "A4" },
                      { v: "a4_doble", t: "A4 doble vía" },
                      { v: "compacta", t: "Compacta" },
                      { v: "ticket", t: "Ticket" },
                    ].map(op => {
                      const activo = (impresionConfig.modelos_habilitados || []).includes(op.v);
                      return (
                        <button key={op.v} type="button" disabled={savingImpresionConfig}
                          onClick={() => {
                            const actuales = impresionConfig.modelos_habilitados || [];
                            const next = activo ? actuales.filter(m => m !== op.v) : [...actuales, op.v];
                            saveImpresionConfig({ modelos_habilitados: next.length > 0 ? next : actuales });
                          }}
                          className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition-all ${activo ? "bg-emerald-500/15 border-emerald-400/50 text-emerald-200" : "bg-white/5 border-white/10 text-slate-400 hover:bg-white/10"}`}>
                          <span className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${activo ? "bg-emerald-500 border-emerald-400" : "border-white/20"}`}>
                            {activo && <CheckCircle className="w-3 h-3 text-white" />}
                          </span>
                          {op.t}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 2c) Máximo de ítems por comprobante — para que Factura/Boleta y Nota de
                    Crédito siempre entren bien en cualquiera de los formatos de hoja fija
                    (A4 / A4 doble vía / Compacta). Ticket no tiene límite. */}
                <div className="mt-4 pt-4 border-t border-white/5">
                  <p className="text-slate-200 text-sm font-medium">Máximo de ítems por comprobante</p>
                  <p className="text-slate-500 text-xs mt-0.5 max-w-md mb-2">
                    Tope de ítems al cargar una Factura/Boleta o Nota de Crédito, para que siempre entre bien en A4, A4 doble vía y Compacta.
                    Ticket no tiene límite (es un rollo continuo). Recomendado: 12.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={200}
                      value={maxItemsDraft}
                      disabled={savingImpresionConfig}
                      onChange={e => setMaxItemsDraft(e.target.value)}
                      onBlur={() => {
                        const n = parseInt(maxItemsDraft, 10);
                        if (Number.isFinite(n) && n > 0) {
                          if (n !== impresionConfig.max_items) saveImpresionConfig({ max_items: n });
                        } else {
                          setMaxItemsDraft(String(impresionConfig.max_items ?? 12));
                        }
                      }}
                      onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      className="w-24 bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-blue-500/50"
                    />
                    <span className="text-slate-500 text-xs">ítems</span>
                  </div>
                </div>

                {/* 2d) Mostrar Observación (Nota Interna) impresa */}
                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-slate-200 text-sm font-medium">Mostrar Observación en la impresión</p>
                    <p className="text-slate-500 text-xs mt-0.5 max-w-md">
                      Imprime la Nota Interna cargada al crear la factura como "OBSERVACIÓN" en A4, A4 doble vía, Compacta y Ticket.
                      Si lo desactivás, la nota se sigue viendo en la Vista previa del sistema, pero no en el papel.
                    </p>
                  </div>
                  <button type="button" disabled={savingImpresionConfig}
                    onClick={() => saveImpresionConfig({ mostrar_observacion: !impresionConfig.mostrar_observacion })}
                    className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 flex items-center ${impresionConfig.mostrar_observacion ? "bg-emerald-500" : "bg-slate-600"}`}>
                    <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform mx-1 ${impresionConfig.mostrar_observacion ? "translate-x-4" : "translate-x-0"}`} />
                  </button>
                </div>

                {/* 3) Personalizar por usuario */}
                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-slate-200 text-sm font-medium">Personalizar por usuario</p>
                    <p className="text-slate-500 text-xs mt-0.5 max-w-md">
                      Cada usuario puede tener su propio formato. El que no configure el suyo no imprime automático (guarda nomás), no hereda el de la empresa.
                    </p>
                  </div>
                  <button type="button" disabled={savingImpresionConfig}
                    onClick={() => saveImpresionConfig({ personalizado_por_usuario: !impresionConfig.personalizado_por_usuario })}
                    className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 flex items-center ${impresionConfig.personalizado_por_usuario ? "bg-emerald-500" : "bg-slate-600"}`}>
                    <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform mx-1 ${impresionConfig.personalizado_por_usuario ? "translate-x-4" : "translate-x-0"}`} />
                  </button>
                </div>

                {/* 4) Tabla de usuarios con su preferencia */}
                {impresionConfig.personalizado_por_usuario && (
                  <div className="mt-3 border border-white/10 rounded-lg overflow-hidden">
                    {loadingImpresionUsuarios ? (
                      <div className="p-4 text-center text-slate-500 text-xs">Cargando usuarios...</div>
                    ) : impresionUsuarios.length === 0 ? (
                      <div className="p-4 text-center text-slate-500 text-xs">No hay usuarios asignados a esta empresa</div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-white/5 text-slate-500">
                            <th className="text-left px-3 py-2 font-medium">Usuario</th>
                            <th className="text-left px-3 py-2 font-medium">Formato</th>
                          </tr>
                        </thead>
                        <tbody>
                          {impresionUsuarios.map(u => (
                            <tr key={u.user_id} className="border-t border-white/5">
                              <td className="px-3 py-2 text-slate-300">{u.nombre}</td>
                              <td className="px-3 py-2">
                                <select
                                  value={u.modo || ""}
                                  disabled={savingImpresionUsuarioId === u.user_id}
                                  onChange={e => saveImpresionUsuario(u.user_id, e.target.value || null)}
                                  className="bg-arandu-dark border border-white/10 rounded-md text-slate-200 text-xs px-2 py-1"
                                >
                                  <option value="">Sin configurar (guarda nomás)</option>
                                  <option value="no">Guardar nomás</option>
                                  <option value="a4">A4</option>
                                  <option value="a4_doble">A4 doble vía</option>
                                  <option value="compacta">Compacta</option>
                                  <option value="ticket">Ticket</option>
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Card: Tipos de factura */}
            {(isAdmin || hasPermission("tipos_factura.gestionar")) && hasModule?.("tipos_factura") && (
              <div className="border border-white/10 rounded-xl p-5 bg-white/3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-indigo-500/15 flex items-center justify-center flex-shrink-0">
                      <Tag className="w-4 h-4 text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Tipos de factura</p>
                      <p className="text-slate-400 text-xs mt-0.5 max-w-sm">
                        Clasificá facturas por categoría (ej: Proyecto, Garantía, Mantenimiento). Cada tipo tiene nombre y color personalizable.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowTiposGestor(v => !v); if (!showTiposGestor) fetchTiposFac(); }}
                    className="h-8 text-xs bg-indigo-600/80 hover:bg-indigo-600 text-white px-3 flex-shrink-0 ml-4 rounded-lg flex items-center gap-1.5 transition-colors"
                  >
                    <Settings className="w-3.5 h-3.5" /> {showTiposGestor ? "Cerrar" : "Gestionar"}
                  </button>
                </div>
                {showTiposGestor && (
                  <div className="mt-4 border-t border-white/10 pt-4 space-y-3">
                    {/* Lista de tipos */}
                    {tiposFac.length === 0 && (
                      <p className="text-slate-500 text-xs text-center py-2">Sin tipos creados. Creá el primero.</p>
                    )}
                    {tiposFac.map(t => (
                      <div key={t.id} className="flex items-center justify-between gap-3 py-2 border-b border-white/5">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{background: t.color}} />
                          <span className="text-white text-sm">{t.nombre}</span>
                          <span className="text-slate-500 text-xs font-mono">{t.color}</span>
                          {t.cliente_nombre && (
                            <span className="text-[10px] bg-violet-500/15 text-violet-300 border border-violet-500/20 px-1.5 py-0.5 rounded-full">
                              Obra de: {t.cliente_nombre}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1.5">
                          <button onClick={() => { setTipoFormItem(t); setTipoFormNombre(t.nombre); setTipoFormColor(t.color); setTipoFormClienteId(t.cliente_id || ""); }}
                            className="px-2.5 py-1 text-xs bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg transition-colors">Editar</button>
                          <button onClick={async () => {
                            if (!window.confirm(`¿Eliminar tipo "${t.nombre}"?`)) return;
                            const r = await fetch(`${API}/admin/tipos-factura/${t.id}`, { method:"DELETE", headers });
                            if (r.ok) { fetchTiposFac(); toast.success("Tipo eliminado"); }
                            else toast.error(await readApiError(r, "No se pudo eliminar el tipo"));
                          }} className="px-2.5 py-1 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors">Eliminar</button>
                        </div>
                      </div>
                    ))}
                    {/* Botón nuevo / form inline */}
                    {tipoFormItem === null ? (
                      <button onClick={() => { setTipoFormItem({}); setTipoFormNombre(""); setTipoFormColor("#6366f1"); setTipoFormClienteId(""); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 border border-indigo-500/20 rounded-lg text-xs transition-colors">
                        <Plus className="w-3.5 h-3.5" /> Nuevo tipo
                      </button>
                    ) : (
                      <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                        <p className="text-white text-sm font-semibold">{tipoFormItem.id ? "Editar tipo" : "Nuevo tipo"}</p>
                        <div className="flex gap-3">
                          <input value={tipoFormNombre} onChange={e => setTipoFormNombre(e.target.value)}
                            placeholder="Nombre del tipo"
                            className="flex-1 bg-arandu-dark border border-white/10 text-white placeholder-slate-500 rounded-lg px-3 py-2 text-sm focus:border-indigo-500/50 outline-none transition-colors" />
                          <div className="flex items-center gap-2 border border-white/10 rounded-lg px-3 bg-arandu-dark">
                            <span className="w-5 h-5 rounded-full border border-white/20" style={{background: tipoFormColor}} />
                            <input type="color" value={tipoFormColor} onChange={e => setTipoFormColor(e.target.value)}
                              className="w-6 h-6 rounded cursor-pointer bg-transparent border-0 outline-none p-0"
                              title="Elegir color" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-slate-400 text-xs mb-1">
                            Cliente dueño de la obra (opcional — usado por el reporte Balancete - Obra)
                          </label>
                          <select value={tipoFormClienteId} onChange={e => setTipoFormClienteId(e.target.value)}
                            className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:border-indigo-500/50 outline-none transition-colors">
                            <option value="">Sin cliente asignado</option>
                            {empresas.map(e => (
                              <option key={e.id} value={e.id}>{e.nombre || e.razon_social}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex gap-2">
                          <button disabled={savingTipo || !tipoFormNombre.trim()} onClick={async () => {
                            setSavingTipo(true);
                            try {
                              const logo = activeEmpresaPropia?.slug || "";
                              const url = tipoFormItem.id
                                ? `${API}/admin/tipos-factura/${tipoFormItem.id}`
                                : `${API}/admin/tipos-factura`;
                              const method = tipoFormItem.id ? "PUT" : "POST";
                              const clienteSel = tipoFormClienteId ? empresas.find(e => e.id === tipoFormClienteId) : null;
                              const body = {
                                nombre: tipoFormNombre.trim(), color: tipoFormColor, logo_tipo: logo,
                                cliente_id: clienteSel?.id || null,
                                cliente_nombre: clienteSel ? (clienteSel.nombre || clienteSel.razon_social) : null,
                              };
                              const r = await fetch(url, { method, headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(body) });
                              if (!r.ok) throw new Error(await readApiError(r, "No se pudo guardar el tipo de factura"));
                              toast.success(tipoFormItem.id ? "Tipo actualizado" : "Tipo creado");
                              setTipoFormItem(null);
                              fetchTiposFac();
                            } catch(e) { toast.error(e?.message || "No se pudo guardar el tipo de factura"); }
                            finally { setSavingTipo(false); }
                          }} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs rounded-lg transition-colors">
                            {savingTipo ? "Guardando..." : "Guardar"}
                          </button>
                          <button onClick={() => setTipoFormItem(null)}
                            className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-slate-400 text-xs rounded-lg transition-colors">Cancelar</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Tab: Presupuestos ─────────────────────────────────── */}
        {tab === "presupuestos" && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-500 text-xs font-body">{filteredPres.length} presupuesto{filteredPres.length !== 1 ? "s" : ""}</span>
            </div>
            {loading ? (
              <div className="text-center py-10 text-slate-500 animate-pulse font-body">Cargando...</div>
            ) : filteredPres.length === 0 ? (
              <div className="text-center py-12 text-slate-500 font-body">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>Sin presupuestos para este período</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm font-body">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/3">
                      <SortTh label="N°"       sortKey="numero"    currentSort={sortBy.presupuestos} onClick={(k) => toggleSort("presupuestos", k)} className="text-left" />
                      <SortTh label="Empresa"  sortKey="empresa_nombre" currentSort={sortBy.presupuestos} onClick={(k) => toggleSort("presupuestos", k)} className="text-left" />
                      <SortTh label="Fecha"    sortKey="fecha"     currentSort={sortBy.presupuestos} onClick={(k) => toggleSort("presupuestos", k)} className="text-left" />
                      <SortTh label="Total"    sortKey="total"     currentSort={sortBy.presupuestos} onClick={(k) => toggleSort("presupuestos", k)} className="text-right" />
                      <th className="text-right px-4 py-3 text-slate-400 font-medium text-xs">Utilidad</th>
                      <SortTh label="Estado"   sortKey="estado"    currentSort={sortBy.presupuestos} onClick={(k) => toggleSort("presupuestos", k)} className="text-center" />
                      <th className="px-4 py-3 text-slate-400 font-medium text-xs text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPres.map(p => {
                      const total = p.total || 0;
                      const { utilidad } = calcUtilidadPresupuesto(p);
                      const utilPct = total > 0 && utilidad != null ? (utilidad / total * 100).toFixed(0) : null;
                      return (
                        <tr key={p.id}
                          onClick={() => openPresDoc(p.id)}
                          data-testid={`pres-row-${p.id}`}
                          className="border-b border-white/5 hover:bg-white/3 cursor-pointer transition-colors">
                          <td className="px-4 py-3">
                            <span className="font-mono text-blue-300">#{p.numero}</span>
                            {p.nombre_archivo && (
                              <p className="text-slate-500 text-xs truncate max-w-[130px]">{p.nombre_archivo}</p>
                            )}
                            {p.aprobacion_estado === "parcial" && (
                              <span title={`Monto aprobado: ${fmtMonto(p.monto_aprobado || 0, p.moneda)}`} className="inline-block mt-0.5 mr-1 text-[10px] bg-amber-500/15 text-amber-300 border border-amber-500/30 px-1.5 py-0.5 rounded-full">Aprob. parcial {p.aprobado_pct}%</span>
                            )}
                            {p.aprobacion_estado === "total" && (
                              <span title={`Monto aprobado: ${fmtMonto(p.monto_aprobado || 0, p.moneda)}`} className="inline-block mt-0.5 mr-1 text-[10px] bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-1.5 py-0.5 rounded-full">Aprob. total</span>
                            )}
                            {p.aprobacion_estado !== "sin_aprobar" && p.compra_estado && p.compra_estado !== "sin_compra" && (
                              <span title="Avance de compras sobre lo aprobado" className={`inline-block mt-0.5 mr-1 text-[10px] border px-1.5 py-0.5 rounded-full ${p.compra_estado === "completa" ? "bg-violet-500/15 text-violet-300 border-violet-500/30" : "bg-sky-500/15 text-sky-300 border-sky-500/30"}`}>
                                {p.compra_estado === "completa" ? "Comprado total" : `Comprado ${p.compra_pct}%`}
                              </span>
                            )}
                            {p.facturas_count > 0 && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  // Buscar primero en el estado local
                                  let facVinc = facturas.find(f =>
                                    (f.presupuesto_ids || []).includes(p.id) || f.presupuesto_id === p.id
                                  );
                                  // Si no está cargada, la fetcheamos directamente
                                  if (!facVinc) {
                                    try {
                                      const r = await fetch(`${API}/admin/facturas?tipo=emitida`, { headers: { Authorization: `Bearer ${token}` } });
                                      if (r.ok) {
                                        const all = await r.json();
                                        facVinc = all.find(f => (f.presupuesto_ids || []).includes(p.id) || f.presupuesto_id === p.id);
                                      }
                                    } catch {}
                                  }
                                  if (facVinc) {
                                    // Cargar detalle completo si hace falta
                                    const full = await fetch(`${API}/admin/facturas/${facVinc.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                    if (full.ok) setFacDoc(await full.json());
                                    else openFacDoc(facVinc.id);
                                  } else {
                                    toast.info("Factura vinculada no encontrada");
                                  }
                                }}
                                title="Ver factura vinculada"
                                data-testid={`pres-${p.id}-fact-link`}
                                className="mt-0.5 text-[10px] bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/20 px-1.5 py-0.5 rounded-full transition-all"
                              >
                                🧾 {p.facturas_count > 1 ? `${p.facturas_count} fact.` : "fact."}
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-300 max-w-[160px] truncate">{p.empresa_nombre || "-"}</td>
                          <td className="px-4 py-3 text-slate-400 text-xs">{p.fecha || "-"}</td>
                          <td className="px-4 py-3 text-right text-white font-medium">
                            {fmtMonto(total, p.moneda)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {utilidad != null ? (
                              <span className={utilidad >= 0 ? "text-emerald-300" : "text-red-300"}>
                                {fmtMonto(utilidad, p.moneda)}
                                {utilPct && <span className="text-xs text-slate-500 ml-1">({utilPct}%)</span>}
                              </span>
                            ) : (
                              <span className="text-slate-600 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                            <StateDropdown
                              estado={p.estado}
                              options={PRESUP_ESTADOS}
                              onChange={(nuevo) => nuevo === "aprobado" ? setPresAprobarId(p.id) : cambiarEstadoPresupuesto(p.id, nuevo)}
                              disabled={!hasPermission("presupuestos.editar") || (p.facturas_count > 0)}
                            />
                          </td>
                          <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              {hasPermission("presupuestos.editar") && (
                                <button
                                  onClick={async () => {
                                    const res = await fetch(`${API}/admin/presupuestos/${p.id}`, { headers });
                                    if (res.ok) {
                                      const full = await res.json();
                                      setPresFormItem({ presupuesto: full, mode: "edit" });
                                    } else {
                                      toast.error("Error al cargar presupuesto");
                                    }
                                  }}
                                  title="Editar"
                                  className="p-1.5 rounded-lg text-yellow-400 hover:bg-yellow-500/10 transition-all"
                                >
                                  <Edit className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {hasPermission("presupuestos.crear") && (
                                <button
                                  onClick={async () => {
                                    const res = await fetch(`${API}/admin/presupuestos/${p.id}`, { headers });
                                    if (res.ok) {
                                      const full = await res.json();
                                      setPresFormItem({ presupuesto: full, mode: "copy" });
                                    } else {
                                      toast.error("Error al cargar presupuesto");
                                    }
                                  }}
                                  title="Duplicar"
                                  className="p-1.5 rounded-lg text-purple-400 hover:bg-purple-500/10 transition-all"
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {hasPermission("costos.editar") && (
                                <button
                                  onClick={async () => {
                                    const res = await fetch(`${API}/admin/presupuestos/${p.id}`, { headers });
                                    if (res.ok) {
                                      const full = await res.json();
                                      setPresCostosItem(full);
                                    } else {
                                      toast.error("Error al cargar presupuesto");
                                    }
                                  }}
                                  title="Costos"
                                  className="p-1.5 rounded-lg text-cyan-400 hover:bg-cyan-500/10 transition-all"
                                >
                                  <Wallet className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {!mostrarEliminadas.presupuestos && !p.eliminada && p.estado !== "cobrado" && (hasPermission("presupuestos.aprobar_parcial") || hasPermission("presupuestos.editar")) && (
                                <button
                                  onClick={() => setPresAprobarId(p.id)}
                                  title="Aprobar (total o parcial) / histórico"
                                  className="p-1.5 rounded-lg text-emerald-400 hover:bg-emerald-500/10 transition-all"
                                >
                                  <CheckCircle className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {!mostrarEliminadas.presupuestos && !p.eliminada && ["aprobado","facturado","cobrado"].includes(p.estado) && hasPermission("presupuestos.editar") && (
                                <button
                                  onClick={() => openPresFacturar(p)}
                                  title="Vincular factura / compra"
                                  className="p-1.5 rounded-lg text-orange-400 hover:bg-orange-500/10 transition-all"
                                >
                                  <Receipt className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {!mostrarEliminadas.presupuestos && hasPermission("presupuestos.eliminar") && (
                                <button
                                  onClick={() => deletePresupuesto(p.id)}
                                  title="Eliminar"
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <AuditoriaRegistroBtn registro={p} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Tab: Facturas ─────────────────────────────────────── */}
        {tab === "facturas" && (
          <div>
            {/* ── Barra de acciones bulk ── */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-500 text-xs font-body">{filteredFac.length} factura{filteredFac.length !== 1 ? "s" : ""}</span>
              {!mostrarEliminadas.facturas && selectedFacIds.size > 0 && hasPermission("facturas.editar") && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">{selectedFacIds.size} seleccionada{selectedFacIds.size !== 1 ? "s" : ""}</span>
                  <button
                    onClick={() => {
                      setBulkFechaPago(new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));
                      const defBulk = cuentasDisp.find(c => c.moneda === "PYG" && c.es_predeterminada) || cuentasDisp.find(c => c.moneda === "PYG") || cuentasDisp[0];
                      setBulkCuentaId(defBulk?.id || "");
                      setShowBulkPagoModal(true);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-300 border border-emerald-600/30 rounded-lg transition-all font-body"
                  >
                    <CheckCircle className="w-3.5 h-3.5" /> Pagar seleccionadas ({selectedFacIds.size})
                  </button>
                  <button onClick={() => setSelectedFacIds(new Set())} className="text-xs text-slate-500 hover:text-slate-300 transition-colors">Limpiar</button>
                </div>
              )}
            </div>
            {loading ? (
              <div className="text-center py-10 text-slate-500 animate-pulse font-body">Cargando...</div>
            ) : filteredFac.length === 0 ? (
              <div className="text-center py-12 text-slate-500 font-body">
                <Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>Sin facturas para este período</p>
                {!mostrarEliminadas.facturas && hasPermission("facturas.crear") && (
                  <button onClick={() => { setFacFormItem(null); setShowFacForm(true); }}
                    className="mt-3 text-amber-400 hover:text-amber-300 text-sm">+ Nueva factura</button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm font-body">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/3">
                      {!mostrarEliminadas.facturas && hasPermission("facturas.editar") && (
                        <th className="pl-4 py-3 w-8">
                          {(() => {
                            const pagables = filteredFac.filter(f => !f.eliminada && (f.estado === "pendiente" || f.estado === "parcial"));
                            const allSelected = pagables.length > 0 && pagables.every(f => selectedFacIds.has(f.id));
                            return (
                              <input type="checkbox" checked={allSelected}
                                onChange={() => {
                                  if (allSelected) {
                                    setSelectedFacIds(prev => { const n = new Set(prev); pagables.forEach(f => n.delete(f.id)); return n; });
                                  } else {
                                    setSelectedFacIds(prev => { const n = new Set(prev); pagables.forEach(f => n.add(f.id)); return n; });
                                  }
                                }}
                                className="rounded border-slate-600 bg-arandu-dark accent-emerald-500 cursor-pointer"
                                title="Seleccionar todas las pendientes"
                              />
                            );
                          })()}
                        </th>
                      )}
                      <SortTh label="N° Factura"  sortKey="numero"       currentSort={sortBy.facturas} onClick={(k) => toggleSort("facturas", k)} className="text-left" />
                      <SortTh label="Cliente" sortKey="empresa_nombre" currentSort={sortBy.facturas} onClick={(k) => toggleSort("facturas", k)} className="text-left" />
                      <SortTh label="Fecha"       sortKey="fecha"        currentSort={sortBy.facturas} onClick={(k) => toggleSort("facturas", k)} className="text-left" />
                      <SortTh label="Monto"       sortKey="monto"        currentSort={sortBy.facturas} onClick={(k) => toggleSort("facturas", k)} className="text-right" />
                      <SortTh label="Estado"      sortKey="estado"       currentSort={sortBy.facturas} onClick={(k) => toggleSort("facturas", k)} className="text-center" />
                      <th className="px-4 py-3 text-slate-400 font-medium text-xs text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFac.map(f => (
                      <tr key={f.id}
                        onClick={() => openFacDoc(f.id)}
                        data-testid={`fact-row-${f.id}`}
                        className={`border-b border-white/5 hover:bg-white/3 cursor-pointer transition-colors ${f.estado === "anulada" ? "opacity-50" : ""} ${selectedFacIds.has(f.id) ? "bg-emerald-500/5" : ""}`}>
                        {!mostrarEliminadas.facturas && hasPermission("facturas.editar") && (
                          <td className="pl-4 py-3" onClick={e => e.stopPropagation()}>
                            {!f.eliminada && (f.estado === "pendiente" || f.estado === "parcial") && (
                              <input type="checkbox"
                                checked={selectedFacIds.has(f.id)}
                                onChange={() => setSelectedFacIds(prev => {
                                  const n = new Set(prev);
                                  n.has(f.id) ? n.delete(f.id) : n.add(f.id);
                                  return n;
                                })}
                                className="rounded border-slate-600 bg-arandu-dark accent-emerald-500 cursor-pointer"
                              />
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <span className={`font-mono ${f.sin_factura ? "text-violet-300" : "text-emerald-300"}`}>
                            {f.sin_factura ? (f.numero_boleta || f.numero || "-") : (f.numero || "-")}
                          </span>
                          {f.sin_factura && (
                            <span className="ml-2 text-[10px] bg-violet-500/15 text-violet-300 border border-violet-500/20 px-1.5 py-0.5 rounded-full">boleta</span>
                          )}
                          {f.forma_pago === "credito" && (
                            <span className="ml-2 text-[10px] bg-orange-500/15 text-orange-300 border border-orange-500/20 px-1.5 py-0.5 rounded-full">crédito</span>
                          )}
                          {f.entrega_posterior && f.estado_entrega && (
                            <button
                              type="button"
                              title="Ver entrega"
                              onClick={() => setSearchParams({ tab: "entregas", entrega_factura: f.id })}
                              className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full border ${f.estado_entrega === "entregado" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/20" : f.estado_entrega === "parcial" ? "bg-blue-500/15 text-blue-300 border-blue-500/20" : "bg-amber-500/15 text-amber-300 border-amber-500/20"}`}
                            >
                              {f.estado_entrega === "entregado" ? "entregada" : f.estado_entrega === "parcial" ? "entrega parcial" : "entrega pendiente"}
                            </button>
                          )}
                          {(f.presupuesto_ids?.length > 0 || f.presupuesto_id) && (() => {
                            // Antes esto era un botón que abría siempre el PRIMER presupuesto
                            // vinculado, aunque el badge mostrara "N pres." — con 2+ vinculados
                            // no había forma de elegir cuál ver (bug real, reportado). Con 1 solo
                            // se mantiene el botón directo; con 2+ se muestra un <select> nativo
                            // con cada uno (número de presupuesto) para elegir cuál abrir.
                            const pids = f.presupuesto_ids?.length ? f.presupuesto_ids : (f.presupuesto_id ? [f.presupuesto_id] : []);
                            if (pids.length <= 1) {
                              return (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    if (pids[0]) openPresDoc(pids[0]);
                                  }}
                                  className="ml-1 text-[10px] bg-blue-500/15 text-blue-300 border border-blue-500/20 px-1.5 py-0.5 rounded-full hover:bg-blue-500/25 transition-colors cursor-pointer"
                                >
                                  📄 pres.
                                </button>
                              );
                            }
                            return (
                              <select
                                onClick={e => e.stopPropagation()}
                                onChange={e => {
                                  const val = e.target.value;
                                  if (val) openPresDoc(val);
                                  e.target.value = "";
                                }}
                                defaultValue=""
                                className="ml-1 text-[10px] bg-blue-500/15 text-blue-300 border border-blue-500/20 px-1.5 py-0.5 rounded-full hover:bg-blue-500/25 transition-colors cursor-pointer"
                                title="Elegí cuál presupuesto vinculado ver"
                              >
                                <option value="" disabled>📄 {pids.length} pres.</option>
                                {pids.map(pid => {
                                  const p = presupuestos.find(x => x.id === pid);
                                  return (
                                    <option key={pid} value={pid}>
                                      {p ? `Ver #${p.numero}` : `Ver ${pid}`}
                                    </option>
                                  );
                                })}
                              </select>
                            );
                          })()}
</td>
                        <td className="px-4 py-3 max-w-[180px]">
                          <p className="text-slate-200 truncate font-medium">{f.empresa_nombre || f.razon_social || "-"}</p>
                          {f.empresa_nombre && f.razon_social && f.empresa_nombre !== f.razon_social && (
                            <p className="text-slate-500 text-xs truncate">{f.razon_social}</p>
                          )}
                          {f.ruc && <p className="text-slate-600 text-[11px]">RUC: {f.ruc}</p>}
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                          {f.fecha || "-"}
                          {f.fecha_vencimiento && f.estado !== "pagada" && (
                            <p className={`text-[10px] ${new Date(f.fecha_vencimiento) < new Date() ? "text-red-400" : "text-slate-500"}`}>
                              Vence: {f.fecha_vencimiento}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-white font-medium">{fmtMonto(f.monto, f.moneda)}</span>
                          {f.monto_pagado > 0 && f.estado !== "pagada" && (
                            <p className="text-blue-400 text-xs">Abonado: {fmtMonto(f.monto_pagado, f.moneda)}</p>
                          )}
                          {f.moneda !== "PYG" && f.tipo_cambio && (
                            <p className="text-slate-500 text-xs">TC: {f.tipo_cambio}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                          <StateBadge estado={f.estado} />
                          {/* Historial de pagos si hay varios */}
                          {(f.pagos || []).length > 1 && (
                            <div className="mt-1 space-y-0.5">
                              {(f.pagos || []).map((p, i) => (
                                <p key={p.id || i} className="text-[10px]">
                                  <span className="text-slate-500">{p.fecha}</span>
                                  <span className="text-blue-300 ml-1">{fmtMonto(p.monto, f.moneda)}</span>
                                  {p.descuento > 0 && <span className="text-amber-400 ml-1">· desc. {Number(p.descuento).toLocaleString("es-PY")}</span>}
                                  {p.registrado_por && <span className="text-slate-500 ml-1">· {p.registrado_por}</span>}
                                </p>
                              ))}
                            </div>
                          )}
                          {(f.pagos || []).length === 0 && f.fecha_pago && (
                            <p className="text-slate-500 text-[10px] mt-0.5">{f.fecha_pago}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {/* Pagar / Registrar pago */}
                            {!mostrarEliminadas.facturas && !f.eliminada && (f.estado === "pendiente" || f.estado === "parcial") && hasPermission("facturas.editar") && (
                              f.forma_pago === "credito" ? (
                                <button onClick={() => openPagoParcial(f)} title="Registrar pago"
                                  className="text-xs bg-blue-600/20 hover:bg-blue-600/40 text-blue-300 border border-blue-600/30 px-2 py-1 rounded-lg transition-all">
                                  Registrar pago
                                </button>
                              ) : (
                                <button onClick={() => openPagoContado(f)} title="Pagar"
                                  className="text-xs bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-300 border border-emerald-600/30 px-2 py-1 rounded-lg transition-all">
                                  Pagar
                                </button>
                              )
                            )}
                            {/* Deshacer pago — deshabilitado, para corregir eliminar y recrear */}
                            {/* Editar factura — deshabilitado, para corregir eliminar y recrear */}
                            {/* Anular */}
                            {!mostrarEliminadas.facturas && f.estado !== "anulada" && hasPermission("facturas.anular") && (
                              <button onClick={() => handleAnularFac(f)} title="Anular"
                                className="p-1.5 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 transition-all">
                                <AlertCircle className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {/* Eliminar */}
                            {!mostrarEliminadas.facturas && hasPermission("facturas.eliminar") && (
                              <button onClick={() => handleDeleteFac(f)} title="Eliminar"
                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <AuditoriaRegistroBtn registro={f} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "ingresos" && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-500 text-xs font-body">{filteredIng.length} ingreso{filteredIng.length !== 1 ? "s" : ""}</span>
            </div>
            {loading ? (
              <div className="text-center py-10 text-slate-500 animate-pulse font-body">Cargando...</div>
            ) : filteredIng.length === 0 ? (
              <div className="text-center py-12 text-slate-500 font-body">
                <TrendingUp className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>Sin ingresos varios para este período</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm font-body">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/3">
                      <SortTh label="Descripción" sortKey="descripcion" currentSort={sortBy.ingresos} onClick={(k) => toggleSort("ingresos", k)} className="text-left" />
                      <SortTh label="Categoría"   sortKey="categoria"   currentSort={sortBy.ingresos} onClick={(k) => toggleSort("ingresos", k)} className="text-left" />
                      <SortTh label="Fecha"       sortKey="fecha"       currentSort={sortBy.ingresos} onClick={(k) => toggleSort("ingresos", k)} className="text-left" />
                      <SortTh label="Monto"       sortKey="monto"       currentSort={sortBy.ingresos} onClick={(k) => toggleSort("ingresos", k)} className="text-right" />
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredIng.filter(i => i && i.id).map(i => (
                      <tr key={i.id}
                        data-testid={`ing-row-${i.id}`}
                        className="border-b border-white/5 hover:bg-white/3 transition-colors">
                        <td className="px-4 py-3 text-slate-200 max-w-[220px] cursor-pointer" onClick={() => openPreview("ingreso", i)}>
                          <p className="truncate">{i.descripcion || "-"}</p>
                          {i.cuenta_nombre && <p className="text-blue-400 text-xs truncate">🏦 {i.cuenta_nombre}</p>}
                          {i.notas && <p className="text-slate-500 text-xs truncate">{i.notas}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/20">
                            {i.categoria || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{i.fecha || "-"}</td>
                        <td className="px-4 py-3 text-right text-violet-300 font-medium">
                          {fmtMonto(i.monto, i.moneda)}
                          {i.moneda !== "PYG" && i.tipo_cambio && (
                            <p className="text-slate-500 text-xs">≈ {fmtMonto(i.monto * i.tipo_cambio, "PYG")}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* editar ingreso deshabilitado — para corregir eliminar y recrear */}
                            {!mostrarEliminadas.ingresos && !i.eliminada && (
                              <button onClick={async () => {
                                if (!window.confirm("¿Eliminar este ingreso?")) return;
                                const res = await fetch(`${API}/admin/ingresos-varios/${i.id}`, { method: "DELETE", headers });
                                if (res.ok) { toast.success("Ingreso eliminado"); fetchIngresos(); }
                                else toast.error("Error al eliminar");
                              }} className="text-slate-400 hover:text-red-400 transition-colors" title="Eliminar">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                            <AuditoriaRegistroBtn registro={i} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

	        {/* ── Tab: Recibos ─────────────────────────────────────── */}
	        {tab === "recibos" && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-500 text-xs font-body">{filteredRecibos.length} recibo{filteredRecibos.length !== 1 ? "s" : ""}</span>
            </div>
            {loading ? (
              <div className="text-center py-10 text-slate-500 animate-pulse font-body">Cargando...</div>
            ) : filteredRecibos.length === 0 ? (
              <div className="text-center py-12 text-slate-500 font-body">
                <Banknote className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No hay recibos para este período</p>
                <p className="text-xs mt-1 text-slate-600">Los recibos se generan al registrar pagos de facturas</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm font-body">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/3">
                      <SortTh label="Nº Recibo"     sortKey="numero"        currentSort={sortBy.recibos} onClick={(k) => toggleSort("recibos", k)} className="text-left" />
                      <SortTh label="Factura"        sortKey="factura_numero" currentSort={sortBy.recibos} onClick={(k) => toggleSort("recibos", k)} className="text-left" />
                      <SortTh label="Empresa / RZ"   sortKey="razon_social"   currentSort={sortBy.recibos} onClick={(k) => toggleSort("recibos", k)} className="text-left" />
                      <SortTh label="Fecha de pago"  sortKey="fecha_pago"     currentSort={sortBy.recibos} onClick={(k) => toggleSort("recibos", k)} className="text-left" />
                      <SortTh label="Monto"          sortKey="monto"          currentSort={sortBy.recibos} onClick={(k) => toggleSort("recibos", k)} className="text-right" />
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecibos.map(r => (
                      <tr key={r.id}
                        onClick={() => setReciboDoc(r)}
                        className="border-b border-white/5 hover:bg-white/3 cursor-pointer transition-colors">
                        <td className="px-4 py-3">
                          <span className="font-mono text-amber-300">#{r.numero}</span>
                          {r.pendiente_edicion && (
                            <span className="ml-2 text-[10px] bg-amber-500/15 text-amber-300 border border-amber-500/25 px-1.5 py-0.5 rounded-full">
                              Pendiente de edición
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {(r.facturas?.length > 1 || r.bulk) ? (
                            <div className="space-y-0.5">
                              {(r.facturas || []).map((f, i) => (
                                <span key={i} className="block text-slate-300 font-mono text-xs">
                                  {f.factura_numero || "-"}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-300 font-mono text-xs">
                              {r.facturas?.length === 1
                                ? r.facturas[0].factura_numero
                                : (r.factura_numero || "-")}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 max-w-[200px]">
                          <p className="text-slate-200 truncate">{r.razon_social || "-"}</p>
                          {r.ruc && <p className="text-slate-500 text-xs">RUC: {r.ruc}</p>}
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{r.fecha_pago || "-"}</td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-white font-medium">{fmtMonto(r.monto, r.moneda)}</span>
                        </td>
                        <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => setReciboDoc(r)} className="p-1.5 rounded-lg text-slate-400 hover:text-blue-300 hover:bg-blue-500/10 transition-all" title="Ver / imprimir">
                              <Printer className="w-3.5 h-3.5" />
                            </button>
                            {hasPermission("recibos.eliminar") && (
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`¿Eliminar el recibo ${r.numero}? Solo se permite si no tiene pagos vinculados.`)) return;
                                  const res = await fetch(`${API}/admin/recibos/${r.id}`, { method: "DELETE", headers });
                                  if (res.ok) { toast.success("Recibo eliminado"); fetchRecibos(); }
                                  else {
                                    const err = await res.json().catch(() => ({}));
                                    toast.error(err.detail || "No se pudo eliminar el recibo");
                                  }
                                }}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
                                title="Eliminar recibo sin pagos"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <AuditoriaRegistroBtn registro={r} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
	          </div>
	        )}
          </div>
        )}

	        {/* ── Tab: Notas de crédito ────────────────────────────── */}
	        {tab === "notas" && (
	          <div>
	            <div className="flex items-center justify-between mb-3">
	              <span className="text-slate-500 text-xs font-body">{filteredNotas.length} nota{filteredNotas.length !== 1 ? "s" : ""} de crédito</span>
	            </div>
	            {loading ? (
	              <div className="text-center py-10 text-slate-500 animate-pulse font-body">Cargando...</div>
	            ) : filteredNotas.length === 0 ? (
	              <div className="text-center py-12 text-slate-500 font-body">
	                <RotateCcw className="w-10 h-10 mx-auto mb-2 opacity-30" />
	                <p>Sin notas de crédito para este período</p>
	              </div>
	            ) : (
	              <div className="overflow-x-auto rounded-xl border border-white/10">
	                <table className="w-full text-sm font-body">
	                  <thead>
	                    <tr className="border-b border-white/10 bg-white/3">
	                      <SortTh label="N°" sortKey="numero" currentSort={sortBy.notas} onClick={(k) => toggleSort("notas", k)} className="text-left" />
	                      <SortTh label="Factura" sortKey="factura_numero" currentSort={sortBy.notas} onClick={(k) => toggleSort("notas", k)} className="text-left" />
	                      <SortTh label="Cliente" sortKey="razon_social" currentSort={sortBy.notas} onClick={(k) => toggleSort("notas", k)} className="text-left" />
	                      <SortTh label="Fecha" sortKey="fecha" currentSort={sortBy.notas} onClick={(k) => toggleSort("notas", k)} className="text-left" />
	                      <SortTh label="Monto" sortKey="monto" currentSort={sortBy.notas} onClick={(k) => toggleSort("notas", k)} className="text-right" />
	                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Acciones</th>
	                    </tr>
	                  </thead>
	                  <tbody>
	                    {filteredNotas.map(n => (
	                      <tr key={n.id} className={`border-b border-white/5 hover:bg-white/3 transition-colors ${n.estado === "anulada" ? "opacity-50" : ""}`}>
	                        <td className="px-4 py-3"><span className="font-mono text-rose-300">{n.numero}</span></td>
	                        <td className="px-4 py-3 text-slate-400">{n.factura_numero || "-"}</td>
	                        <td className="px-4 py-3 max-w-[220px]">
	                          <p className="text-slate-200 truncate">{n.razon_social || "-"}</p>
	                          <p className="text-slate-500 text-xs truncate">{n.motivo}</p>
	                        </td>
	                        <td className="px-4 py-3 text-slate-400 text-xs">{n.fecha || "-"}</td>
	                        <td className="px-4 py-3 text-right text-rose-300 font-medium">{fmtMonto(n.monto, n.moneda)}</td>
	                        <td className="px-4 py-3 text-right">
	                          <div className="flex items-center justify-end gap-2">
	                            <button onClick={() => setNotaDoc(n)} className="text-slate-400 hover:text-rose-300 transition-colors" title="Ver / Imprimir">
	                              <Printer className="w-4 h-4" />
	                            </button>
	                            {/* editar nota crédito deshabilitado — para corregir eliminar y recrear */}
	                            {!mostrarEliminadas.notas && hasPermission("notas_credito.eliminar") && (
	                              <button onClick={async () => {
	                                if (!window.confirm("¿Eliminar esta nota de crédito?")) return;
	                                const res = await fetch(`${API}/admin/notas-credito/${n.id}`, { method: "DELETE", headers });
	                                if (res.ok) { toast.success("Nota eliminada"); fetchNotas(); }
	                                else toast.error("Error al eliminar");
	                              }} className="text-slate-400 hover:text-red-400 transition-colors" title="Eliminar">
	                                <Trash2 className="w-4 h-4" />
	                              </button>
	                            )}
	                            <AuditoriaRegistroBtn registro={n} />
	                          </div>
	                        </td>
	                      </tr>
	                    ))}
	                  </tbody>
	                </table>
	              </div>
	            )}
	          </div>
	        )}
      </div>

      {/* Carga de documento presupuesto */}
      {presDocLoading && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="text-white font-body text-sm animate-pulse">Cargando presupuesto…</div>
        </div>
      )}

      {/* Documento completo de presupuesto */}
      {presDoc && !presDocLoading && (
        <PresupuestoDocModal
          presupuesto={presDoc}
          activeEmpresaPropia={empresasPropias.find(ep => ep.slug === presDoc.logo_tipo) || activeEmpresaPropia}
          onClose={() => setPresDoc(null)}
          onEdit={() => { setPresDoc(null); setPresFormItem({ presupuesto: presDoc, mode: "edit" }); }}
          onDelete={() => deletePresupuesto(presDoc.id)}
          onDuplicate={() => duplicatePresupuesto(presDoc)}
          onCostos={() => { setPresDoc(null); setPresCostosItem(presDoc); }}
          onEstadoChange={(nuevo) => { if (nuevo === "aprobado") { setPresAprobarId(presDoc.id); return; } cambiarEstadoPresupuesto(presDoc.id, nuevo); setPresDoc(p => ({ ...p, estado: nuevo })); }}
          onAprobar={() => setPresAprobarId(presDoc.id)}
          canAprobar={!presDoc.eliminada && presDoc.estado !== "cobrado" && presDoc.estado !== "rechazado" && (hasPermission("presupuestos.aprobar_parcial") || hasPermission("presupuestos.editar"))}
          canEdit={!presDoc.eliminada && hasPermission("presupuestos.editar")}
          canDelete={!presDoc.eliminada && hasPermission("presupuestos.eliminar")}
          canCreate={!presDoc.eliminada && hasPermission("presupuestos.crear")}
          canCostos={!presDoc.eliminada && hasPermission("costos.editar")}
          formatNumber={formatNumber}
          getCurrencySymbol={getCurrencySymbol}
          PRESUP_ESTADOS={PRESUP_ESTADOS}
        />
      )}

      {/* ── Nuevo / Editar Ingreso ── */}
	      {showIngForm && (
	        <IngresosFormModal
          ingreso={ingFormItem}
          cuentasDisp={cuentasDisp}
          activeLogoTipo={activeEmpresaPropia?.slug || "arandujar"}
          empresas={empresas}
          canCrearVale={isAdmin || hasPermission("ingresos_varios.crear_vale")}
          token={token}
          API={API}
          onClose={() => { setShowIngForm(false); setIngFormItem(null); }}
          onSaved={() => { setShowIngForm(false); setIngFormItem(null); fetchIngresos(); toast.success("Ingreso guardado"); }}
	        />
	      )}

	      {showNotaForm && (
	        <NotaCreditoFormModal
	          nota={notaFormItem}
	          facturas={facturas.filter(f => f.tipo === "emitida" && f.estado !== "anulada")}
	          productos={productos}
	          cuentasDisp={cuentasDisp}
	          activeLogoTipo={activeEmpresaPropia?.slug || "arandujar"}
	          token={token}
	          API={API}
	          onClose={() => { setShowNotaForm(false); setNotaFormItem(null); }}
	          onSaved={() => { setShowNotaForm(false); setNotaFormItem(null); setTab("notas"); fetchNotas(); toast.success("Nota de crédito guardada"); }}
	        />
	      )}

      {/* Vista previa inline (facturas/ingresos) */}
      {previewItem && (
        <PreviewModal
          item={previewItem}
          onClose={closePreview}
          navigate={navigate}
          token={token}
          onUpdated={() => { closePreview(); fetchActiveTab(); }}
          cuentasDisp={cuentasDisp}
        />
      )}


      {/* Presupuesto Edit/Copy Form */}
      {presFormItem && (
        <PresupuestoFormModal
          presupuesto={presFormItem.presupuesto}
          mode={presFormItem.mode}
          onClose={() => setPresFormItem(null)}
          onSaved={(saved) => {
            setPresFormItem(null);
            if (saved?.id) {
              setPresupuestos(prev => {
                const idx = prev.findIndex(p => p.id === saved.id);
                if (idx >= 0) {
                  const next = [...prev];
                  next[idx] = { ...next[idx], ...saved };
                  return next;
                }
                return [saved, ...prev];
              });
            }
            fetchPresupuestos(true);
          }}
          token={token}
          API={API}
          empresas={empresas}
          proveedores={proveedores}
          productos={productos}
          productosHabilitados={hasModule?.("productos_stock")}
          activeEmpresaPropia={activeEmpresaPropia}
          isAdmin={user?.role === "admin"}
          hasPermission={hasPermission}
          defaultEmpresaId={presFormItem?.mode === "new" ? contextEmpresaId : ""}
          canUseDescuento={canUseDescuento}
        />
      )}

      {showRecepConfig && activeEmpresaPropia?.slug && (
        <RecepcionConfigModal API={API} token={token} logoTipo={activeEmpresaPropia.slug}
          canEdit={isAdmin || hasPermission("presupuestos.recepcion_configurar")} onClose={() => setShowRecepConfig(false)} />
      )}

      {/* Recepción de Equipo Form */}
      {recepFormItem && (
        <RecepcionEquipoFormModal
          recepcion={recepFormItem?.id ? recepFormItem : null}
          onClose={() => setRecepFormItem(null)}
          onSaved={() => { setRecepFormItem(null); fetchRecepciones(); }}
          token={token}
          API={API}
          empresas={empresas}
          activeEmpresaPropia={activeEmpresaPropia}
          canEditar={isAdmin || hasPermission("presupuestos.recepcion_editar")}
        />
      )}

      {/* Costos Modal */}
      {presCostosItem && (
        <PresupuestoCostosModal
          presupuesto={presCostosItem}
          onClose={() => setPresCostosItem(null)}
          onSaved={() => { setPresCostosItem(null); fetchPresupuestos(true); }}
          token={token}
          API={API}
          proveedores={proveedores}
        />
      )}

      {presAprobarId && (
        <PresupuestoAprobacionModal
          presupuestoId={presAprobarId}
          token={token}
          canEdit={hasPermission("presupuestos.aprobar_parcial") || hasPermission("presupuestos.editar")}
          onClose={() => setPresAprobarId(null)}
          onSaved={() => { fetchPresupuestos(true); if (presDoc?.id === presAprobarId) openPresDoc(presAprobarId); }}
        />
      )}

      {/* Facturar/Vincular Modal */}
      {presFacturarItem && (
        <FacturarModal
          presupuesto={presFacturarItem}
          onClose={() => setPresFacturarItem(null)}
          facturaForm={facturaForm}
          setFacturaForm={setFacturaForm}
          facturaMode={facturaMode}
          setFacturaMode={setFacturaMode}
          facturaSearch={facturaSearch}
          setFacturaSearch={setFacturaSearch}
          facturasDisponibles={facturasDisponibles}
          facturaSeleccionada={facturaSeleccionada}
          setFacturaSeleccionada={setFacturaSeleccionada}
          savingFactura={savingFactura}
          onFacturar={handleFacturar}
          onVincular={handleVincularExistente}
          comprasDisponibles={comprasDisponibles}
          compraSeleccionada={compraSeleccionada}
          setCompraSeleccionada={setCompraSeleccionada}
          compraSearch={compraSearch}
          setCompraSearch={setCompraSearch}
          onVincularCompra={handleVincularCompra}
          onDesvincular={hasPermission("presupuestos.desvincular") ? handleDesvincularFactura : undefined}
          vincQty={vincQty}
          setVincQty={setVincQty}
        />
      )}

      {/* ── Modal Config Timbrado (por punto de expedición) ── */}
      {showTimbradoConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-amber-400" />
                <h2 className="text-white font-heading font-bold text-lg">Configuración de Timbrado</h2>
              </div>
              <button onClick={() => setShowTimbradoConfig(false)} className="text-slate-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {timbradoLoading ? (
              <div className="flex items-center justify-center py-16 gap-3 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" /> Cargando...
              </div>
            ) : (
              <div className="p-6 space-y-6">
                <p className="text-slate-500 text-xs font-body -mt-2">
                  Cada punto de expedición tiene su propio timbrado, tipo de emisión y modo de numeración — podés tener, por ejemplo, un punto preimpreso y otro autoimpresor bajo el mismo RUC.
                </p>

                {/* Alertas (de todos los puntos) */}
                {timbradoData?.alertas?.map((a, i) => (
                  <div key={i} className={`flex items-start gap-2 p-3 rounded-lg text-sm font-body ${a.tipo === "error" ? "bg-red-500/10 border border-red-500/20 text-red-300" : "bg-amber-500/10 border border-amber-500/20 text-amber-300"}`}>
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {a.mensaje}
                  </div>
                ))}
                {timbradoData?.legacy_sin_migrar && (
                  <div className="flex items-start gap-2 p-3 rounded-lg text-sm font-body bg-blue-500/10 border border-blue-500/20 text-blue-300">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Esta empresa todavía no corrió la migración al esquema por punto — se está mostrando normalizado en memoria. Corré scripts/migrar_timbrado_por_punto.py --fix para dejarlo guardado así.
                  </div>
                )}

                {/* Habilitación de Autoimpresor — GLOBAL para toda la empresa, no por punto.
                    Solo se muestra si algún punto está configurado como "autoimpresor". */}
                {timbradoForm.puntos_expedicion.some(p => p.tipo_emision === "autoimpresor") && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <label className="text-amber-200 text-xs font-body font-semibold block mb-1">N° de Habilitación de Autoimpresor</label>
                    <input
                      value={timbradoForm.habilitacion_autoimpresor}
                      onChange={e => setTimbradoForm(f => ({...f, habilitacion_autoimpresor: e.target.value}))}
                      placeholder="ej: 123456"
                      className="w-full max-w-xs bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-body focus:outline-none focus:border-amber-500"
                    />
                    <p className="text-amber-300/70 text-[11px] mt-1 font-body">
                      Dato de toda la empresa (no por punto), exigido por DNIT — aparece en la factura impresa de cualquier punto configurado como Autoimpresor.
                    </p>
                  </div>
                )}

                {/* Punto por defecto */}
                <div>
                  <label className="text-slate-400 text-xs font-body block mb-2">Punto de expedición por defecto</label>
                  <select
                    value={timbradoForm.punto_default}
                    onChange={e => setTimbradoForm(f => ({...f, punto_default: e.target.value}))}
                    style={{ colorScheme: "dark" }}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-body focus:outline-none focus:border-amber-500"
                  >
                    {timbradoForm.puntos_expedicion.map(p => (
                      <option key={p.codigo} value={p.codigo}>{p.codigo}{p.descripcion ? ` — ${p.descripcion}` : ""}</option>
                    ))}
                  </select>
                  <p className="text-slate-500 text-xs mt-1 font-body">
                    El punto que usa un usuario si no tiene uno asignado personalmente (o si no está activada la personalización por usuario).
                  </p>
                </div>

                {/* Personalizar por usuario */}
                <div className="pt-4 border-t border-white/5 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-slate-200 text-sm font-medium">Personalizar punto por usuario</p>
                    <p className="text-slate-500 text-xs mt-0.5 max-w-md">
                      Asigná a cada usuario qué punto de expedición usa al facturar. El que no tenga asignación propia usa el punto por defecto de arriba.
                    </p>
                  </div>
                  <button type="button"
                    onClick={() => { const next = !timbradoForm.personalizado_por_usuario; setTimbradoForm(f => ({...f, personalizado_por_usuario: next})); if (next) fetchTimbradoUsuarios(); }}
                    className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 flex items-center ${timbradoForm.personalizado_por_usuario ? "bg-emerald-500" : "bg-slate-600"}`}>
                    <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform mx-1 ${timbradoForm.personalizado_por_usuario ? "translate-x-4" : "translate-x-0"}`} />
                  </button>
                </div>

                {timbradoForm.personalizado_por_usuario && (
                  <div className="border border-white/10 rounded-lg overflow-hidden">
                    {loadingTimbradoUsuarios ? (
                      <div className="p-4 text-center text-slate-500 text-xs">Cargando usuarios...</div>
                    ) : timbradoUsuarios.length === 0 ? (
                      <div className="p-4 text-center text-slate-500 text-xs">No hay usuarios asignados a esta empresa</div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-white/5 text-slate-500">
                            <th className="text-left px-3 py-2 font-medium">Usuario</th>
                            <th className="text-left px-3 py-2 font-medium">Punto de expedición</th>
                          </tr>
                        </thead>
                        <tbody>
                          {timbradoUsuarios.map(u => (
                            <tr key={u.user_id} className="border-t border-white/5">
                              <td className="px-3 py-2 text-slate-300">{u.nombre}</td>
                              <td className="px-3 py-2">
                                <select
                                  value={u.punto_expedicion || ""}
                                  disabled={savingTimbradoUsuarioId === u.user_id}
                                  onChange={e => saveTimbradoUsuario(u.user_id, e.target.value || null)}
                                  style={{ colorScheme: "dark" }}
                                  className="bg-arandu-dark border border-white/10 rounded-md text-slate-200 text-xs px-2 py-1"
                                >
                                  <option value="">Usa el default ({timbradoForm.punto_default})</option>
                                  {timbradoForm.puntos_expedicion.map(p => (
                                    <option key={p.codigo} value={p.codigo}>{p.codigo}{p.descripcion ? ` — ${p.descripcion}` : ""}</option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* Puntos de expedición — cada uno con su propio timbrado */}
                <div className="pt-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-slate-400 text-xs font-body">Puntos de expedición</label>
                    <button onClick={() => setTimbradoForm(f => ({...f, puntos_expedicion: [...f.puntos_expedicion, nuevoPuntoVacio(`00${f.puntos_expedicion.length+1}`.slice(-3))]}))}
                      className="text-xs text-amber-400 hover:text-amber-300 font-body">+ Agregar punto</button>
                  </div>
                  <div className="space-y-4">
                    {timbradoForm.puntos_expedicion.map((pto, idx) => {
                      const dataPunto = timbradoData?.puntos_expedicion?.find(p => p.codigo === pto.codigo);
                      const ta = dataPunto?.timbrado_activo;
                      return (
                      <div key={idx} className="bg-white/3 border border-white/10 rounded-xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-300 text-xs font-body font-semibold">
                            Punto {pto.codigo || idx+1}
                            {dataPunto && !dataPunto.vigente && ta && <span className="ml-2 text-red-400">vencido</span>}
                          </span>
                          {timbradoForm.puntos_expedicion.length > 1 && (
                            <button onClick={() => setTimbradoForm(f => ({...f, puntos_expedicion: f.puntos_expedicion.filter((_,i) => i !== idx)}))}
                              className="text-slate-500 hover:text-red-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
                          )}
                        </div>

                        {/* Huecos de numeración — números liberados por facturas eliminadas
                            dentro del rango ya usado de este punto. Solo visible con el
                            permiso granular (reutilizar un número es sensible). */}
                        {dataPunto && ta && hasPermission("configuracion_ventas.ver_huecos_timbrado") && (
                          <div className="border border-dashed border-amber-500/30 rounded-lg p-2.5 bg-amber-500/5">
                            <button
                              onClick={() => fetchHuecosNumeracion(pto.codigo)}
                              className="text-amber-300 hover:text-amber-200 text-xs font-body font-semibold transition-colors"
                            >
                              {huecosPuntoAbierto === pto.codigo ? "▾" : "▸"} Ver huecos de numeración
                            </button>
                            {huecosPuntoAbierto === pto.codigo && (
                              <div className="mt-2 space-y-1.5">
                                {huecosLoading ? (
                                  <p className="text-slate-500 text-xs font-body">Buscando...</p>
                                ) : huecosLista.length === 0 ? (
                                  <p className="text-slate-500 text-xs font-body">Sin huecos en este punto — la numeración está completa.</p>
                                ) : huecosLista.map(h => (
                                  <div key={h.numero} className="flex items-center justify-between bg-white/5 rounded-lg px-2.5 py-1.5">
                                    <div>
                                      <span className="text-white text-xs font-body font-mono">{h.numero}</span>
                                      <span className="text-slate-500 text-[11px] font-body ml-2">
                                        fecha válida: {h.fecha_desde || "sin mínimo"} a {h.fecha_hasta || "sin máximo"}
                                      </span>
                                    </div>
                                    <button
                                      onClick={() => {
                                        setHuecoParaFactura(h);
                                        setShowTimbradoConfig(false);
                                        setFacFormItem(null);
                                        setShowFacForm(true);
                                      }}
                                      className="text-amber-400 hover:text-amber-300 text-[11px] font-body font-semibold transition-colors shrink-0"
                                    >
                                      Crear factura
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-slate-500 text-xs font-body block mb-1">Código (ej: 001)</label>
                            <input value={pto.codigo} onChange={e => { const p=[...timbradoForm.puntos_expedicion]; p[idx]={...p[idx],codigo:e.target.value}; setTimbradoForm(f=>({...f,puntos_expedicion:p})); }}
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-body focus:outline-none focus:border-amber-500" maxLength={3} />
                          </div>
                          <div>
                            <label className="text-slate-500 text-xs font-body block mb-1">Descripción</label>
                            <input value={pto.descripcion} onChange={e => { const p=[...timbradoForm.puntos_expedicion]; p[idx]={...p[idx],descripcion:e.target.value}; setTimbradoForm(f=>({...f,puntos_expedicion:p})); }}
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-body focus:outline-none focus:border-amber-500" placeholder="ej: Oficina central" />
                          </div>
                        </div>

                        {/* Modo de numeración — por punto */}
                        <div>
                          <label className="text-slate-500 text-xs font-body block mb-1">Modo de numeración</label>
                          <div className="flex rounded-lg border border-white/10 overflow-hidden w-fit">
                            {[{v:"manual",label:"Manual"},{v:"automatico",label:"Automático"}].map(op => (
                              <button key={op.v} onClick={() => { const p=[...timbradoForm.puntos_expedicion]; p[idx]={...p[idx],modo_numeracion:op.v}; setTimbradoForm(f=>({...f,puntos_expedicion:p})); }}
                                className={`px-3 py-1.5 text-xs font-body transition-all ${pto.modo_numeracion === op.v ? "bg-amber-600 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
                                {op.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Tipo de emisión — por punto */}
                        <div>
                          <label className="text-slate-500 text-xs font-body block mb-1">Tipo de emisión</label>
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { v: "preimpreso", label: "Preimpreso" },
                              { v: "autoimpresor", label: "Autoimpresor" },
                              { v: "electronico", label: "Electrónico" },
                            ].map(op => {
                              const active = pto.tipo_emision === op.v;
                              return (
                                <button
                                  key={op.v}
                                  onClick={() => { const p=[...timbradoForm.puntos_expedicion]; p[idx]={...p[idx],tipo_emision:op.v, fecha_vigencia: op.v === "electronico" ? "" : p[idx].fecha_vigencia}; setTimbradoForm(f=>({...f,puntos_expedicion:p})); }}
                                  className={`text-left rounded-lg border px-3 py-2 transition-all ${active ? "bg-amber-500/15 border-amber-400/50 text-amber-100" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"}`}
                                >
                                  <span className="block text-xs font-semibold">{op.label}</span>
                                </button>
                              );
                            })}
                          </div>
                          {pto.tipo_emision === "autoimpresor" && (
                            <p className="text-[11px] text-amber-300/80 mt-1.5">
                              Este punto va a mostrar en la factura impresa el N° de Habilitación de Autoimpresor configurado abajo (dato de toda la empresa, exigido por DNIT).
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-slate-500 text-xs font-body block mb-1">Nro. Timbrado <span className="text-red-400">*</span></label>
                            <input value={pto.nro_timbrado} onChange={e => { const p=[...timbradoForm.puntos_expedicion]; p[idx]={...p[idx],nro_timbrado:e.target.value}; setTimbradoForm(f=>({...f,puntos_expedicion:p})); }}
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-body focus:outline-none focus:border-amber-500"
                              placeholder="ej: 12345678" />
                          </div>
                          <div>
                            <label className="text-slate-500 text-xs font-body block mb-1">Establecimiento</label>
                            <input value={pto.establecimiento} onChange={e => { const p=[...timbradoForm.puntos_expedicion]; p[idx]={...p[idx],establecimiento:e.target.value}; setTimbradoForm(f=>({...f,puntos_expedicion:p})); }}
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-body focus:outline-none focus:border-amber-500"
                              placeholder="001" maxLength={3} />
                          </div>
                          <div>
                            <label className="text-slate-500 text-xs font-body block mb-1">Fecha de inicio</label>
                            <DateInput value={pto.fecha_inicio} onChange={e => { const p=[...timbradoForm.puntos_expedicion]; p[idx]={...p[idx],fecha_inicio:e.target.value}; setTimbradoForm(f=>({...f,puntos_expedicion:p})); }}
                              style={{ colorScheme: "dark" }}
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-body focus:outline-none focus:border-amber-500" />
                          </div>
                          {pto.tipo_emision !== "electronico" ? (
                            <div>
                              <label className="text-slate-500 text-xs font-body block mb-1">Fecha de vigencia <span className="text-red-400">*</span></label>
                              <DateInput value={pto.fecha_vigencia} onChange={e => { const p=[...timbradoForm.puntos_expedicion]; p[idx]={...p[idx],fecha_vigencia:e.target.value}; setTimbradoForm(f=>({...f,puntos_expedicion:p})); }}
                                style={{ colorScheme: "dark" }}
                                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-body focus:outline-none focus:border-amber-500" />
                            </div>
                          ) : (
                            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-1.5 flex items-center">
                              <p className="text-emerald-300 text-xs">Sin vencimiento (electrónico)</p>
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-slate-500 text-xs font-body block mb-1">Nro. factura desde</label>
                            <input
                              type="text"
                              value={pto.numero_desde}
                              onChange={e => { const raw = e.target.value.replace(/\D/g,""); const p=[...timbradoForm.puntos_expedicion]; p[idx]={...p[idx],numero_desde:raw}; setTimbradoForm(f=>({...f,puntos_expedicion:p})); }}
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-mono focus:outline-none focus:border-amber-500"
                              placeholder="ej: 500"
                            />
                            <p className="text-[10px] text-slate-600 mt-0.5 font-mono">{pto.establecimiento||"001"}-{pto.codigo||"001"}-{pto.numero_desde||1}</p>
                          </div>
                          <div>
                            <label className="text-slate-500 text-xs font-body block mb-1">Nro. factura hasta</label>
                            <input
                              type="text"
                              value={pto.numero_hasta}
                              onChange={e => { const raw = e.target.value.replace(/\D/g,""); const p=[...timbradoForm.puntos_expedicion]; p[idx]={...p[idx],numero_hasta:raw}; setTimbradoForm(f=>({...f,puntos_expedicion:p})); }}
                              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-mono focus:outline-none focus:border-amber-500"
                              placeholder="ej: 1000"
                            />
                            <p className="text-[10px] text-slate-600 mt-0.5 font-mono">{pto.establecimiento||"001"}-{pto.codigo||"001"}-{pto.numero_hasta||9999999}</p>
                          </div>
                        </div>

                        {/* Rango de Nota de Crédito — mismo timbrado del punto (nro/vigencia/
                            modo de arriba), secuencia propia. Si no está activado, Nota de
                            Crédito sigue usando número libre para este punto (como siempre). */}
                        <div className="pt-2 border-t border-white/5">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-slate-400 text-xs font-body">Nota de Crédito (mismo timbrado, secuencia propia)</span>
                            <button type="button"
                              onClick={() => { const p=[...timbradoForm.puntos_expedicion]; p[idx]={...p[idx],nc_activo:!p[idx].nc_activo}; setTimbradoForm(f=>({...f,puntos_expedicion:p})); }}
                              className={`w-9 h-5 rounded-full transition-colors flex-shrink-0 flex items-center ${pto.nc_activo ? "bg-emerald-500" : "bg-slate-600"}`}>
                              <div className={`w-3.5 h-3.5 bg-white rounded-full shadow transition-transform mx-1 ${pto.nc_activo ? "translate-x-4" : "translate-x-0"}`} />
                            </button>
                          </div>
                          {pto.nc_activo && (
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-slate-500 text-xs font-body block mb-1">Nro. NC desde</label>
                                <input
                                  type="text"
                                  value={pto.nc_numero_desde}
                                  onChange={e => { const raw = e.target.value.replace(/\D/g,""); const p=[...timbradoForm.puntos_expedicion]; p[idx]={...p[idx],nc_numero_desde:raw}; setTimbradoForm(f=>({...f,puntos_expedicion:p})); }}
                                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-mono focus:outline-none focus:border-amber-500"
                                  placeholder="ej: 1"
                                />
                                <p className="text-[10px] text-slate-600 mt-0.5 font-mono">{pto.establecimiento||"001"}-{pto.codigo||"001"}-{pto.nc_numero_desde||1}</p>
                              </div>
                              <div>
                                <label className="text-slate-500 text-xs font-body block mb-1">Nro. NC hasta</label>
                                <input
                                  type="text"
                                  value={pto.nc_numero_hasta}
                                  onChange={e => { const raw = e.target.value.replace(/\D/g,""); const p=[...timbradoForm.puntos_expedicion]; p[idx]={...p[idx],nc_numero_hasta:raw}; setTimbradoForm(f=>({...f,puntos_expedicion:p})); }}
                                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-mono focus:outline-none focus:border-amber-500"
                                  placeholder="ej: 9999999"
                                />
                                <p className="text-[10px] text-slate-600 mt-0.5 font-mono">{pto.establecimiento||"001"}-{pto.codigo||"001"}-{pto.nc_numero_hasta||9999999}</p>
                              </div>
                            </div>
                          )}
                          {pto.nc_activo && pto.modo_numeracion === "automatico" && dataPunto?.timbrado_activo?.rango_nota_credito && (() => {
                            const rnc = dataPunto.timbrado_activo.rango_nota_credito;
                            const totalNc = rnc.numero_hasta - rnc.numero_desde + 1;
                            const usadoNc = rnc.ultimo_numero >= rnc.numero_desde ? rnc.ultimo_numero - rnc.numero_desde + 1 : 0;
                            const restantesNc = rnc.numero_hasta - Math.max(rnc.ultimo_numero, rnc.numero_desde - 1);
                            const pctNc = totalNc > 0 ? Math.round(usadoNc / totalNc * 100) : 0;
                            return (
                              <div className="mt-2">
                                <div className="flex justify-between text-xs text-slate-500 font-body mb-1">
                                  <span>Uso del rango (Nota de Crédito)</span>
                                  <span>{usadoNc}/{totalNc} ({pctNc}%) — quedan {restantesNc}</span>
                                </div>
                                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full transition-all ${pctNc >= 90 ? "bg-red-500" : pctNc >= 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{width:`${pctNc}%`}} />
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        {pto.modo_numeracion === "automatico" && ta && (() => {
                          const total = ta.numero_hasta - ta.numero_desde + 1;
                          const usado = ta.ultimo_numero >= ta.numero_desde ? ta.ultimo_numero - ta.numero_desde + 1 : 0;
                          const restantes = ta.numero_hasta - Math.max(ta.ultimo_numero, ta.numero_desde - 1);
                          const pct = total > 0 ? Math.round(usado / total * 100) : 0;
                          return (
                            <div className="mt-1">
                              <div className="flex justify-between text-xs text-slate-500 font-body mb-1">
                                <span>Uso del rango</span>
                                <span>{usado}/{total} ({pct}%) — quedan {restantes}</span>
                              </div>
                              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{width:`${pct}%`}} />
                              </div>
                            </div>
                          );
                        })()}

                        {dataPunto?.historial_timbrados?.length > 0 && (
                          <div>
                            <p className="text-slate-500 text-xs font-body mb-1.5">Historial de este punto</p>
                            <div className="space-y-1.5">
                              {dataPunto.historial_timbrados.map((h, i) => (
                                <div key={i} className="bg-white/3 border border-white/8 rounded-lg px-3 py-1.5 text-[11px] font-body flex items-center justify-between">
                                  <div>
                                    <span className="text-slate-300 font-mono">{h.nro_timbrado}</span>
                                    <span className="text-slate-500 ml-2">{h.establecimiento}</span>
                                    <span className="text-slate-600 ml-2">{h.fecha_inicio} → {h.tipo_emision === "electronico" ? "sin vencimiento" : h.fecha_vigencia}</span>
                                  </div>
                                  <span className={`px-2 py-0.5 rounded-full border text-[10px] ${h.motivo_cierre === "agotado" ? "bg-red-500/10 border-red-500/20 text-red-300" : h.motivo_cierre === "vencido" ? "bg-amber-500/10 border-amber-500/20 text-amber-300" : "bg-slate-500/10 border-slate-500/20 text-slate-400"}`}>
                                    {h.motivo_cierre}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>

                {/* Acciones */}
                <div className="flex justify-end gap-3 pt-2 border-t border-white/10">
                  <button onClick={() => setShowTimbradoConfig(false)}
                    className="px-4 py-2 text-sm text-slate-400 hover:text-white border border-white/10 rounded-lg transition-all font-body">
                    Cancelar
                  </button>
                  <button
                    disabled={savingTimbrado}
                    onClick={async () => {
                      // Antes esto vivía en el atributo `disabled` del botón: si algún punto
                      // quedaba incompleto (ej. un punto nuevo recién agregado sin nro_timbrado
                      // o sin fecha_vigencia todavía), el botón quedaba deshabilitado SIN avisar
                      // por qué — el guardado (incluso de otros puntos ya completos, o de un
                      // simple cambio de modo automático/manual) parecía "no hacer nada".
                      // Ahora el botón siempre es clickeable y acá se valida con mensajes
                      // específicos por punto, para que quede claro qué falta completar.
                      for (const pto of timbradoForm.puntos_expedicion) {
                        if (!pto.codigo) {
                          toast.error("Todos los puntos de expedición necesitan un código");
                          return;
                        }
                        if (!pto.nro_timbrado) {
                          toast.error(`Punto ${pto.codigo}: falta el número de timbrado`);
                          return;
                        }
                        if (pto.tipo_emision !== "electronico" && !pto.fecha_vigencia) {
                          toast.error(`Punto ${pto.codigo}: falta la fecha de vigencia (o marcá el timbrado como electrónico)`);
                          return;
                        }
                      }
                      const codigos = timbradoForm.puntos_expedicion.map(p => p.codigo);
                      if (new Set(codigos).size !== codigos.length) {
                        toast.error("Los códigos de punto de expedición deben ser únicos");
                        return;
                      }
                      for (const pto of timbradoForm.puntos_expedicion) {
                        const desde = parseInt(pto.numero_desde);
                        const hasta = parseInt(pto.numero_hasta);
                        if (pto.numero_desde !== "" && pto.numero_hasta !== "" && !isNaN(desde) && !isNaN(hasta) && desde >= hasta) {
                          toast.error(`Punto ${pto.codigo}: el número "desde" debe ser menor al "hasta"`);
                          return;
                        }
                        if (pto.nc_activo) {
                          const ncDesde = parseInt(pto.nc_numero_desde);
                          const ncHasta = parseInt(pto.nc_numero_hasta);
                          if (isNaN(ncDesde) || isNaN(ncHasta) || ncDesde >= ncHasta) {
                            toast.error(`Punto ${pto.codigo}: el rango de Nota de Crédito es inválido (el "desde" debe ser menor al "hasta")`);
                            return;
                          }
                        }
                      }
                      setSavingTimbrado(true);
                      try {
                        const payload = {
                          punto_default: timbradoForm.punto_default || timbradoForm.puntos_expedicion[0]?.codigo,
                          personalizado_por_usuario: timbradoForm.personalizado_por_usuario,
                          habilitacion_autoimpresor: (timbradoForm.habilitacion_autoimpresor || "").trim() || null,
                          puntos_expedicion: timbradoForm.puntos_expedicion.map(p => ({
                            codigo: p.codigo,
                            descripcion: p.descripcion,
                            modo_numeracion: p.modo_numeracion,
                            timbrado: {
                              nro_timbrado: p.nro_timbrado,
                              establecimiento: p.establecimiento || "001",
                              fecha_inicio: p.fecha_inicio,
                              fecha_vigencia: p.tipo_emision === "electronico" ? null : p.fecha_vigencia,
                              tipo_emision: p.tipo_emision,
                              numero_desde: p.numero_desde !== "" ? parseInt(p.numero_desde) : 1,
                              numero_hasta: p.numero_hasta !== "" ? parseInt(p.numero_hasta) : 9999999,
                            },
                            rango_nota_credito: p.nc_activo ? {
                              numero_desde: p.nc_numero_desde !== "" ? parseInt(p.nc_numero_desde) : 1,
                              numero_hasta: p.nc_numero_hasta !== "" ? parseInt(p.nc_numero_hasta) : 9999999,
                            } : null,
                          })),
                        };
                        const res = await fetch(`${API}/admin/configuracion-timbrado/${activeEmpresaPropia.slug}`, {
                          method: "PUT", headers: {...headers, "Content-Type": "application/json"}, body: JSON.stringify(payload)
                        });
                        if (res.ok) { toast.success("Timbrado guardado"); setShowTimbradoConfig(false); fetchTimbradoConfig(); }
                        else { const e = await res.json(); toast.error(e.detail || "Error al guardar"); }
                      } catch { toast.error("Error de red"); }
                      setSavingTimbrado(false);
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-lg transition-all font-body">
                    {savingTimbrado ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Guardar timbrado
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Documento visual de factura ── */}
      {facDoc && (
        <FacturaDocModal
          factura={facDoc}
          activeEmpresaPropia={empresasPropias.find(ep => ep.slug === facDoc.logo_tipo) || activeEmpresaPropia}
          presupuestos={presupuestos}
          onClose={() => setFacDoc(null)}
          onEdit={() => { setFacFormItem(facDoc); setShowFacForm(true); setFacDoc(null); }}
          onDelete={() => { handleDeleteFac(facDoc); setFacDoc(null); }}
          onPagar={(cuotasDoc = []) => {
            if (facDoc.forma_pago === "credito") { openPagoParcial(facDoc, Array.isArray(cuotasDoc) ? cuotasDoc : []); setFacDoc(null); }
            else { openPagoContado(facDoc); setFacDoc(null); }
          }}
          onDeshacer={() => { handleDeshacerFac(facDoc); setFacDoc(null); }}
          canEdit={!facDoc.eliminada && hasPermission("facturas.editar")}
          canDelete={!facDoc.eliminada && hasPermission("facturas.eliminar")}
          onReciboClick={(r) => setReciboDoc(r)}
          onPresClick={(presId) => { setFacDoc(null); openPresDoc(presId); }}
          onEditPago={!facDoc.eliminada ? (fac, pago) => setEditPagoCtx({ factura: fac, pago }) : null}
          onDeletePago={!facDoc.eliminada ? handleDeletePago : null}
          cuentasDisp={cuentasDisp}
          token={token}
          API={API}
          onCuotaCobrada={() => {
            fetchFacturas(true);
            if (facDoc?.id) openFacDoc(facDoc.id); // Recargar facDoc con datos frescos del API
          }}
          tiposFac={tiposFac}
          canAsignarTipo={isAdmin || hasPermission("facturas.asignar_tipo")}
          canEditarTipo={isAdmin || hasPermission("facturas.editar_tipo")}
          canEditarVendedor={isAdmin || hasPermission("facturas.editar_vendedor_pendiente")}
          onTipoChanged={() => { fetchFacturas(true); if (facDoc?.id) openFacDoc(facDoc.id); }}
          autoPrint={autoPrintFacDoc}
          onAutoPrinted={() => setAutoPrintFacDoc("no")}
        />
      )}

      {/* ── Aviso: licencias pendientes de facturar (empresa emisora) ── */}
      {showAvisoLicencias && licenciasPendientesFacturar.length > 0 && createPortal(
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-arandu-dark-light border border-amber-500/30 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-amber-500/15 border border-amber-500/25">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                </div>
                <h2 className="text-white font-heading font-bold text-base">Licencias por facturar</h2>
              </div>
              <button onClick={handleDismissAvisoLicencias} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-all">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-2 max-h-80 overflow-y-auto">
              <p className="text-slate-400 text-sm mb-3">
                Estas empresas tienen una licencia próxima a vencer (o vencida) que se factura desde acá y todavía no se emitió la factura de renovación:
              </p>
              {licenciasPendientesFacturar.map(p => (
                <div key={p.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/15">
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">{p.empresa_nombre || p.logo_tipo}</p>
                    <p className="text-slate-500 text-xs truncate">
                      {p.cliente_nombre ? `Cliente: ${p.cliente_nombre}` : "Sin cliente asignado (no se renueva sola)"}
                    </p>
                    <p className="text-slate-400 text-xs">
                      {p.dias_restantes < 0
                        ? `Vencida hace ${Math.abs(p.dias_restantes)} día${Math.abs(p.dias_restantes) !== 1 ? "s" : ""}`
                        : p.dias_restantes === 0 ? "Vence hoy"
                        : `Vence en ${p.dias_restantes} día${p.dias_restantes !== 1 ? "s" : ""}`}
                      {p.fecha_vencimiento ? ` · ${p.fecha_vencimiento.split("-").reverse().join("/")}` : ""}
                    </p>
                  </div>
                  <span className="flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25">
                    {p.periodo === "anual" ? "Anual" : "Mensual"}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center gap-2 px-5 pb-5">
              <button onClick={() => navigate("/sistema/licencias")} className="text-xs text-blue-400 hover:text-blue-300 hover:underline">
                Ir a Control de Licencias →
              </button>
              <button onClick={handleDismissAvisoLicencias} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors">
                Entendido
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Avisos de licencias: Pendientes + Histórico ── */}
      {showAvisosHist && createPortal(
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-3xl shadow-2xl">
            <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-400" />
                <h2 className="text-white font-heading font-bold text-base">Avisos de licencias</h2>
              </div>
              <button onClick={() => setShowAvisosHist(false)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-all">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex gap-2 px-6 pt-3">
              {[["pendientes", `Pendientes (${avisosLic.total_pendientes})`], ["historico", `Histórico (${avisosLic.avisos.length})`]].map(([k, l]) => (
                <button key={k} onClick={() => setAvisosTab(k)}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${avisosTab === k ? "bg-blue-600 border-blue-500 text-white" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
                  {l}
                </button>
              ))}
            </div>
            <div className="p-6 max-h-[60vh] overflow-auto">
              {(() => {
                const lista = avisosTab === "pendientes" ? avisosLic.pendientes : avisosLic.avisos;
                const fmtF = (iso) => iso ? iso.slice(0, 10).split("-").reverse().join("/") : "—";
                if (lista.length === 0) return <p className="text-center text-slate-500 text-sm py-8">{avisosTab === "pendientes" ? "No hay avisos pendientes." : "Todavía no hay avisos registrados."}</p>;
                return (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-400 border-b border-white/10">
                        <th className="py-2 pr-3">Fecha aviso</th>
                        <th className="py-2 pr-3">Empresa / Cliente</th>
                        <th className="py-2 pr-3">Vencimiento</th>
                        <th className="py-2 pr-3">Estado</th>
                        {avisosTab === "pendientes" && <th className="py-2"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {lista.map(a => (
                        <tr key={a.id} className="border-b border-white/5">
                          <td className="py-2 pr-3 text-slate-300 whitespace-nowrap">{fmtF(a.fecha_aviso)}</td>
                          <td className="py-2 pr-3">
                            <div className="text-white">{a.empresa_nombre || "—"}</div>
                            <div className="text-xs text-slate-500">{a.cliente_nombre || "Sin cliente asignado"}</div>
                          </td>
                          <td className="py-2 pr-3 text-slate-300 whitespace-nowrap">{fmtF(a.fecha_vencimiento)}</td>
                          <td className="py-2 pr-3">
                            {a.estado === "atendido" ? (
                              <div>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">Atendido</span>
                                <div className="text-[11px] text-slate-500 mt-0.5">
                                  {fmtF(a.atendido_at)}{a.factura_numero ? ` · Factura ${a.factura_numero}` : a.atendido_por ? ` · ${a.atendido_por}` : ""}
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25">Pendiente</span>
                            )}
                          </td>
                          {avisosTab === "pendientes" && (
                            <td className="py-2 text-right">
                              <button onClick={() => atenderAvisoLic(a.id)} className="text-xs text-blue-400 hover:underline whitespace-nowrap">Marcar atendido</button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Modal Pago en lote (bulk) ── */}
      {showBulkPagoModal && createPortal(
        (() => {
          // Calcular grupos y totales para mostrar en el modal
          const facsPendBulk = filteredFac.filter(f => selectedFacIds.has(f.id) && (f.estado === "pendiente" || f.estado === "parcial"));
          // Agrupar por cliente (mismo criterio que el backend)
          const gruposBulk = {};
          facsPendBulk.forEach(f => {
            const clave = f.empresa_id || f.razon_social || f.id;
            const label = f.empresa_nombre || f.razon_social || "Sin cliente";
            if (!gruposBulk[clave]) gruposBulk[clave] = { label, facturas: [] };
            gruposBulk[clave].facturas.push(f);
          });
          const gruposArr = Object.values(gruposBulk);
          const totalGeneral = facsPendBulk.reduce((s, f) => s + ((f.monto || 0) - (f.monto_pagado || 0)), 0);
          const multipleClientes = gruposArr.length > 1;

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
              <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-white/10">
                  <div>
                    <h2 className="text-white font-heading font-bold text-lg">Pagar facturas seleccionadas</h2>
                    <p className="text-slate-400 text-sm mt-0.5">
                      {facsPendBulk.length} factura{facsPendBulk.length !== 1 ? "s" : ""} · {gruposArr.length} cliente{gruposArr.length !== 1 ? "s" : ""}
                      {multipleClientes && <span className="ml-2 text-xs bg-blue-500/15 text-blue-300 border border-blue-500/20 px-1.5 py-0.5 rounded-full">1 recibo por cliente</span>}
                    </p>
                  </div>
                  <button onClick={() => setShowBulkPagoModal(false)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-all">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                  {/* Facturas agrupadas por cliente con subtotales */}
                  <div className="max-h-52 overflow-y-auto space-y-3">
                    {gruposArr.map((grupo, gi) => (
                      <div key={gi}>
                        {/* Cabecera del grupo (cliente) — solo si hay más de 1 cliente */}
                        {multipleClientes && (
                          <div className="flex items-center justify-between mb-1 px-1">
                            <span className="text-xs font-medium text-slate-300 truncate">{grupo.label}</span>
                            <span className="text-xs text-emerald-300 font-medium ml-2 shrink-0">
                              {fmtPYG(grupo.facturas.reduce((s, f) => s + ((f.monto || 0) - (f.monto_pagado || 0)), 0))}
                            </span>
                          </div>
                        )}
                        {/* Filas de facturas */}
                        <div className="space-y-1">
                          {grupo.facturas.map(f => (
                            <div key={f.id} className="flex items-center justify-between bg-arandu-dark rounded-lg px-3 py-2 text-sm">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-emerald-300 font-mono shrink-0">{f.numero || f.numero_boleta || "-"}</span>
                                {!multipleClientes && <span className="text-slate-400 text-xs truncate">{f.empresa_nombre || f.razon_social || "-"}</span>}
                              </div>
                              <span className="text-white font-medium shrink-0 ml-2">{fmtMonto((f.monto || 0) - (f.monto_pagado || 0), f.moneda)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Total general */}
                  <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-2.5">
                    <span className="text-emerald-300 text-sm font-medium">Total general</span>
                    <span className="text-emerald-300 font-bold text-base">{fmtPYG(totalGeneral)}</span>
                  </div>

                  {/* Fecha de pago */}
                  <div>
                    <label className="block text-slate-400 text-xs mb-1.5">Fecha de pago</label>
                    <DateInput value={bulkFechaPago} onChange={e => setBulkFechaPago(e.target.value)}
                      className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" />
                  </div>

                  {/* Cuenta bancaria */}
                  {cuentasDisp.length > 0 && (
                    <div>
                      <label className="block text-slate-400 text-xs mb-1.5">
                        Cuenta bancaria <span className="text-red-400">*</span>
                      </label>
                      <select value={bulkCuentaId} onChange={e => setBulkCuentaId(e.target.value)}
                        className={`w-full bg-arandu-dark border text-white rounded-lg px-3 py-2 text-sm ${!bulkCuentaId ? "border-red-500/50" : "border-white/10"}`}>
                        <option value="">— Seleccioná una cuenta —</option>
                        {renderCuentasPorMoneda(cuentasDisp, activeEmpresaPropia?.slug)}
                      </select>
                      {!bulkCuentaId && (
                        <p className="text-red-400 text-[11px] mt-1">⚠ Seleccioná una cuenta bancaria para continuar</p>
                      )}
                    </div>
                  )}
                  <p className="text-slate-500 text-xs">Se registra el monto pendiente completo de cada factura. {multipleClientes ? `Se generarán ${gruposArr.length} recibos (uno por cliente).` : "Se generará 1 recibo."}</p>
                </div>

                <div className="flex items-center justify-end gap-3 px-6 pb-5">
                  <button onClick={() => setShowBulkPagoModal(false)}
                    className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors font-body">
                    Cancelar
                  </button>
                  <button onClick={handleBulkPago} disabled={bulkLoading || (cuentasDisp.length > 0 && !bulkCuentaId)}
                    className={`flex items-center gap-2 px-5 py-2 text-sm text-white rounded-lg transition-all font-body ${bulkLoading || (cuentasDisp.length > 0 && !bulkCuentaId) ? "bg-slate-600 cursor-not-allowed opacity-60" : "bg-emerald-600 hover:bg-emerald-700"}`}>
                    {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    {bulkLoading ? "Procesando..." : `Confirmar ${facsPendBulk.length} pago${facsPendBulk.length !== 1 ? "s" : ""}`}
                  </button>
                </div>
              </div>
            </div>
          );
        })()
        ,
        document.body
      )}

      {/* ── Factura Form Modal (nueva/editar) ── */}
      {showFacForm && (
        <FacturaFormModal
          factura={facFormItem || null}
          sinFactura={facFormItem ? !!facFormItem.sin_factura : facSinFactura}
          onClose={() => { setShowFacForm(false); setFacFormItem(null); setFacSinFactura(false); setOrdenTransporte(null); setPesajeBascula(null); setHuecoParaFactura(null); }}
          onSaved={async (savedData, opts) => {
            setShowFacForm(false); setFacFormItem(null); setFacSinFactura(false); setHuecoParaFactura(null);
            fetchFacturas(true); fetchPresupuestos(true);
            // Si esta factura vino de "Liquidación al cliente" de una Orden de
            // Carga (Transporte), la vinculamos de vuelta a la orden.
            if (ordenTransporte?.id && savedData?.id) {
              try {
                await fetch(`${API}/admin/transporte/ordenes/vincular-factura-lote`, {
                  method: "PATCH",
                  headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                  body: JSON.stringify({ orden_ids: (ordenTransporte.ordenes || [ordenTransporte]).map(o => o.id), factura_id: savedData.id, factura_numero: savedData.numero }),
                });
              } catch { /* no bloquea: la factura ya se guardó */ }
              setOrdenTransporte(null);
            }
            // Si esta factura vino de "Facturar" de un Pesaje de Báscula, la
            // vinculamos de vuelta al pesaje.
            if (pesajeBascula?.id && savedData?.id) {
              for (const pz of (pesajeBascula.lista || [pesajeBascula])) {
                try {
                  await fetch(`${API}/admin/bascula/pesajes/${pz.id}/vincular-factura`, {
                    method: "PATCH",
                    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                    body: JSON.stringify({ factura_id: savedData.id, factura_numero: savedData.numero }),
                  });
                } catch { /* no bloquea: la factura ya se guardó */ }
              }
              setPesajeBascula(null);
            }
            // "Guardar e Imprimir": abre la vista de documento de la factura recién creada
            // y dispara la impresión automáticamente en el formato configurado
            // (empresa o preferencia personal del usuario, en Ventas > Configuración).
            if (opts?.autoPrint && savedData?.id) {
              setAutoPrintFacDoc(opts?.autoPrintModo || "a4");
              openFacDoc(savedData.id);
            }
            // Delivery: si la factura trae datos de entrega y la empresa eligió
            // "imprimir al emitir la factura", sale también la Orden de Delivery.
            if (savedData?.delivery && savedData?.id && hasPermission("facturas.imprimir_delivery")) {
              try {
                const rc = await fetch(`${API}/admin/config-delivery?logo_tipo=${activeEmpresaPropia?.slug || ""}`, { headers: { Authorization: `Bearer ${token}` } });
                const cd = rc.ok ? await rc.json() : null;
                if (cd?.imprimir_modo === "con_factura") {
                  await imprimirOrdenDelivery({ factura: savedData, token, empresaPropia: activeEmpresaPropia, formato: cd.formato });
                }
              } catch { /* no bloquea: la factura ya se guardó */ }
            }
          }}
          token={token}
          API={API}
          empresas={empresas}
          onEmpresaCreada={fetchShared}
          presupuestosDisp={hasPermission("presupuestos.ver") ? presupuestos : []}
          activeEmpresaPropia={activeEmpresaPropia}
          hasPermission={hasPermission}
          productos={productos}
          productosHabilitados={hasModule?.("productos_stock")}
          cuentasDisp={cuentasDisp}
          planCuentasDisp={planCuentas}
          initialValues={!facFormItem ? (facturaInicialTransporte || facturaInicialBascula || facturaInicialHueco) : null}
          defaultEmpresaId={!facFormItem ? (ordenTransporte?.cliente_id || pesajeBascula?.entidad_id || contextEmpresaId) : ""}
          canUseDescuento={canUseDescuento}
        />
      )}

      {/* ── Modal: confirmar anulación de factura (con motivo obligatorio) ── */}
      {anularFacTarget && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-arandu-dark-light border border-red-500/30 rounded-2xl w-full max-w-md p-6 my-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-bold text-white">Anular factura</h2>
                <p className="text-slate-400 text-xs mt-0.5">
                  {anularFacTarget.numero || anularFacTarget.numero_boleta || "Sin número"}
                </p>
              </div>
            </div>

            <p className="text-slate-300 text-sm mb-4">
              La factura pasa a estado <strong>Anulada</strong>, se revierte el stock (si aplica) y sus pagos dejan
              de contar en los saldos de caja/banco. Esta acción queda registrada en auditoría.
            </p>

            <label className="text-slate-400 text-xs mb-1.5 block">Motivo de la anulación *</label>
            <textarea
              value={anularFacMotivo}
              onChange={e => setAnularFacMotivo(e.target.value)}
              rows={3}
              className="w-full bg-arandu-dark border border-white/10 rounded-md text-white text-sm px-3 py-2 placeholder:text-slate-600 resize-none mb-5"
              placeholder="Ej: Error en el monto, cliente canceló la compra, factura duplicada..."
              autoFocus
            />

            <div className="flex gap-3">
              <button type="button" onClick={() => setAnularFacTarget(null)} disabled={anulandoFac}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-white/10 text-slate-400 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-50">
                <X className="w-4 h-4" /> Cancelar
              </button>
              <button type="button" onClick={confirmarAnularFac} disabled={anulandoFac || !anularFacMotivo.trim()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-white font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <AlertTriangle className="w-4 h-4" /> {anulandoFac ? "Anulando..." : "Sí, anular"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Pago Contado ── */}
      {showPagoModal && pagoFac && (
        <PagoModal
          sinRetencion={sinRetencionPago}
          setSinRetencion={setSinRetencionPago}
          puedeOmitirRetencion={hasPermission("retenciones.omitir_retencion")}
          fac={pagoFac}
          fechaPago={fechaPago}
          setFechaPago={setFechaPago}
          numeroReciboManual={numeroReciboManual}
          setNumeroReciboManual={setNumeroReciboManual}
          cuentasDisp={cuentasDisp}
          cuentasPYG={cuentasPYG}
          setCuentasPYG={setCuentasPYG}
          cuentasUSD={cuentasUSD}
          setCuentasUSD={setCuentasUSD}
          tcPago={tcPago}
          setTcPago={setTcPago}
          montoUSD={montoUSD}
          setMontoUSD={setMontoUSD}
          canEditTcDoc={canEditTcDoc}
          fetchTcDelDia={fetchTcDelDia}
          saldoFavor={saldoFavorFac}
          aplicarSaldoFavor={aplicarSaldoFavor}
          setAplicarSaldoFavor={setAplicarSaldoFavor}
          montoSaldoAplicar={montoSaldoAplicar}
          setMontoSaldoAplicar={setMontoSaldoAplicar}
          vale={valeFac}
          aplicarVale={aplicarVale}
          setAplicarVale={setAplicarVale}
          montoValeAplicar={montoValeAplicar}
          setMontoValeAplicar={setMontoValeAplicar}
          onMontoItemsChange={setPagoItemsMonto}
          onMontoRecibidoChange={setPagoMontoRecibido}
          onVueltoCuentaChange={setPagoVueltoCuenta}
          onPagoItemsDataChange={setPagoItemsData}
          puedeCobroItems={hasPermission("facturas.cobro_por_items")}
          onClose={() => setShowPagoModal(false)}
          pagoRef={pagoRef}
          setPagoRef={setPagoRef}
          procesadorasCobro={procesadorasCobro}
          medioCobro={medioCobro}
          setMedioCobro={setMedioCobro}
          onConfirm={handlePagarContado}
          fmtMonto={fmtMonto}
        />
      )}

      {/* ── Modal Pago Parcial (crédito) ── */}
      {showPagoParcialModal && pagoParcialFac && (
        <PagoParcialModal
          sinRetencion={sinRetencionPago}
          setSinRetencion={setSinRetencionPago}
          puedeOmitirRetencion={hasPermission("retenciones.omitir_retencion")}
          onVueltoCuentaChange={setPagoVueltoCuenta}
          procesadorasCobro={procesadorasCobro}
          medioCobro={medioCobro}
          setMedioCobro={setMedioCobro}
          descuentoPago={descuentoPagoParcial}
          setDescuentoPago={setDescuentoPagoParcial}
          puedeDescuentoPago={isAdmin || user?.role === "gerente" || user?.role === "super_admin" || hasPermission("facturas.descuento_pago")}
          fac={pagoParcialFac}
          cuotasFac={pagoParcialCuotas}
          selectedCuotaIds={pagoParcialCuotaIds}
          setSelectedCuotaIds={setPagoParcialCuotaIds}
          montoParcial={montoParcial}
          setMontoParcial={setMontoParcial}
          fechaPagoParcial={fechaPagoParcial}
          setFechaPagoParcial={setFechaPagoParcial}
          numeroReciboManual={numeroReciboManual}
          setNumeroReciboManual={setNumeroReciboManual}
          cuentasDisp={cuentasDisp}
          cuentasPYG={cuentasPYG}
          setCuentasPYG={setCuentasPYG}
          cuentasUSD={cuentasUSD}
          setCuentasUSD={setCuentasUSD}
          tcPago={tcPago}
          setTcPago={setTcPago}
          montoUSD={montoUSD}
          setMontoUSD={setMontoUSD}
          canEditTcDoc={canEditTcDoc}
          fetchTcDelDia={fetchTcDelDia}
          saldoFavor={saldoFavorFac}
          aplicarSaldoFavor={aplicarSaldoFavor}
          setAplicarSaldoFavor={setAplicarSaldoFavor}
          montoSaldoAplicar={montoSaldoAplicar}
          setMontoSaldoAplicar={setMontoSaldoAplicar}
          vale={valeFac}
          aplicarVale={aplicarVale}
          setAplicarVale={setAplicarVale}
          montoValeAplicar={montoValeAplicar}
          setMontoValeAplicar={setMontoValeAplicar}
          onPagoItemsDataChange={setPagoItemsData}
          puedeCobroItems={hasPermission("facturas.cobro_por_items")}
          onClose={() => setShowPagoParcialModal(false)}
          pagoRef={pagoRef}
          setPagoRef={setPagoRef}
          onConfirm={handlePagoParcial}
          fmtMonto={fmtMonto}
        />
      )}

      {/* ── Editar Pago Individual ── */}
      {editPagoCtx && (
        <EditPagoModal
          factura={editPagoCtx.factura}
          pago={editPagoCtx.pago}
          cuentasDisp={cuentasDisp}
          onClose={() => setEditPagoCtx(null)}
          onSave={(cambios) => handleSaveEditPago(editPagoCtx.factura, editPagoCtx.pago, cambios)}
          fmtMonto={fmtMonto}
        />
      )}

      {/* ── Recibo visual ── */}
      {reciboDoc && (
        <ReciboDocModal
          recibo={reciboDoc}
          activeEmpresaPropia={empresasPropias.find(ep => ep.slug === reciboDoc.logo_tipo) || activeEmpresaPropia}
          onClose={() => setReciboDoc(null)}
          fmtMonto={fmtMonto}
          cuentasDisp={cuentasDisp}
        />
      )}

      {notaDoc && (
        <NotaCreditoDocModal
          nota={notaDoc}
          activeEmpresaPropia={empresasPropias.find(ep => ep.slug === notaDoc.logo_tipo) || activeEmpresaPropia}
          onClose={() => setNotaDoc(null)}
          fmtMonto={fmtMonto}
        />
      )}
    </div>
  );
}

// ═══ Logos (letras tricolor PY; cuadrado sólido por marca) ════════════════
const LogoArandu = () => <LogoMarcaArandu />;
const LogoJar = () => <LogoMarcaJar />;
const LogoAranduJarDoc = () => <LogoMarcaAranduJar />;

// ═══ Documento completo de presupuesto ══════════════════════════════════
function PresupuestoDocModal({
  presupuesto: p, activeEmpresaPropia = null, onClose, onEdit, onDelete, onDuplicate, onCostos,
  onEstadoChange, onAprobar, canAprobar, canEdit, canDelete, canCreate, canCostos,
  formatNumber, getCurrencySymbol, PRESUP_ESTADOS
}) {
  const [printFileName, setPrintFileName] = React.useState(
    p.nombre_archivo ? `${p.numero} - ${p.nombre_archivo}` : `Presupuesto ${p.numero}`
  );

  React.useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const fmt = (num) => formatNumber(num, p.moneda);
  const sym = getCurrencySymbol(p.moneda);
  const accentColor = p.logo_tipo === "jar" ? "#dc2626" : "#2563eb";
  const empresaEmisora = activeEmpresaPropia || {};
  const emisorNombre = p.emisor_razon_social || (p.logo_tipo === "jar" ? "JAR Informática" : p.logo_tipo === "arandu" ? "Arandu Informática" : "AranduJAR Informática");
  const emisorNombreFantasia = p.emisor_nombre_fantasia || empresaEmisora?.nombre_fantasia || "";
  const emisorFantasiaHtml = (colorNombre, colorSub) => emisorNombreFantasia
    ? `<div style="font-weight:800;font-size:13px;color:${colorNombre};line-height:1.25">${emisorNombreFantasia}</div><div style="font-size:9px;color:${colorSub};margin-top:1px">${emisorNombre}${emisorRuc ? ` · RUC: ${emisorRuc}` : ""}</div>`
    : `<div style="font-weight:700;color:${colorNombre}">${emisorNombre}${emisorRuc ? ` · RUC: ${emisorRuc}` : ""}</div>`;
  const emisorRuc = p.emisor_ruc || "";
  const emisorDireccion = p.emisor_direccion || "";
  const emisorTelefono = p.emisor_telefono || "";
  const emisorEmail = p.emisor_email || "";
  const emisorLogoUrl = p.emisor_logo_url || null;
  const emisorRubrosDinet = p.emisor_rubros_dinet || "";
  const imageItems = (p.items || []).filter(item => item.imagen);
  const buildImageAnnexHTML = (headerBg, headerAccent) => imageItems.length ? `
    <section class="print-annex" style="page-break-before:always;break-before:page;background:white">
      <div style="border-bottom:3px solid ${headerAccent};padding-bottom:8px;margin-bottom:12px">
        <div style="font-size:18px;font-weight:800;color:${headerBg};letter-spacing:1px">ANEXO DE IMÁGENES</div>
        <div style="font-size:11px;color:#64748b;margin-top:2px">Presupuesto ${p.numero} · ${p.empresa_nombre || ""}</div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
        ${imageItems.map((item, idx) => `
          <article style="border:1px solid #dbe3ee;border-radius:8px;overflow:hidden;page-break-inside:avoid;break-inside:avoid;background:#ffffff">
            <div style="background:#f8fafc;padding:8px 10px;border-bottom:1px solid #e2e8f0">
              <div style="font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:.6px">Ítem ${idx + 1}</div>
              <div style="font-size:12px;color:#0f172a;font-weight:700;margin-top:2px">${item.descripcion || "Producto / servicio"}</div>
              ${item.imagen_comentario ? `<div style="font-size:10.5px;color:#475569;margin-top:2px">${item.imagen_comentario}</div>` : ""}
            </div>
            <div style="height:190px;display:flex;align-items:center;justify-content:center;background:#fff;padding:8px">
              <img src="${item.imagen}" style="max-width:100%;max-height:100%;object-fit:contain" />
            </div>
          </article>
        `).join("")}
      </div>
    </section>` : "";

  const buildLogoHTML = (logoTipo) => svgLogoMarcaRow(logoTipo, emisorLogoUrl);

  // ── Imprimir por partes: máx 15 ítems por hoja (diseño colorido) ─────────
  const handlePrintPorPartes = () => {
    const moneda = p.moneda || "PYG";
    const isUSD = moneda === "USD";
    const formaPagoLabel = (p.forma_pago || "contado") === "credito" ? "A crédito" : "Al contado";
    const roundVal = v => isUSD ? Math.round(v * 100) / 100 : Math.round(v);
    const ITEMS_POR_HOJA = 15;
    const allItems = p.items || [];
    const chunks = allItems.length > 0
      ? Array.from({ length: Math.ceil(allItems.length / ITEMS_POR_HOJA) }, (_, i) => allItems.slice(i * ITEMS_POR_HOJA, (i + 1) * ITEMS_POR_HOJA))
      : [[]];
    const totalHojas = chunks.length;

    const isJar = p.logo_tipo === "jar";
    const headerBg = isJar ? "#7f1d1d" : "#1e3a5f";
    const headerAccent = isJar ? "#ef4444" : "#3b82f6";
    const printUid = Math.random().toString(36).slice(2, 11);
    const marca = normalizeLogoTipo(p.logo_tipo);
    const _docsUrl1 = emisorLogoUrl;
    const iconBox = _docsUrl1
      ? `<div style="flex-shrink:0;margin-right:8px"><img src="${_docsUrl1}" alt="Logo" style="max-height:52px;max-width:140px;object-fit:contain;display:block"/></div>`
      : `<div style="width:44px;height:44px;flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;margin-right:8px">${svgMarcaIcon(marca, printUid, 44)}</div>`;
    const logoName = _docsUrl1 ? `` : `<div style="line-height:1">${svgPrintLogoName(p.logo_tipo, printUid, { darkHeader: true })}</div>`;

    const totalItemsDoc = allItems.reduce((s, item) => s + (parseFloat(item.cantidad) || 0), 0);
    const descuentoGlobalDoc = parseFloat(p.descuento_global_monto) || 0;

    const pagesHTML = chunks.map((chunkItems, idx) => {
      const partNum = idx + 1;
      const numero = partNum === 1 ? p.numero : `${p.numero}-${partNum}`;
      const isLastHoja = idx === totalHojas - 1;
      const chunkTotal = chunkItems.reduce((s, item) => s + (parseFloat(item.subtotal) || 0), 0);
      const chunkIva = roundVal(chunkTotal / 11);
      const chunkSubtotal = roundVal(chunkTotal - chunkIva);

      const itemsRows = chunkItems.map((item, i) => `
        <tr style="background:${i % 2 === 0 ? "white" : "#f8fafc"}">
          <td style="border:1px solid #e2e8f0;padding:5px 8px;color:#1e293b;font-size:11px">
            ${item.descripcion || ""}
            ${item.variante_texto ? `<div style="color:#7c3aed;font-size:10px;margin-top:1px">Variante: ${item.variante_texto}</div>` : ""}
            ${Number(item.descuento_pct || 0) > 0 ? `<div style="color:#059669;font-size:10px;margin-top:1px">Descuento aplicado: ${Number(item.descuento_pct).toLocaleString("es-PY")}%${Number(item.descuento_monto || 0) > 0 ? ` (-${fmt(item.descuento_monto)} ${sym})` : ""}</div>` : ""}
            ${item.observacion ? `<div style="color:#64748b;font-size:10px;font-style:italic;margin-top:1px">Obs: ${item.observacion}</div>` : ""}
          </td>
          <td style="border:1px solid #e2e8f0;padding:5px 4px;text-align:center;color:#334155;font-size:11px">${item.cantidad}</td>
          <td style="border:1px solid #e2e8f0;padding:5px 6px;text-align:right;color:#334155;font-size:11px">${fmt(item.precio_unitario)} ${sym}</td>
          <td style="border:1px solid #e2e8f0;padding:5px 6px;text-align:right;color:#1e293b;font-size:11px;font-weight:600">${fmt(item.subtotal)} ${sym}</td>
        </tr>`).join("");

      const footerSection = isLastHoja ? `
        ${p.observaciones || p.condiciones ? `<div style="margin-top:10px">
          ${p.observaciones ? `<div style="background:#fefce8;padding:7px 10px;border-left:3px solid #eab308;border-radius:0 4px 4px 0;margin-bottom:6px;font-size:10px"><strong style="color:#92400e">OBSERVACIONES:</strong><br><span style="color:#713f12">${p.observaciones.replace(/\n/g, "<br>")}</span></div>` : ""}
          ${p.condiciones ? `<div style="background:#f0fdf4;padding:7px 10px;border-left:3px solid #22c55e;border-radius:0 4px 4px 0;font-size:10px"><strong style="color:#166534">CONDICIONES:</strong><br><span style="color:#15803d">${p.condiciones.replace(/\n/g, "<br>")}</span></div>` : ""}
        </div>` : ""}` :
        `<div style="text-align:right;font-size:10px;color:#64748b;margin-top:6px;font-style:italic">Hoja ${partNum} de ${totalHojas} — continúa en siguiente hoja →</div>`;

      return `
        <div class="print-page" style="page-break-after:${isLastHoja ? "avoid" : "always"};page-break-inside:avoid;background:white;min-height:0">
          <!-- ENCABEZADO OSCURO -->
          <div style="background:${headerBg};border-radius:8px;padding:12px 16px;margin-bottom:10px;display:flex;justify-content:space-between;align-items:center">
            <div style="display:flex;align-items:center">
              ${iconBox}
              <div>
                ${logoName}
                <div style="font-size:8px;color:#cbd5e1;letter-spacing:2px;margin-top:1px">INFORMÁTICA</div>
                <div style="margin-top:6px;font-size:9px;color:#94a3b8;line-height:1.5">
                  ${emisorFantasiaHtml("#e2e8f0", "#cbd5e1")}
                  <div>${emisorDireccion}</div>
                  <div>${emisorTelefono ? `Tel: ${emisorTelefono}` : ""}${emisorTelefono && emisorEmail ? " · " : ""}${emisorEmail || ""}</div>
                  ${emisorRubrosDinet ? `<div style="color:#cbd5e1;font-style:italic">Rubro: ${emisorRubrosDinet}</div>` : ""}
                </div>
              </div>
            </div>
            <div style="text-align:right">
              <div style="background:${headerAccent};color:white;font-size:13px;font-weight:800;letter-spacing:2px;padding:4px 14px;border-radius:5px;margin-bottom:6px;display:inline-block">PRESUPUESTO</div>
              <div style="font-size:17px;font-weight:700;color:white;margin-bottom:2px">${numero}</div>
              ${p.nombre_archivo ? `<div style="font-size:9.5px;color:#e2e8f0;margin-bottom:2px">${p.nombre_archivo}</div>` : ""}
              ${emisorRuc ? `<div style="font-size:9px;color:#e2e8f0;margin-bottom:2px">RUC: ${emisorRuc}</div>` : ""}
              ${totalHojas > 1 ? `<div style="font-size:9px;color:#94a3b8;margin-bottom:2px">Hoja ${partNum} de ${totalHojas}</div>` : ""}
              <div style="font-size:10px;color:#94a3b8">Fecha: <span style="color:#e2e8f0">${p.fecha}</span></div>
              <div style="font-size:9px;color:#94a3b8;margin-top:1px">Validez: ${p.validez_dias || 15} días</div>
            </div>
          </div>
          <!-- CLIENTE (solo en primera hoja) -->
          ${partNum === 1 ? `
          <div style="background:#f0f7ff;border-left:4px solid ${headerAccent};padding:7px 12px;border-radius:0 5px 5px 0;margin-bottom:10px">
            <div style="font-size:8px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:1px;margin-bottom:3px">Cliente</div>
            <div style="font-size:13px;font-weight:700;color:#1e293b">${p.empresa_nombre || ""}</div>
            ${p.empresa_ruc ? `<div style="font-size:10px;color:#475569;margin-top:1px">RUC: ${p.empresa_ruc}</div>` : ""}
            <div style="font-size:10px;color:#475569;margin-top:1px">Forma de pago: <strong>${formaPagoLabel}</strong></div>
          </div>` : ""}
          <!-- TABLA -->
          <table style="width:100%;border-collapse:collapse">
            <thead>
              <tr style="background:${headerBg}">
                <th style="border:1px solid ${headerBg};padding:6px 8px;text-align:left;font-size:10px;color:white;font-weight:700">Descripción</th>
                <th style="border:1px solid ${headerBg};padding:6px 4px;text-align:center;width:40px;font-size:10px;color:white;font-weight:700">Cant.</th>
                <th style="border:1px solid ${headerBg};padding:6px;text-align:right;width:110px;font-size:10px;color:white;font-weight:700">Precio Unit.</th>
                <th style="border:1px solid ${headerBg};padding:6px;text-align:right;width:110px;font-size:10px;color:white;font-weight:700">Subtotal</th>
              </tr>
            </thead>
            <tbody>${itemsRows}</tbody>
            <tfoot>
              <tr style="background:${headerBg}">
                <td colspan="3" style="border:1px solid ${headerBg};padding:6px 8px;text-align:right;font-weight:800;font-size:12px;color:white">${isLastHoja ? "TOTAL A PAGAR:" : `SUBTOTAL hoja ${partNum}:`}</td>
                <td style="border:1px solid ${headerBg};padding:6px 8px;text-align:right;font-weight:800;font-size:12px;color:white">${fmt(chunkTotal)} ${sym}</td>
              </tr>
              ${isLastHoja ? `
              <tr style="background:#f8fafc">
                <td colspan="3" style="border:1px solid #e2e8f0;padding:4px 8px;text-align:right;font-size:10px;color:#64748b">Total ítems:</td>
                <td style="border:1px solid #e2e8f0;padding:4px 8px;text-align:right;font-size:10px;color:#64748b">${totalItemsDoc}</td>
              </tr>` : ""}
              ${(isLastHoja && descuentoGlobalDoc > 0) ? `
              <tr style="background:#f8fafc">
                <td colspan="3" style="border:1px solid #e2e8f0;padding:4px 8px;text-align:right;font-size:10px;color:#059669">Descuento aplicado:</td>
                <td style="border:1px solid #e2e8f0;padding:4px 8px;text-align:right;font-size:10px;color:#059669">-${fmt(descuentoGlobalDoc)} ${sym}</td>
              </tr>` : ""}
              <tr style="background:#f8fafc">
                <td colspan="3" style="border:1px solid #e2e8f0;padding:4px 8px;text-align:right;font-size:10px;color:#64748b">Base imponible:</td>
                <td style="border:1px solid #e2e8f0;padding:4px 8px;text-align:right;font-size:10px;color:#64748b">${fmt(chunkSubtotal)} ${sym}</td>
              </tr>
              <tr style="background:#f8fafc">
                <td colspan="3" style="border:1px solid #e2e8f0;padding:4px 8px;text-align:right;font-size:10px;color:#64748b">IVA incluido (10%):</td>
                <td style="border:1px solid #e2e8f0;padding:4px 8px;text-align:right;font-size:10px;color:#64748b">${fmt(chunkIva)} ${sym}</td>
              </tr>
            </tfoot>
          </table>
          ${footerSection}
        </div>`;
    }).join("");

    const anexosHTML = buildImageAnnexHTML(headerBg, headerAccent);
    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>${printFileName}</title>
      <style>
        @page{size:A4;margin:16mm 18mm}
        *{box-sizing:border-box;margin:0;padding:0}
        html,body{height:auto;overflow:visible;font-family:Arial,Helvetica,sans-serif;background:white;-webkit-print-color-adjust:exact;print-color-adjust:exact}
        body{padding:0}
        .print-page,.print-annex{width:100%;max-width:174mm;margin:0 auto}
        table{width:100%;max-width:100%;border-collapse:collapse}
        tr{page-break-inside:avoid;break-inside:avoid}
        @media print{@page{size:A4;margin:16mm 18mm}html,body{height:auto;-webkit-print-color-adjust:exact;print-color-adjust:exact}tr{page-break-inside:avoid;break-inside:avoid}}
      </style>
      </head><body>${pagesHTML}${anexosHTML}</body></html>`;

    const pw = window.open("", "_blank", "width=900,height=700");
    if (!pw) { alert("Permita ventanas emergentes para imprimir."); return; }
    pw.document.write(html);
    pw.document.close();
    pw.focus();
    setTimeout(() => pw.print(), 700);
  };

  // handlePrint — presupuesto completo en una sola pasada, sin cortes forzados
  // El browser maneja los saltos de página naturalmente; page-break-inside:avoid evita cortar filas
  const handlePrint = () => {
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { alert("Permita ventanas emergentes para imprimir."); return; }
    const isJar = p.logo_tipo === "jar";
    const headerBg = isJar ? "#7f1d1d" : "#1e3a5f";
    const headerAccent = isJar ? "#ef4444" : "#3b82f6";
    const printUid2 = Math.random().toString(36).slice(2, 11);
    const marca2 = normalizeLogoTipo(p.logo_tipo);
    const _docsUrl2 = emisorLogoUrl;
    const iconBox = _docsUrl2
      ? `<div style="flex-shrink:0;margin-right:10px"><img src="${_docsUrl2}" alt="Logo" style="max-height:54px;max-width:145px;object-fit:contain;display:block"/></div>`
      : `<div style="width:46px;height:46px;flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;margin-right:10px">${svgMarcaIcon(marca2, printUid2, 46)}</div>`;
    const logoName = _docsUrl2 ? `` : `<div style="line-height:1">${svgPrintLogoName(p.logo_tipo, printUid2, { darkHeader: true })}</div>`;
    const rows = (p.items || []).map((item, idx) => `
      <tr style="background:${idx % 2 === 0 ? "white" : "#f8fafc"};page-break-inside:avoid">
        <td style="border:1px solid #e2e8f0;padding:6px 9px;color:#1e293b;font-size:11.5px">
          ${item.descripcion || ""}
          ${item.variante_texto ? `<div style="color:#7c3aed;font-size:10px;margin-top:2px">Variante: ${item.variante_texto}</div>` : ""}
          ${Number(item.descuento_pct || 0) > 0 ? `<div style="color:#059669;font-size:10px;margin-top:2px">Descuento aplicado: ${Number(item.descuento_pct).toLocaleString("es-PY")}%${Number(item.descuento_monto || 0) > 0 ? ` (-${fmt(item.descuento_monto)} ${sym})` : ""}</div>` : ""}
          ${item.observacion ? `<div style="color:#64748b;font-size:10px;font-style:italic;margin-top:2px">Obs: ${item.observacion}</div>` : ""}
        </td>
        <td style="border:1px solid #e2e8f0;padding:6px 5px;text-align:center;color:#334155;font-size:11.5px">${item.cantidad}</td>
        <td style="border:1px solid #e2e8f0;padding:6px 8px;text-align:right;color:#334155;font-size:11.5px">${fmt(item.precio_unitario)} ${sym}</td>
        <td style="border:1px solid #e2e8f0;padding:6px 8px;text-align:right;color:#1e293b;font-size:11.5px;font-weight:600">${fmt(item.subtotal)} ${sym}</td>
      </tr>`).join("");
    const total = p.total || 0;
    const iva = p.moneda === "USD" ? Math.round((total/11)*100)/100 : Math.round(total/11);
    const subtotal = total - iva;
    const totalItemsDoc = (p.items || []).reduce((s, item) => s + (parseFloat(item.cantidad) || 0), 0);
    const descuentoGlobalDoc = parseFloat(p.descuento_global_monto) || 0;
    const formaPagoLabel = (p.forma_pago||"contado")==="credito" ? "A crédito" : "Al contado";
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${printFileName}</title>
      <style>
        @page{size:A4;margin:16mm 18mm}
        *{box-sizing:border-box;margin:0;padding:0}
        html,body{height:auto;overflow:visible;font-family:Arial,Helvetica,sans-serif;background:white;-webkit-print-color-adjust:exact;print-color-adjust:exact}
        body{padding:0}
        .print-page,.print-annex{width:100%;max-width:174mm;margin:0 auto}
        table{border-collapse:collapse;width:100%;max-width:100%}
        tr{page-break-inside:avoid;break-inside:avoid}
        thead{display:table-header-group}
        tfoot{display:table-footer-group}
        @media print{@page{size:A4;margin:16mm 18mm}html,body{height:auto;-webkit-print-color-adjust:exact;print-color-adjust:exact}tr{page-break-inside:avoid;break-inside:avoid}}
      </style></head><body>
      <div class="print-page">
        <!-- ENCABEZADO -->
        <div style="background:${headerBg};border-radius:9px;padding:14px 18px;margin-bottom:12px;display:flex;justify-content:space-between;align-items:center">
          <div style="display:flex;align-items:center">
            ${iconBox}
            <div>
              ${logoName}
              <div style="font-size:8px;color:#cbd5e1;letter-spacing:2px;margin-top:1px">INFORMÁTICA</div>
              <div style="margin-top:7px;font-size:9.5px;color:#94a3b8;line-height:1.5">
                ${emisorFantasiaHtml("#e2e8f0", "#cbd5e1")}
                <div>${emisorDireccion}</div>
                <div>${emisorTelefono ? `Tel: ${emisorTelefono}` : ""}${emisorTelefono && emisorEmail ? " · " : ""}${emisorEmail || ""}</div>
              </div>
            </div>
          </div>
          <div style="text-align:right">
            <div style="background:${headerAccent};color:white;font-size:15px;font-weight:800;letter-spacing:3px;padding:5px 16px;border-radius:5px;margin-bottom:7px;display:inline-block">PRESUPUESTO</div>
            <div style="font-size:19px;font-weight:700;color:white;margin-bottom:2px">${p.numero}</div>
            ${p.nombre_archivo ? `<div style="font-size:10.5px;color:#e2e8f0;margin-bottom:2px">${p.nombre_archivo}</div>` : ""}
            ${emisorRuc ? `<div style="font-size:9.5px;color:#e2e8f0;margin-bottom:2px">RUC: ${emisorRuc}</div>` : ""}
            <div style="font-size:10.5px;color:#94a3b8">Fecha: <span style="color:#e2e8f0">${p.fecha}</span></div>
            <div style="font-size:9.5px;color:#94a3b8;margin-top:2px">Validez: ${p.validez_dias || 15} días</div>
          </div>
        </div>
        <!-- CLIENTE -->
        <div style="background:#f0f7ff;border-left:4px solid ${headerAccent};padding:8px 12px;border-radius:0 5px 5px 0;margin-bottom:12px">
          <div style="font-size:8px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:1px;margin-bottom:3px">Cliente</div>
          <div style="font-size:14px;font-weight:700;color:#1e293b">${p.empresa_nombre || ""}</div>
          ${p.empresa_ruc ? `<div style="font-size:10.5px;color:#475569;margin-top:1px">RUC: ${p.empresa_ruc}</div>` : ""}
          <div style="font-size:10.5px;color:#475569;margin-top:1px">Forma de pago: <strong>${formaPagoLabel}</strong></div>
        </div>
        <!-- TABLA COMPLETA (sin cortes forzados entre filas) -->
        <table>
          <thead>
            <tr style="background:${headerBg}">
              <th style="border:1px solid ${headerBg};padding:7px 9px;text-align:left;font-size:10.5px;color:white;font-weight:700">Descripción</th>
              <th style="border:1px solid ${headerBg};padding:7px 5px;text-align:center;width:45px;font-size:10.5px;color:white;font-weight:700">Cant.</th>
              <th style="border:1px solid ${headerBg};padding:7px;text-align:right;width:115px;font-size:10.5px;color:white;font-weight:700">Precio Unit.</th>
              <th style="border:1px solid ${headerBg};padding:7px;text-align:right;width:115px;font-size:10.5px;color:white;font-weight:700">Subtotal</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr style="background:${headerBg};page-break-inside:avoid">
              <td colspan="3" style="border:1px solid ${headerBg};padding:8px 9px;text-align:right;font-weight:800;font-size:13px;color:white">TOTAL A PAGAR:</td>
              <td style="border:1px solid ${headerBg};padding:8px 9px;text-align:right;font-weight:800;font-size:13px;color:white">${fmt(total)} ${sym}</td>
            </tr>
            <tr style="background:#f8fafc;page-break-inside:avoid">
              <td colspan="3" style="border:1px solid #e2e8f0;padding:4px 9px;text-align:right;font-size:10.5px;color:#64748b">Total ítems:</td>
              <td style="border:1px solid #e2e8f0;padding:4px 9px;text-align:right;font-size:10.5px;color:#64748b">${totalItemsDoc}</td>
            </tr>
            ${descuentoGlobalDoc > 0 ? `
            <tr style="background:#f8fafc;page-break-inside:avoid">
              <td colspan="3" style="border:1px solid #e2e8f0;padding:4px 9px;text-align:right;font-size:10.5px;color:#059669">Descuento aplicado:</td>
              <td style="border:1px solid #e2e8f0;padding:4px 9px;text-align:right;font-size:10.5px;color:#059669">-${fmt(descuentoGlobalDoc)} ${sym}</td>
            </tr>` : ""}
            <tr style="background:#f8fafc;page-break-inside:avoid">
              <td colspan="3" style="border:1px solid #e2e8f0;padding:4px 9px;text-align:right;font-size:10.5px;color:#64748b">Base imponible:</td>
              <td style="border:1px solid #e2e8f0;padding:4px 9px;text-align:right;font-size:10.5px;color:#64748b">${fmt(subtotal)} ${sym}</td>
            </tr>
            <tr style="background:#f8fafc;page-break-inside:avoid">
              <td colspan="3" style="border:1px solid #e2e8f0;padding:4px 9px;text-align:right;font-size:10.5px;color:#64748b">IVA incluido (10%):</td>
              <td style="border:1px solid #e2e8f0;padding:4px 9px;text-align:right;font-size:10.5px;color:#64748b">${fmt(iva)} ${sym}</td>
            </tr>
          </tfoot>
        </table>
        ${p.observaciones || p.condiciones ? `
        <div style="margin-top:12px;page-break-inside:avoid;break-inside:avoid">
          ${p.observaciones ? `<div style="background:#fefce8;padding:9px 11px;border-left:4px solid #eab308;border-radius:0 5px 5px 0;margin-bottom:7px;page-break-inside:avoid;break-inside:avoid"><strong style="color:#92400e;font-size:10.5px">OBSERVACIONES:</strong><br><span style="font-size:10.5px;color:#713f12">${p.observaciones.replace(/\n/g,"<br>")}</span></div>` : ""}
          ${p.condiciones ? `<div style="background:#f0fdf4;padding:9px 11px;border-left:4px solid #22c55e;border-radius:0 5px 5px 0;page-break-inside:avoid;break-inside:avoid"><strong style="color:#166534;font-size:10.5px">CONDICIONES:</strong><br><span style="font-size:10.5px;color:#15803d">${p.condiciones.replace(/\n/g,"<br>")}</span></div>` : ""}
        </div>` : ""}
      </div>
      ${buildImageAnnexHTML(headerBg, headerAccent)}
    </body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); }, 700);
  };

  const imprimirTicketPresupuesto = () => {
    const w = window.open("", "_blank");
    if (!w) return;

    const fmtT = (n) => {
      if (n == null || isNaN(n)) return "-";
      if (p.moneda === "PYG") return `₲ ${Math.round(n).toLocaleString("es-PY")}`;
      return `${p.moneda} ${Number(n).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };
    const sep = (ch = "=", n = 40) => `<div class="sep">${ch.repeat(n)}</div>`;
    const now = new Date();
    const hora = now.toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" });

    const total = p.total || 0;
    const iva = p.moneda === "USD" ? Math.round((total / 11) * 100) / 100 : Math.round(total / 11);
    const base = total - iva;
    const formaPagoLabel = (p.forma_pago || "contado") === "credito" ? "A crédito" : "Al contado";

    const itemsHtml = (p.items || []).map(item => {
      const cant = item.cantidad || 1;
      const desc = item.descripcion || "";
      const punit = item.precio_unitario || 0;
      const subt = item.subtotal || punit * cant;
      const descuentoPct = Number(item.descuento_pct || 0);
      return `
        <div class="item">
          <div class="item-top">
            <span class="item-cant">${cant}</span>
            <span class="item-desc">${desc}</span>
            <span class="item-total">${fmtT(subt)}</span>
          </div>
          ${item.variante_texto ? `<div class="item-sub" style="color:#7c3aed">Variante: ${item.variante_texto}</div>` : ""}
          ${descuentoPct > 0 ? `<div class="item-sub" style="color:#059669">Desc. ${descuentoPct.toLocaleString("es-PY")}%${Number(item.descuento_monto || 0) > 0 ? ` · -${fmtT(item.descuento_monto)}` : ""}</div>` : ""}
          ${punit > 0 && cant > 1 ? `<div class="item-sub">${fmtT(punit)} c/u</div>` : ""}
          ${item.observacion ? `<div class="item-sub" style="font-style:italic">→ ${item.observacion}</div>` : ""}
        </div>`;
    }).join("");

    const condicionesHtml = p.condiciones ? `
      <div style="font-size:8.5px;line-height:1.6;margin-top:4px;color:#444;">
        ${p.condiciones.replace(/\n/g, "<br>")}
      </div>` : "";

    w.document.write(`<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>Presupuesto Ticket · ${p.numero}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{
      font-family:'Courier New',Courier,monospace;
      font-size:11px;color:#000;background:#fff;
      width:300px;margin:0 auto;padding:10px 6px 20px;
    }
    @media print{
      @page{size:80mm auto;margin:0}
      body{width:80mm;padding:4px 4px 16px;}
      .no-print{display:none!important}
    }
    .center{text-align:center}
    .bold{font-weight:bold}
    .sep{text-align:center;letter-spacing:-0.5px;color:#444;margin:4px 0;font-size:11px;overflow:hidden;white-space:nowrap}

    .logo-name{font-size:20px;font-weight:900;text-align:center;letter-spacing:1px;line-height:1.1;margin-bottom:2px}
    .empresa-info{font-size:8.5px;text-align:center;line-height:1.6;color:#222}

    .doc-label{font-size:13px;font-weight:bold;text-align:center;letter-spacing:2px}
    .doc-num{font-size:15px;font-weight:900;text-align:center}
    .doc-meta{font-size:9px;line-height:1.7;text-align:center;margin-top:2px}

    .cli-row{display:flex;gap:4px;font-size:9.5px;line-height:1.7}
    .cli-label{width:54px;flex-shrink:0;color:#555}
    .cli-val{font-weight:600;flex:1}

    .items-head{display:flex;font-size:8.5px;font-weight:bold;border-bottom:1px solid #000;padding-bottom:2px;margin-bottom:2px}
    .item{margin:2px 0}
    .item-top{display:flex;font-size:10px}
    .item-cant{width:24px;flex-shrink:0;text-align:center}
    .item-desc{flex:1;padding:0 4px;word-break:break-word}
    .item-total{width:68px;flex-shrink:0;text-align:right;font-weight:600}
    .item-sub{font-size:8.5px;color:#555;padding-left:24px;margin-top:-1px}

    .tot-row{display:flex;justify-content:space-between;font-size:10.5px;margin:1px 0}
    .tot-row.big{font-size:14px;font-weight:900;margin-top:4px}
    .tot-row.muted{color:#666;font-size:9.5px}

    .footer{font-size:8.5px;text-align:center;line-height:1.7;margin-top:8px;color:#333}
    .btn-print{display:block;width:100%;margin-top:16px;padding:10px;background:#1d4ed8;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-family:Arial,sans-serif;font-weight:600}
  </style>
</head>
<body>

  <div class="logo-name">${emisorNombreFantasia || emisorNombre}</div>
  <div class="empresa-info">
    ${emisorNombreFantasia ? `${emisorNombre}<br>` : ""}
    ${emisorRuc     ? `RUC: ${emisorRuc}<br>`         : ""}
    ${emisorDireccion ? `${emisorDireccion}<br>`       : ""}
    ${emisorTelefono  ? `Tel: ${emisorTelefono}<br>`   : ""}
    ${emisorEmail     ? `${emisorEmail}<br>`            : ""}
  </div>

  ${sep("=")}

  <div class="doc-label">PRESUPUESTO</div>
  <div class="doc-num">${p.numero}</div>
  ${p.nombre_archivo ? `<div class="doc-meta" style="font-weight:600">${p.nombre_archivo}</div>` : ""}
  <div class="doc-meta">
    Fecha: ${p.fecha}&nbsp;&nbsp;Hora: ${hora}<br>
    Validez: ${p.validez_dias || 15} días
  </div>

  ${sep("-")}

  <div class="cli-row"><span class="cli-label">Cliente:</span><span class="cli-val">${p.empresa_nombre || "—"}</span></div>
  ${p.empresa_ruc ? `<div class="cli-row"><span class="cli-label">RUC:</span><span class="cli-val">${p.empresa_ruc}</span></div>` : ""}

  ${sep("-")}

  <div class="items-head">
    <span style="width:24px;text-align:center">Ct</span>
    <span style="flex:1;padding-left:4px">Descripción</span>
    <span style="width:68px;text-align:right">Total</span>
  </div>

  ${itemsHtml}

  ${sep("-")}

  <div class="tot-row muted"><span>Base imponible</span><span>${fmtT(base)}</span></div>
  <div class="tot-row muted"><span>IVA (10%)</span><span>${fmtT(iva)}</span></div>
  ${sep("=")}
  <div class="tot-row big"><span>TOTAL</span><span>${fmtT(total)}</span></div>
  ${sep("=")}

  <div class="tot-row" style="margin-top:4px"><span>Forma de pago:</span><span>${formaPagoLabel}</span></div>

  ${p.condiciones || p.observaciones ? `
  ${sep("-")}
  ${condicionesHtml}
  ${p.observaciones ? `<div style="font-size:8.5px;margin-top:4px;color:#444;font-style:italic">${p.observaciones}</div>` : ""}
  ` : ""}

  ${sep("=")}

  <div class="footer">
    Este presupuesto no es un comprobante fiscal.<br>
    <span style="font-size:7.5px;color:#aaa">Impreso el ${now.toLocaleString("es-PY")}</span>
  </div>

  <button class="btn-print no-print" onclick="window.print()">🖨️ Imprimir Ticket</button>

</body>
</html>`);
    w.document.close();
    setTimeout(() => w.print(), 600);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto relative" onClick={e => e.stopPropagation()}>
        {/* Toolbar */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-200 px-4 py-3 flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-medium text-sm">Vista previa del presupuesto</span>
            <div className="flex items-center gap-2">
              <label className="text-gray-500 text-xs whitespace-nowrap">Nombre:</label>
              <input type="text" value={printFileName} onChange={e => setPrintFileName(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700 w-48 focus:outline-none focus:border-blue-400" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex gap-1.5">
              <button onClick={handlePrint}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-all"
                title="Imprimir formato A4">
                <Printer className="w-3.5 h-3.5" /> Imprimir A4
              </button>
              {(p.items || []).length > 0 && (
                <button onClick={handlePrintPorPartes}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white text-xs rounded-lg transition-all"
                  title="Divide en hojas de máx. 15 ítems con IVA y totales por hoja">
                  <Printer className="w-3.5 h-3.5" /> Por partes
                </button>
              )}
              <button onClick={imprimirTicketPresupuesto}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs rounded-lg transition-all"
                title="Imprimir formato ticket 80mm">
                🧾 Ticket
              </button>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {canEdit && (
                <button onClick={onEdit}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-700 text-xs rounded-lg border border-yellow-500/30 transition-all">
                  <Edit className="w-3.5 h-3.5" /> Editar
                </button>
              )}
              {canCreate && (
                <button onClick={onDuplicate}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-700 text-xs rounded-lg border border-purple-500/30 transition-all">
                  <Copy className="w-3.5 h-3.5" /> Copiar
                </button>
              )}
              {canCostos && (
                <button onClick={onCostos}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-700 text-xs rounded-lg border border-cyan-500/30 transition-all">
                  <Wallet className="w-3.5 h-3.5" /> Costos
                </button>
              )}
              {canAprobar && (
                <button onClick={onAprobar}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-700 text-xs rounded-lg border border-emerald-500/30 transition-all"
                  title="Aprobar total o parcialmente (cantidad por renglón) / ver histórico">
                  <CheckCircle className="w-3.5 h-3.5" /> {p.aprobacion_parcial ? "Aprobación parcial" : "Aprobar"}
                </button>
              )}
              {canEdit && (
                <div onClick={e => e.stopPropagation()}>
                  <StateDropdown
                    estado={p.estado}
                    options={PRESUP_ESTADOS}
                    onChange={onEstadoChange}
                    disabled={(p.facturas_count || 0) > 0}
                  />
                </div>
              )}
              {canDelete && (
                <button onClick={onDelete}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-600 text-xs rounded-lg border border-red-500/20 transition-all">
                  <Trash2 className="w-3.5 h-3.5" /> Eliminar
                </button>
              )}
              <button onClick={onClose}
                className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs rounded-lg transition-all">
                <X className="w-3.5 h-3.5" /> Cerrar
              </button>
            </div>
          </div>
        </div>

        {/* Header del documento */}
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              {p.logo_tipo === "arandu" && <LogoArandu />}
              {p.logo_tipo === "jar" && <LogoJar />}
              {(!p.logo_tipo || p.logo_tipo === "arandujar") && <LogoAranduJarDoc />}
              <div className="mt-4 text-sm text-gray-600">
                {emisorNombreFantasia ? (
                  <>
                    <p className="font-bold text-gray-900 text-base leading-tight">{emisorNombreFantasia}</p>
                    <p className="text-xs text-gray-500">{emisorNombre}{emisorRuc ? ` · RUC: ${emisorRuc}` : ""}</p>
                  </>
                ) : (
                  <p className="font-semibold text-gray-700">{emisorNombre}{emisorRuc ? ` · RUC: ${emisorRuc}` : ""}</p>
                )}
                {emisorDireccion && <p>{emisorDireccion}</p>}
                {emisorTelefono && <p>Tel: {emisorTelefono}</p>}
                {emisorEmail && <p>{emisorEmail}</p>}
              </div>
            </div>
            <div className="text-right">
              <h1 className="text-2xl font-bold text-gray-800">PRESUPUESTO</h1>
              <p className="text-lg font-semibold" style={{ color: accentColor }}>{p.numero}</p>
              {emisorRuc && <p className="text-gray-600 text-sm font-semibold">RUC: {emisorRuc}</p>}
              {p.nombre_archivo && <p className="text-gray-500 text-sm">{p.nombre_archivo}</p>}
              <p className="text-gray-600">Fecha: {p.fecha}</p>
              <p className="text-gray-500 text-sm">Validez: {p.validez_dias || 15} días</p>
            </div>
          </div>
        </div>

        {/* Cliente */}
        <div className="p-6 bg-gray-50">
          <h3 className="font-semibold text-gray-700 mb-2">CLIENTE:</h3>
          <p className="text-lg font-medium text-gray-800">{p.empresa_nombre}</p>
          {p.empresa_ruc && <p className="text-sm text-gray-600">RUC: {p.empresa_ruc}</p>}
          <p className="text-sm text-gray-600 mt-1">Forma de pago: <strong>{(p.forma_pago || "contado") === "credito" ? "A crédito" : "Al contado"}</strong></p>
          {p.aprobacion_parcial && (
            <p className="mt-2 inline-block text-xs font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded-full px-3 py-1">
              Aprobación parcial: ver cantidades aprobadas por renglón
            </p>
          )}
        </div>

        {/* Items */}
        <div className="p-6">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-gray-400 p-2 text-left text-gray-800 font-bold">Descripción</th>
                <th className="border border-gray-400 p-2 text-center w-20 text-gray-800 font-bold">Cant.</th>
                <th className="border border-gray-400 p-2 text-right w-32 text-gray-800 font-bold">Precio Unit.</th>
                <th className="border border-gray-400 p-2 text-right w-32 text-gray-800 font-bold">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {(p.items || []).map((item, idx) => (
                <tr key={idx} className="bg-white">
                  <td className="border border-gray-400 p-2 text-gray-800">
                    {item.descripcion}
                    {item.variante_texto && (
                      <p className="text-violet-600 text-xs mt-1">Variante: {item.variante_texto}</p>
                    )}
                    {Number(item.descuento_pct || 0) > 0 && (
                      <p className="text-emerald-600 text-xs mt-1">
                        Descuento aplicado: {Number(item.descuento_pct).toLocaleString("es-PY")}%
                        {Number(item.descuento_monto || 0) > 0 ? ` (-${fmt(item.descuento_monto)} ${sym})` : ""}
                      </p>
                    )}
                    {item.observacion && <p className="text-gray-500 text-xs italic mt-1">Obs: {item.observacion}</p>}
                    {item.cantidad_aprobada != null && (p.aprobacion_parcial || (p.aprobaciones || []).length > 0) && (
                      <p className={`text-xs mt-1 font-medium ${Number(item.cantidad_aprobada) < Number(item.cantidad) ? "text-amber-600" : "text-emerald-600"}`}>
                        Aprobado: {item.cantidad_aprobada} de {item.cantidad}
                        {item.cantidad_facturada != null ? ` · Facturado: ${item.cantidad_facturada} · Pendiente: ${item.pendiente_facturar ?? 0}` : ""}
                      </p>
                    )}
                  </td>
                  <td className="border border-gray-400 p-2 text-center text-gray-800">{item.cantidad}</td>
                  <td className="border border-gray-400 p-2 text-right text-gray-800">{fmt(item.precio_unitario)} {sym}</td>
                  <td className="border border-gray-400 p-2 text-right text-gray-800">{fmt(item.subtotal)} {sym}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: "#dbeafe" }}>
                <td colSpan="3" className="border border-gray-400 p-2 text-right font-bold text-lg text-gray-900">TOTAL:</td>
                <td className="border border-gray-400 p-2 text-right font-bold text-lg" style={{ color: accentColor }}>
                  {fmt(p.total || 0)} {sym}
                </td>
              </tr>
              <tr className="bg-gray-50">
                <td colSpan="3" className="border border-gray-400 p-2 text-right text-sm text-gray-600">Total ítems:</td>
                <td className="border border-gray-400 p-2 text-right text-sm text-gray-600">
                  {(p.items || []).reduce((s, it) => s + (parseFloat(it.cantidad) || 0), 0)}
                </td>
              </tr>
              <tr className="bg-gray-50">
                <td colSpan="3" className="border border-gray-400 p-2 text-right text-sm text-gray-600">Base imponible:</td>
                <td className="border border-gray-400 p-2 text-right text-sm text-gray-600">{fmt(p.subtotal || 0)} {sym}</td>
              </tr>
              <tr className="bg-gray-50">
                <td colSpan="3" className="border border-gray-400 p-2 text-right text-sm text-gray-600">IVA incluido (10%):</td>
                <td className="border border-gray-400 p-2 text-right text-sm text-gray-600">{fmt(p.iva || 0)} {sym}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Observaciones y Condiciones */}
        {(p.observaciones || p.condiciones) && (
          <div className="px-6 pb-6 space-y-3">
            {p.observaciones && (
              <div className="bg-yellow-50 p-3 border-l-4 border-yellow-400 rounded-r">
                <strong className="text-yellow-800 text-sm">Observaciones:</strong>
                <p className="text-gray-700 text-sm mt-1 whitespace-pre-line">{p.observaciones}</p>
              </div>
            )}
            {p.condiciones && (
              <div>
                <strong className="text-gray-700 text-sm">Condiciones:</strong>
                <p className="text-gray-600 text-sm mt-1 whitespace-pre-line">{p.condiciones}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══ Vista previa inline (modal) ════════════════════════════════════════
function PreviewModal({ item, onClose, navigate, token, onUpdated, cuentasDisp = [] }) {
  const { kind, data } = item;
  const d = data || {};
  React.useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const title = {
    presupuesto: `Presupuesto #${d.numero || ""}`,
    factura:     `Factura ${d.numero || ""}`,
    ingreso:     `Ingreso: ${d.descripcion || ""}`,
  }[kind] || "Detalle";

  const editUrl = {
    presupuesto: "/sistema/presupuestos",
    factura:     "/sistema/facturas",
    ingreso:     "/sistema/ingresos-varios",
  }[kind];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-arandu-dark rounded-2xl border border-white/15 shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-arandu-dark border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <h3 className="font-heading text-white text-lg">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-3 text-sm font-body text-slate-200">
          {kind === "presupuesto" && <PresupuestoPreview p={d} />}
          {kind === "factura"     && <FacturaPreview f={d} token={token} onUpdated={onUpdated} />}
          {kind === "ingreso"     && <IngresoPreview i={d} cuentasDisp={cuentasDisp} />}
        </div>
        <div className="border-t border-white/10 px-6 py-3 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-xs bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/30 transition-all"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

const PreviewRow = ({ label, value, mono = false }) => (
  <div className="flex items-start justify-between gap-4 border-b border-white/5 pb-2">
    <span className="text-slate-400 text-xs uppercase tracking-wider min-w-[100px]">{label}</span>
    <span className={`text-white ${mono ? "font-mono" : ""} text-right flex-1`}>{value || <span className="text-slate-500">—</span>}</span>
  </div>
);

function PresupuestoPreview({ p }) {
  return (
    <>
      <PreviewRow label="Número"   value={`#${p.numero || ""}`} mono />
      <PreviewRow label="Nombre"   value={p.nombre} />
      <PreviewRow label="Cliente"  value={p.empresa_nombre} />
      <PreviewRow label="Fecha"    value={p.fecha} />
      <PreviewRow label="Moneda"   value={p.moneda} />
      <PreviewRow label="Forma pago" value={p.forma_pago} />
      <PreviewRow label="Total"    value={fmtMonto(p.total || 0, p.moneda)} />
      <PreviewRow label="Estado"   value={<StateBadge estado={p.estado} />} />
      {p.aprobacion_estado && p.aprobacion_estado !== "sin_aprobar" && (
        <PreviewRow label="Aprobación" value={p.aprobacion_estado === "parcial" ? `Parcial (${p.aprobado_pct ?? 0}%) · ${fmtMonto(p.monto_aprobado || 0, p.moneda)}` : "Total"} />
      )}
      {p.items?.length > 0 && (
        <div className="mt-3">
          <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Ítems ({p.items.length})</p>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {p.items.map((it, idx) => (
              <div key={idx} className="flex justify-between text-xs border-b border-white/5 py-1">
                <span className="text-slate-300 truncate max-w-[60%]">{it.descripcion || it.nombre}
                  {it.cantidad_aprobada != null && Number(it.cantidad_aprobada) < Number(it.cantidad || 0) && (
                    <span className="ml-1 text-amber-300">(aprob. {it.cantidad_aprobada}/{it.cantidad})</span>
                  )}
                </span>
                <span className="text-white">{fmtMonto((it.precio_unitario || it.precio || 0) * (it.cantidad || 1), p.moneda)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {p.observaciones && <PreviewRow label="Observ." value={p.observaciones} />}
    </>
  );
}

function FacturaPreview({ f, token }) {
  const { hasPermission } = useContext(AuthContext);
  const [notasLog, setNotasLog] = useState(f.notas_log || []);
  const [agregando, setAgregando]   = useState(false);
  const [nuevoTexto, setNuevoTexto] = useState("");
  const [saving, setSaving]         = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const puedeAgregar = hasPermission("facturas.agregar_nota");
  const puedeEditar  = hasPermission("facturas.editar_nota");

  // Toda la sección de notas es visible si: hay nota de creación, hay log, o el user puede agregar
  const mostrarSeccion = f.notas || notasLog.length > 0 || puedeAgregar || puedeEditar;

  const agregarNota = async () => {
    if (!nuevoTexto.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/facturas/${f.id}/notas`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ texto: nuevoTexto.trim() }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Error"); }
      const nueva = await res.json();
      setNotasLog(prev => [...prev, nueva]);
      setNuevoTexto("");
      setAgregando(false);
      toast.success("Nota agregada");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const eliminarNota = async (notaId) => {
    setDeletingId(notaId);
    try {
      const res = await fetch(`${API}/admin/facturas/${f.id}/notas/${notaId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Error"); }
      setNotasLog(prev => prev.filter(n => n.id !== notaId));
      toast.success("Nota eliminada");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <PreviewRow label="Número"     value={f.numero} mono />
      <PreviewRow label="Cliente"    value={f.empresa_nombre || f.razon_social} />
      {f.empresa_nombre && f.razon_social && f.empresa_nombre !== f.razon_social && (
        <PreviewRow label="Razón soc." value={f.razon_social} />
      )}
      <PreviewRow label="RUC"        value={f.ruc} mono />
      <PreviewRow label="Concepto"   value={f.concepto} />
      <PreviewRow label="Fecha"      value={f.fecha} />
      <PreviewRow label="Forma pago" value={f.forma_pago} />
      <PreviewRow label="Moneda"     value={f.moneda} />
      <PreviewRow label="Monto"      value={fmtMonto(f.monto || 0, f.moneda)} />
      <PreviewRow label="IVA"        value={f.iva != null ? fmtMonto(f.iva, f.moneda) : "—"} />
      <PreviewRow label="Estado"     value={<StateBadge estado={f.estado} />} />

      {/* ── Hilo de notas ─────────────────────────────── */}
      {mostrarSeccion && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Notas</span>
            {!agregando && (puedeAgregar || puedeEditar) && (
              <button
                onClick={() => setAgregando(true)}
                className="flex items-center gap-1 px-2 py-0.5 rounded text-xs text-emerald-300 hover:bg-emerald-500/10 border border-emerald-500/20 transition-all"
              >
                <Plus className="w-3 h-3" /> Agregar nota
              </button>
            )}
          </div>

          <div className="space-y-2">
            {/* Nota de creación (campo notas, seteado al crear la factura) */}
            {f.notas && (
              <div className="bg-slate-700/40 rounded-lg p-3 border border-white/8">
                <p className="text-slate-200 text-sm whitespace-pre-line leading-relaxed">{f.notas}</p>
                <p className="text-slate-500 text-xs mt-1.5">Al crear la factura</p>
              </div>
            )}

            {/* Entradas del log (post-creación) */}
            {notasLog.map(n => (
              <div key={n.id} className="group relative bg-slate-700/40 rounded-lg p-3 border border-white/8">
                <p className="text-slate-200 text-sm whitespace-pre-line leading-relaxed pr-7">{n.texto}</p>
                <p className="text-slate-500 text-xs mt-1.5">{n.autor} · {n.fecha}</p>
                {puedeEditar && (
                  <button
                    onClick={() => eliminarNota(n.id)}
                    disabled={deletingId === n.id}
                    className="absolute top-2.5 right-2.5 p-1 rounded opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-all disabled:opacity-50"
                    title="Eliminar nota"
                  >
                    {deletingId === n.id
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <Trash2 className="w-3 h-3" />
                    }
                  </button>
                )}
              </div>
            ))}

            {!f.notas && notasLog.length === 0 && !agregando && (
              <p className="text-slate-500 text-xs italic">Sin notas.</p>
            )}
          </div>

          {/* Formulario nueva nota */}
          {agregando && (
            <div className="space-y-2 mt-3">
              <textarea
                value={nuevoTexto}
                onChange={e => setNuevoTexto(e.target.value)}
                rows={3}
                className="w-full bg-slate-800/80 text-white text-sm rounded-lg p-2.5 border border-white/15 focus:border-emerald-400/40 focus:outline-none resize-none placeholder-slate-500"
                placeholder="Escribí la nota…"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setAgregando(false); setNuevoTexto(""); }}
                  className="px-3 py-1 rounded-lg text-xs text-slate-400 hover:text-white border border-white/10 hover:border-white/20 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={agregarNota}
                  disabled={saving || !nuevoTexto.trim()}
                  className="flex items-center gap-1 px-3 py-1 rounded-lg text-xs bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/30 transition-all disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Guardar
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}


function IngresoPreview({ i, cuentasDisp = [] }) {
  const moneda = i.moneda || "PYG";
  const montoConvertido = moneda !== "PYG" && i.tipo_cambio
    ? i.monto * i.tipo_cambio
    : null;

  return (
    <>
      {/* Monto destacado */}
      <div className="flex flex-col items-center justify-center bg-violet-500/10 border border-violet-500/20 rounded-xl py-5 mb-4">
        <p className="text-violet-300 text-xs uppercase tracking-widest mb-1 font-body">Monto ingresado</p>
        <p className="font-heading font-bold text-3xl text-violet-200">
          {fmtMonto(i.monto || 0, moneda)}
        </p>
        {montoConvertido && (
          <p className="text-slate-400 text-sm mt-1">
            ≈ {fmtMonto(montoConvertido, "PYG")} <span className="text-slate-500 text-xs">TC {i.tipo_cambio}</span>
          </p>
        )}
      </div>

      {/* Categoría badge */}
      <div className="flex items-center justify-between border-b border-white/5 pb-2 mb-1">
        <span className="text-slate-400 text-xs uppercase tracking-wider">Categoría</span>
        <span className="text-xs px-2.5 py-1 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/20 font-body">
          {i.categoria || "—"}
        </span>
      </div>

      <PreviewRow label="Descripción" value={i.descripcion} />
      <PreviewRow label="Fecha"       value={i.fecha} />
      <PreviewRow label="Moneda"      value={moneda} />

      {/* Cuenta bancaria — con fallback a predeterminada si el registro es viejo */}
      {(() => {
        const cuentaEf = i.cuenta_nombre
          || cuentasDisp.find(c => c.logo_tipo === i.logo_tipo && c.moneda === moneda && c.es_predeterminada)?.nombre
          || cuentasDisp.find(c => c.logo_tipo === i.logo_tipo && c.moneda === moneda)?.nombre
          || null;
        if (!cuentaEf) return null;
        return (
          <div className="flex items-center justify-between border-b border-white/5 pb-2">
            <span className="text-slate-400 text-xs uppercase tracking-wider">Cuenta</span>
            <span className="text-blue-300 text-sm flex items-center gap-1 font-body">
              🏦 {cuentaEf}
            </span>
          </div>
        );
      })()}

      {/* Empresa */}
      {i.logo_tipo && (
        <PreviewRow label="Empresa" value={
          i.logo_tipo === "arandu" ? "Arandu Informática"
          : i.logo_tipo === "jar"  ? "JAR Informática"
          : "Arandu&JAR"
        } />
      )}

      {i.notas && (
        <div className="mt-3 bg-amber-500/5 border border-amber-500/15 rounded-lg px-4 py-3">
          <p className="text-amber-400 text-xs uppercase tracking-wider mb-1">Notas</p>
          <p className="text-slate-200 text-sm font-body">{i.notas}</p>
        </div>
      )}

      {/* Fecha de carga */}
      {i.created_at && (
        <p className="text-slate-600 text-xs text-right mt-3 font-body">
          Registrado: {i.created_at.slice(0, 10)}
        </p>
      )}
    </>
  );
}

function FacturarModal({
  presupuesto, onClose, facturaForm, setFacturaForm, facturaMode, setFacturaMode,
  facturaSearch, setFacturaSearch, facturasDisponibles, facturaSeleccionada,
  setFacturaSeleccionada, savingFactura, onFacturar, onVincular,
  comprasDisponibles, compraSeleccionada, setCompraSeleccionada, compraSearch, setCompraSearch,
  onVincularCompra, onDesvincular, vincQty = {}, setVincQty = () => {}
}) {
  const montoFmt = presupuesto.moneda === "USD"
    ? `USD ${presupuesto.total}`
    : `₲ ${Number(presupuesto.total).toLocaleString("es-PY")}`;

  // Facturas ya vinculadas a este presupuesto
  const facturasVinculadas = facturasDisponibles.filter(f =>
    (f.presupuesto_ids || []).includes(presupuesto.id) || f.presupuesto_id === presupuesto.id
  );
  // Compras ya vinculadas a este presupuesto
  const comprasVinculadas = comprasDisponibles.filter(c => c.presupuesto_id === presupuesto.id);
  // Si cobrado + ya tiene compra → no permitir más compras
  const cobradoConCompra = presupuesto.estado === "cobrado" && comprasVinculadas.length > 0;

  const renderFacturaItem = (f, isLinked) => {
    const isSelected = facturaSeleccionada?.id === f.id;
    return (
      <button
        key={f.id}
        type="button"
        onClick={() => !isLinked && setFacturaSeleccionada(prev => prev?.id === f.id ? null : f)}
        className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
          isLinked
            ? "border-emerald-500/40 bg-emerald-500/10 cursor-default"
            : isSelected
              ? "border-orange-400 bg-orange-500/15 text-white"
              : "border-white/10 text-slate-300 hover:border-white/25 hover:bg-white/5"
        }`}
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{f.numero}</span>
          <span className={`text-xs ${f.estado === "pagada" ? "text-emerald-400" : f.estado === "anulada" ? "text-red-400" : "text-amber-400"}`}>{f.estado}</span>
        </div>
        <p className="text-xs text-slate-400 mt-0.5">{f.empresa_nombre || f.razon_social} · {f.fecha}</p>
        {f.concepto && <p className="text-xs text-slate-500 truncate">{f.concepto}</p>}
        {isLinked && (
          <div className="flex items-center justify-between mt-0.5">
            <p className="text-xs text-emerald-400">✓ Ya vinculada a este presupuesto</p>
            {onDesvincular && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDesvincular(f.id); }}
                className="text-xs text-red-400 hover:text-red-300 transition-colors ml-2 underline"
              >
                Desvincular
              </button>
            )}
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative bg-arandu-dark-light border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="font-heading text-lg text-white mb-1">Vincular presupuesto</h2>
        <p className="text-slate-400 text-sm mb-4">
          <span className="text-white font-medium">{presupuesto.numero}</span>
          {presupuesto.nombre_archivo && <span className="text-slate-300"> — {presupuesto.nombre_archivo}</span>}
          <br/>
          <span className="text-slate-500">{presupuesto.empresa_nombre} · {montoFmt}</span>
        </p>

        {/* Tabs: Factura / Compra */}
        <div className="flex gap-1 bg-white/5 rounded-xl p-1 mb-4">
          {[
            { key: "existente", label: `📄 Factura${facturasVinculadas.length > 0 ? ` (${facturasVinculadas.length})` : ""}` },
            { key: "compra",    label: `🛒 Compra${comprasVinculadas.length > 0 ? ` (${comprasVinculadas.length})` : ""}` },
          ].map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => setFacturaMode(t.key)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                facturaMode === t.key ? "bg-orange-500 text-white shadow" : "text-slate-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── TAB: FACTURA EXISTENTE ── */}
        {facturaMode === "existente" && (
          <div className="space-y-3">
            {/* Ya vinculadas */}
            {facturasVinculadas.length > 0 && (
              <div>
                <p className="text-emerald-400/80 text-[10px] font-bold uppercase tracking-wider mb-1.5">Ya vinculadas ({facturasVinculadas.length})</p>
                <div className="space-y-1 mb-3">
                  {facturasVinculadas.map(f => renderFacturaItem(f, true))}
                </div>
                <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1.5">Agregar otra</p>
              </div>
            )}
            <input
              type="text"
              value={facturaSearch}
              onChange={e => setFacturaSearch(e.target.value)}
              placeholder="Buscar por número, empresa o concepto..."
              className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-400"
              autoFocus
            />
            <div className="max-h-52 overflow-y-auto space-y-1 pr-1">
              {(() => {
                const filtered = facturasDisponibles.filter(f => {
                  // No mostrar las ya vinculadas en la lista de búsqueda
                  if ((f.presupuesto_ids || []).includes(presupuesto.id) || f.presupuesto_id === presupuesto.id) return false;
                  if (!facturaSearch.trim()) return true;
                  const q = facturaSearch.toLowerCase();
                  return (f.numero || "").toLowerCase().includes(q)
                    || (f.empresa_nombre || "").toLowerCase().includes(q)
                    || (f.razon_social || "").toLowerCase().includes(q)
                    || (f.concepto || "").toLowerCase().includes(q);
                });
                // Ordenar: sin vínculo a otro presupuesto primero, vinculadas a otro al final
                filtered.sort((a, b) => {
                  const aVinc = !!(a.presupuesto_id || (a.presupuesto_ids || []).length > 0);
                  const bVinc = !!(b.presupuesto_id || (b.presupuesto_ids || []).length > 0);
                  return aVinc - bVinc;
                });
                return filtered.length > 0 ? filtered.map(f => renderFacturaItem(f, false)) : (
                  <p className="text-slate-500 text-sm text-center py-6">
                    {facturaSearch ? "Sin resultados" : facturasVinculadas.length > 0 ? "No hay más facturas disponibles" : "No hay facturas emitidas disponibles"}
                  </p>
                );
              })()}
            </div>
            {facturaSeleccionada && (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2 text-xs text-orange-300">
                Seleccionada: <strong>{facturaSeleccionada.numero}</strong> — {facturaSeleccionada.empresa_nombre || facturaSeleccionada.razon_social}
              </div>
            )}
            {facturaSeleccionada && (presupuesto.aprobaciones || []).length > 0 && (
              <div className="border border-white/10 rounded-lg p-3">
                <p className="text-xs text-slate-300 mb-2">Presupuesto con aprobación parcial: indicá cuánto de lo aprobado cubre esta factura.</p>
                <table className="w-full text-xs">
                  <thead className="text-slate-500"><tr><th className="text-left py-1">Renglón</th><th className="text-center">Pendiente</th><th className="text-center w-20">Cubre</th></tr></thead>
                  <tbody>
                    {(presupuesto.items || []).map((it, i) => (
                      Number(it.pendiente_facturar || 0) > 0 ? (
                        <tr key={i} className="border-t border-white/5 text-slate-300">
                          <td className="py-1 pr-2 truncate max-w-[180px]">{it.descripcion}</td>
                          <td className="text-center">{it.pendiente_facturar}</td>
                          <td className="text-center"><input type="number" min="0" max={it.pendiente_facturar} value={vincQty[i] ?? ""} onChange={e => setVincQty({ ...vincQty, [i]: e.target.value })}
                            className="w-16 bg-arandu-dark border border-white/10 rounded px-1 py-0.5 text-center text-white" /></td>
                        </tr>
                      ) : null
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: COMPRA ── */}
        {facturaMode === "compra" && (
          <div className="space-y-3">
            {/* Ya vinculadas */}
            {comprasVinculadas.length > 0 && (
              <div>
                <p className="text-emerald-400/80 text-[10px] font-bold uppercase tracking-wider mb-1.5">Ya vinculadas ({comprasVinculadas.length})</p>
                <div className="space-y-1 mb-3">
                  {comprasVinculadas.map(c => {
                    const concepto = (c.items || []).length > 0
                      ? c.items[0].descripcion + (c.items.length > 1 ? ` +${c.items.length - 1}` : "")
                      : (c.notas || "—");
                    const montoC = c.moneda === "USD"
                      ? `US$ ${Number(c.monto_total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                      : `₲ ${Math.round(c.monto_total || 0).toLocaleString("es-PY")}`;
                    return (
                      <div key={c.id} className="px-3 py-2.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-white truncate">{c.proveedor_nombre || "—"}</span>
                          <span className="text-xs text-slate-400 ml-2 flex-shrink-0">{montoC}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{concepto} · {c.fecha}</p>
                        <p className="text-xs text-emerald-400 mt-0.5">✓ Ya vinculada a este presupuesto</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {cobradoConCompra ? (
              <p className="text-slate-400 text-sm text-center py-4 bg-white/5 rounded-lg">
                El presupuesto ya está cobrado y tiene una compra vinculada.
              </p>
            ) : (
              <>
                {comprasVinculadas.length > 0 && (
                  <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1.5">Agregar otra</p>
                )}
                <input
                  type="text"
                  value={compraSearch}
                  onChange={e => setCompraSearch(e.target.value)}
                  placeholder="Buscar por proveedor, concepto..."
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-400"
                  autoFocus={comprasVinculadas.length === 0}
                />
                <div className="max-h-52 overflow-y-auto space-y-1 pr-1">
                  {(() => {
                    const q = compraSearch.trim().toLowerCase();
                    const filtered = comprasDisponibles.filter(c => {
                      if (c.presupuesto_id === presupuesto.id) return false; // ya vinculada
                      if (!q) return true;
                      const concepto = (c.items || []).map(i => i.descripcion).join(" ");
                      return (c.proveedor_nombre || "").toLowerCase().includes(q)
                        || concepto.toLowerCase().includes(q)
                        || (c.notas || "").toLowerCase().includes(q);
                    });
                    return filtered.length > 0 ? filtered.map(c => {
                      const isSelected = compraSeleccionada?.id === c.id;
                      const concepto = (c.items || []).length > 0
                        ? c.items[0].descripcion + (c.items.length > 1 ? ` +${c.items.length - 1}` : "")
                        : (c.notas || "—");
                      const montoC = c.moneda === "USD"
                        ? `US$ ${Number(c.monto_total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                        : `₲ ${Math.round(c.monto_total || 0).toLocaleString("es-PY")}`;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setCompraSeleccionada(prev => prev?.id === c.id ? null : c)}
                          className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
                            isSelected
                              ? "border-orange-400 bg-orange-500/15 text-white"
                              : "border-white/10 text-slate-300 hover:border-white/25 hover:bg-white/5"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium truncate">{c.proveedor_nombre || "—"}</span>
                            <span className="text-xs text-slate-400 ml-2 flex-shrink-0">{montoC}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{concepto} · {c.fecha}</p>
                          {c.presupuesto_id && c.presupuesto_id !== presupuesto.id && (
                            <p className="text-xs text-amber-400 mt-0.5">⚠ Ya vinculada a otro presupuesto</p>
                          )}
                        </button>
                      );
                    }) : (
                      <p className="text-slate-500 text-sm text-center py-6">
                        {compraSearch ? "Sin resultados" : "No hay compras disponibles"}
                      </p>
                    );
                  })()}
                </div>
                {compraSeleccionada && (
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2 text-xs text-orange-300">
                    Seleccionada: <strong>{compraSeleccionada.proveedor_nombre}</strong> — {compraSeleccionada.fecha}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-5">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-white/10 rounded-lg text-slate-300 hover:text-white hover:border-white/30 transition-all text-sm font-body"
          >
            Cancelar
          </button>
          {!(facturaMode === "compra" && cobradoConCompra) && (
            <button
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-all text-sm font-body flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={facturaMode === "compra" ? onVincularCompra : onVincular}
              disabled={savingFactura || (facturaMode === "existente" && !facturaSeleccionada) || (facturaMode === "compra" && !compraSeleccionada)}
            >
              <Receipt className="w-4 h-4" />
              {savingFactura ? "Guardando..." : facturaMode === "compra" ? "Vincular compra" : "Vincular factura"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FacturaDocModal — vista visual de factura (estilo documento)
// ═══════════════════════════════════════════════════════════════
export function FacturaDocModal({ factura: f, activeEmpresaPropia = null, presupuestos = [], onClose, onEdit, onDelete, onPagar, onDeshacer, canEdit, canDelete, onReciboClick, onPresClick, onEditPago, onDeletePago, cuentasDisp = [], token, API: apiUrl, onCuotaCobrada, tiposFac = [], canAsignarTipo = false, canEditarTipo = false, canEditarVendedor = false, onTipoChanged, autoPrint = "no", onAutoPrinted }) {
  const { hasPermission } = React.useContext(AuthContext);

  React.useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  // ── Modelos de impresión habilitados para esta empresa ──────────────────────
  // Por defecto (empresa sin configurar todavía, o fetch fallido) se muestran
  // los 4 — así ninguna empresa existente pierde botones que ya tenía.
  const TODOS_LOS_MODELOS = ["a4", "a4_doble", "ticket", "compacta"];
  const [modelosHabilitados, setModelosHabilitados] = React.useState(TODOS_LOS_MODELOS);
  // Máximo de ítems configurado — usado para las filas de relleno de la Compacta
  // (rellena la tabla hasta este tope para que el total quede siempre a la misma
  // altura, sea cual sea la cantidad real de ítems de la factura).
  const [maxItemsCompacta, setMaxItemsCompacta] = React.useState(12);
  // Observación impresa (contenido de la Nota Interna) — default true mientras
  // no llega la config (así no se pierde para empresas que no la tocaron).
  const [mostrarObservacion, setMostrarObservacion] = React.useState(true);
  React.useEffect(() => {
    const logo = f?.logo_tipo;
    if (!logo || !token) return;
    let cancelled = false;
    fetch(`${apiUrl}/admin/configuracion-impresion-facturas?logo_tipo=${logo}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        const lista = Array.isArray(data.modelos_habilitados) && data.modelos_habilitados.length > 0
          ? data.modelos_habilitados.filter(m => TODOS_LOS_MODELOS.includes(m))
          : TODOS_LOS_MODELOS;
        setModelosHabilitados(lista.length > 0 ? lista : TODOS_LOS_MODELOS);
        if (Number.isFinite(data.max_items) && data.max_items > 0) setMaxItemsCompacta(data.max_items);
        setMostrarObservacion(data.mostrar_observacion !== false);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [apiUrl, token, f?.logo_tipo]);

  // ── Notas log state ───────────────────────────────────────────────────────────
  const [notasLog, setNotasLog]     = React.useState(f?.notas_log || []);
  const [notaCreacion, setNotaCreacion] = React.useState(f?.notas || "");
  const [agregandoNota, setAgregandoNota] = React.useState(false);
  const [nuevoTextoNota, setNuevoTextoNota] = React.useState("");
  const [savingNota, setSavingNota] = React.useState(false);
  const [deletingNotaId, setDeletingNotaId] = React.useState(null);
  const [deletingCreacion, setDeletingCreacion] = React.useState(false);

  const puedeAgregarNota = hasPermission("facturas.agregar_nota");
  const puedeEditarNota  = hasPermission("facturas.editar_nota");

  // ── Tipo de factura ───────────────────────────────────────────────────────────
  const [tipoFacId, setTipoFacId]     = React.useState(f?.tipo_factura_id || null);
  const [tipoFacNombre, setTipoFacNombre] = React.useState(f?.tipo_factura_nombre || null);
  const [tipoFacColor, setTipoFacColor]   = React.useState(f?.tipo_factura_color || null);
  const [showTipoDropdown, setShowTipoDropdown] = React.useState(false);
  const [savingTipoFac, setSavingTipoFac] = React.useState(false);

  const canActualizarTipo = !f?.eliminada && (
    tipoFacId ? canEditarTipo : canAsignarTipo
  );

  // ── Vendedor (agregar/corregir mientras la factura siga pendiente) ──────────────
  const [vendNombre, setVendNombre] = React.useState(f?.vendedor_nombre || null);
  const [showVendDropdown, setShowVendDropdown] = React.useState(false);
  const [vendOpciones, setVendOpciones] = React.useState([]);
  const [savingVend, setSavingVend] = React.useState(false);
  const vendEditable = canEditarVendedor && !f?.eliminada && f?.tipo !== "recibida"
    && ["pendiente", "parcial"].includes(f?.estado) && !f?.comision_liquidada;
  const abrirVendedores = async () => {
    if (!vendEditable) return;
    setShowVendDropdown(v => !v);
    if (vendOpciones.length === 0) {
      try {
        const r = await fetch(`${apiUrl}/admin/vendedores/disponibles?logo_tipo=${encodeURIComponent(f?.logo_tipo || "")}`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setVendOpciones(await r.json());
      } catch (_) {}
    }
  };
  const handleSetVendedor = async (v) => {
    setSavingVend(true);
    setShowVendDropdown(false);
    try {
      const r = await fetch(`${apiUrl}/admin/facturas/${f.id}/vendedor`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ vendedor_id: v.id }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || "Error"); }
      setVendNombre(v.nombre);
      if (onTipoChanged) onTipoChanged();
      toast.success(`Vendedor: ${v.nombre}`);
    } catch (e) { toast.error(e.message); }
    finally { setSavingVend(false); }
  };

  const handleSetTipo = async (tipo) => {
    setSavingTipoFac(true);
    setShowTipoDropdown(false);
    try {
      const r = await fetch(`${apiUrl}/admin/facturas/${f.id}/tipo-factura`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ tipo_factura_id: tipo?.id || null }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      setTipoFacId(tipo?.id || null);
      setTipoFacNombre(tipo?.nombre || null);
      setTipoFacColor(tipo?.color || null);
      if (onTipoChanged) onTipoChanged();
      toast.success(tipo ? `Tipo: ${tipo.nombre}` : "Tipo removido");
    } catch(e) { toast.error(e.message); }
    finally { setSavingTipoFac(false); }
  };

  const agregarNota = async () => {
    if (!nuevoTextoNota.trim()) return;
    setSavingNota(true);
    try {
      const res = await fetch(`${apiUrl}/admin/facturas/${f.id}/notas`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ texto: nuevoTextoNota.trim() }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Error"); }
      const nueva = await res.json();
      setNotasLog(prev => [...prev, nueva]);
      setNuevoTextoNota("");
      setAgregandoNota(false);
      toast.success("Nota agregada");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingNota(false);
    }
  };

  const eliminarNota = async (notaId) => {
    setDeletingNotaId(notaId);
    try {
      const res = await fetch(`${apiUrl}/admin/facturas/${f.id}/notas/${notaId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Error"); }
      setNotasLog(prev => prev.filter(n => n.id !== notaId));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeletingNotaId(null);
    }
  };

  const eliminarNotaCreacion = async () => {
    setDeletingCreacion(true);
    try {
      const res = await fetch(`${apiUrl}/admin/facturas/${f.id}/nota-creacion`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Error"); }
      setNotaCreacion("");
      toast.success("Nota eliminada");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeletingCreacion(false);
    }
  };

  // ── Cuotas state ─────────────────────────────────────────────────────────────
  const [cuotas, setCuotas] = React.useState([]);
  const [loadingCuotas, setLoadingCuotas] = React.useState(false);
  const [selectedCuotaIds, setSelectedCuotaIds] = React.useState(new Set());
  const [cobrarCuotasModal, setCobrarCuotasModal] = React.useState(false);
  const [cobrarFecha, setCobrarFecha] = React.useState(new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));
  const [cobrarCuentaId, setCobrarCuentaId] = React.useState(cuentasDisp[0]?.id || "");
  const [cobrandoCuotas, setCobrandoCuotas] = React.useState(false);

  React.useEffect(() => {
    if (!f?.id || !apiUrl || !token) return;
    // Cargar cuotas si es crédito (con o sin es_credito_cuotas — soporta facturas antiguas)
    if (f?.forma_pago !== "credito" && !f?.es_credito_cuotas) return;
    setLoadingCuotas(true);
    fetch(`${apiUrl}/admin/cuotas?doc_id=${f.id}&limit=60`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setCuotas(Array.isArray(data) ? data : (data.items || [])))
      .catch(() => setCuotas([]))
      .finally(() => setLoadingCuotas(false));
  }, [f?.id, f?.forma_pago, f?.es_credito_cuotas, apiUrl, token]);

  const toggleCuota = (id) => setSelectedCuotaIds(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const cobrarCuotas = async () => {
    if (selectedCuotaIds.size === 0) return;
    const cuenta = cuentasDisp.find(c => String(c.id) === String(cobrarCuentaId));
    setCobrandoCuotas(true);
    try {
      const res = await fetch(`${apiUrl}/admin/cuotas/cobrar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          cuota_ids: Array.from(selectedCuotaIds),
          fecha_pago: cobrarFecha,
          cuenta_id: cobrarCuentaId || null,
          cuenta_nombre: cuenta?.nombre || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Error al cobrar");
      }
      const updated = await res.json();
      // Refresh cuotas list
      const r2 = await fetch(`${apiUrl}/admin/cuotas?doc_id=${f.id}&limit=60`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r2.ok) {
        const d = await r2.json();
        setCuotas(Array.isArray(d) ? d : (d.items || []));
      }
      setSelectedCuotaIds(new Set());
      setCobrarCuotasModal(false);
      if (onCuotaCobrada) onCuotaCobrada();
    } catch (e) {
      alert(e.message || "Error al cobrar cuotas");
    } finally {
      setCobrandoCuotas(false);
    }
  };

  const imprimirPagares = () => {
    if (!cuotas.length) return;
    const w = window.open("", "_blank");
    if (!w) return;

    const moneda = f.moneda || "PYG";
    const monedaLabel = moneda === "PYG" ? "Guaraníes" : moneda === "USD" ? "Dólares Americanos" : moneda === "BRL" ? "Reales Brasileños" : moneda;
    const monedaSimbolo = moneda === "PYG" ? "Gs." : moneda;
    const fmtM = (n) => moneda === "PYG"
      ? Math.round(n).toLocaleString("es-PY")
      : Number(n).toLocaleString("es-PY", { minimumFractionDigits: 2 });

    const MESES = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
    const fmtFechaES = (s) => {
      if (!s) return "__ de __________ de 20__";
      const [y, m, d] = s.split("-");
      return `${parseInt(d)} de ${MESES[parseInt(m) - 1]} de ${y}`;
    };

    const numToWords = (num) => {
      num = Math.round(num);
      if (num === 0) return "CERO";
      if (num < 0) return "MENOS " + numToWords(-num);
      const U = ["","UN","DOS","TRES","CUATRO","CINCO","SEIS","SIETE","OCHO","NUEVE",
                 "DIEZ","ONCE","DOCE","TRECE","CATORCE","QUINCE","DIECISÉIS","DIECISIETE","DIECIOCHO","DIECINUEVE","VEINTE"];
      const D = ["","","VEINTE","TREINTA","CUARENTA","CINCUENTA","SESENTA","SETENTA","OCHENTA","NOVENTA"];
      const C = ["","CIENTO","DOSCIENTOS","TRESCIENTOS","CUATROCIENTOS","QUINIENTOS","SEISCIENTOS","SETECIENTOS","OCHOCIENTOS","NOVECIENTOS"];
      const menorMil = (n) => {
        if (n === 0) return "";
        if (n === 100) return "CIEN";
        let r = "";
        const c = Math.floor(n / 100), re = n % 100;
        if (c) r += C[c] + (re ? " " : "");
        if (re <= 20) r += U[re];
        else { const dg = Math.floor(re / 10), u = re % 10; r += D[dg] + (u ? " Y " + U[u] : ""); }
        return r;
      };
      const millones = Math.floor(num / 1_000_000);
      const miles    = Math.floor((num % 1_000_000) / 1000);
      const unids    = num % 1000;
      const parts = [];
      if (millones === 1) parts.push("UN MILLÓN");
      else if (millones > 1) parts.push(menorMil(millones) + " MILLONES");
      if (miles === 1) parts.push("MIL");
      else if (miles > 1) parts.push(menorMil(miles) + " MIL");
      if (unids > 0 || parts.length === 0) parts.push(menorMil(unids) || "CERO");
      return parts.join(" ");
    };

    // Imprimir todas las cuotas (históricas también); si todas están pagadas se imprime igual como registro
    const pendientes = cuotas.length ? cuotas : [];
    if (!pendientes.length) { w.close(); return; }

    const totalCuotas = cuotas.length;
    const acreedorNombre = f.emisor_razon_social || activeEmpresaPropia?.nombre || "_______________";
    const acreedorRuc    = activeEmpresaPropia?.ruc || "";
    const ciudad         = activeEmpresaPropia?.ciudad || "Asunción";
    const deudorNombre   = f.razon_social || "_______________";
    const deudorRuc      = f.ruc || "";

    const htmlPagares = pendientes.map((c, idx) => {
      const montoStr    = fmtM(c.monto);
      const montoLetras = numToWords(c.monto);
      const fechaVence  = fmtFechaES(c.fecha_vencimiento);
      const isLast      = idx === pendientes.length - 1;
      return `
<div class="pagare${isLast ? " last" : ""}">
  <div class="encabezado">
    <div class="empresa-nombre">${acreedorNombre}${acreedorRuc ? ` &nbsp;&middot;&nbsp; R.U.C.: ${acreedorRuc}` : ""}</div>
    <div class="titulo-pagare">PAGARÉ COMERCIAL</div>
    <div class="num-op">N.º Operación: <strong>${c.numero} / ${totalCuotas}</strong></div>
  </div>

  <div class="monto-box">
    <span class="monto-moneda">${monedaSimbolo}</span>
    <span class="monto-num">${montoStr}</span>
  </div>

  <div class="vence-line">Vence el: <strong>${fechaVence}</strong></div>

  <div class="cuerpo">
    <p>
      Yo/Nosotros, <strong>${deudorNombre}</strong>${deudorRuc ? `, con R.U.C. N.º <strong>${deudorRuc}</strong>,` : ","} por medio del presente instrumento nos comprometemos a pagar
      incondicionalmente, a la orden de <strong>${acreedorNombre}</strong>${acreedorRuc ? ` (R.U.C. ${acreedorRuc})` : ""},
      la suma de <strong>${monedaSimbolo} ${montoStr}</strong> (<strong>${montoLetras} ${monedaLabel.toUpperCase()}</strong>),
      en la ciudad de ${ciudad}, a la fecha de vencimiento indicada.
    </p>
    <p>
      En caso de mora, el deudor incurrirá automáticamente en ella sin necesidad de interpelación judicial ni extrajudicial
      (Art. 424 del Código Civil del Paraguay), devengando la suma adeudada un interés moratorio del <strong>______%</strong> anual
      y un interés punitorio del <strong>______%</strong> anual, conforme a los límites establecidos por el Banco Central del Paraguay.
      Los intereses moratorios y punitorios comenzarán a correr a partir del día siguiente al vencimiento hasta el día del efectivo pago.
    </p>
    <p>
      El incumplimiento del pago a su vencimiento habilitará al acreedor a promover el Juicio Ejecutivo por el total del crédito,
      siendo de cargo del deudor todos los gastos judiciales y honorarios profesionales que se generen. Asimismo, el deudor
      autoriza expresamente la inclusión de sus datos en los sistemas de información crediticia que correspondan,
      incluyendo EQUIFAX / INFORMCONF u organismos similares, en caso de incumplimiento.
    </p>
    <p>
      Para todos los efectos legales derivados del presente, las partes se someten expresamente a la jurisdicción y competencia
      de los Juzgados y Tribunales de la ciudad de ${ciudad}, renunciando a cualquier otro fuero o domicilio que pudiere corresponder.
    </p>
  </div>

  <div class="firmado-en">
    Firmado en la ciudad de ${ciudad}, el día ______ del mes de __________________________ del año 20______
  </div>

  <div class="firmas">
    <div class="firma-bloque">
      <div class="firma-linea"></div>
      <div class="firma-titulo">FIRMA DEL DEUDOR</div>
      <table class="firma-datos">
        <tr><td class="fd-lbl">Nombre / Razón Social:</td><td class="fd-val">${deudorNombre}</td></tr>
        <tr><td class="fd-lbl">C.I. / R.U.C.:</td><td class="fd-val">${deudorRuc || "____________________"}</td></tr>
        <tr><td class="fd-lbl">Dirección Comercial:</td><td class="fd-val">____________________</td></tr>
        <tr><td class="fd-lbl">Teléfono Comercial:</td><td class="fd-val">____________________</td></tr>
      </table>
    </div>
    <div class="firma-bloque">
      <div class="firma-linea"></div>
      <div class="firma-titulo">FIRMA DEL CODEUDOR / AVAL</div>
      <table class="firma-datos">
        <tr><td class="fd-lbl">Nombre / Razón Social:</td><td class="fd-val">____________________</td></tr>
        <tr><td class="fd-lbl">C.I. / R.U.C.:</td><td class="fd-val">____________________</td></tr>
        <tr><td class="fd-lbl">Dirección Comercial:</td><td class="fd-val">____________________</td></tr>
        <tr><td class="fd-lbl">Teléfono Comercial:</td><td class="fd-val">____________________</td></tr>
      </table>
    </div>
  </div>
</div>`;
    }).join("");

    w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Pagarés — ${deudorNombre}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Times New Roman', Times, serif; background: #e8e8e8; font-size: 12pt; color: #111; }
    .toolbar { background: #1e3a8a; color: #fff; padding: 10px 20px; display: flex; gap: 12px; align-items: center; position: sticky; top: 0; z-index: 100; font-family: Arial, sans-serif; }
    .toolbar button { background: #fff; color: #1e3a8a; border: none; padding: 6px 18px; border-radius: 5px; cursor: pointer; font-weight: bold; font-size: 13px; }
    .toolbar .info { font-size: 12px; opacity: 0.8; }
    .pagare { width: 210mm; min-height: 297mm; padding: 14mm 18mm 12mm; background: #fff; margin: 16px auto; page-break-after: always; display: flex; flex-direction: column; gap: 10px; border: 1px solid #bbb; }
    .pagare.last { page-break-after: avoid; }
    @media print {
      body { background: #fff; }
      .toolbar { display: none; }
      .pagare { margin: 0; border: none; min-height: unset; box-shadow: none; }
    }
    .encabezado { border-top: 3px double #111; border-bottom: 3px double #111; padding: 7px 0; text-align: center; }
    .empresa-nombre { font-size: 9pt; color: #444; margin-bottom: 3px; }
    .titulo-pagare { font-size: 20pt; font-weight: bold; letter-spacing: 3px; text-transform: uppercase; }
    .num-op { font-size: 9.5pt; color: #333; margin-top: 3px; }
    .monto-box { display: flex; align-items: baseline; justify-content: center; gap: 10px; border: 2px solid #111; padding: 10px 28px; margin: 2px auto; width: fit-content; min-width: 280px; }
    .monto-moneda { font-size: 13pt; font-weight: bold; }
    .monto-num { font-size: 24pt; font-weight: bold; letter-spacing: 1px; }
    .vence-line { text-align: center; font-size: 11pt; border-bottom: 1px solid #aaa; padding-bottom: 8px; }
    .cuerpo { flex: 1; }
    .cuerpo p { font-size: 10.5pt; line-height: 1.65; text-align: justify; margin-bottom: 10px; }
    .firmado-en { font-size: 10pt; font-style: italic; text-align: center; margin-top: 4px; }
    .firmas { display: flex; gap: 28px; margin-top: 10px; }
    .firma-bloque { flex: 1; }
    .firma-linea { border-top: 1.5px solid #111; margin-bottom: 4px; }
    .firma-titulo { font-size: 8.5pt; font-weight: bold; text-align: center; letter-spacing: 0.5px; margin-bottom: 7px; }
    .firma-datos { width: 100%; border-collapse: collapse; font-size: 9pt; }
    .firma-datos td { padding: 3px 0; vertical-align: bottom; }
    .fd-lbl { color: #555; white-space: nowrap; padding-right: 6px; font-style: italic; }
    .fd-val { border-bottom: 0.5px solid #aaa; width: 100%; }
  </style>
</head>
<body>
  <div class="toolbar">
    <button onclick="window.print()">🖨 Imprimir / Guardar PDF</button>
    <span class="info">${pendientes.length} pagaré${pendientes.length !== 1 ? "s" : ""} &nbsp;&middot;&nbsp; ${deudorNombre}</span>
  </div>
  ${htmlPagares}
</body>
</html>`);
    w.document.close();
  };

  const imprimirTicket = () => {
    const w = window.open("", "_blank");
    if (!w) return;

    // ── helpers ──────────────────────────────────────────────────────────────
    const fmtT = (n) => {
      if (n == null || isNaN(n)) return "-";
      if (f.moneda === "PYG") return `₲ ${Math.round(n).toLocaleString("es-PY")}`;
      return `${f.moneda} ${Number(n).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };
    const sep  = (ch = "=", n = 40) => `<div class="sep">${ch.repeat(n)}</div>`;
    const now  = new Date();
    const hora = now.toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" });

    // ── datos del documento ───────────────────────────────────────────────
    const esBoleta       = !!f.sin_factura;
    const docLabel       = esBoleta ? "BOLETA / COMPROBANTE" : "TICKET FACTURA";
    const eRubrosDinet   = emisorRubrosDinet || f.emisor_rubros_dinet || "";
    const docNumero = f.numero || f.numero_boleta || "—";
    const fecha     = f.fecha || now.toLocaleDateString("es-PY");
    const cajero    = f.usuario_nombre || f.cajero || "Operador";

    // ── emisor ───────────────────────────────────────────────────────────
    const eName  = emisorNombre || "—";
    const eRuc   = emisorRuc   || "";
    const eDir   = emisorDireccion || "";
    const eTel   = emisorTelefono  || "";
    const eEmail = emisorEmail     || "";
    const eWeb   = f.emisor_web    || "";

    // ── cliente ──────────────────────────────────────────────────────────
    const cliNombre = f.empresa_nombre || f.razon_social || "Consumidor Final";
    const cliRuc    = f.ruc || "—";
    const cliTel    = f.cliente_telefono || "";
    const cliEmail  = f.cliente_email || "";
    const cliDir    = f.direccion || "";

    // ── items ─────────────────────────────────────────────────────────────
    const conceptos  = f.conceptos || [];
    const total      = f.monto || 0;
    const descuento  = f.total_descuentos || f.descuento || 0;
    const subtotal   = total + descuento;
    const pagosTicket = (f.pagos || []);
    const ultimoPago  = pagosTicket.at(-1);
    const efectivo    = f.efectivo_recibido || (ultimoPago?.monto_recibido ?? 0);
    const cambio      = efectivo > total ? efectivo - total : (ultimoPago?.vuelto ?? 0);
    // Para ticket: historial de pagos con vuelto (cuando hay múltiples)
    const pagosConVuelto = pagosTicket.filter(p => p.monto_recibido || p.vuelto);
    const formaPago  = f.forma_pago === "credito" ? "Crédito" : f.metodo_pago || "Efectivo";

    const itemsHtml = conceptos.length > 0
      ? conceptos.map(c => {
          const cant  = c.cantidad || 1;
          const codigoProd = c.producto_sku || c.codigo_barras || "";
          const desc  = c.descripcion || "";
          const punit = c.precio_unitario || 0;
          const subt  = c.subtotal ?? c.monto ?? (punit * cant);
          const descuentoPct = Number(c.descuento_pct || 0);
          const descuentoMonto = Number(c.descuento_monto || 0);
          const trazLines = [
            c.codigo_fabricante ? `Fab: ${c.codigo_fabricante}` : "",
            c.lote              ? `Lote: ${c.lote}` : "",
            c.fecha_vencimiento ? `Vence: ${c.fecha_vencimiento}` : "",
          ].filter(Boolean).join(" · ");
          return `
          <div class="item">
            <div class="item-top">
              <span class="item-cant">${cant}</span>
              <span class="item-desc">${desc}</span>
              <span class="item-total">${fmtT(subt)}</span>
            </div>
            ${codigoProd ? `<div class="item-sub" style="color:#64748b;font-size:8px">SKU: ${codigoProd}</div>` : ""}
            ${c.variante_texto ? `<div class="item-sub" style="color:#7c3aed;font-size:8px">Variante: ${c.variante_texto}</div>` : ""}
            ${punit > 0 && cant > 1 ? `<div class="item-sub">${fmtT(punit)} c/u</div>` : ""}
            ${descuentoPct > 0 ? `<div class="item-sub" style="color:#059669">Desc. ${descuentoPct.toLocaleString("es-PY")}%${descuentoMonto > 0 ? ` · -${fmtT(descuentoMonto)}` : ""}</div>` : ""}
            ${trazLines ? `<div class="item-sub" style="color:#64748b;font-size:8px">${trazLines}</div>` : ""}
          </div>`;
        }).join("")
      : `<div class="item"><div class="item-top"><span class="item-cant">1</span><span class="item-desc">${f.concepto || "—"}</span><span class="item-total">${fmtT(total)}</span></div></div>`;

    // ── timbrado (solo facturas) ──────────────────────────────────────────
    const timbradoHtml = !esBoleta && f.nro_timbrado ? `
      <div class="block" style="border:1px dashed #999;padding:4px 6px;margin:4px 0;font-size:9.5px;text-align:center">
        TIMBRADO N°: <b>${f.nro_timbrado}</b>
        ${f.fecha_vigencia_timbrado ? `&nbsp;· Vigencia hasta: ${f.fecha_vigencia_timbrado}` : ""}
        ${f.tipo_emision === "autoimpresor" && f.habilitacion_autoimpresor ? `<br>HABILITACIÓN DE AUTOIMPRESOR N°: <b>${f.habilitacion_autoimpresor}</b>` : ""}
      </div>` : "";

    // ── cuotas resumidas ─────────────────────────────────────────────────
    const _numCuotas = f.num_cuotas || cuotas.length;
    const cuotasHtml = (f.es_credito_cuotas || cuotas.length > 0) && _numCuotas ? `
      <div class="block" style="font-size:10px;margin-top:2px;">
        Venta en ${_numCuotas} cuota(s)${f.tasa_interes_cuotas > 0 ? ` · ${f.tasa_interes_cuotas}% interés` : ""}
      </div>` : "";

    // Observación (Nota Interna) — antes nunca se imprimía, solo se veía en la
    // Vista previa del sistema. Ahora se imprime si hay contenido y la empresa
    // no lo desactivó en Configuración > Impresión (default: activado).
    const notasHtml = (mostrarObservacion && f.notas) ? `
      <div class="block" style="font-size:10px;margin-top:2px;border-top:1px dashed #999;padding-top:3px">
        <b>OBSERVACIÓN:</b> ${f.notas}
      </div>` : "";

    w.document.write(`<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>Ticket · ${docNumero}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{
      font-family:'Courier New',Courier,monospace;
      font-size:11px;
      color:#000;
      background:#fff;
      width:300px;
      margin:0 auto;
      padding:10px 6px 20px;
    }
    @media print{
      @page{size:80mm auto;margin:0}
      body{width:80mm;padding:4px 4px 16px;}
      .no-print{display:none!important}
    }
    .center{text-align:center}
    .right{text-align:right}
    .bold{font-weight:bold}
    .sep{text-align:center;letter-spacing:-0.5px;color:#444;margin:4px 0;font-size:11px;overflow:hidden;white-space:nowrap}
    .block{margin:2px 0}

    /* logo / empresa */
    .logo-name{font-size:22px;font-weight:900;text-align:center;letter-spacing:1px;line-height:1.1;margin-bottom:2px}
    .tagline{font-size:9.5px;text-align:center;letter-spacing:3px;color:#444;margin-bottom:4px;text-transform:uppercase}
    .empresa-info{font-size:9.5px;text-align:center;line-height:1.6;color:#222}

    /* doc */
    .doc-label{font-size:11px;font-weight:bold;text-align:center}
    .doc-num{font-size:13px;font-weight:900;text-align:center}
    .doc-meta{font-size:10px;line-height:1.7;margin-top:2px}

    /* cliente */
    .cli-row{display:flex;gap:4px;font-size:9.5px;line-height:1.7}
    .cli-label{width:62px;flex-shrink:0;color:#555}
    .cli-val{font-weight:600;flex:1}

    /* items */
    .items-head{display:flex;font-size:9.5px;font-weight:bold;border-bottom:1px solid #000;padding-bottom:2px;margin-bottom:2px}
    .item{margin:2px 0}
    .item-top{display:flex;font-size:10px}
    .item-cant{width:24px;flex-shrink:0;text-align:center}
    .item-desc{flex:1;padding:0 4px;word-break:break-word}
    .item-total{width:68px;flex-shrink:0;text-align:right;font-weight:600}
    .item-sub{font-size:9.5px;color:#555;padding-left:24px;margin-top:-1px}

    /* totales */
    .tot-row{display:flex;justify-content:space-between;font-size:10.5px;margin:1px 0}
    .tot-row.big{font-size:14px;font-weight:900;margin-top:4px}
    .tot-row.muted{color:#666;font-size:9.5px}

    /* footer */
    .footer{font-size:9.5px;text-align:center;line-height:1.7;margin-top:8px;color:#333}

    /* btn */
    .btn-print{display:block;width:100%;margin-top:16px;padding:10px;background:#1d4ed8;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-family:Arial,sans-serif;font-weight:600}
        *{color:#000!important;font-weight:700!important;-webkit-print-color-adjust:exact;print-color-adjust:exact}.btn-print{color:#fff!important}.item-sub,.cli-label,.tot-row.muted,.footer,.sep,.tagline{color:#000!important}
  </style>
</head>
<body>

  <!-- LOGO / EMPRESA -->
  <div class="logo-name">${eName}</div>
  ${f.emisor_eslogan ? `<div class="tagline">${f.emisor_eslogan}</div>` : ""}
  <div class="empresa-info">
    ${eRuc        ? `RUC / NIT: ${eRuc}<br>`          : ""}
    ${eDir        ? `Dirección: ${eDir}<br>`          : ""}
    ${eTel        ? `Teléfono: ${eTel}<br>`           : ""}
    ${eEmail      ? `${eEmail}<br>`                   : ""}
    ${eWeb        ? `${eWeb}<br>`                     : ""}
    ${eRubrosDinet ? `Rubro: ${eRubrosDinet}<br>`     : ""}
  </div>

  ${sep("=")}

  <!-- TIMBRADO (si aplica) -->
  ${timbradoHtml}

  <!-- N° DOCUMENTO -->
  <div class="doc-label">${docLabel}</div>
  <div class="doc-num">${docNumero}</div>
  <div class="doc-meta center">
    Fecha: ${fecha}&nbsp;&nbsp;Hora: ${hora}<br>
    Cajero: ${cajero}
  </div>

  ${sep("-")}

  <!-- CLIENTE -->
  <div class="cli-row"><span class="cli-label">Cliente:</span><span class="cli-val">${cliNombre}</span></div>
  <div class="cli-row"><span class="cli-label">RUC/CI:</span><span class="cli-val">${cliRuc}</span></div>
  <div class="cli-row"><span class="cli-label">Dirección:</span><span class="cli-val">${cliDir || "—"}</span></div>
  <div class="cli-row"><span class="cli-label">Tel:</span><span class="cli-val">${cliTel || "—"}</span></div>
  <div class="cli-row"><span class="cli-label">E-mail:</span><span class="cli-val">${cliEmail || "—"}</span></div>

  ${sep("-")}

  <!-- ITEMS HEADER -->
  <div class="items-head">
    <span style="width:24px;text-align:center">Ct</span>
    <span style="flex:1;padding-left:4px">Descripción</span>
    <span style="width:68px;text-align:right">Total</span>
  </div>

  <!-- ITEMS -->
  ${itemsHtml}

  ${sep("-")}

  <!-- TOTALES -->
  ${descuento > 0 ? `<div class="tot-row muted"><span>SUBTOTAL</span><span>${fmtT(subtotal)}</span></div>` : ""}
  ${descuento > 0 ? `<div class="tot-row muted"><span>DESCUENTO</span><span>- ${fmtT(descuento)}</span></div>` : ""}

  ${sep("=")}
  <div class="tot-row big"><span>TOTAL A PAGAR</span><span>${fmtT(total)}</span></div>
  ${sep("=")}

  ${f.moneda !== "PYG" && Number(f.tipo_cambio) > 0 ? `
  <div class="tot-row" style="font-size:9px"><span>Tipo de cambio</span><span>₲ ${Number(f.tipo_cambio).toLocaleString("es-PY")}</span></div>
  <div class="tot-row big"><span>TOTAL EN GS.</span><span>₲ ${Math.round(total * f.tipo_cambio).toLocaleString("es-PY")}</span></div>
  ${sep("=")}
  ` : ""}

  <!-- PAGO -->
  <div class="tot-row" style="margin-top:4px"><span>Forma de Pago: ${formaPago}</span></div>
  ${pagosConVuelto.length > 1 ? `
  <div style="border-top:1px dashed #ccc;margin-top:4px;padding-top:4px">
    <div class="tot-row" style="font-weight:700;font-size:9px;text-transform:uppercase;color:#666"><span>Pagos</span><span>Recibido / Vuelto</span></div>
    ${pagosConVuelto.map((p, i) => {
      const rec = p.monto_recibido || 0;
      const vue = p.vuelto || (rec > p.monto ? rec - p.monto : 0);
      return `<div class="tot-row" style="font-size:9px"><span>Pago ${i+1}${p.recibo_numero ? ` ${p.recibo_numero}` : ""}</span><span>${rec > 0 ? fmtT(rec) : fmtT(p.monto)}${vue > 0 ? ` / ${fmtT(vue)}` : ""}</span></div>`;
    }).join("")}
  </div>` : `
  ${efectivo > 0 ? `<div class="tot-row"><span>Efectivo Recibido</span><span>${fmtT(efectivo)}</span></div>` : ""}
  ${cambio  > 0 ? `<div class="tot-row"><span>Cambio / Vuelto</span><span>${fmtT(cambio)}</span></div>` : ""}
  `}

  ${cuotasHtml}
  ${notasHtml}

  ${sep("=")}

  <!-- FOOTER -->
  <div class="footer">
    ${f.emisor_mensaje_ticket
      ? f.emisor_mensaje_ticket
      : "¡Gracias por su compra!"
    }
    ${eWeb ? `<br>${eWeb}` : ""}
    <br><span style="font-size:7.5px;color:#aaa">Impreso el ${now.toLocaleString("es-PY")}</span>
  </div>

  <button class="btn-print no-print" onclick="window.print()">🖨️ Imprimir Ticket</button>

</body>
</html>`);
    w.document.close();
    setTimeout(() => w.print(), 600);
  };

  const accentColor = f.logo_tipo === "jar" ? "#dc2626" : "#2563eb";
  const empresaEmisora = activeEmpresaPropia || {};
  const emisorNombre = f.emisor_razon_social || (f.logo_tipo === "jar" ? "JAR Informática" : f.logo_tipo === "arandu" ? "Arandu Informática" : "AranduJAR Informática");
  const emisorNombreFantasia = f.emisor_nombre_fantasia || empresaEmisora?.nombre_fantasia || "";
  const emisorFantasiaHtml = (colorNombre, colorSub) => emisorNombreFantasia
    ? `<div style="font-weight:800;font-size:13px;color:${colorNombre};line-height:1.25">${emisorNombreFantasia}</div><div style="font-size:9px;color:${colorSub};margin-top:1px">${emisorNombre}${emisorRuc ? ` · RUC: ${emisorRuc}` : ""}</div>`
    : `<div style="font-weight:700;color:${colorNombre}">${emisorNombre}${emisorRuc ? ` · RUC: ${emisorRuc}` : ""}</div>`;
  const emisorRuc = f.emisor_ruc || "";
  const emisorDireccion = f.emisor_direccion || "";
  const emisorTelefono = f.emisor_telefono || "";
  const emisorEmail = f.emisor_email || "";
  const emisorLogoUrl = f.emisor_logo_url || null;
  // fallback a la empresa activa para facturas antiguas sin snapshot del rubro
  const emisorRubrosDinet = f.emisor_rubros_dinet || activeEmpresaPropia?.rubros_dinet || "";
  const monedaSym = f.moneda === "PYG" ? "₲" : f.moneda;
  const fmt = (n) => {
    if (n == null) return "-";
    if (f.moneda === "PYG") return `₲ ${Math.round(n).toLocaleString("es-PY")}`;
    return `${f.moneda} ${Number(n).toLocaleString("es-PY", { minimumFractionDigits: 2 })}`;
  };
  const total = f.monto || 0;
  const descuentoTotal = f.total_descuentos || f.descuento || 0;
  const subtotalBruto = total + descuentoTotal;
  // ANTES esto siempre multiplicaba (monto * tc), asumiendo que el pago
  // siempre terminaba convertido a guaraníes. Funciona bien cuando la
  // factura está en moneda extranjera y se cobra con una cuenta en
  // guaraníes, pero está mal al revés (factura en guaraníes cobrada con una
  // cuenta en dólares/reales): ahí hay que DIVIDIR, no multiplicar.
  const equivalentePago = (p) => {
    const tc = Number(p?.tipo_cambio || 0);
    const monto = Number(p?.monto || 0);
    if (!tc || !monto) return null;
    return f.moneda === "PYG" ? (monto / tc) : Math.round(monto * tc);
  };
  // Moneda real de la cuenta usada en ese pago puntual (para mostrar el
  // equivalente con el símbolo correcto) — si no se encuentra la cuenta,
  // se asume la contraparte obvia de la moneda de la factura.
  const monedaCuentaPago = (p) => cuentasDisp.find(c => c.id === p?.cuenta_id)?.moneda || (f.moneda === "PYG" ? "" : "PYG");
  // IVA breakdown por tipo de ítem (vista detalle)
  const ivaDetailBreakdown = (() => {
    let exenta = 0, grav5 = 0, grav10 = 0;
    (f.conceptos || []).forEach(c => {
      const sub = c.subtotal ?? c.monto ?? ((c.precio_unitario || 0) * (c.cantidad || 1));
      const tipo = c.iva_tipo || "10";
      if (tipo === "exenta") exenta += sub;
      else if (tipo === "5") grav5 += sub;
      else grav10 += sub;
    });
    const i5 = Math.round(grav5 / 21);
    const i10 = Math.round(grav10 / 11);
    return { exenta, grav5, grav10, i5, i10, totalIva: i5 + i10 };
  })();
  const tieneConceptos = (f.conceptos || []).length > 0;
  const iva = tieneConceptos
    ? ivaDetailBreakdown.totalIva
    : (f.iva ?? (f.moneda === "PYG" ? Math.round(total / 11) : Math.round((total / 11) * 100) / 100));
  const base = total - iva;

  // Monto en letras — mismo algoritmo que usa el HTML de impresión (buildFacturaViaData),
  // duplicado acá para que la "Vista previa" en pantalla también lo muestre (antes solo
  // aparecía en el documento ya impreso/PDF, no en esta vista previa).
  const numToLetrasPreview = (n) => {
    const num = Math.round(Math.abs(n || 0));
    if (num === 0) return "Cero";
    const un = ["","un","dos","tres","cuatro","cinco","seis","siete","ocho","nueve","diez","once","doce","trece","catorce","quince","dieciséis","diecisiete","dieciocho","diecinueve"];
    const dec = ["","","veinte","treinta","cuarenta","cincuenta","sesenta","setenta","ochenta","noventa"];
    const cen = ["","cien","doscientos","trescientos","cuatrocientos","quinientos","seiscientos","setecientos","ochocientos","novecientos"];
    const g = (x) => {
      if (x < 20) return un[x];
      const d = Math.floor(x/10), u = x%10;
      if (x < 100) return dec[d] + (u ? " y " + un[u] : "");
      const c2 = Math.floor(x/100), r = x%100;
      return (c2===1&&r>0?"ciento ":cen[c2]+(r?" ":"")) + (r?g(r):"");
    };
    const mil = (x) => x===0?"":x===1?"un mil ":g(x)+" mil ";
    const m = Math.floor(num/1000000), rest = num%1000000;
    const k = Math.floor(rest/1000), u2 = rest%1000;
    let r = (m>0?(m===1?"un millón ":g(m)+" millones "):"") + mil(k) + (u2>0?g(u2):"");
    return r.trim().charAt(0).toUpperCase() + r.trim().slice(1);
  };
  const montoLetrasPreview = f.moneda === "PYG"
    ? numToLetrasPreview(total) + " guaraníes"
    : numToLetrasPreview(total) + " " + (f.moneda || "");

  const ESTADO_STYLE = {
    pagada:   { bg: "#d1fae5", text: "#065f46", border: "#a7f3d0" },
    pendiente:{ bg: "#fef3c7", text: "#92400e", border: "#fde68a" },
    parcial:  { bg: "#dbeafe", text: "#1e40af", border: "#bfdbfe" },
    anulada:  { bg: "#f1f5f9", text: "#64748b", border: "#e2e8f0" },
  };
  const est = ESTADO_STYLE[f.estado] || ESTADO_STYLE.pendiente;

  // Cuenta predeterminada por moneda/empresa (fallback para registros viejos sin cuenta asignada)
  const getCuentaFallback = (moneda, logo_tipo) =>
    cuentasDisp.find(c => c.logo_tipo === logo_tipo && c.moneda === moneda && c.es_predeterminada)?.nombre
    || cuentasDisp.find(c => c.logo_tipo === logo_tipo && c.moneda === moneda)?.nombre
    || null;

  // ── Datos + HTML de "una vía" del comprobante — compartido entre el A4 normal,
  // el A4 doble vía y (a futuro) cualquier otro formato basado en la misma factura.
  // fillerTarget: sólo lo usa la Compacta — cuando viene seteado (=tope de ítems
  // configurado), rellena la tabla con filas en blanco hasta llegar a esa
  // cantidad de filas, así el total queda siempre a la misma altura sin
  // importar si la factura tiene 1 ítem o 12. A4 simple/doble vía no pasan
  // este parámetro y quedan exactamente como estaban.
  const buildFacturaViaData = (copiaLabel = "ORIGINAL", fillerTarget = null) => {
    const docLogo = svgDocumentHeaderLogoHtml(f.logo_tipo, emisorLogoUrl);
    const esBoleta = !!f.sin_factura;
    const docRef = esBoleta ? (f.numero_boleta || f.numero || "—") : (f.numero || "—");
    const docTitle = esBoleta ? "COMPROBANTE DE VENTA" : "FACTURA";

    // IVA breakdown por tipo de ítem (impresión)
    let subExenta = 0, subGrav5 = 0, subGrav10 = 0;
    let subExentaBruto = 0, subGrav5Bruto = 0, subGrav10Bruto = 0;
    (f.conceptos || []).forEach(c => {
      const sub = c.subtotal ?? c.monto ?? ((c.precio_unitario || 0) * (c.cantidad || 1));
      const subBruto = c.subtotal_bruto ?? ((c.precio_unitario || 0) * (c.cantidad || 1));
      const tipo = c.iva_tipo || "10";
      if (tipo === "exenta") {
        subExenta += sub;
        subExentaBruto += subBruto;
      } else if (tipo === "5") {
        subGrav5 += sub;
        subGrav5Bruto += subBruto;
      } else {
        subGrav10 += sub;
        subGrav10Bruto += subBruto;
      }
    });
    const iva5Print = esBoleta ? 0 : Math.round(subGrav5 / 21);
    const iva10 = esBoleta ? 0 : Math.round(subGrav10 / 11);
    const base5 = esBoleta ? 0 : (subGrav5 - iva5Print);
    const base10 = esBoleta ? 0 : (subGrav10 - iva10);
    const totalIva = iva5Print + iva10;

    // Monto en letras (guaraníes)
    const numToLetras = (n) => {
      const num = Math.round(Math.abs(n || 0));
      if (num === 0) return "Cero";
      const un = ["","un","dos","tres","cuatro","cinco","seis","siete","ocho","nueve","diez","once","doce","trece","catorce","quince","dieciséis","diecisiete","dieciocho","diecinueve"];
      const dec = ["","","veinte","treinta","cuarenta","cincuenta","sesenta","setenta","ochenta","noventa"];
      const cen = ["","cien","doscientos","trescientos","cuatrocientos","quinientos","seiscientos","setecientos","ochocientos","novecientos"];
      const g = (x) => {
        if (x < 20) return un[x];
        const d = Math.floor(x/10), u = x%10;
        if (x < 100) return dec[d] + (u ? " y " + un[u] : "");
        const c2 = Math.floor(x/100), r = x%100;
        return (c2===1&&r>0?"ciento ":cen[c2]+(r?" ":"")) + (r?g(r):"");
      };
      const mil = (x) => x===0?"":x===1?"un mil ":g(x)+" mil ";
      const m = Math.floor(num/1000000), rest = num%1000000;
      const k = Math.floor(rest/1000), u2 = rest%1000;
      let r = (m>0?(m===1?"un millón ":g(m)+" millones "):"") + mil(k) + (u2>0?g(u2):"");
      return r.trim().charAt(0).toUpperCase() + r.trim().slice(1);
    };
    const montoLetras = f.moneda === "PYG"
      ? numToLetras(total) + " guaraníes"
      : numToLetras(total) + " " + (f.moneda || "");

    const itemRows = (f.conceptos || []).length > 0
      ? (f.conceptos || []).map((c, i) => {
          const monto = c.subtotal ?? c.monto ?? ((c.precio_unitario||0)*(c.cantidad||1));
          const tipo = c.iva_tipo || "10";
          const colExenta = (!esBoleta && tipo === "exenta") ? fmt(monto) : "";
          const col5 = (!esBoleta && tipo === "5") ? fmt(monto) : "";
          const col10 = (!esBoleta && tipo !== "exenta" && tipo !== "5") ? fmt(monto) : "";
          const codigoProd = c.producto_sku || c.codigo_barras || "";
          const trazParts = [
            c.codigo_fabricante ? `Fab: <strong>${c.codigo_fabricante}</strong>` : "",
            c.lote              ? `Lote: <strong>${c.lote}</strong>` : "",
            c.fecha_vencimiento ? `Vence: <strong>${c.fecha_vencimiento}</strong>` : "",
          ].filter(Boolean).join(" &nbsp;·&nbsp; ");
          return `<tr style="background:${i%2===0?"#fff":"#fafafa"}">
            <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:center;font-size:12px">${c.cantidad || 1}</td>
            <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px">
              ${codigoProd ? `<span style="color:#6b7280;font-size:9px">SKU: ${codigoProd}</span> &nbsp;·&nbsp; ` : ""}${c.descripcion || ""}
              ${c.variante_texto ? `<div style="font-size:9px;color:#7c3aed;margin-top:2px">Variante: ${c.variante_texto}</div>` : ""}
              ${Number(c.descuento_pct || 0) > 0 ? `<div style="font-size:9px;color:#059669;margin-top:2px">Descuento aplicado: ${Number(c.descuento_pct).toLocaleString("es-PY")}%${Number(c.descuento_monto || 0) > 0 ? ` (-${fmt(c.descuento_monto)})` : ""}</div>` : ""}
              ${trazParts ? `<div style="font-size:9px;color:#64748b;margin-top:2px">${trazParts}</div>` : ""}
            </td>
            <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:right;font-size:12px">${fmt(c.precio_unitario || 0)}</td>
            <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:right;font-size:12px">${esBoleta ? fmt(monto) : colExenta}</td>
            <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:right;font-size:12px">${col5}</td>
            <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:right;font-size:12px;font-weight:600">${esBoleta ? "" : col10}</td>
          </tr>`;
        }).join("")
      : `<tr><td style="padding:10px 8px;font-size:12px;border:1px solid #d1d5db" colspan="6">${f.concepto || ""}</td></tr>`;

    // Filas de relleno (solo Compacta) — completan la tabla hasta fillerTarget
    // filas para que el bloque de Subtotal/Total quede siempre a la misma altura.
    const cantidadReal = (f.conceptos || []).length;
    const fillerRowsHtml = (fillerTarget && cantidadReal > 0 && cantidadReal < fillerTarget)
      ? Array.from({ length: fillerTarget - cantidadReal }).map((_, j) => {
          const i = cantidadReal + j;
          return `<tr style="background:${i%2===0?"#fff":"#fafafa"}">
            <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px">&nbsp;</td>
            <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px">&nbsp;</td>
            <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px">&nbsp;</td>
            <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px">&nbsp;</td>
            <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px">&nbsp;</td>
            <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px">&nbsp;</td>
          </tr>`;
        }).join("")
      : "";
    const itemRowsConRelleno = itemRows + fillerRowsHtml;

    // Marca de agua PAGADO — diagonal, discreta
    const pagadoWatermark = f.estado === "pagada" ? `
      <div style="position:fixed;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-30deg);
        font-size:72px;font-weight:900;color:rgba(22,163,74,0.09);font-family:Arial,sans-serif;
        pointer-events:none;white-space:nowrap;z-index:0;letter-spacing:8px">PAGADO</div>` : "";

    // Caja de timbrado arriba a la derecha — como factura real paraguaya
    const timbradoBox = !esBoleta ? `
      <div style="border:2px solid #374151;border-radius:6px;padding:8px 12px;text-align:center;min-width:220px">
        <div style="font-size:9.5px;color:#374151;font-weight:700;text-transform:uppercase;letter-spacing:0.3px">
          TIMBRADO N°: <span style="font-size:13px;color:#111827;font-weight:800">${f.nro_timbrado || "—"}</span>
        </div>
        ${f.fecha_inicio_timbrado ? `<div style="font-size:9px;color:#6b7280;margin-top:2px">Inicio vigencia: ${f.fecha_inicio_timbrado}</div>` : ""}
        ${f.fecha_vigencia_timbrado ? `<div style="font-size:9px;color:#6b7280">Fin vigencia: ${f.fecha_vigencia_timbrado}</div>` : ""}
        <div style="margin-top:6px;border-top:1px solid #d1d5db;padding-top:6px">
          <div style="font-size:18px;font-weight:900;color:#111827;letter-spacing:1px">${docTitle}</div>
          <div style="font-size:14px;font-weight:800;color:${accentColor};letter-spacing:0.5px">${docRef}</div>
          ${emisorRuc ? `<div style="font-size:10px;color:#374151;font-weight:700;margin-top:2px">RUC: ${emisorRuc}</div>` : ""}
          <div style="font-size:9.5px;color:#6b7280;font-weight:700;letter-spacing:1px;margin-top:4px">${copiaLabel}</div>
          ${f.tipo_emision === "autoimpresor" && f.habilitacion_autoimpresor ? `
          <div style="font-size:8.5px;color:#374151;font-weight:600;margin-top:4px;border-top:1px dashed #d1d5db;padding-top:4px;line-height:1.3">
            HABILITACIÓN DE AUTOIMPRESOR N°: ${f.habilitacion_autoimpresor}
          </div>` : ""}
        </div>
      </div>` : `
      <div style="border:2px solid #7c3aed;border-radius:6px;padding:10px 16px;text-align:center;min-width:200px">
        <div style="font-size:20px;font-weight:900;color:#7c3aed">${docTitle}</div>
        <div style="font-size:14px;font-weight:700;color:#7c3aed;margin-top:2px">${docRef}</div>
        ${emisorRuc ? `<div style="font-size:10px;color:#4c1d95;font-weight:700;margin-top:2px">RUC: ${emisorRuc}</div>` : ""}
        <div style="font-size:9.5px;color:#9ca3af;margin-top:4px">Comprobante interno · no fiscal</div>
      </div>`;

    return {
      docLogo, esBoleta, docRef, docTitle, itemRows: itemRowsConRelleno, pagadoWatermark, timbradoBox,
      subExentaBruto, subGrav5Bruto, subGrav10Bruto, iva5Print, iva10, base5, base10, totalIva, montoLetras,
    };
  };

  // ── CSS de una vía (compartido entre A4 simple y A4 doble vía) ──────────────
  const viaStyleCss = `
    .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid ${accentColor};padding-bottom:12px;margin-bottom:10px;gap:16px}
    .company-block{min-width:250px;max-width:330px;flex:0 0 330px}
    .company-info{font-size:10.5px;color:#475569;line-height:1.55;margin-top:6px}
    .rubro-block{flex:1;min-width:180px;padding-top:58px;font-size:10px;color:#64748b;line-height:1.35;font-style:italic;word-break:break-word}
    .rubro-label{font-weight:700;color:#475569;font-style:normal}
    .client-section{border:1px solid #d1d5db;border-radius:4px;padding:8px 10px;margin-bottom:10px;font-size:11.5px}
    .cl-row{display:flex;gap:0;margin-bottom:3px}
    .cl-label{color:#6b7280;font-size:10px;font-weight:700;text-transform:uppercase;width:130px;flex-shrink:0}
    .cl-val{color:#111827;font-weight:600;border-bottom:1px dotted #d1d5db;flex:1}
    .cl-cond{display:flex;align-items:center;gap:12px;margin-top:6px;padding-top:6px;border-top:1px solid #e5e7eb;font-size:10.5px}
    table.items{width:100%;border-collapse:collapse;font-size:11.5px;margin-bottom:0}
    table.items thead tr{background:${accentColor};color:#fff}
    table.items thead th{padding:6px 8px;font-size:10px;font-weight:700;text-transform:uppercase;border:1px solid rgba(255,255,255,0.15)}
    table.items tfoot td{border:1px solid #d1d5db;padding:5px 8px;font-size:11.5px}
    .sub-row{background:#f8fafc}
    .iva-row{background:#f0fdf4}
    .total-row{background:${accentColor}12;font-weight:800;font-size:13px}
    .footer-note{margin-top:14px;font-size:9.5px;color:#94a3b8;text-align:center;border-top:1px dashed #e2e8f0;padding-top:8px}
  `;

  // ── HTML de "una vía" del comprobante (cabecera + cliente + ítems + totales) ─
  const buildViaBodyHtml = (data) => `
      ${data.pagadoWatermark}
      <div style="position:relative;z-index:1">

        <!-- CABECERA: logo izq, timbrado+factura der -->
        <div class="header">
          <div class="company-block">
            ${data.docLogo}
            <div class="company-info">
              ${emisorFantasiaHtml("#334155", "#64748b")}
              <div>${emisorDireccion}</div>
              <div>${emisorTelefono ? `Tel: ${emisorTelefono}` : ""}${emisorTelefono && emisorEmail ? " &nbsp;|&nbsp; " : ""}${emisorEmail || ""}</div>
            </div>
          </div>
          ${emisorRubrosDinet ? `<div class="rubro-block"><span class="rubro-label">Rubro:</span> ${emisorRubrosDinet}</div>` : `<div class="rubro-block"></div>`}
          ${data.timbradoBox}
        </div>

        <!-- DATOS DEL CLIENTE -->
        <div class="client-section">
          <div class="cl-row">
            <span class="cl-label">RUC</span>
            <span class="cl-val">${f.ruc || "&nbsp;"}</span>
            <span class="cl-label" style="margin-left:16px">Fecha</span>
            <span class="cl-val" style="max-width:110px">${f.fecha || "&nbsp;"}</span>
          </div>
          <div class="cl-row">
            <span class="cl-label">Nombre / Razón Social</span>
            <span class="cl-val">${f.empresa_nombre || f.razon_social || "&nbsp;"}</span>
          </div>
          <div class="cl-row">
            <span class="cl-label">Dirección</span>
            <span class="cl-val">${f.direccion || "&nbsp;"}</span>
            <span class="cl-label" style="margin-left:16px">E-mail</span>
            <span class="cl-val">${f.cliente_email || "&nbsp;"}</span>
          </div>
          <div class="cl-cond">
            Condición de venta:
            <span style="display:inline-flex;align-items:center;gap:5px">
              <span style="width:12px;height:12px;border:1.5px solid #374151;display:inline-flex;align-items:center;justify-content:center;font-size:10px;line-height:1;font-weight:900">${f.forma_pago!=="credito"?"X":""}</span> Contado
            </span>
            <span style="display:inline-flex;align-items:center;gap:5px">
              <span style="width:12px;height:12px;border:1.5px solid #374151;display:inline-flex;align-items:center;justify-content:center;font-size:10px;line-height:1;font-weight:900">${f.forma_pago==="credito"?"X":""}</span> Crédito
            </span>
            <span style="margin-left:auto">Nota de Remisión N°: ______________________</span>
            <span style="margin-left:16px">Tel: ${f.cliente_telefono || "_______________"}</span>
          </div>
          ${f.moneda !== "PYG" && Number(f.tipo_cambio) > 0 ? `
          <div class="cl-cond" style="border-top:1px dashed #e5e7eb;margin-top:4px;padding-top:4px">
            <span style="font-weight:700;color:#374151">Moneda: ${f.moneda}</span>
            <span>Tipo de Cambio: <strong>₲ ${Number(f.tipo_cambio).toLocaleString("es-PY")}</strong></span>
          </div>` : ""}
          ${mostrarObservacion && f.notas ? `
          <div class="cl-cond" style="border-top:1px dashed #e5e7eb;margin-top:4px;padding-top:4px;align-items:flex-start">
            <span class="cl-label" style="width:auto;margin-right:8px">Observación</span>
            <span style="flex:1;color:#111827;white-space:pre-line">${f.notas}</span>
          </div>` : ""}
        </div>

        <!-- TABLA DE ITEMS -->
        <table class="items">
          <thead>
            <tr>
              <th style="width:5%;text-align:center">Cant.</th>
              <th style="width:50%;text-align:left">Descripción</th>
              <th style="width:15%;text-align:right">Precio Unit.</th>
              <th style="width:10%;text-align:right">Exentas</th>
              <th style="width:10%;text-align:right">5%</th>
              <th style="width:10%;text-align:right">10%</th>
            </tr>
          </thead>
          <tbody>${data.itemRows}</tbody>
          <tfoot>
            <tr class="sub-row">
              <td colspan="3" style="text-align:right;font-weight:700">SUBTOTAL</td>
              <td style="text-align:right">${data.esBoleta ? fmt(subtotalBruto) : (data.subExentaBruto > 0 ? fmt(data.subExentaBruto) : "")}</td>
              <td style="text-align:right">${!data.esBoleta && data.subGrav5Bruto > 0 ? fmt(data.subGrav5Bruto) : ""}</td>
              <td style="text-align:right;font-weight:700">${!data.esBoleta && data.subGrav10Bruto > 0 ? fmt(data.subGrav10Bruto) : (!data.esBoleta && data.subGrav5Bruto === 0 && data.subExentaBruto === 0 ? fmt(subtotalBruto) : "")}</td>
            </tr>
            ${descuentoTotal > 0 ? `
            <tr class="sub-row">
              <td colspan="5" style="text-align:right;font-weight:700;color:#059669">DESCUENTO</td>
              <td style="text-align:right;font-weight:700;color:#059669">- ${fmt(descuentoTotal)}</td>
            </tr>` : ""}
            ${!data.esBoleta ? `
            <tr class="iva-row">
              <td colspan="2" style="font-size:10px;color:#374151;font-weight:600">LIQUIDACIÓN DEL IVA</td>
              <td style="text-align:right;font-size:10.5px;color:#374151">5% de ${fmt(data.base5)}:</td>
              <td></td>
              <td style="text-align:right;font-size:10.5px">${fmt(data.iva5Print)}</td>
              <td></td>
            </tr>
            <tr class="iva-row">
              <td colspan="2"></td>
              <td style="text-align:right;font-size:10.5px;color:#374151">10% de ${fmt(data.base10)}:</td>
              <td></td>
              <td></td>
              <td style="text-align:right;font-size:10.5px">${fmt(data.iva10)}</td>
            </tr>
            <tr class="sub-row">
              <td colspan="4" style="font-size:10px;color:#374151">
                <strong>Son:</strong> ${data.montoLetras}
              </td>
              <td style="text-align:right;font-weight:700;font-size:10.5px">TOTAL IVA</td>
              <td style="text-align:right;font-weight:700;font-size:10.5px">${fmt(data.totalIva)}</td>
            </tr>` : ""}
            <tr class="total-row">
              <td colspan="4" style="font-size:10px;color:#6b7280">
              </td>
              <td style="text-align:right;font-size:12px">TOTAL A PAGAR</td>
              <td style="text-align:right;font-size:14px;color:${accentColor}">${fmt(total)}</td>
            </tr>
            ${f.moneda !== "PYG" && Number(f.tipo_cambio) > 0 ? `
            ${!data.esBoleta ? `
            <tr class="iva-row" style="border-top:2px solid #d1d5db">
              <td colspan="2" style="font-size:10px;color:#374151;font-weight:600">LIQUIDACIÓN DEL IVA (EN GUARANÍES)</td>
              <td style="text-align:right;font-size:10.5px;color:#374151">5% de ₲ ${Math.round(data.base5 * f.tipo_cambio).toLocaleString("es-PY")}:</td>
              <td></td>
              <td style="text-align:right;font-size:10.5px">₲ ${Math.round(data.iva5Print * f.tipo_cambio).toLocaleString("es-PY")}</td>
              <td></td>
            </tr>
            <tr class="iva-row">
              <td colspan="2"></td>
              <td style="text-align:right;font-size:10.5px;color:#374151">10% de ₲ ${Math.round(data.base10 * f.tipo_cambio).toLocaleString("es-PY")}:</td>
              <td></td>
              <td></td>
              <td style="text-align:right;font-size:10.5px">₲ ${Math.round(data.iva10 * f.tipo_cambio).toLocaleString("es-PY")}</td>
            </tr>
            <tr class="sub-row">
              <td colspan="4" style="font-size:9px;color:#6b7280">Tipo de cambio aplicado: ₲ ${Number(f.tipo_cambio).toLocaleString("es-PY")}</td>
              <td style="text-align:right;font-weight:700;font-size:10.5px">TOTAL IVA (₲)</td>
              <td style="text-align:right;font-weight:700;font-size:10.5px">₲ ${Math.round(data.totalIva * f.tipo_cambio).toLocaleString("es-PY")}</td>
            </tr>` : ""}
            <tr class="total-row">
              <td colspan="4" style="font-size:10px;color:#6b7280"></td>
              <td style="text-align:right;font-size:12px">TOTAL EN GUARANÍES</td>
              <td style="text-align:right;font-size:14px;color:${accentColor}">₲ ${Math.round(total * f.tipo_cambio).toLocaleString("es-PY")}</td>
            </tr>` : ""}
          </tfoot>
        </table>

        ${(() => {
          const pagosConDetalle = (f.pagos || []).filter(p => p.monto_recibido || p.vuelto);
          if (pagosConDetalle.length === 0) return "";
          const filas = pagosConDetalle.map((p, i) => {
            const montoRec = p.monto_recibido || 0;
            const vueltoP  = p.vuelto || (montoRec > p.monto ? montoRec - p.monto : 0);
            return `<tr>
              <td style="padding:4px 8px;font-size:10px;color:#374151">Pago ${i+1}${p.recibo_numero ? ` · ${p.recibo_numero}` : ""}${p.fecha ? ` · ${p.fecha}` : ""}</td>
              <td style="padding:4px 8px;font-size:10px;color:#374151;text-align:right">${fmt(p.monto)}</td>
              <td style="padding:4px 8px;font-size:10px;color:#374151;text-align:right">${montoRec > 0 ? fmt(montoRec) : "—"}</td>
              <td style="padding:4px 8px;font-size:10px;color:#065f46;font-weight:700;text-align:right">${vueltoP > 0 ? fmt(vueltoP) : "—"}</td>
            </tr>`;
          }).join("");
          return `<div style="margin-top:10px;border:1px solid #d1d5db;border-radius:6px;overflow:hidden;max-width:400px;margin-left:auto">
            <div style="background:#f9fafb;padding:6px 12px;font-size:10px;font-weight:700;color:#374151;text-transform:uppercase;letter-spacing:.5px;border-bottom:1px solid #e5e7eb">Historial de pagos y vueltos</div>
            <table style="width:100%;border-collapse:collapse">
              <thead><tr style="background:#f3f4f6">
                <th style="padding:3px 8px;font-size:9px;text-align:left;color:#6b7280">Pago</th>
                <th style="padding:3px 8px;font-size:9px;text-align:right;color:#6b7280">Monto</th>
                <th style="padding:3px 8px;font-size:9px;text-align:right;color:#6b7280">Recibido</th>
                <th style="padding:3px 8px;font-size:9px;text-align:right;color:#6b7280">Vuelto</th>
              </tr></thead>
              <tbody>${filas}</tbody>
            </table>
          </div>`;
        })()}
      </div>`;

  // ── Impresión "Compacta" — dos comprobantes apilados en una sola hoja A4
  // vertical. A pedido de Jose: en vez de un diseño aparte simplificado (que
  // no traía la liquidación de IVA detallada, ni Dirección, ni la condición
  // de venta como el A4), esto reusa el MISMO bloque completo del A4
  // (buildViaBodyHtml) reducido con zoom — la misma técnica que ya usa el
  // doble vía para achicar cada columna. Así el compacto queda igual al A4
  // real en todo (columnas de IVA, dirección, condición, SKU, etc.) y
  // cualquier arreglo futuro al A4 se hereda acá automáticamente.
  //
  // Rediseño (alturas fijas 50/50): cada vía va dentro de un "slot" de altura
  // FIJA en cm (no zoom, no vh) que ocupa exactamente la mitad de la hoja
  // imprimible, sin importar cuántos ítems tenga la factura. Para que el
  // total no quede flotando en medio de la hoja cuando hay pocos ítems, la
  // tabla se rellena con filas en blanco hasta el tope de ítems configurado
  // (fillerTarget) — así el bloque Subtotal/Total cae siempre a la misma
  // altura. overflow:hidden en el slot actúa como resguardo por si un caso
  // extremo (ítems con textos muy largos) igual se pasara del alto fijo.
  const handlePrintCompacta = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const dataOriginal = buildFacturaViaData("ORIGINAL", maxItemsCompacta);
    const dataArchivo = buildFacturaViaData("ARCHIVO TRIBUTARIO", maxItemsCompacta);
    const viaHtmlOriginal = buildViaBodyHtml(dataOriginal);
    const viaHtmlArchivo = buildViaBodyHtml(dataArchivo);
    const VIA_WIDTH = 860;
    const SCALE = 0.62;
    // Hoja A4 vertical (29.7cm) con margen de 0.6cm arriba/abajo = 28.5cm
    // imprimibles. Se reserva ~1.1cm para la línea de corte + separaciones,
    // y el resto se divide en dos mitades exactamente iguales.
    const SLOT_HEIGHT_CM = 13.7;
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <title>${dataOriginal.docTitle} ${dataOriginal.docRef} (compacta)</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        @page{size:A4 portrait;margin:0.6cm}
        body{font-family:'Segoe UI',Arial,sans-serif;color:#111827;background:#fff}
        .compacta-wrap{display:flex;flex-direction:column;align-items:center;gap:6px}
        .compacta-slot{width:100%;height:${SLOT_HEIGHT_CM}cm;overflow:hidden;display:flex;justify-content:center}
        .compacta-copy{width:${VIA_WIDTH}px;zoom:${SCALE}}
        .compacta-cut{width:100%;border-top:2px dashed #94a3b8;position:relative;margin:4px 0}
        .compacta-cut span{position:absolute;top:-9px;left:50%;transform:translateX(-50%);background:#fff;padding:0 10px;font-size:8px;color:#64748b}
        ${viaStyleCss}
      </style>
    </head><body>
      <div class="compacta-wrap">
        <div class="compacta-slot"><div class="compacta-copy">${viaHtmlOriginal}</div></div>
        <div class="compacta-cut"><span>✂ Cortar aquí</span></div>
        <div class="compacta-slot"><div class="compacta-copy">${viaHtmlArchivo}</div></div>
      </div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // ── Impresión A4 — una vía por hoja (formato clásico, vertical) ─────────────
  const handlePrint = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const data = buildFacturaViaData();
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <title>${data.docTitle} ${data.docRef}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Segoe UI',Arial,sans-serif;color:#111827;padding:20px 24px;max-width:860px;margin:0 auto;background:#fff;position:relative;font-size:12px}
        @media print{body{padding:10px 14px}@page{margin:0.8cm}}
        ${viaStyleCss}
      </style>
    </head><body>${buildViaBodyHtml(data)}</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // ── Impresión A4 doble vía — hoja horizontal con 2 copias, una al lado de la
  // otra, separadas por una línea de corte (vía cliente / vía archivo tributario
  // del comercio). Cada columna se genera con su propia etiqueta para que no
  // queden ambas marcadas como "ORIGINAL" — la primera es la que se entrega al
  // cliente, la segunda queda en el comercio como archivo tributario.
  const handlePrintDobleVia = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const dataOriginal = buildFacturaViaData("ORIGINAL");
    const dataArchivo = buildFacturaViaData("ARCHIVO TRIBUTARIO");
    const viaHtmlOriginal = buildViaBodyHtml(dataOriginal);
    const viaHtmlArchivo = buildViaBodyHtml(dataArchivo);
    const VIA_WIDTH = 860;
    const SCALE = 0.6;
    const COL_WIDTH = Math.round(VIA_WIDTH * SCALE);
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <title>${dataOriginal.docTitle} ${dataOriginal.docRef} (doble vía)</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        @page{size:A4 landscape;margin:0.6cm}
        body{font-family:'Segoe UI',Arial,sans-serif;color:#111827;background:#fff;font-size:12px}
        .doble-via-wrap{display:flex;align-items:stretch;justify-content:center;gap:0;width:100%;position:relative}
        .via-col{width:${COL_WIDTH}px;flex:0 0 ${COL_WIDTH}px;overflow:hidden;padding:8px 6px}
        .via-col-inner{width:${VIA_WIDTH}px;zoom:${SCALE}}
        .cut-line{flex:0 0 1px;border-left:2px dashed #94a3b8;position:relative;margin:0 8px}
        .cut-label{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;padding:4px 10px;
          border:1px solid #cbd5e1;border-radius:12px;font-size:9px;color:#64748b;white-space:nowrap;box-shadow:0 1px 2px rgba(0,0,0,0.06)}
        ${viaStyleCss}
      </style>
    </head><body>
      <div class="doble-via-wrap">
        <div class="via-col"><div class="via-col-inner">${viaHtmlOriginal}</div></div>
        <div class="cut-line"><span class="cut-label">✂ Cortar aquí</span></div>
        <div class="via-col"><div class="via-col-inner">${viaHtmlArchivo}</div></div>
      </div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // Impresión automática al guardar — dispara una sola vez cuando el padre pide
  // "Guardar e Imprimir" (config de Ventas por empresa o por usuario). autoPrint
  // es el modo resuelto: "no" | "a4" | "a4_doble" | "ticket". Se resetea vía
  // onAutoPrinted para no reimprimir si el modal se re-renderiza (ej. al refrescar facDoc).
  React.useEffect(() => {
    if (!autoPrint || autoPrint === "no") return;
    if (autoPrint === "a4_doble") handlePrintDobleVia();
    else if (autoPrint === "ticket") imprimirTicket();
    else if (autoPrint === "compacta") handlePrintCompacta();
    else handlePrint();
    onAutoPrinted?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrint]);

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[92vh] overflow-y-auto overflow-x-hidden relative my-4" onClick={e => e.stopPropagation()}>
        {/* Toolbar */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-200 px-4 py-3 flex items-center justify-between gap-2 flex-wrap">
          <span className="text-gray-600 font-medium text-sm flex items-center gap-2 flex-wrap">
            Vista previa · {f.sin_factura ? <span className="text-violet-600">Boleta</span> : "Factura"}
            {(f.es_credito_cuotas || cuotas.length > 0) && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300">
                💳 Venta a crédito · {cuotas.length || f.num_cuotas || "?"} cuota{(cuotas.length || f.num_cuotas || 1) !== 1 ? "s" : ""}
              </span>
            )}
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {modelosHabilitados.includes("a4") && (
              <button onClick={handlePrint}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-all"
                title="Imprimir formato A4">
                <Printer className="w-3.5 h-3.5" /> Imprimir A4
              </button>
            )}
            {modelosHabilitados.includes("a4_doble") && (
              <button onClick={handlePrintDobleVia}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs rounded-lg transition-all"
                title="Imprimir A4 horizontal con 2 vías (cliente / archivo tributario)">
                <Printer className="w-3.5 h-3.5" /> A4 doble vía
              </button>
            )}
            {modelosHabilitados.includes("compacta") && (
              <button onClick={handlePrintCompacta}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs rounded-lg transition-all"
                title="Imprimir formato compacto — 2 comprobantes apilados en una hoja">
                <Printer className="w-3.5 h-3.5" /> Compacta
              </button>
            )}
            {f.delivery && hasPermission("facturas.imprimir_delivery") && (
              <button onClick={async () => {
                  let formato = "doble_a5";
                  try {
                    const rc = await fetch(`${apiUrl}/admin/config-delivery?logo_tipo=${f.logo_tipo || activeEmpresaPropia?.slug || ""}`, { headers: { Authorization: `Bearer ${token}` } });
                    if (rc.ok) formato = (await rc.json()).formato || formato;
                  } catch { /* usa el formato por defecto */ }
                  const ok = await imprimirOrdenDelivery({ factura: f, token, empresaPropia: activeEmpresaPropia, formato });
                  if (ok === false) toast.error("El navegador bloqueó la ventana de impresión");
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs rounded-lg transition-all"
                title="Imprimir la Orden de Delivery (datos de tu empresa y del cliente)">
                <Truck className="w-3.5 h-3.5" /> Orden de Delivery
              </button>
            )}
            {modelosHabilitados.includes("ticket") && (
              <button onClick={imprimirTicket}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs rounded-lg transition-all"
                title="Imprimir formato ticket 80mm">
                🧾 Ticket
              </button>
            )}
            {(cuotas.length > 0 || f.es_credito_cuotas) && (
              <button onClick={imprimirPagares}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs rounded-lg transition-all"
                title="Imprimir pagarés de cuotas">
                📄 Pagarés
              </button>
            )}
            {/* Vendedor */}
            {(vendEditable || vendNombre) && (
              <div className="relative">
                <button onClick={abrirVendedores} disabled={savingVend}
                  title={vendEditable ? (vendNombre ? "Cambiar vendedor (la factura sigue pendiente)" : "Agregar vendedor") : `Vendedor: ${vendNombre}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-all border bg-white/5 border-white/15 text-slate-300">
                  <User className="w-3.5 h-3.5" />
                  {vendNombre || "Sin vendedor"}
                  {vendEditable && <ChevronDown className="w-3 h-3 ml-0.5 opacity-60" />}
                </button>
                {showVendDropdown && vendEditable && (
                  <div className="absolute top-full right-0 mt-1 z-50 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl py-1 min-w-[200px] max-h-64 overflow-y-auto"
                    onMouseLeave={() => setShowVendDropdown(false)}>
                    {vendOpciones.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">Sin vendedores disponibles</p>}
                    {vendOpciones.map(v => (
                      <button key={v.id} onClick={() => handleSetVendedor(v)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-white/10 transition-colors">
                        <span className="text-gray-200">{v.nombre}</span>
                        {v.tipo === "catastro" && <span className="text-[10px] text-slate-500">Catastro</span>}
                        {vendNombre === v.nombre && <span className="ml-auto text-emerald-400">✓</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Tipo de factura */}
            {tiposFac.length > 0 && (canActualizarTipo || tipoFacNombre) && (
              <div className="relative">
                <button
                  onClick={() => canActualizarTipo && setShowTipoDropdown(v => !v)}
                  disabled={savingTipoFac}
                  title={tipoFacNombre ? `Tipo: ${tipoFacNombre}` : "Asignar tipo de factura"}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-all border"
                  style={tipoFacColor ? {
                    background: tipoFacColor + "22",
                    borderColor: tipoFacColor + "55",
                    color: tipoFacColor,
                  } : {
                    background: "rgba(255,255,255,0.05)",
                    borderColor: "rgba(255,255,255,0.15)",
                    color: "#94a3b8",
                  }}
                >
                  <Tag className="w-3.5 h-3.5" />
                  {tipoFacNombre || "Sin tipo"}
                  {canActualizarTipo && <ChevronDown className="w-3 h-3 ml-0.5 opacity-60" />}
                </button>
                {showTipoDropdown && canActualizarTipo && (
                  <div className="absolute top-full right-0 mt-1 z-50 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl py-1 min-w-[160px]"
                    onMouseLeave={() => setShowTipoDropdown(false)}>
                    {tiposFac.map(t => (
                      <button key={t.id} onClick={() => handleSetTipo(t)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-white/10 transition-colors ${tipoFacId === t.id ? "font-bold" : ""}`}>
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{background: t.color}} />
                        <span className="text-gray-200">{t.nombre}</span>
                        {tipoFacId === t.id && <span className="ml-auto text-emerald-400">✓</span>}
                      </button>
                    ))}
                    {tipoFacId && (
                      <>
                        <div className="border-t border-gray-700 my-1" />
                        <button onClick={() => handleSetTipo(null)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-white/10 transition-colors text-gray-400">
                          <span className="w-2.5 h-2.5 rounded-full border border-gray-500 flex-shrink-0" />
                          Sin tipo
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            {canEdit && (f.estado === "pendiente" || f.estado === "parcial") && (
              <button onClick={() => onPagar(cuotas)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-lg transition-all">
                <Banknote className="w-3.5 h-3.5" /> {f.forma_pago === "credito" ? "Registrar pago" : "Pagar"}
              </button>
            )}
            {/* Deshacer pago deshabilitado — para corregir, eliminar y recrear */}
{/* Editar factura deshabilitado — para corregir, eliminar y recrear */}
            {canDelete && (
              <button onClick={onDelete}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs rounded-lg border border-red-200 transition-all">
                <Trash2 className="w-3.5 h-3.5" /> Eliminar
              </button>
            )}
            <button onClick={onClose}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs rounded-lg transition-all">
              <X className="w-3.5 h-3.5" /> Cerrar
            </button>
          </div>
        </div>

        {/* Cabecera del documento */}
        <div className="relative p-6 border-b border-gray-200 overflow-hidden">
          {/* PAGADO watermark */}
          {f.estado === "pagada" && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none" style={{zIndex:0}}>
              <span className="text-[100px] font-black text-emerald-500/10 rotate-[-35deg] leading-none tracking-widest whitespace-nowrap">PAGADO</span>
            </div>
          )}
          <div className="relative flex flex-col lg:flex-row justify-between items-start gap-6" style={{zIndex:1}}>
            <div className="shrink-0 w-full lg:w-[330px] min-w-0">
              {f.logo_tipo === "arandu" && <LogoArandu />}
              {f.logo_tipo === "jar" && <LogoJar />}
              {(!f.logo_tipo || f.logo_tipo === "arandujar") && <LogoAranduJarDoc />}
              <div className="mt-4 text-sm text-gray-500">
                {emisorNombreFantasia ? (
                  <>
                    <p className="font-bold text-gray-900 text-base leading-tight">{emisorNombreFantasia}</p>
                    <p className="text-xs text-gray-500">{emisorNombre}{emisorRuc ? ` · RUC: ${emisorRuc}` : ""}</p>
                  </>
                ) : (
                  <p className="font-semibold text-gray-700">{emisorNombre}{emisorRuc ? ` · RUC: ${emisorRuc}` : ""}</p>
                )}
                {emisorDireccion && <p>{emisorDireccion}</p>}
                {emisorTelefono && <p>Tel: {emisorTelefono}</p>}
                {emisorEmail && <p>{emisorEmail}</p>}
              </div>
            </div>
            <div className="flex-1 lg:pt-20 text-xs text-gray-500 italic leading-relaxed break-words min-w-0">
              {emisorRubrosDinet && (
                <p><span className="not-italic font-semibold text-gray-600">Rubro:</span> {emisorRubrosDinet}</p>
              )}
            </div>
            <div className="text-left lg:text-right shrink-0 min-w-0 lg:min-w-[240px] max-w-full">
              <h1 className={`text-2xl font-bold ${f.sin_factura ? "text-violet-700" : "text-gray-800"}`}>
                {f.sin_factura ? "COMPROBANTE DE VENTA" : "FACTURA"}
              </h1>
              {f.sin_factura && (
                <p className="text-xs text-violet-500 font-medium -mt-1 mb-1">Sin comprobante fiscal</p>
              )}
              <p className="text-xl font-semibold mt-1" style={{ color: f.sin_factura ? "#7c3aed" : accentColor }}>
                {f.sin_factura ? (f.numero_boleta || f.numero) : f.numero}
              </p>
              {emisorRuc && <p className="text-gray-600 text-sm font-semibold">RUC: {emisorRuc}</p>}
              <p className="text-gray-500 text-sm mt-1">Fecha: <strong className="text-gray-700">{f.fecha || "-"}</strong></p>
              <p className="text-gray-500 text-sm">Forma de pago: <strong className="text-gray-700">{f.forma_pago === "credito" ? "A crédito" : "Al contado"}</strong></p>
              {f.fecha_vencimiento && (
                <p className="text-gray-500 text-sm">Vencimiento: <strong className={new Date(f.fecha_vencimiento) < new Date() && f.estado !== "pagada" ? "text-red-600" : "text-gray-700"}>{f.fecha_vencimiento}</strong></p>
              )}
              {/* Estado badge */}
              <div className="mt-2">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-bold" style={{ background: est.bg, color: est.text, border: `1px solid ${est.border}` }}>
                  {f.estado?.toUpperCase()}
                </span>
              </div>
              {/* Cuenta bancaria — visible sin scroll (usa fallback si el pago no tiene cuenta) */}
              {(() => {
                const pagos = f.pagos || [];
                const cuentas = [...new Set(
                  pagos.length > 0
                    ? pagos.map(p => p.cuenta_nombre || getCuentaFallback(f.moneda, f.logo_tipo)).filter(Boolean)
                    : [getCuentaFallback(f.moneda, f.logo_tipo)].filter(Boolean)
                )];
                if (!cuentas.length) return null;
                return (
                  <div className="mt-2 flex flex-col items-end gap-1">
                    {cuentas.map((c, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">
                        🏦 {c}
                      </span>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Cliente */}
        <div className={`p-6 border-b ${f.sin_factura ? "bg-violet-50" : "bg-gray-50"}`}>
          <p className={`text-xs font-semibold mb-2 uppercase tracking-wider ${f.sin_factura ? "text-violet-400" : "text-gray-400"}`}>
            {f.sin_factura ? "COMPRADOR" : "CLIENTE"}
          </p>
          <p className="text-xl font-semibold text-gray-800">{f.empresa_nombre || f.razon_social || "-"}</p>
          {f.empresa_nombre && f.razon_social && f.empresa_nombre !== f.razon_social && (
            <p className="text-sm text-gray-500">{f.razon_social}</p>
          )}
          {f.ruc && <p className="text-sm text-gray-500 mt-0.5">RUC: {f.ruc}</p>}
        </div>

        {/* Conceptos / Items */}
        <div className="p-6 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-gray-300 p-2 text-left text-gray-700 font-bold text-sm">Concepto / Descripción</th>
                <th className="border border-gray-300 p-2 text-center text-gray-700 font-bold text-sm w-20">Cant.</th>
                <th className="border border-gray-300 p-2 text-right text-gray-700 font-bold text-sm w-36">Precio unit.</th>
                <th className="border border-gray-300 p-2 text-right text-gray-700 font-bold text-sm w-40">Monto</th>
              </tr>
            </thead>
            <tbody>
              {(f.conceptos || []).length > 0 ? (
                (f.conceptos || []).map((c, idx) => (
                  <tr key={idx} className="bg-white">
                    <td className="border border-gray-300 p-2 text-gray-800 text-sm">
                      {c.descripcion || ""}
                      {c.variante_texto && <p className="text-violet-600 text-xs mt-1">Variante: {c.variante_texto}</p>}
                      {c.observacion && <p className="text-gray-500 text-xs italic mt-1">{c.observacion}</p>}
                      {Number(c.descuento_pct || 0) > 0 && (
                        <p className="text-emerald-600 text-xs mt-1">
                          Descuento aplicado: {Number(c.descuento_pct).toLocaleString("es-PY")}%
                          {Number(c.descuento_monto || 0) > 0 ? ` (-${fmt(c.descuento_monto)})` : ""}
                        </p>
                      )}
                    </td>
                    <td className="border border-gray-300 p-2 text-center text-gray-800 text-sm">{c.cantidad || 1}</td>
                    <td className="border border-gray-300 p-2 text-right text-gray-800 text-sm">{fmt(c.precio_unitario || (c.monto || 0))}</td>
                    <td className="border border-gray-300 p-2 text-right text-gray-800 text-sm">{fmt(c.subtotal ?? c.monto ?? ((c.precio_unitario || 0) * (c.cantidad || 1)))}</td>
                  </tr>
                ))
              ) : (
                <tr className="bg-white">
                  <td className="border border-gray-300 p-3 text-gray-800" colSpan={4}>{f.concepto || "-"}</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              {descuentoTotal > 0 && (
                <tr className="bg-gray-50">
                  <td colSpan={3} className="border border-gray-300 p-2 text-right text-sm text-gray-500">Subtotal antes de descuentos:</td>
                  <td className="border border-gray-300 p-2 text-right text-sm text-gray-500">{fmt(subtotalBruto)}</td>
                </tr>
              )}
              {descuentoTotal > 0 && (
                <tr className="bg-gray-50">
                  <td colSpan={3} className="border border-gray-300 p-2 text-right text-sm text-emerald-600">Descuentos aplicados:</td>
                  <td className="border border-gray-300 p-2 text-right text-sm text-emerald-600">-{fmt(descuentoTotal)}</td>
                </tr>
              )}
              {!f.sin_factura && ivaDetailBreakdown.exenta > 0 && (
                <tr className="bg-gray-50">
                  <td colSpan={3} className="border border-gray-300 p-2 text-right text-sm text-gray-500">Exentas:</td>
                  <td className="border border-gray-300 p-2 text-right text-sm text-gray-500">{fmt(ivaDetailBreakdown.exenta)}</td>
                </tr>
              )}
              {!f.sin_factura && ivaDetailBreakdown.i5 > 0 && (
                <tr className="bg-gray-50">
                  <td colSpan={3} className="border border-gray-300 p-2 text-right text-sm text-gray-500">IVA incluido (5%):</td>
                  <td className="border border-gray-300 p-2 text-right text-sm text-gray-500">{fmt(ivaDetailBreakdown.i5)}</td>
                </tr>
              )}
              {!f.sin_factura && ivaDetailBreakdown.i10 > 0 && (
                <tr className="bg-gray-50">
                  <td colSpan={3} className="border border-gray-300 p-2 text-right text-sm text-gray-500">IVA incluido (10%):</td>
                  <td className="border border-gray-300 p-2 text-right text-sm text-gray-500">{fmt(ivaDetailBreakdown.i10)}</td>
                </tr>
              )}
              {!f.sin_factura && ivaDetailBreakdown.i5 === 0 && ivaDetailBreakdown.i10 === 0 && ivaDetailBreakdown.exenta === 0 && (
                <tr className="bg-gray-50">
                  <td colSpan={3} className="border border-gray-300 p-2 text-right text-sm text-gray-500">Base imponible:</td>
                  <td className="border border-gray-300 p-2 text-right text-sm text-gray-500">{fmt(base)}</td>
                </tr>
              )}
              <tr style={{ background: "#dbeafe" }}>
                <td colSpan={3} className="border border-gray-300 p-2 text-right font-bold text-lg text-gray-900">TOTAL:</td>
                <td className="border border-gray-300 p-2 text-right font-bold text-lg" style={{ color: accentColor }}>{fmt(total)}</td>
              </tr>
            </tfoot>
          </table>
          {f.moneda !== "PYG" && f.tipo_cambio && (
            <p className="text-gray-500 text-xs mt-2">TC: 1 {f.moneda} = ₲ {Number(f.tipo_cambio).toLocaleString("es-PY")} · Equivalente ≈ ₲ {Math.round(total * f.tipo_cambio).toLocaleString("es-PY")}</p>
          )}
          <p className="text-gray-600 text-xs mt-2"><strong>Son:</strong> {montoLetrasPreview}</p>
          {f.es_credito_cuotas && Number(f.tasa_interes_cuotas) > 0 && (
            <p className="text-amber-600 text-xs mt-1 font-medium">
              Total financiado (con {f.tasa_interes_cuotas}% interés): {fmt(
                cuotas.length > 0
                  ? cuotas.filter(c => c.estado !== "cancelado").reduce((s, c) => s + (c.monto || 0), 0)
                  : total * (1 + Number(f.tasa_interes_cuotas) / 100)
              )}
            </p>
          )}
        </div>

        {/* Historial de pagos */}
        {(f.pagos || []).length > 0 && (
          <div className="px-6 pb-4">
            <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2">Historial de pagos</p>
            <div className="space-y-1.5">
              {(f.pagos || []).map((p, i) => (
                <div key={p.id || i} className="flex items-start justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-gray-700 font-medium">Pago {i + 1}</span>
                      {p.fecha && <span className="text-gray-500 text-xs">{p.fecha}</span>}
                      {p.registrado_por && <span className="text-xs text-gray-500">· por {p.registrado_por}</span>}
                      {(p.cuenta_nombre || getCuentaFallback(f.moneda, f.logo_tipo)) && (
                        <span className="text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded px-1.5 py-0.5">
                          🏦 {p.cuenta_nombre || getCuentaFallback(f.moneda, f.logo_tipo)}
                        </span>
                      )}
                      {p.tipo_cambio && (() => {
                        const eq = equivalentePago(p);
                        const destMoneda = monedaCuentaPago(p);
                        const eqTxt = eq == null ? "-" : (destMoneda === "PYG" || !destMoneda
                          ? `₲ ${Math.round(eq).toLocaleString("es-PY")}`
                          : `${destMoneda} ${eq.toLocaleString("es-PY", { minimumFractionDigits: 2 })}`);
                        return (
                          <span className="text-xs text-purple-600 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5">
                            TC {Number(p.tipo_cambio).toLocaleString("es-PY")} → {eqTxt}
                          </span>
                        );
                      })()}
                      {p.monto_recibido && p.monto_recibido > p.monto && (
                        <span className="text-xs text-teal-600 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5">
                          💰 {p.vuelto_moneda && p.vuelto_moneda !== "PYG" && p.vuelto_monto_moneda
                            ? `Recibido ${(p.monto_recibido_moneda ?? 0).toLocaleString("es-PY", { maximumFractionDigits: 2 })} ${p.vuelto_moneda} · Vuelto ${p.vuelto_monto_moneda.toLocaleString("es-PY", { maximumFractionDigits: 2 })} ${p.vuelto_moneda}${p.vuelto_cuenta_nombre ? ` (${p.vuelto_cuenta_nombre})` : ""}`
                            : `Recibido ${fmt(p.monto_recibido)} · Vuelto ${fmt(p.monto_recibido - p.monto)}${p.vuelto_cuenta_nombre ? ` (${p.vuelto_cuenta_nombre})` : ""}`}
                        </span>
                      )}
                      {(p.monto_retencion || 0) > 0 && (
                        <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                          📉 Retención IVA{p.pct_retencion ? ` ${p.pct_retencion}%` : ""} − {fmt(p.monto_retencion)} · Neto {fmt(p.monto - p.monto_retencion)}
                        </span>
                      )}
                    </div>
                    {(p.referencia_pago || p.nota_pago || p.comprobante_pago) && (
                      <div className="mt-0.5 text-xs text-slate-600 flex flex-wrap items-center gap-2">
                        {p.referencia_pago && <span className="bg-blue-50 border border-blue-200 text-blue-700 rounded px-1.5 py-0.5">🔖 Comprobante: <strong>{p.referencia_pago}</strong></span>}
                        {p.nota_pago && <span className="italic">📝 {p.nota_pago}</span>}
                        {p.comprobante_pago && (
                          <button type="button" onClick={() => { const w = window.open(); if (w) { w.document.write(`<img src="${p.comprobante_pago}" style="max-width:100%">`); } }}
                            className="underline text-blue-600">🖼 Ver imagen</button>
                        )}
                      </div>
                    )}
                    {p.recibo_numero && (
                      <button
                        onClick={() => {
                          const rec = { numero: p.recibo_numero, id: p.recibo_id, factura_numero: f.numero, razon_social: f.razon_social, ruc: f.ruc, monto: p.monto, moneda: f.moneda, fecha_pago: p.fecha, logo_tipo: f.logo_tipo, cuenta_id: p.cuenta_id, cuenta_nombre: p.cuenta_nombre };
                          onReciboClick(rec);
                        }}
                        className="mt-0.5 text-amber-600 hover:text-amber-700 text-xs underline"
                      >
                        📄 Recibo #{p.recibo_numero}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-emerald-700 font-semibold">{fmt(p.monto)}</span>
                    {canEdit && onEditPago && f.estado !== "pagada" && (
                      <button
                        onClick={() => onEditPago(f, p)}
                        className="text-slate-400 hover:text-blue-500 transition-colors"
                        title="Editar pago"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {canEdit && onDeletePago && (
                      <button
                        onClick={() => onDeletePago(f, p)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                        title="Eliminar pago"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Panel de cuotas ─────────────────────────────────────── */}
        {(f.es_credito_cuotas || cuotas.length > 0) && (() => {
          const cuotasPendientes = cuotas.filter(c => c.estado !== "pagado" && c.estado !== "cancelado");
          const montoSeleccionado = cuotas.filter(c => selectedCuotaIds.has(c.id)).reduce((s, c) => s + (c.monto || 0), 0);
          return (
          <div className="px-6 pb-4">
            {/* Header del panel */}
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">
                Cuotas ({cuotas.filter(c => c.estado !== "cancelado").length})
                {Number(f.tasa_interes_cuotas) > 0 && (
                  <span className="ml-2 text-amber-600 normal-case font-medium">
                    · Total financiado (con {f.tasa_interes_cuotas}% interés): {fmt(cuotas.filter(c => c.estado !== "cancelado").reduce((s, c) => s + (c.monto || 0), 0))}
                  </span>
                )}
              </p>
              <div className="flex gap-2 flex-wrap items-center">
                {/* Seleccionar todas las pendientes */}
                {cuotasPendientes.length > 0 && selectedCuotaIds.size === 0 && (
                  <button
                    onClick={() => setSelectedCuotaIds(new Set(cuotasPendientes.map(c => c.id)))}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Seleccionar todas ({cuotasPendientes.length})
                  </button>
                )}
                {selectedCuotaIds.size > 0 && (
                  <button onClick={() => setSelectedCuotaIds(new Set())} className="text-xs text-gray-400 hover:text-gray-600 underline">
                    Limpiar
                  </button>
                )}
                {/* Botón cobrar — siempre visible, deshabilitado si nada seleccionado */}
                <button
                  onClick={() => selectedCuotaIds.size > 0 && setCobrarCuotasModal(true)}
                  disabled={selectedCuotaIds.size === 0}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-medium transition-all ${
                    selectedCuotaIds.size > 0
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-gray-100 text-gray-400 cursor-default"
                  }`}
                  title={selectedCuotaIds.size === 0 ? "Seleccioná una o más cuotas para cobrar" : `Cobrar ${selectedCuotaIds.size} cuota(s)`}
                >
                  <CheckCircle className="w-3 h-3" />
                  {selectedCuotaIds.size > 0
                    ? `Cobrar ${selectedCuotaIds.size} cuota(s) — ${fmt(montoSeleccionado)}`
                    : "Cobrar cuotas"}
                </button>
                {cuotasPendientes.length > 0 && (
                  <button
                    onClick={imprimirPagares}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg border"
                  >
                    <Printer className="w-3 h-3" /> Pagarés
                  </button>
                )}
              </div>
            </div>
            {/* Hint cuando nada está seleccionado */}
            {selectedCuotaIds.size === 0 && cuotasPendientes.length > 0 && (
              <p className="text-[11px] text-blue-500 mb-2">
                ☝️ Tildá las cuotas que querés cobrar (podés elegir una o varias a la vez)
              </p>
            )}
            {loadingCuotas ? (
              <p className="text-gray-400 text-xs">Cargando cuotas...</p>
            ) : (
              <div className="space-y-1.5">
                {cuotas.filter(c => c.estado !== "cancelado").map(c => {
                  const isVencida = c.estado === "vencido";
                  const isPagada = c.estado === "pagado";
                  const isSelected = selectedCuotaIds.has(c.id);
                  const canSelect = !isPagada;
                  return (
                    <div
                      key={c.id}
                      onClick={() => canSelect && toggleCuota(c.id)}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm cursor-pointer transition-all ${
                        isSelected ? "bg-emerald-50 border-2 border-emerald-400" :
                        isVencida ? "bg-red-50 border border-red-200 hover:border-red-300" :
                        isPagada ? "bg-green-50 border border-green-200 cursor-default" :
                        "bg-gray-50 border border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                      }`}
                    >
                      {canSelect ? (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleCuota(c.id)}
                          onClick={e => e.stopPropagation()}
                          className="w-4 h-4 flex-shrink-0 accent-emerald-600"
                        />
                      ) : (
                        <div className="w-4 h-4 flex-shrink-0" />
                      )}
                      <span className={`font-medium ${isVencida ? "text-red-700" : isPagada ? "text-green-700" : "text-gray-700"}`}>
                        Cuota {c.numero}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        isPagada ? "bg-green-100 text-green-700" :
                        isVencida ? "bg-red-100 text-red-700" :
                        "bg-amber-100 text-amber-700"
                      }`}>
                        {isPagada ? "Pagada" : isVencida ? "Vencida" : "Pendiente"}
                      </span>
                      <span className={`text-xs ${isVencida ? "text-red-600" : "text-gray-500"}`}>
                        Vence: {c.fecha_vencimiento || "—"}
                      </span>
                      <span className={`ml-auto font-semibold ${isVencida ? "text-red-700" : isPagada ? "text-green-700" : "text-gray-800"}`}>
                        {fmt(c.monto)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Modal cobrar cuotas */}
            {cobrarCuotasModal && (
              <div className="fixed inset-0 bg-black/60 z-[300] flex items-center justify-center p-4">
                <div className="bg-white rounded-xl w-full max-w-sm shadow-2xl p-6 space-y-4">
                  <h3 className="font-semibold text-gray-800">Cobrar {selectedCuotaIds.size} cuota(s)</h3>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm text-emerald-800">
                    Total a cobrar: <strong>{fmt(cuotas.filter(c => selectedCuotaIds.has(c.id)).reduce((s, c) => s + (c.monto || 0), 0))}</strong>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase mb-1 block">Fecha de pago</label>
                    <DateInput value={cobrarFecha} onChange={e => setCobrarFecha(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase mb-1 block">Cuenta bancaria</label>
                    <select value={cobrarCuentaId} onChange={e => setCobrarCuentaId(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800">
                      <option value="">Sin cuenta específica</option>
                      {renderCuentasPorMoneda(cuentasDisp, f?.logo_tipo || activeEmpresaPropia?.slug)}
                    </select>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button onClick={() => setCobrarCuotasModal(false)} className="flex-1 px-4 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg">Cancelar</button>
                    <button onClick={cobrarCuotas} disabled={cobrandoCuotas}
                      className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg disabled:opacity-60">
                      {cobrandoCuotas ? "Cobrando..." : "Confirmar"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          );
        })()}

        {/* Presupuestos vinculados */}
        {(() => {
          const allIds = [...(f.presupuesto_ids || []), ...(f.presupuesto_id ? [f.presupuesto_id] : [])];
          const unique = [...new Set(allIds)];
          const vinculados = unique.map(id => presupuestos.find(p => p.id === id)).filter(Boolean);
          if (vinculados.length === 0) return null;
          const fmtP = (n, mon) => mon === "PYG" ? `₲ ${Math.round(n).toLocaleString("es-PY")}` : `${mon} ${Number(n).toFixed(2)}`;
          return (
            <div className="px-6 pb-4">
              <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2">
                Presupuesto{vinculados.length > 1 ? "s" : ""} vinculado{vinculados.length > 1 ? "s" : ""}
              </p>
              <div className="flex flex-wrap gap-2">
                {vinculados.map(p => (
                  <button
                    key={p.id}
                    onClick={() => onPresClick && onPresClick(p.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-xs text-blue-700 transition-all"
                  >
                    <FileText className="w-3 h-3" />
                    #{p.numero}
                    {p.nombre_archivo && <span className="text-blue-400">· {p.nombre_archivo.slice(0, 18)}</span>}
                    <span className="font-semibold ml-1">{fmtP(p.total, p.moneda)}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── Hilo de notas (no se imprime) ─────────────────────────────── */}
        {(notaCreacion || notasLog.length > 0 || puedeAgregarNota || puedeEditarNota) && (
          <div className="px-6 pb-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-amber-700">📝 Notas internas</span>
                {!agregandoNota && (puedeAgregarNota || puedeEditarNota) && (
                  <button
                    onClick={() => setAgregandoNota(true)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 transition-all"
                  >
                    <Plus className="w-3 h-3" /> Agregar nota
                  </button>
                )}
              </div>

              <div className="space-y-2">
                {/* Nota de creación */}
                {notaCreacion && (
                  <div className="group relative bg-white rounded-lg p-3 border border-amber-100 shadow-sm">
                    <p className="text-gray-700 text-sm whitespace-pre-line leading-relaxed pr-7">{notaCreacion}</p>
                    <p className="text-gray-400 text-xs mt-1.5 italic">Al crear la factura</p>
                    {puedeEditarNota && (
                      <button
                        onClick={eliminarNotaCreacion}
                        disabled={deletingCreacion}
                        className="absolute top-2.5 right-2.5 p-1 rounded opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-50"
                        title="Eliminar nota"
                      >
                        {deletingCreacion
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Trash2 className="w-3 h-3" />
                        }
                      </button>
                    )}
                  </div>
                )}

                {/* Entradas del log */}
                {notasLog.map(n => (
                  <div key={n.id} className="group relative bg-white rounded-lg p-3 border border-amber-100 shadow-sm">
                    <p className="text-gray-700 text-sm whitespace-pre-line leading-relaxed pr-7">{n.texto}</p>
                    <p className="text-gray-400 text-xs mt-1.5 italic">{n.autor} · {n.fecha}</p>
                    {puedeEditarNota && (
                      <button
                        onClick={() => eliminarNota(n.id)}
                        disabled={deletingNotaId === n.id}
                        className="absolute top-2.5 right-2.5 p-1 rounded opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-50"
                        title="Eliminar nota"
                      >
                        {deletingNotaId === n.id
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Trash2 className="w-3 h-3" />
                        }
                      </button>
                    )}
                  </div>
                ))}

                {!notaCreacion && notasLog.length === 0 && !agregandoNota && (
                  <p className="text-amber-600/60 text-xs italic">Sin notas.</p>
                )}
              </div>

              {/* Formulario nueva nota */}
              {agregandoNota && (
                <div className="space-y-2 mt-3">
                  <textarea
                    value={nuevoTextoNota}
                    onChange={e => setNuevoTextoNota(e.target.value)}
                    rows={3}
                    className="w-full bg-white text-gray-800 text-sm rounded-lg p-2.5 border border-amber-300 focus:border-amber-400 focus:outline-none resize-none placeholder-gray-400 shadow-sm"
                    placeholder="Escribí la nota…"
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => { setAgregandoNota(false); setNuevoTextoNota(""); }}
                      className="px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:text-gray-700 border border-gray-300 hover:border-gray-400 bg-white transition-all"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={agregarNota}
                      disabled={savingNota || !nuevoTextoNota.trim()}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition-all disabled:opacity-50"
                    >
                      {savingNota ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      Guardar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Timbrado info */}
        {!f.sin_factura && f.nro_timbrado && (
          <div className="px-6 pb-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5 flex items-center gap-2 text-xs text-emerald-700">
              <Shield className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                <strong>Timbrado N° {f.nro_timbrado}</strong>
                {f.establecimiento && <span className="ml-2 text-emerald-500">Est. {f.establecimiento}</span>}
                {f.fecha_vigencia_timbrado && <span className="ml-2 text-emerald-500">Vigencia: {f.fecha_vigencia_timbrado}</span>}
              </span>
            </div>
          </div>
        )}
        {f.sin_factura && (
          <div className="px-6 pb-4">
            <div className="bg-violet-50 border border-violet-200 rounded-lg px-4 py-2.5 flex items-center gap-2 text-xs text-violet-600">
              <Receipt className="w-3.5 h-3.5 flex-shrink-0" />
              <span>Comprobante interno · {f.numero_boleta || f.numero} · No genera crédito fiscal IVA</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PagoModal — pago total (contado) con multi-cuentas
// ═══════════════════════════════════════════════════════════════
// Monto en la moneda de la cuenta elegida (factura en PYG cobrada a cuenta USD/BRL).
// Se edita en moneda de la cuenta y se convierte a guaraníes con el TC; el TC no se toca acá.
function AltMontoInput({ montoPYG, setMontoPYG, tc, moneda, fullPYG, readOnly = false, className = "" }) {
  const [txt, setTxt] = React.useState(null);
  const tcNum = parseFloat(tc) || 0;
  const pygNum = parseFloat(montoPYG) || 0;
  const computed = tcNum > 0 && pygNum > 0 ? (pygNum / tcNum).toFixed(2) : "";
  const onChange = (e) => {
    const v = e.target.value.replace(/[^\d.,]/g, "").replace(",", ".");
    setTxt(v);
    const n = parseFloat(v);
    if (!tcNum || !(n >= 0)) { setMontoPYG(""); return; }
    const full = parseFloat(fullPYG) || 0;
    const fullAlt = full > 0 ? full / tcNum : 0;
    if (fullAlt > 0 && Math.abs(n - fullAlt) < 0.01) { setMontoPYG(String(full)); return; }
    // Sin tope: si el cliente entrega MÁS que el total, se acepta y se calcula el vuelto.
    const gs = Math.round(n * tcNum);
    setMontoPYG(String(gs));
  };
  return (
    <input type="text" inputMode="decimal" readOnly={readOnly}
      value={txt !== null ? txt : computed}
      onChange={onChange}
      onBlur={() => setTxt(null)}
      placeholder={tcNum ? "0.00" : "Cargá el TC"}
      className={className} />
  );
}

// ── Referencia / nota / comprobante de un pago (cruce con cobros bancarios) ──
const comprimirImagenPago = (file, maxDim = 1400, quality = 0.75) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error("No se pudo leer la imagen"));
  reader.onload = () => {
    const img = new Image();
    img.onerror = () => reject(new Error("Imagen inválida"));
    img.onload = () => {
      const k = Math.min(1, maxDim / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", quality));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

function PagoReferenciaFields({ value, onChange }) {
  const v = value || {};
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Seleccioná una imagen"); return; }
    try { onChange({ ...v, imagen: await comprimirImagenPago(f) }); }
    catch (err) { toast.error(err.message || "Error con la imagen"); }
  };
  const inp = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500";
  return (
    <div className="space-y-2 border border-white/10 rounded-lg p-3 bg-white/[0.02]">
      <div>
        <label className="text-slate-400 text-xs block mb-1">N° de depósito / transferencia / comprobante <span className="text-slate-500 text-[10px]">(opcional)</span></label>
        <input type="text" maxLength={100} value={v.referencia || ""} onChange={e => onChange({ ...v, referencia: e.target.value })}
          placeholder="Ej: 123456789" className={inp} />
      </div>
      <div>
        <label className="text-slate-400 text-xs block mb-1">Nota <span className="text-slate-500 text-[10px]">(opcional)</span></label>
        <textarea rows={2} maxLength={500} value={v.nota || ""} onChange={e => onChange({ ...v, nota: e.target.value })}
          placeholder="Observaciones del pago" className={inp + " resize-none"} />
      </div>
      <div>
        <label className="text-slate-400 text-xs block mb-1">Imagen del comprobante <span className="text-slate-500 text-[10px]">(opcional)</span></label>
        {v.imagen ? (
          <div className="flex items-center gap-3">
            <img src={v.imagen} alt="Comprobante" className="h-16 w-16 object-cover rounded border border-white/10" />
            <button type="button" onClick={() => onChange({ ...v, imagen: "" })} className="text-xs text-red-400 hover:text-red-300">Quitar</button>
          </div>
        ) : (
          <input type="file" accept="image/*" onChange={onFile}
            className="block w-full text-xs text-slate-400 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-white/10 file:text-white file:text-xs hover:file:bg-white/20" />
        )}
      </div>
    </div>
  );
}

function PagoModal({ sinRetencion = false, setSinRetencion = () => {}, puedeOmitirRetencion = false, fac, pagoRef, setPagoRef, fechaPago, setFechaPago, numeroReciboManual, setNumeroReciboManual,
  cuentasDisp, cuentasPYG, setCuentasPYG, cuentasUSD, setCuentasUSD,
  tcPago, setTcPago, montoUSD, setMontoUSD, canEditTcDoc, fetchTcDelDia,
  saldoFavor, aplicarSaldoFavor, setAplicarSaldoFavor, montoSaldoAplicar, setMontoSaldoAplicar,
  vale, aplicarVale, setAplicarVale, montoValeAplicar, setMontoValeAplicar,
  onMontoItemsChange, onMontoRecibidoChange, onPagoItemsDataChange, onVueltoCuentaChange,
  procesadorasCobro = [], medioCobro = { procesadora_id: "", metodo_cobro: "" }, setMedioCobro = () => {},
  puedeCobroItems, onClose, onConfirm, fmtMonto }) {

  React.useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  // ── Pago por ítems ─────────────────────────────────────────
  const [itemsMode, setItemsMode] = React.useState(false);
  const [itemCantidades, setItemCantidades] = React.useState({});
  const [aplicarSobrante, setAplicarSobrante] = React.useState(false);
  const itemsFac = fac.conceptos || fac.items || [];

  const monedaFac = fac.moneda || "PYG";
  const total = (fac.monto || 0) - (fac.monto_pagado || 0);

  // ── Estado por ítem (qué está pagado/parcial del pagos_items guardado) ──────
  const pagosItemsMap = React.useMemo(() => {
    const m = {};
    (fac.pagos_items || []).forEach(pi => { m[pi.idx] = pi; });
    return m;
  }, [fac.pagos_items]);

  const itemInfo = itemsFac.map((item, idx) => {
    const pi = pagosItemsMap[idx] || { monto_pagado: 0, pagado: false };
    const precioUnit = item.precio_unitario != null ? item.precio_unitario
      : (item.cantidad ? (item.subtotal || 0) / item.cantidad : 0);
    const cantidad = item.cantidad || 1;
    const precioTotal = precioUnit * cantidad;
    const yaPageado = pi.monto_pagado || 0;
    const restante = Math.max(0, precioTotal - yaPageado);
    const pagado = pi.pagado || restante < 0.01;
    return { precioUnit, precioTotal, yaPageado, restante, pagado, cantidad };
  });

  // montoItems: cada ítem contribuye min(qty * precioUnit, restante)
  const montoItems = itemsFac.reduce((sum, _item, idx) => {
    const qty = Math.max(0, parseInt(itemCantidades[idx] ?? 0) || 0);
    if (qty === 0) return sum;
    const info = itemInfo[idx];
    if (info.pagado) return sum;
    return sum + Math.min(qty * info.precioUnit, info.restante);
  }, 0);

  React.useEffect(() => {
    if (onMontoItemsChange) onMontoItemsChange(itemsMode ? montoItems : null);
  }, [itemsMode, montoItems]); // eslint-disable-line

  // ── Retención de IVA — cuánto de ESTE pago se va a retener ────
  // Proporcional al monto de este pago (ver utils/retencion.py, misma regla del backend).

  // ── Monto recibido / Vuelto ─────────────────────────────────
  const montoEfectivo = itemsMode ? montoItems : total;
  // Lo que realmente debería ENTRAR a la cuenta/caja: el monto a registrar
  // menos la retención que el cliente ya se queda (no llega en efectivo).
  const retBase = retencionProporcional(fac, montoEfectivo);
  const retEnEstePago = sinRetencion ? 0 : retBase;
  const netoEsperado = montoEfectivo - retEnEstePago;
  const retEnEstePagoTotal = sinRetencion ? 0 : retencionProporcional(fac, total);
  const [montoRecibido, setMontoRecibido] = React.useState(String(Math.ceil(total - retEnEstePagoTotal)));
  // Al tildar/destildar "No aplicar retención" el neto esperado cambia: se reinicia el monto recibido.
  React.useEffect(() => { setMontoRecibido(String(Math.ceil(total - retEnEstePagoTotal))); }, [sinRetencion]); // eslint-disable-line
  const [cuentaVueltoId, setCuentaVueltoId] = React.useState(cuentasUSD[0] || cuentasPYG[0] || "");
  // Por defecto el vuelto sale de la MISMA cuenta/moneda con la que cobraste (si te pagaron en reales, vuelto en reales)
  React.useEffect(() => { const d = cuentasUSD[0] || cuentasPYG[0]; if (d) setCuentaVueltoId(d); }, [cuentasPYG, cuentasUSD]); // eslint-disable-line
  const montoRecibidoNum = parseFloat(montoRecibido) || 0;
  const vueltoBase = montoRecibidoNum > netoEsperado ? montoRecibidoNum - netoEsperado : 0;
  const esParcial = netoEsperado > 0 && montoRecibidoNum > 0 && montoRecibidoNum < netoEsperado;
  const itemsExceden = false; // ya no puede exceder: cada ítem capeado a su restante

  // ── "Aplicar sobrante al siguiente ítem" ─────────────────────
  // Siguiente ítem pendiente no seleccionado
  const siguienteItemIdx = itemsMode ? itemsFac.findIndex((_item, idx) => {
    const info = itemInfo[idx];
    const qty = parseInt(itemCantidades[idx] ?? 0) || 0;
    return !info.pagado && info.restante > 0.01 && qty === 0;
  }) : -1;
  const sobranteAplicable = siguienteItemIdx >= 0 && vueltoBase > 0.01
    ? Math.min(vueltoBase, itemInfo[siguienteItemIdx]?.restante || 0)
    : 0;
  const vuelto = aplicarSobrante && sobranteAplicable > 0
    ? Math.max(0, vueltoBase - sobranteAplicable)
    : vueltoBase;

  // ── Notificar al parent ───────────────────────────────────────
  React.useEffect(() => {
    if (onMontoRecibidoChange) onMontoRecibidoChange(montoRecibidoNum > 0 ? montoRecibidoNum : null);
  }, [montoRecibidoNum]); // eslint-disable-line
  React.useEffect(() => {
    if (onVueltoCuentaChange) onVueltoCuentaChange(vuelto > 0 ? cuentaVueltoId : "");
  }, [vuelto, cuentaVueltoId]); // eslint-disable-line

  // Construir y notificar pago_items al parent
  React.useEffect(() => {
    if (!onPagoItemsDataChange) return;
    if (!itemsMode) { onPagoItemsDataChange(null); return; }
    const arr = itemsFac.reduce((acc, _item, idx) => {
      const qty = Math.max(0, parseInt(itemCantidades[idx] ?? 0) || 0);
      if (qty === 0) return acc;
      const info = itemInfo[idx];
      if (info.pagado) return acc;
      const monto = Math.min(qty * info.precioUnit, info.restante);
      if (monto > 0.01) acc.push({ idx, monto });
      return acc;
    }, []);
    // Agregar sobrante al siguiente ítem si aplica
    if (aplicarSobrante && siguienteItemIdx >= 0 && sobranteAplicable > 0.01) {
      arr.push({ idx: siguienteItemIdx, monto: sobranteAplicable });
    }
    onPagoItemsDataChange(arr.length > 0 ? arr : null);
  }, [itemsMode, itemCantidades, aplicarSobrante, montoRecibidoNum]); // eslint-disable-line

  // Cuentas en la misma moneda que la factura (primarias)
  const cuentasMoneda = cuentasDisp.filter(c => c.activa !== false && c.logo_tipo === fac.logo_tipo && c.moneda === monedaFac);
  // Cuentas en moneda diferente (secundarias para conversión)
  const cuentasOtraMoneda = cuentasDisp.filter(c => c.activa !== false && c.logo_tipo === fac.logo_tipo && c.moneda !== monedaFac);
  const monedaLabels = { PYG: "Guaraníes", USD: "Dólares", BRL: "Reales", ARS: "Pesos" };
  const monedasOrden = ["PYG", "USD", "BRL", "ARS", ...Array.from(new Set(cuentasOtraMoneda.map(c => c.moneda || "PYG"))).filter(m => !["PYG", "USD", "BRL", "ARS"].includes(m))];
  const gruposOtraMoneda = monedasOrden
    .filter(moneda => moneda !== monedaFac)
    .map(moneda => ({ moneda, cuentas: cuentasOtraMoneda.filter(c => (c.moneda || "PYG") === moneda) }))
    .filter(g => g.cuentas.length > 0);

  // La cuenta seleccionada actualmente (cuentasPYG = [id] del principal)
  const cuentaSeleccionadaId = cuentasPYG[0] || null;
  const cuentaSeleccionada = cuentasDisp.find(c => c.id === cuentaSeleccionadaId);

  // Mostrar TC solo si la cuenta seleccionada tiene moneda diferente a la factura
  // O si hay cuentas alternativas de otra moneda seleccionadas (cuentasUSD)
  const cuentaAltSeleccionadaId = cuentasUSD[0] || null;
  const hasCurrencyMismatch = (cuentaSeleccionada && cuentaSeleccionada.moneda !== monedaFac) ||
                               !!cuentaAltSeleccionadaId;
  // Moneda real de la cuenta alternativa elegida (USD o BRL) — antes las etiquetas
  // de esta sección decían "USD" siempre, aunque la cuenta marcada fuera en BRL.
  const monedaAlt = cuentasDisp.find(c => c.id === cuentaAltSeleccionadaId)?.moneda || "USD";
  const monedaTc = monedaFac === "PYG" ? monedaAlt : monedaFac;

  // Mantener "Equivalente" sincronizado con el TC ingresado de forma reactiva.
  // Antes el cálculo vivía solo dentro del onChange del campo TC, así que si
  // el monto (total) todavía no estaba disponible en ese instante, o el campo
  // se completaba por otra vía (auto-fill de cotización), el equivalente
  // podía quedar en 0 aunque el TC ya estuviera cargado.
  // Base del equivalente: lo que realmente se cobra (monto recibido) si la factura es en PYG
  const baseTc = monedaFac === "PYG" ? (montoRecibidoNum > 0 ? Math.min(montoRecibidoNum, total) : total) : total;
  React.useEffect(() => {
    if (!tcPago || !baseTc) return;
    const tc = parseFloat(tcPago);
    if (!tc) return;
    setMontoUSD(monedaFac === "PYG"
      ? String((baseTc / tc).toFixed(2))
      : String((baseTc * tc).toFixed(0)));
  }, [tcPago, baseTc, monedaFac]); // eslint-disable-line

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[400] overflow-y-auto p-4">
      <div className="flex min-h-full items-center justify-center" onClick={onClose}>
      <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-sm my-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <h2 className="text-white font-heading font-bold">Registrar pago</h2>
            <p className="text-slate-400 text-sm mt-0.5">Factura {fac.numero} — {fac.empresa_nombre || fac.razon_social}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Monto / toggle pago por ítems */}
          {itemsFac.length > 0 && puedeCobroItems ? (
            <div className="space-y-2">
              <div className={`border rounded-lg p-3 text-center transition-all ${itemsMode ? "bg-violet-500/10 border-violet-500/30" : "bg-emerald-500/10 border-emerald-500/20"}`}>
                <p className={`font-heading font-bold text-xl ${itemsMode ? "text-violet-300" : "text-emerald-300"}`}>
                  {fmtMonto(itemsMode ? montoItems : total, monedaFac)}
                </p>
                <p className="text-slate-500 text-xs mt-0.5">
                  {itemsMode ? `${Object.values(itemCantidades).reduce((s,v)=>s+(parseInt(v)||0),0)} ítem(s) seleccionado(s)` : (monedaFac === "PYG" ? "Al contado" : `En ${monedaFac}`)}
                </p>
                {itemsMode && montoItems < total && (
                  <p className="text-amber-400 text-[10px] mt-1">⚠ Pago parcial — saldo restante: {fmtMonto(total - montoItems, monedaFac)}</p>
                )}
                {itemsExceden && (
                  <p className="text-red-400 text-[10px] mt-1">✕ Los ítems superan el saldo pendiente ({fmtMonto(total, monedaFac)})</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => { setItemsMode(v => !v); setItemCantidades({}); }}
                style={{ color: "#ffffff" }}
                className={`w-full text-sm font-semibold py-2 rounded-lg border-2 shadow-md transition-all ${itemsMode ? "border-violet-300 bg-violet-800 ring-2 ring-violet-400/60" : "border-violet-400 bg-violet-600 hover:bg-violet-500"}`}
              >
                {itemsMode ? "✓ Modo: Pago por ítems — click para volver al modo normal" : "🍽 Pago por ítems (restaurante)"}
              </button>
              {itemsMode && (
                <div className="border border-white/10 rounded-xl overflow-hidden">
                  <div className="bg-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex justify-between">
                    <span>Ítem</span><span>Estado / Seleccionar</span>
                  </div>
                  <div className="divide-y divide-white/5 max-h-56 overflow-y-auto">
                    {itemsFac.map((item, idx) => {
                      const info = itemInfo[idx];
                      const maxQty = parseInt(item.cantidad) || 1;
                      // Para ítems con pago parcial previo: máx qty ajustado
                      const maxQtyEfectivo = info.pagado ? 0
                        : info.yaPageado > 0 ? 1  // ítem parcial: solo 1 unidad (= el restante)
                        : maxQty;
                      const qty = Math.max(0, Math.min(maxQtyEfectivo, parseInt(itemCantidades[idx] ?? 0) || 0));
                      const montoEfectivoItem = info.pagado ? 0
                        : info.yaPageado > 0 ? info.restante  // precio efectivo = restante
                        : qty * info.precioUnit;

                      if (info.pagado) {
                        return (
                          <div key={idx} className="flex items-center gap-3 px-3 py-2.5 bg-emerald-500/5 opacity-70">
                            <div className="flex-1 min-w-0">
                              <p className="text-emerald-300 text-xs font-medium truncate line-through">{item.descripcion || item.nombre || `Ítem ${idx+1}`}</p>
                              <p className="text-emerald-600 text-[10px]">✓ Pagado — {fmtMonto(info.precioTotal, monedaFac)}</p>
                            </div>
                            <span className="text-emerald-500 text-[10px] font-semibold">PAGADO</span>
                          </div>
                        );
                      }

                      if (info.yaPageado > 0) {
                        // Ítem parcialmente pagado — mostrar lo que resta
                        const seleccionado = qty > 0;
                        return (
                          <div key={idx} className={`flex items-center gap-3 px-3 py-2.5 transition-all ${seleccionado ? "bg-violet-500/10" : ""}`}>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-xs font-medium truncate">{item.descripcion || item.nombre || `Ítem ${idx+1}`}</p>
                              <p className="text-amber-400 text-[10px]">Parcial — ya pagado {fmtMonto(info.yaPageado, monedaFac)} · resta {fmtMonto(info.restante, monedaFac)}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button type="button"
                                onClick={() => setItemCantidades(p => ({...p, [idx]: seleccionado ? 0 : 1}))}
                                className={`px-2 py-1 rounded text-[10px] font-semibold border transition-all ${seleccionado ? "border-violet-400 bg-violet-500/20 text-violet-300" : "border-white/20 text-slate-400 hover:border-violet-400 hover:text-violet-300"}`}
                              >
                                {seleccionado ? "✓ Sel." : "Pagar"}
                              </button>
                              <span className="text-violet-300 text-xs w-20 text-right">{seleccionado ? fmtMonto(info.restante, monedaFac) : "—"}</span>
                            </div>
                          </div>
                        );
                      }

                      // Ítem pendiente normal — selector de cantidad
                      return (
                        <div key={idx} className="flex items-center gap-3 px-3 py-2.5">
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-xs font-medium truncate">{item.descripcion || item.nombre || `Ítem ${idx+1}`}</p>
                            <p className="text-slate-500 text-[10px]">{fmtMonto(info.precioUnit, monedaFac)} c/u · {maxQty} en factura</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button type="button" onClick={() => setItemCantidades(p => ({...p, [idx]: Math.max(0, (parseInt(p[idx])||0)-1)}))}
                              className="w-6 h-6 rounded-full bg-white/10 text-white text-sm flex items-center justify-center hover:bg-white/20">−</button>
                            <input
                              type="number" min="0" max={maxQtyEfectivo} value={itemCantidades[idx] ?? 0}
                              onChange={e => setItemCantidades(p => ({...p, [idx]: Math.min(maxQtyEfectivo, Math.max(0, parseInt(e.target.value)||0))}))}
                              className="w-10 text-center bg-arandu-dark border border-white/10 rounded text-white text-xs py-1 focus:outline-none focus:border-violet-500"
                            />
                            <button type="button" onClick={() => setItemCantidades(p => ({...p, [idx]: Math.min(maxQtyEfectivo, (parseInt(p[idx])||0)+1)}))}
                              className="w-6 h-6 rounded-full bg-white/10 text-white text-sm flex items-center justify-center hover:bg-white/20">+</button>
                            <span className="text-violet-300 text-xs w-20 text-right">{qty > 0 ? fmtMonto(montoEfectivoItem, monedaFac) : "—"}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="bg-white/5 px-3 py-2 flex justify-between items-center">
                    <button type="button"
                      onClick={() => setItemCantidades(Object.fromEntries(
                        itemsFac.map((_,i) => [i, itemInfo[i].pagado ? 0 : itemInfo[i].yaPageado > 0 ? 1 : (parseInt(itemsFac[i].cantidad)||1)])
                      ))}
                      className="text-[10px] text-slate-400 hover:text-white">Seleccionar pendientes</button>
                    <p className="text-violet-300 text-xs font-bold">{fmtMonto(montoItems, monedaFac)}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 text-center">
              <p className="text-emerald-300 font-heading font-bold text-xl">{fmtMonto(total, monedaFac)}</p>
              <p className="text-slate-500 text-xs mt-0.5">{monedaFac === "PYG" ? "Al contado" : `En ${monedaFac}`}</p>
            </div>
          )}
          {/* Desglose de Retención de IVA — informativo. Lo que se registra en el
              sistema sigue siendo el monto bruto de la factura; esto solo muestra
              cuánto va a quedar retenido y cuál es el saldo neto que realmente
              va a ingresar al banco. */}
          {fac.retencion_info && puedeOmitirRetencion && retBase > 0 && (
            <label className="flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-slate-300 cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={sinRetencion} onChange={e => setSinRetencion(e.target.checked)} />
              <span>No aplicar retención en este pago (el cliente pagó el importe completo, sin descontar la retención)</span>
            </label>
          )}
          {fac.retencion_info && montoEfectivo > 0 && retEnEstePago > 0 && (
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 space-y-1 text-xs">
              <div className="flex justify-between text-slate-300">
                <span>Monto a registrar</span><span>{fmtMonto(montoEfectivo, monedaFac)}</span>
              </div>
              <div className="flex justify-between text-amber-300">
                <span>Retención IVA ({fac.retencion_info.pct}%)</span><span>− {fmtMonto(retEnEstePago, monedaFac)}</span>
              </div>
              <div className="flex justify-between text-emerald-300 font-semibold border-t border-white/10 pt-1">
                <span>Saldo neto a recibir</span><span>{fmtMonto(netoEsperado, monedaFac)}</span>
              </div>
            </div>
          )}
          <MedioCobroSelector procesadoras={procesadorasCobro} value={medioCobro} onChange={setMedioCobro}
            monto={(netoEsperado || 0) + (retEnEstePago || 0)} fmt={(n) => fmtMonto(n, monedaFac)} />
          <div>
            <label className="text-slate-400 text-xs block mb-1">Fecha de pago *</label>
            <DateInput value={fechaPago}
              onChange={e => setFechaPago(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500 disabled:opacity-60"
            />
          </div>
          {fac.forma_pago !== "contado" && (
          <div>
            <label className="text-slate-400 text-xs block mb-1">Nº Recibo <span className="text-slate-500 text-[10px]">(opcional — autogenera si vacío)</span></label>
            <input type="text" value={numeroReciboManual} onChange={e => setNumeroReciboManual(e.target.value)}
              placeholder="000123"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500"
            />
          </div>
          )}
          <PagoReferenciaFields value={pagoRef} onChange={setPagoRef} />
          {/* Cuentas bancarias — en moneda de la factura (primarias) */}
          {cuentasMoneda.length > 0 && (
            <div>
              <label className="text-slate-400 text-xs block mb-1">
                Cuenta bancaria {monedaLabels[monedaFac] || monedaFac} ({monedaFac}) <span className="text-red-400">*</span>
              </label>
              <div className="space-y-1 max-h-28 overflow-y-auto pr-1">
                {cuentasMoneda.map(c => (
                  <label key={c.id} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-white/5 transition-all">
                    <input type="radio" name="cuenta-pago" checked={cuentasPYG.includes(c.id)}
                      onChange={() => {
                        setCuentasPYG([c.id]);
                        setCuentasUSD([]);
                        if (monedaFac === "PYG") { setTcPago(""); setMontoUSD(""); }
                      }}
                      className="accent-emerald-500 w-3.5 h-3.5"
                    />
                    <span className="text-white text-xs flex-1">{c.nombre}{c.banco ? ` · ${c.banco}` : ""}</span>
                    {c.es_predeterminada && <span className="text-amber-400 text-[10px]">★ predeterminada</span>}
                  </label>
                ))}
              </div>
              {cuentasPYG.length === 0 && cuentasUSD.length === 0 && (
                <p className="text-red-400 text-[11px] mt-1">⚠ Seleccioná una cuenta bancaria para continuar</p>
              )}
            </div>
          )}
          {/* Cuentas en moneda diferente — opcional, activa TC */}
          {gruposOtraMoneda.map(({ moneda, cuentas }) => (
            <div key={moneda}>
              <label className="text-slate-400 text-xs block mb-1">
                Cuenta bancaria {monedaLabels[moneda] || moneda} ({moneda}) <span className="text-slate-500 text-[10px]">(opcional — requiere tipo de cambio)</span>
              </label>
              <div className="space-y-1 max-h-24 overflow-y-auto pr-1">
                {cuentas.map(c => (
                  <label key={c.id} className="flex items-center gap-2 cursor-pointer p-1.5 rounded-lg hover:bg-white/5 transition-all">
                    <input type="radio" name="cuenta-pago" checked={cuentasUSD.includes(c.id)}
                      onChange={() => {
                        setCuentasUSD([c.id]);
                        setCuentasPYG([]);
                        if (monedaFac === "PYG") {
                          // Factura en guaraníes, cobrando en la moneda de esta cuenta (USD/BRL):
                          // traer el TC configurado en Financiero → Cotizaciones para esa moneda.
                          // Antes esto solo se auto-completaba cuando la FACTURA (no la cuenta)
                          // estaba en moneda extranjera, así que acá siempre quedaba vacío.
                          setTcPago("");
                          fetchTcDelDia(c.moneda, fechaPago).then(tc => { if (tc) setTcPago(String(tc)); });
                        }
                      }}
                      className="accent-blue-500 w-3.5 h-3.5"
                    />
                    <span className="text-white text-xs flex-1">{c.nombre}{c.banco ? ` · ${c.banco}` : ""}</span>
                    {c.es_predeterminada && <span className="text-amber-400 text-[10px]">★ predeterminada</span>}
                  </label>
                ))}
              </div>
            </div>
          ))}
          {/* Tipo de cambio — solo si hay una cuenta de moneda diferente seleccionada */}
          {hasCurrencyMismatch && cuentaAltSeleccionadaId && (
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 space-y-2">
              <p className="text-blue-300 text-xs font-medium">
                Tipo de cambio ({monedaFac} ↔ cuenta seleccionada)
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-slate-400 text-[10px] block mb-1">
                    TC (1 {monedaTc} = ? PYG)
                  </label>
                  <input type="number" value={tcPago} onChange={e => {
                    if (!canEditTcDoc) return;
                    setTcPago(e.target.value);
                  }}
                    placeholder="0"
                    readOnly={!canEditTcDoc}
                    title={!canEditTcDoc ? "El tipo de cambio se configura en Financiero → Cotizaciones" : undefined}
                    className={`w-full bg-arandu-dark border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-blue-400${!canEditTcDoc ? " opacity-60 cursor-not-allowed" : ""}`}
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-[10px] block mb-1">
                    {monedaFac === "PYG" ? `Equivalente ${monedaAlt}` : "Equivalente PYG"}
                  </label>
                  <input type="number" value={montoUSD} onChange={e => {
                    if (monedaFac === "PYG" || !canEditTcDoc) return;
                    setMontoUSD(e.target.value);
                    if (e.target.value && total) {
                      const eq = parseFloat(e.target.value);
                      setTcPago(String((eq / total).toFixed(2)));
                    }
                  }}
                    placeholder="0"
                    readOnly={monedaFac === "PYG" || !canEditTcDoc}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-blue-400 opacity-60 cursor-not-allowed"
                  />
                </div>
              </div>
              {tcPago && total && (
                <p className="text-blue-300 text-xs">
                  {monedaFac === "PYG"
                    ? `₲ ${Math.round(baseTc).toLocaleString("es-PY")} ÷ TC ${tcPago} = ${monedaAlt} ${(baseTc / parseFloat(tcPago)).toFixed(2)}`
                    : `${monedaFac} ${total} × TC ${tcPago} = ₲ ${Math.round(total * parseFloat(tcPago)).toLocaleString("es-PY")}`}
                </p>
              )}
            </div>
          )}
          {/* Banner saldo a favor */}
          {saldoFavor && (saldoFavor.saldos || []).some(s => s.monto_disponible > 0) && (() => {
            const sf = (saldoFavor.saldos || []).find(s => s.moneda === (fac.moneda || "PYG"));
            if (!sf || sf.monto_disponible <= 0) return null;
            return (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-amber-300 text-xs font-semibold">⭐ Saldo a favor del cliente</p>
                  <span className="text-amber-200 text-xs font-bold">{fmtMonto(sf.monto_disponible, sf.moneda)}</span>
                </div>
                <p className="text-slate-400 text-[11px]">{fac.empresa_nombre || fac.razon_social} tiene un crédito disponible. ¿Desea aplicarlo?</p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={aplicarSaldoFavor} onChange={e => setAplicarSaldoFavor(e.target.checked)} className="accent-amber-500 w-3.5 h-3.5" />
                  <span className="text-amber-200 text-xs">Aplicar saldo a favor al pago</span>
                </label>
                {aplicarSaldoFavor && (
                  <div>
                    <label className="text-slate-400 text-[10px] block mb-1">Monto a aplicar</label>
                    <MoneyInput min="0" max={Math.min(sf.monto_disponible, total)} step="any" moneda={sf.moneda || monedaFac || "PYG"}
                      value={montoSaldoAplicar}
                      onChange={value => setMontoSaldoAplicar(value)}
                      className="w-full bg-arandu-dark border border-amber-500/30 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-amber-400"
                    />
                    {parseFloat(montoSaldoAplicar) > 0 && (
                      <p className="text-amber-300 text-[11px] mt-1">
                        Saldo restante después: {fmtMonto(sf.monto_disponible - Math.min(parseFloat(montoSaldoAplicar) || 0, sf.monto_disponible), sf.moneda)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          {/* Banner vale de cliente (crédito cargado vía Ingresos) */}
          {vale && (vale.vales || []).length > 0 && (
            <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-violet-300 text-xs font-semibold">🎟 Vale de cliente disponible</p>
                <span className="text-violet-200 text-xs font-bold">{fmtMonto(vale.totalDisponible, fac.moneda || "PYG")}</span>
              </div>
              <p className="text-slate-400 text-[11px]">{fac.empresa_nombre || fac.razon_social} tiene crédito de vale disponible. ¿Desea aplicarlo?</p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={aplicarVale} onChange={e => setAplicarVale(e.target.checked)} className="accent-violet-500 w-3.5 h-3.5" />
                <span className="text-violet-200 text-xs">Aplicar vale al pago</span>
              </label>
              {aplicarVale && (
                <div>
                  <label className="text-slate-400 text-[10px] block mb-1">Monto a aplicar</label>
                  <MoneyInput min="0" max={Math.min(vale.totalDisponible, total)} step="any" moneda={fac.moneda || "PYG"}
                    value={montoValeAplicar}
                    onChange={value => setMontoValeAplicar(value)}
                    className="w-full bg-arandu-dark border border-violet-500/30 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-violet-400"
                  />
                  {parseFloat(montoValeAplicar) > 0 && (
                    <p className="text-violet-300 text-[11px] mt-1">
                      Vale restante después: {fmtMonto(vale.totalDisponible - Math.min(parseFloat(montoValeAplicar) || 0, vale.totalDisponible), fac.moneda || "PYG")}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Monto recibido / Vuelto */}
          {monedaFac === "PYG" && montoEfectivo > 0 && (
            <div className="space-y-2">
              <div>
                <label className="text-slate-400 text-xs block mb-1">
                  Monto recibido del cliente{hasCurrencyMismatch && cuentaAltSeleccionadaId ? ` (${monedaAlt})` : ""}
                </label>
                {hasCurrencyMismatch && cuentaAltSeleccionadaId ? (
                  <>
                    <AltMontoInput
                      montoPYG={montoRecibido} setMontoPYG={setMontoRecibido}
                      tc={tcPago} moneda={monedaAlt}
                      fullPYG={Math.ceil(total - retEnEstePagoTotal)}
                      className={`w-full bg-white/5 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none ${esParcial ? "border-amber-500/60 focus:border-amber-400" : "border-white/10 focus:border-emerald-500"}`}
                    />
                    {montoRecibidoNum > 0 && <p className="text-slate-400 text-[11px] mt-1">Equivale a {fmtMonto(montoRecibidoNum, monedaFac)}</p>}
                  </>
                ) : (
                <MoneyInput
                  min="0" step="1000"
                  moneda={monedaFac}
                  value={montoRecibido}
                  onChange={value => setMontoRecibido(value)}
                  className={`w-full bg-white/5 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none ${esParcial ? "border-amber-500/60 focus:border-amber-400" : "border-white/10 focus:border-emerald-500"}`}
                />
                )}
                {esParcial && (
                  <p className="text-amber-400 text-xs mt-1">⚠ Pago parcial — quedará pendiente {fmtMonto(netoEsperado - montoRecibidoNum, monedaFac)}</p>
                )}
              </div>
              {vueltoBase > 0 && (
                <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-teal-300 text-xs font-semibold">💰 Vuelto a dar al cliente</p>
                    <div className="text-right">
                      <p className="text-teal-200 font-bold text-xl">
                        {hasCurrencyMismatch && cuentaAltSeleccionadaId && parseFloat(tcPago) > 0
                          ? fmtMonto(Math.round(vuelto / parseFloat(tcPago) * 100) / 100, monedaAlt)
                          : fmtMonto(vuelto, monedaFac)}
                      </p>
                      {hasCurrencyMismatch && cuentaAltSeleccionadaId && parseFloat(tcPago) > 0 && (
                        <p className="text-teal-400/80 text-[11px]">equivale a {fmtMonto(vuelto, monedaFac)}</p>
                      )}
                    </div>
                  </div>
                  <p className="text-teal-400/90 text-[11px]">El cliente entregó más que el total: hay que devolverle la diferencia. Queda registrado como vuelto (egreso) del cobro.</p>
                  {/* Toggle: aplicar sobrante al siguiente ítem sin pagar */}
                  {itemsMode && siguienteItemIdx >= 0 && sobranteAplicable > 0.01 && (() => {
                    const sigItem = itemsFac[siguienteItemIdx];
                    const sigInfo = itemInfo[siguienteItemIdx];
                    return (
                      <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all">
                        <input type="checkbox" checked={aplicarSobrante}
                          onChange={e => setAplicarSobrante(e.target.checked)}
                          className="accent-violet-500 w-3.5 h-3.5 mt-0.5 flex-shrink-0"
                        />
                        <div className="text-[10px] leading-relaxed">
                          <span className="text-violet-300 font-semibold">Aplicar {fmtMonto(sobranteAplicable, monedaFac)} al siguiente ítem</span>
                          <br/>
                          <span className="text-slate-400">
                            "{sigItem.descripcion || sigItem.nombre || `Ítem ${siguienteItemIdx+1}`}"
                            {sigInfo.yaPageado > 0
                              ? ` (resta ${fmtMonto(sigInfo.restante, monedaFac)})`
                              : ` (${fmtMonto(sigInfo.precioTotal, monedaFac)})`}
                            {sobranteAplicable < sigInfo.restante
                              ? ` — quedará pendiente ${fmtMonto(sigInfo.restante - sobranteAplicable, monedaFac)}`
                              : " — queda pagado"}
                          </span>
                        </div>
                      </label>
                    );
                  })()}
                  {vuelto > 0 && (
                    <div>
                      <label className="text-slate-400 text-[10px] block mb-1">Cuenta desde la que se da el vuelto</label>
                      <select
                        value={cuentaVueltoId}
                        onChange={e => setCuentaVueltoId(e.target.value)}
                        className="w-full bg-arandu-dark border border-teal-500/30 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-teal-400"
                      >
                        <option value="">— Sin especificar —</option>
                        {cuentasDisp.filter(c => c.logo_tipo === fac.logo_tipo && c.activa !== false && c.moneda === (hasCurrencyMismatch && cuentaAltSeleccionadaId ? monedaAlt : monedaFac)).map(c => (
                          <option key={c.id} value={c.id}>
                            {c.nombre}{c.banco ? ` · ${c.banco}` : ""}{c.es_predeterminada ? " ★" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg text-sm transition-all">
              Cancelar
            </button>
            {(() => {
              const sinCuenta = (cuentasMoneda.length > 0 || cuentasUSD.length > 0) && cuentasPYG.length === 0 && cuentasUSD.length === 0;
              const bloqueado = itemsExceden || sinCuenta;
              return (
                <button
                  onClick={onConfirm}
                  disabled={bloqueado}
                  className={`flex-1 px-4 py-2.5 text-white rounded-lg text-sm font-medium transition-all ${bloqueado ? "bg-slate-600 cursor-not-allowed opacity-60" : "bg-emerald-600 hover:bg-emerald-700"}`}
                >
                  {esParcial ? "Registrar pago parcial" : vuelto > 0 ? "Confirmar y dar vuelto" : "Confirmar y emitir recibo"}
                </button>
              );
            })()}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PagoParcialModal — pago parcial (crédito) con multi-cuentas
// ═══════════════════════════════════════════════════════════════
function PagoParcialModal({ sinRetencion = false, setSinRetencion = () => {}, puedeOmitirRetencion = false, onVueltoCuentaChange, pagoRef, setPagoRef, procesadorasCobro = [], medioCobro = { procesadora_id: "", metodo_cobro: "" }, setMedioCobro = () => {}, descuentoPago = "", setDescuentoPago = () => {}, puedeDescuentoPago = false, fac, cuotasFac = [], selectedCuotaIds, setSelectedCuotaIds, montoParcial, setMontoParcial, fechaPagoParcial, setFechaPagoParcial,
  numeroReciboManual, setNumeroReciboManual, cuentasDisp, cuentasPYG, setCuentasPYG,
  cuentasUSD, setCuentasUSD, tcPago, setTcPago, montoUSD, setMontoUSD, canEditTcDoc, fetchTcDelDia,
  saldoFavor, aplicarSaldoFavor, setAplicarSaldoFavor, montoSaldoAplicar, setMontoSaldoAplicar,
  vale, aplicarVale, setAplicarVale, montoValeAplicar, setMontoValeAplicar,
  puedeCobroItems, onClose, onConfirm, fmtMonto }) {

  React.useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  // ── Selección de cuotas ────────────────────────────────────
  // selectedCuotaIds/setSelectedCuotaIds vienen del padre (VentasPage) para poder
  // enviarse explícitos al backend en el submit — no se adivinan por monto.
  const cuotasPend = cuotasFac.filter(c => c.estado !== "pagado" && c.estado !== "cancelado");
  const cuotasMode = selectedCuotaIds.size > 0;

  const toggleCuota = (id) => {
    setSelectedCuotaIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const montoCuotasSelec = cuotasPend.filter(c => selectedCuotaIds.has(c.id)).reduce((s, c) => s + (c.monto || 0), 0);

  // Auto-fill monto cuando se seleccionan cuotas
  const descuentoNum = Math.max(0, parseFloat(descuentoPago) || 0);
  React.useEffect(() => {
    if (cuotasMode) setMontoParcial(String(Math.max(0, Math.round(montoCuotasSelec - descuentoNum))));
  }, [cuotasMode, montoCuotasSelec, descuentoNum]); // eslint-disable-line

  // ── Pago por ítems ─────────────────────────────────────────
  const [itemsMode, setItemsMode] = React.useState(false);
  const [itemCantidades, setItemCantidades] = React.useState({});
  const itemsFac = fac.conceptos || fac.items || [];

  const montoItems = itemsFac.reduce((sum, item, idx) => {
    const qty = Math.max(0, parseInt(itemCantidades[idx] ?? 0) || 0);
    const precioUnit = item.precio_unitario != null ? item.precio_unitario
      : (item.cantidad ? (item.subtotal || 0) / item.cantidad : 0);
    return sum + qty * precioUnit;
  }, 0);

  React.useEffect(() => {
    if (itemsMode) setMontoParcial(String(Math.round(montoItems)));
  }, [itemsMode, montoItems]); // eslint-disable-line

  const monedaFac = fac.moneda || "PYG";
  const pendiente = (fac.monto || 0) - (fac.monto_pagado || 0);
  const nuevoMonto = parseFloat(montoParcial) || 0;
  const quedaria = pendiente - nuevoMonto - descuentoNum;
  const pagoExcede = nuevoMonto > 0 && (nuevoMonto + descuentoNum) > pendiente;
  const vueltoParcial = pagoExcede ? (nuevoMonto + descuentoNum) - pendiente : 0;
  // Cuotas tildadas que suman más que el saldo (interés de una factura anterior al nuevo esquema)
  const difCuotasVsSaldo = cuotasMode ? Math.round((montoCuotasSelec - pendiente) * 100) / 100 : 0;

  const [cuentaVueltoIdParcial, setCuentaVueltoIdParcial] = React.useState(cuentasUSD[0] || cuentasPYG[0] || "");
  React.useEffect(() => { const d = cuentasUSD[0] || cuentasPYG[0]; if (d) setCuentaVueltoIdParcial(d); }, [cuentasPYG, cuentasUSD]);
  React.useEffect(() => {
    if (onVueltoCuentaChange) onVueltoCuentaChange(vueltoParcial > 0 ? cuentaVueltoIdParcial : "");
  }, [vueltoParcial, cuentaVueltoIdParcial]); // eslint-disable-line

  // Cuentas en la misma moneda que la factura (primarias)
  const cuentasMoneda = cuentasDisp.filter(c => c.activa !== false && c.logo_tipo === fac.logo_tipo && c.moneda === monedaFac);
  // Cuentas en moneda diferente (secundarias)
  const cuentasOtraMoneda = cuentasDisp.filter(c => c.activa !== false && c.logo_tipo === fac.logo_tipo && c.moneda !== monedaFac);
  const monedaLabels = { PYG: "Guaraníes", USD: "Dólares", BRL: "Reales", ARS: "Pesos" };
  const monedasOrden = ["PYG", "USD", "BRL", "ARS", ...Array.from(new Set(cuentasOtraMoneda.map(c => c.moneda || "PYG"))).filter(m => !["PYG", "USD", "BRL", "ARS"].includes(m))];
  const gruposOtraMoneda = monedasOrden
    .filter(moneda => moneda !== monedaFac)
    .map(moneda => ({ moneda, cuentas: cuentasOtraMoneda.filter(c => (c.moneda || "PYG") === moneda) }))
    .filter(g => g.cuentas.length > 0);

  const cuentaAltSeleccionadaId = cuentasUSD[0] || null;
  const hasCurrencyMismatch = !!cuentaAltSeleccionadaId;
  const monedaAlt = cuentasDisp.find(c => c.id === cuentaAltSeleccionadaId)?.moneda || "USD";
  const monedaTc = monedaFac === "PYG" ? monedaAlt : monedaFac;

  // Mantener "Equivalente" sincronizado con el TC ingresado de forma reactiva
  // (ver mismo fix en PagoModal — antes solo se calculaba dentro del onChange
  // del campo TC y podía quedar en 0 si el monto todavía no estaba listo).
  React.useEffect(() => {
    if (!tcPago || !nuevoMonto) return;
    const tc = parseFloat(tcPago);
    if (!tc) return;
    setMontoUSD(monedaFac === "PYG"
      ? String((nuevoMonto / tc).toFixed(2))
      : String((nuevoMonto * tc).toFixed(0)));
  }, [tcPago, nuevoMonto, monedaFac]); // eslint-disable-line

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[400] flex items-center justify-center p-4">
      <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-sm max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <h2 className="text-white font-heading font-bold">Registrar pago parcial</h2>
            <p className="text-slate-400 text-sm mt-0.5">Factura {fac.numero} — {fac.empresa_nombre || fac.razon_social}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Resumen */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Total factura</span>
              <span className="text-white font-semibold">{fmtMonto(fac.monto, monedaFac)}</span>
            </div>
            {(fac.monto_pagado || 0) > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Ya abonado</span>
                <span className="text-blue-300">{fmtMonto(fac.monto_pagado, monedaFac)}</span>
              </div>
            )}
            <div className="flex justify-between text-xs border-t border-white/10 pt-1">
              <span className="text-slate-400">Saldo pendiente</span>
              <span className="text-amber-300 font-semibold">{fmtMonto(pendiente, monedaFac)}</span>
            </div>
          </div>
          {/* Desglose de Retención de IVA — informativo. Proporcional al pago. */}
          {fac.retencion_info && nuevoMonto > 0 && (() => {
            const retBase = retencionProporcional(fac, nuevoMonto);
            const retPrevista = sinRetencion ? 0 : retBase;
            if (retBase <= 0) return null;
            return (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 space-y-1 text-xs">
                {puedeOmitirRetencion && (
                  <label className="flex items-start gap-2 text-slate-300 cursor-pointer pb-1">
                    <input type="checkbox" className="mt-0.5" checked={sinRetencion} onChange={e => setSinRetencion(e.target.checked)} />
                    <span>No aplicar retención en este pago (el cliente pagó el importe completo)</span>
                  </label>
                )}
                <div className="flex justify-between text-slate-300">
                  <span>Monto a registrar</span><span>{fmtMonto(nuevoMonto, monedaFac)}</span>
                </div>
                <div className="flex justify-between text-amber-300">
                  <span>Retención IVA ({fac.retencion_info.pct}%)</span><span>− {fmtMonto(retPrevista, monedaFac)}</span>
                </div>
                <div className="flex justify-between text-emerald-300 font-semibold border-t border-white/10 pt-1">
                  <span>Saldo neto a recibir</span><span>{fmtMonto(nuevoMonto - retPrevista, monedaFac)}</span>
                </div>
              </div>
            );
          })()}
          {/* Historial de pagos anteriores */}
          {(fac.pagos || []).length > 0 && (
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide mb-2">Pagos anteriores</p>
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {(fac.pagos || []).map((p, i) => (
                  <div key={p.id || i} className="flex justify-between bg-white/5 rounded-lg px-2 py-1.5 text-xs">
                    <span className="text-slate-300">Pago {i + 1}{p.fecha ? ` · ${p.fecha}` : ""}</span>
                    <span className="text-blue-300">{fmtMonto(p.monto, monedaFac)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* ── Panel de cuotas ── */}
          {cuotasPend.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-slate-400 text-xs uppercase tracking-wide font-semibold">Cuotas pendientes</p>
                <button type="button"
                  onClick={() => {
                    if (selectedCuotaIds.size === cuotasPend.length) setSelectedCuotaIds(new Set());
                    else setSelectedCuotaIds(new Set(cuotasPend.map(c => c.id)));
                  }}
                  className="text-[10px] text-blue-400 hover:text-blue-300 underline">
                  {selectedCuotaIds.size === cuotasPend.length ? "Limpiar" : `Todas (${cuotasPend.length})`}
                </button>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto rounded-xl border border-white/10 p-1">
                {cuotasPend.map(c => {
                  const sel = selectedCuotaIds.has(c.id);
                  const vencida = c.estado === "vencido";
                  return (
                    <div key={c.id} onClick={() => toggleCuota(c.id)}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer border transition-all select-none
                        ${sel ? "bg-emerald-500/20 border-emerald-500/50" : "bg-white/5 border-transparent hover:border-white/20"}`}>
                      <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center transition-all
                        ${sel ? "bg-emerald-500 border-emerald-500" : "border-slate-500"}`}>
                        {sel && <span className="text-white text-[8px] font-bold">✓</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-white text-xs">Cuota {c.numero}</span>
                        {c.fecha_vencimiento && (
                          <span className={`text-[10px] ml-1 ${vencida ? "text-red-400" : "text-slate-500"}`}>
                            · vence {c.fecha_vencimiento}{vencida ? " ⚠" : ""}
                          </span>
                        )}
                      </div>
                      <span className={`text-xs font-semibold ${sel ? "text-emerald-300" : "text-slate-300"}`}>
                        {fmtMonto(c.monto, c.moneda || monedaFac)}
                      </span>
                    </div>
                  );
                })}
              </div>
              {cuotasMode && (
                <div className="mt-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 flex justify-between items-center">
                  <span className="text-emerald-300 text-xs">{selectedCuotaIds.size} cuota(s) · monto auto-calculado</span>
                  <span className="text-emerald-200 text-xs font-bold">{fmtMonto(montoCuotasSelec, monedaFac)}</span>
                </div>
              )}
              {!cuotasMode && (
                <p className="text-slate-500 text-[10px] mt-1">☝️ Seleccioná las cuotas a cobrar o ingresá un monto manual abajo</p>
              )}
            </div>
          )}
          {/* Toggle pago por ítems */}
          {itemsFac.length > 0 && puedeCobroItems && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => { setItemsMode(v => !v); setItemCantidades({}); }}
                style={{ color: "#ffffff" }}
                className={`w-full text-sm font-semibold py-2 rounded-lg border-2 shadow-md transition-all ${itemsMode ? "border-violet-300 bg-violet-800 ring-2 ring-violet-400/60" : "border-violet-400 bg-violet-600 hover:bg-violet-500"}`}
              >
                {itemsMode ? "✓ Modo: Pago por ítems — click para modo manual" : "🍽 Pago por ítems (restaurante)"}
              </button>
              {itemsMode && (
                <div className="border border-white/10 rounded-xl overflow-hidden">
                  <div className="bg-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex justify-between">
                    <span>Ítem</span><span>Cant. / Subtotal</span>
                  </div>
                  <div className="divide-y divide-white/5 max-h-48 overflow-y-auto">
                    {itemsFac.map((item, idx) => {
                      const maxQty = parseInt(item.cantidad) || 1;
                      const precioUnit = item.precio_unitario != null ? item.precio_unitario
                        : (item.cantidad ? (item.subtotal || 0) / item.cantidad : 0);
                      const qty = Math.max(0, Math.min(maxQty, parseInt(itemCantidades[idx] ?? 0) || 0));
                      return (
                        <div key={idx} className="flex items-center gap-3 px-3 py-2.5">
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-xs font-medium truncate">{item.descripcion || item.nombre || `Ítem ${idx+1}`}</p>
                            <p className="text-slate-500 text-[10px]">{fmtMonto(precioUnit, monedaFac)} c/u · {maxQty} en factura</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button type="button" onClick={() => setItemCantidades(p => ({...p, [idx]: Math.max(0, (parseInt(p[idx])||0)-1)}))}
                              className="w-6 h-6 rounded-full bg-white/10 text-white text-sm flex items-center justify-center hover:bg-white/20">−</button>
                            <input
                              type="number" min="0" max={maxQty} value={itemCantidades[idx] ?? 0}
                              onChange={e => setItemCantidades(p => ({...p, [idx]: Math.min(maxQty, Math.max(0, parseInt(e.target.value)||0))}))}
                              className="w-10 text-center bg-arandu-dark border border-white/10 rounded text-white text-xs py-1 focus:outline-none focus:border-violet-500"
                            />
                            <button type="button" onClick={() => setItemCantidades(p => ({...p, [idx]: Math.min(maxQty, (parseInt(p[idx])||0)+1)}))}
                              className="w-6 h-6 rounded-full bg-white/10 text-white text-sm flex items-center justify-center hover:bg-white/20">+</button>
                            <span className="text-violet-300 text-xs w-20 text-right">{qty > 0 ? fmtMonto(qty * precioUnit, monedaFac) : "—"}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="bg-white/5 px-3 py-2 flex justify-between items-center">
                    <button type="button" onClick={() => setItemCantidades(Object.fromEntries(itemsFac.map((_,i)=>[i, parseInt(itemsFac[i].cantidad)||1])))}
                      className="text-[10px] text-slate-400 hover:text-white">Seleccionar todos</button>
                    <p className="text-violet-300 text-xs font-bold">{fmtMonto(montoItems, monedaFac)}</p>
                  </div>
                </div>
              )}
            </div>
          )}
          <MedioCobroSelector procesadoras={procesadorasCobro} value={medioCobro} onChange={setMedioCobro}
            monto={nuevoMonto} fmt={(n) => fmtMonto(n, monedaFac)} />
          {puedeDescuentoPago && (
            <div>
              <label className="text-slate-400 text-xs block mb-1">Descuento en este pago <span className="text-slate-500">(opcional — p. ej. quitar el interés)</span></label>
              <MoneyInput min="0" step="any" moneda={monedaFac || "PYG"} value={descuentoPago}
                onChange={value => setDescuentoPago(value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500" />
              {difCuotasVsSaldo > 0.5 && descuentoNum === 0 && (
                <button type="button" onClick={() => setDescuentoPago(String(Math.round(difCuotasVsSaldo)))}
                  className="mt-1 text-[11px] text-amber-300 hover:text-amber-200 underline">
                  Las cuotas suman {fmtMonto(difCuotasVsSaldo, monedaFac)} más que el saldo — aplicar esa diferencia como descuento
                </button>
              )}
              {descuentoNum > 0 && <p className="text-amber-300 text-[11px] mt-1">Se cobra {fmtMonto(nuevoMonto, monedaFac)} y se descuentan {fmtMonto(descuentoNum, monedaFac)} del saldo.</p>}
            </div>
          )}
          <div>
            <label className="text-slate-400 text-xs block mb-1">
              Monto que abona ahora *
              {cuotasMode && <span className="text-emerald-400 ml-1">(calculado por cuotas)</span>}
              {!cuotasMode && itemsMode && <span className="text-violet-400 ml-1">(calculado por ítems)</span>}
            </label>
            {monedaFac === "PYG" && cuentaAltSeleccionadaId ? (
              <>
                <AltMontoInput
                  montoPYG={montoParcial}
                  setMontoPYG={v => { if (!itemsMode && !cuotasMode) setMontoParcial(v); }}
                  tc={tcPago} moneda={monedaAlt} fullPYG={pendiente}
                  readOnly={itemsMode || cuotasMode}
                  className={`w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 ${(itemsMode || cuotasMode) ? "opacity-70 cursor-not-allowed" : ""}`}
                />
                <p className="text-slate-400 text-[11px] mt-1">Moneda de cobro: {monedaAlt}{nuevoMonto > 0 ? ` · equivale a ${fmtMonto(nuevoMonto, monedaFac)}` : ""}</p>
              </>
            ) : (
            <MoneyInput min="0" step="any" moneda={monedaFac || "PYG"} value={montoParcial}
              onChange={value => { if (!itemsMode && !cuotasMode) setMontoParcial(value); }}
              readOnly={itemsMode || cuotasMode}
              className={`w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 ${(itemsMode || cuotasMode) ? "opacity-70 cursor-not-allowed" : ""}`}
            />
            )}
            {nuevoMonto > 0 && (
              pagoExcede
                ? <p className="text-teal-400 text-xs mt-1">💰 Supera el saldo — se registrará el pago completo y se dará vuelto</p>
                : quedaria <= 0
                  ? <p className="text-emerald-400 text-xs mt-1">✓ Con este pago la factura quedará <strong>pagada</strong></p>
                  : <p className="text-slate-500 text-xs mt-1">Quedará pendiente: {fmtMonto(quedaria, monedaFac)}</p>
            )}
            {pagoExcede && monedaFac === "PYG" && (
              <div className="mt-2 bg-teal-500/10 border border-teal-500/30 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-teal-300 text-xs font-semibold">💰 Vuelto a dar al cliente</p>
                  <div className="text-right">
                    <p className="text-teal-200 font-bold text-xl">
                      {cuentaAltSeleccionadaId && parseFloat(tcPago) > 0
                        ? fmtMonto(Math.round(vueltoParcial / parseFloat(tcPago) * 100) / 100, monedaAlt)
                        : fmtMonto(vueltoParcial, monedaFac)}
                    </p>
                    {cuentaAltSeleccionadaId && parseFloat(tcPago) > 0 && (
                      <p className="text-teal-400/80 text-[11px]">equivale a {fmtMonto(vueltoParcial, monedaFac)}</p>
                    )}
                  </div>
                </div>
                <p className="text-slate-400 text-[10px]">Se cobrará {fmtMonto(pendiente, monedaFac)} (saldo exacto)</p>
                <div>
                  <label className="text-slate-400 text-[10px] block mb-1">Cuenta desde la que se da el vuelto</label>
                  <select value={cuentaVueltoIdParcial} onChange={e => setCuentaVueltoIdParcial(e.target.value)}
                    className="w-full bg-arandu-dark border border-teal-500/30 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-teal-400">
                    <option value="">— Sin especificar —</option>
                    {cuentasDisp.filter(c => c.logo_tipo === fac.logo_tipo && c.activa !== false && c.moneda === (cuentaAltSeleccionadaId ? monedaAlt : monedaFac)).map(c => (
                      <option key={c.id} value={c.id}>{c.nombre}{c.banco ? ` · ${c.banco}` : ""}{c.es_predeterminada ? " ★" : ""}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="text-slate-400 text-xs block mb-1">Fecha de pago *</label>
            <DateInput value={fechaPagoParcial}
              onChange={e => setFechaPagoParcial(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-slate-400 text-xs block mb-1">Nº Recibo <span className="text-slate-500 text-[10px]">(autogenera si vacío)</span></label>
            <input type="text" value={numeroReciboManual} onChange={e => setNumeroReciboManual(e.target.value)}
              placeholder="000123"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <PagoReferenciaFields value={pagoRef} onChange={setPagoRef} />
          {/* Cuentas */}
          {cuentasMoneda.length > 0 && (
            <div>
              <label className="text-slate-400 text-xs block mb-1">
                Cuenta bancaria {monedaLabels[monedaFac] || monedaFac} ({monedaFac}) <span className="text-red-400">*</span>
              </label>
              <div className="space-y-1 max-h-28 overflow-y-auto pr-1">
                {cuentasMoneda.map(c => (
                  <label key={c.id} className="flex items-center gap-2 cursor-pointer p-1.5 rounded-lg hover:bg-white/5 transition-all">
                    <input type="radio" name="cuenta-pago-parcial" checked={cuentasPYG.includes(c.id)}
                      onChange={() => {
                        setCuentasPYG([c.id]);
                        setCuentasUSD([]);
                        if (monedaFac === "PYG") { setTcPago(""); setMontoUSD(""); }
                      }}
                      className="accent-blue-500 w-3.5 h-3.5"
                    />
                    <span className="text-white text-xs flex-1">{c.nombre}{c.banco ? ` · ${c.banco}` : ""}</span>
                    {c.es_predeterminada && <span className="text-amber-400 text-[10px]">★</span>}
                  </label>
                ))}
              </div>
              {cuentasPYG.length === 0 && cuentasUSD.length === 0 && (
                <p className="text-red-400 text-[11px] mt-1">⚠ Seleccioná una cuenta bancaria para continuar</p>
              )}
            </div>
          )}
          {/* Cuentas en moneda diferente — activa TC cuando se selecciona */}
          {gruposOtraMoneda.map(({ moneda, cuentas }) => (
            <div key={moneda}>
              <label className="text-slate-400 text-xs block mb-1">
                Cuenta bancaria {monedaLabels[moneda] || moneda} ({moneda}) <span className="text-slate-500 text-[10px]">(opcional — requiere tipo de cambio)</span>
              </label>
              <div className="space-y-1 max-h-24 overflow-y-auto pr-1">
                {cuentas.map(c => (
                  <label key={c.id} className="flex items-center gap-2 cursor-pointer p-1.5 rounded-lg hover:bg-white/5 transition-all">
                    <input type="radio" name="cuenta-pago-parcial" checked={cuentasUSD.includes(c.id)}
                      onChange={() => {
                        setCuentasUSD([c.id]);
                        setCuentasPYG([]);
                        if (monedaFac === "PYG") {
                          setTcPago("");
                          fetchTcDelDia(c.moneda, fechaPagoParcial).then(tc => { if (tc) setTcPago(String(tc)); });
                        }
                      }}
                      className="accent-blue-500 w-3.5 h-3.5"
                    />
                    <span className="text-white text-xs flex-1">{c.nombre}{c.banco ? ` · ${c.banco}` : ""}</span>
                    {c.es_predeterminada && <span className="text-amber-400 text-[10px]">★</span>}
                  </label>
                ))}
              </div>
            </div>
          ))}
          {/* TC — solo si se seleccionó una cuenta de moneda diferente */}
          {hasCurrencyMismatch && cuentaAltSeleccionadaId && (
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 space-y-2">
              <p className="text-blue-300 text-xs font-medium">
                Tipo de cambio {monedaFac === "PYG" ? `(Gs → ${monedaAlt})` : `(${monedaFac} → Gs)`}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-slate-400 text-[10px] block mb-1">TC (1 {monedaTc} = ? PYG)</label>
                  <input type="number" value={tcPago} onChange={e => {
                    if (!canEditTcDoc) return;
                    setTcPago(e.target.value);
                  }}
                    placeholder="0"
                    readOnly={!canEditTcDoc}
                    title={!canEditTcDoc ? "El tipo de cambio se configura en Financiero → Cotizaciones" : undefined}
                    className={`w-full bg-arandu-dark border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-blue-400${!canEditTcDoc ? " opacity-60 cursor-not-allowed" : ""}`}
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-[10px] block mb-1">
                    {monedaFac === "PYG" ? `Equiv. ${monedaAlt}` : "Equiv. PYG"}
                  </label>
                  <input type="number" value={montoUSD} onChange={e => {
                    if (monedaFac === "PYG" || !canEditTcDoc) return;
                    setMontoUSD(e.target.value);
                    if (e.target.value && nuevoMonto) {
                      const eq = parseFloat(e.target.value);
                      setTcPago(String((eq / nuevoMonto).toFixed(2)));
                    }
                  }}
                    placeholder="0"
                    readOnly={monedaFac === "PYG" || !canEditTcDoc}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-blue-400 opacity-60 cursor-not-allowed"
                  />
                </div>
              </div>
              {tcPago && nuevoMonto > 0 && (
                <p className="text-blue-300 text-xs">
                  {monedaFac === "PYG"
                    ? `₲ ${Math.round(nuevoMonto).toLocaleString("es-PY")} ÷ TC ${tcPago} = ${monedaAlt} ${(nuevoMonto / parseFloat(tcPago)).toFixed(2)}`
                    : `${monedaFac} ${nuevoMonto} × TC ${tcPago} = ₲ ${Math.round(nuevoMonto * parseFloat(tcPago)).toLocaleString("es-PY")}`}
                </p>
              )}
            </div>
          )}
          {/* Banner saldo a favor */}
          {saldoFavor && (saldoFavor.saldos || []).some(s => s.monto_disponible > 0) && (() => {
            const sf = (saldoFavor.saldos || []).find(s => s.moneda === monedaFac);
            if (!sf || sf.monto_disponible <= 0) return null;
            return (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-amber-300 text-xs font-semibold">⭐ Saldo a favor del cliente</p>
                  <span className="text-amber-200 text-xs font-bold">{fmtMonto(sf.monto_disponible, sf.moneda)}</span>
                </div>
                <p className="text-slate-400 text-[11px]">{fac.empresa_nombre || fac.razon_social} tiene un crédito disponible. ¿Desea aplicarlo?</p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={aplicarSaldoFavor} onChange={e => setAplicarSaldoFavor(e.target.checked)} className="accent-amber-500 w-3.5 h-3.5" />
                  <span className="text-amber-200 text-xs">Aplicar saldo a favor al pago</span>
                </label>
                {aplicarSaldoFavor && (
                  <div>
                    <label className="text-slate-400 text-[10px] block mb-1">Monto a aplicar</label>
                    <MoneyInput min="0" max={Math.min(sf.monto_disponible, pendiente)} step="any" moneda={sf.moneda || monedaFac || "PYG"}
                      value={montoSaldoAplicar}
                      onChange={value => setMontoSaldoAplicar(value)}
                      className="w-full bg-arandu-dark border border-amber-500/30 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-amber-400"
                    />
                    {parseFloat(montoSaldoAplicar) > 0 && (
                      <p className="text-amber-300 text-[11px] mt-1">
                        Saldo restante después: {fmtMonto(sf.monto_disponible - Math.min(parseFloat(montoSaldoAplicar) || 0, sf.monto_disponible), sf.moneda)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          {/* Banner vale de cliente (crédito cargado vía Ingresos) */}
          {vale && (vale.vales || []).length > 0 && (
            <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-violet-300 text-xs font-semibold">🎟 Vale de cliente disponible</p>
                <span className="text-violet-200 text-xs font-bold">{fmtMonto(vale.totalDisponible, monedaFac)}</span>
              </div>
              <p className="text-slate-400 text-[11px]">{fac.empresa_nombre || fac.razon_social} tiene crédito de vale disponible. ¿Desea aplicarlo?</p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={aplicarVale} onChange={e => setAplicarVale(e.target.checked)} className="accent-violet-500 w-3.5 h-3.5" />
                <span className="text-violet-200 text-xs">Aplicar vale al pago</span>
              </label>
              {aplicarVale && (
                <div>
                  <label className="text-slate-400 text-[10px] block mb-1">Monto a aplicar</label>
                  <MoneyInput min="0" max={Math.min(vale.totalDisponible, pendiente)} step="any" moneda={monedaFac}
                    value={montoValeAplicar}
                    onChange={value => setMontoValeAplicar(value)}
                    className="w-full bg-arandu-dark border border-violet-500/30 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-violet-400"
                  />
                  {parseFloat(montoValeAplicar) > 0 && (
                    <p className="text-violet-300 text-[11px] mt-1">
                      Vale restante después: {fmtMonto(vale.totalDisponible - Math.min(parseFloat(montoValeAplicar) || 0, vale.totalDisponible), monedaFac)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg text-sm transition-all">
              Cancelar
            </button>
            {(() => {
              const sinCuenta = (cuentasMoneda.length > 0 || cuentasUSD.length > 0) && cuentasPYG.length === 0 && cuentasUSD.length === 0;
              return (
                <button
                  onClick={onConfirm}
                  disabled={sinCuenta}
                  className={`flex-1 px-4 py-2.5 text-white rounded-lg text-sm font-medium transition-all ${sinCuenta ? "bg-slate-600 cursor-not-allowed opacity-60" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                  {pagoExcede ? "Registrar y dar vuelto" : "Registrar y emitir recibo"}
                </button>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ReciboDocModal — vista visual de recibo de pago
// ═══════════════════════════════════════════════════════════════
function ReciboDocModal({ recibo: r, activeEmpresaPropia = null, onClose, fmtMonto, cuentasDisp = [] }) {
  React.useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const empresaEmisora = activeEmpresaPropia || {};
  const accentColor = r.logo_tipo === "jar" ? "#dc2626" : r.logo_tipo === "arandu" ? "#2563eb" : (empresaEmisora?.color || "#1d4ed8");
  const emisorNombre = r.emisor_razon_social || empresaEmisora?.razon_social || empresaEmisora?.nombre || (r.logo_tipo === "jar" ? "JAR Informática" : r.logo_tipo === "arandu" ? "Arandu Informática" : "AranduJAR Informática");
  const emisorNombreFantasia = r.emisor_nombre_fantasia || empresaEmisora?.nombre_fantasia || "";
  const emisorRuc = r.emisor_ruc || "";
  const emisorDireccion = r.emisor_direccion || "";
  const emisorTelefono = r.emisor_telefono || "";
  const emisorEmail = r.emisor_email || "";
  const emisorLogoUrl = r.emisor_logo_url || null;
  const fmt = (n, moneda) => fmtMonto(n, moneda || r.moneda);
  const lineasFacturas = (r.facturas?.length > 0)
    ? r.facturas
    : (r.factura_numero ? [{ factura_numero: r.factura_numero, monto: r.monto, moneda: r.moneda }] : []);
  const esReciboMultiple = lineasFacturas.length > 1 || r.bulk;

  // Cuenta efectiva: la guardada en el recibo o la predeterminada por moneda/empresa
  const cuentaEfectiva = r.cuenta_nombre
    || cuentasDisp.find(c => c.logo_tipo === r.logo_tipo && c.moneda === (r.moneda || "PYG") && c.es_predeterminada)?.nombre
    || cuentasDisp.find(c => c.logo_tipo === r.logo_tipo && c.moneda === (r.moneda || "PYG"))?.nombre
    || null;

  const handlePrint = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const docLogo = svgDocumentHeaderLogoHtml(r.logo_tipo, emisorLogoUrl);
    const facturasRows = lineasFacturas.map(f => `
      <tr>
        <td style="padding:5px 8px;border-bottom:1px solid #e5e7eb;font-family:monospace;font-weight:700">${f.factura_numero || "-"}</td>
        <td style="padding:5px 8px;border-bottom:1px solid #e5e7eb;text-align:right">${fmt(f.monto, f.moneda)}</td>
      </tr>
    `).join("");
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Recibo ${r.numero}</title>
      <style>
        @page{margin:14mm}
        body{font-family:Arial,sans-serif;color:#111;padding:0;max-width:720px;margin:0 auto}
        .receipt{border:2px solid ${accentColor};border-radius:8px;padding:20px}
        table{page-break-inside:auto}
        tr{page-break-inside:avoid;page-break-after:auto}
      </style></head><body><div class="receipt">
      <div style="display:flex;justify-content:center;margin-bottom:16px">${docLogo}</div>
      <div style="text-align:center;margin-bottom:20px;border-bottom:2px solid ${accentColor};padding-bottom:16px">
        <h1 style="font-size:28px;font-weight:900;color:${accentColor};margin:0">RECIBO DE PAGO</h1>
        <p style="font-size:20px;font-weight:700;color:#1f2937;margin:4px 0">N° ${r.numero}</p>
        ${emisorRuc ? `<p style="font-size:12px;font-weight:700;color:#374151;margin:2px 0">RUC: ${emisorRuc}</p>` : ""}
      </div>
      <table style="width:100%;border-collapse:collapse;margin-bottom:16px;font-size:14px">
        <tr><td style="padding:8px 4px;color:#6b7280;width:40%">Empresa:</td><td style="padding:8px 4px;font-weight:600">${emisorNombreFantasia ? `${emisorNombreFantasia}<div style="font-weight:400;font-size:11px;color:#6b7280;margin-top:2px">${emisorNombre}${emisorRuc ? ` · RUC: ${emisorRuc}` : ""}</div>` : `${emisorNombre}${emisorRuc ? ` · RUC: ${emisorRuc}` : ""}`}</td></tr>
        <tr style="background:#f9fafb"><td style="padding:8px 4px;color:#6b7280">Recibido de:</td><td style="padding:8px 4px;font-weight:600">${r.razon_social || "-"}</td></tr>
        ${r.ruc ? `<tr><td style="padding:8px 4px;color:#6b7280">RUC:</td><td style="padding:8px 4px">${r.ruc}</td></tr>` : ""}
        <tr><td style="padding:8px 4px;color:#6b7280">Fecha de pago:</td><td style="padding:8px 4px;font-weight:600">${r.fecha_pago || "-"}</td></tr>
        ${cuentaEfectiva ? `<tr style="background:#f9fafb"><td style="padding:8px 4px;color:#6b7280">Cuenta bancaria:</td><td style="padding:8px 4px;font-weight:600">🏦 ${cuentaEfectiva}</td></tr>` : ""}
      </table>
      <div style="margin:14px 0;page-break-inside:avoid">
        <div style="font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:.08em;margin-bottom:6px">Facturas pagadas (${lineasFacturas.length})</div>
        <table style="width:100%;border-collapse:collapse;font-size:13px;background:#f9fafb;border-radius:8px;overflow:hidden">
          <thead><tr><th style="padding:7px 8px;text-align:left;color:#6b7280;border-bottom:1px solid #d1d5db">Factura</th><th style="padding:7px 8px;text-align:right;color:#6b7280;border-bottom:1px solid #d1d5db">Monto</th></tr></thead>
          <tbody>${facturasRows}</tbody>
        </table>
      </div>
      <div style="background:${accentColor}10;border:2px solid ${accentColor};border-radius:8px;padding:16px;text-align:center;margin:20px 0">
        <p style="color:#6b7280;font-size:12px;margin:0 0 4px">MONTO RECIBIDO</p>
        <p style="font-size:32px;font-weight:900;color:${accentColor};margin:0">${fmt(r.monto)}</p>
      </div>
      ${r.notas ? `<p style="font-size:12px;color:#6b7280;border-top:1px solid #e5e7eb;padding-top:12px">Notas: ${r.notas}</p>` : ""}
      <p style="font-size:11px;color:#9ca3af;text-align:center;margin-top:20px">${emisorDireccion}${emisorTelefono ? ` · Tel: ${emisorTelefono}` : ""}${emisorEmail ? ` · ${emisorEmail}` : ""}</p>
    </div></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] shadow-2xl relative flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
          <span className="text-gray-600 font-medium text-sm">Recibo de pago</span>
          <div className="flex gap-2">
            <button onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-all">
              <Printer className="w-3.5 h-3.5" /> Imprimir
            </button>
            <button onClick={onClose}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs rounded-lg transition-all">
              Cerrar
            </button>
          </div>
        </div>

        {/* Header del recibo */}
        <div className="p-6 text-center border-b-4 shrink-0" style={{ borderColor: accentColor }}>
          <div className="mb-3 flex justify-center">
            {(() => {
              const logoUrl = emisorLogoUrl || empresaEmisora?.logo_url;
              if (logoUrl) return <img src={logoUrl} alt="Logo" className="max-h-16 max-w-[160px] object-contain" />;
              if (r.logo_tipo === "arandu") return <LogoArandu />;
              if (r.logo_tipo === "jar") return <LogoJar />;
              return <LogoAranduJarDoc />;
            })()}
          </div>
          <h2 className="text-2xl font-black text-gray-800 tracking-wider">RECIBO DE PAGO</h2>
          <p className="text-4xl font-black mt-1" style={{ color: accentColor }}>N° {r.numero}</p>
          {emisorRuc && <p className="text-gray-600 text-sm font-semibold mt-1">RUC: {emisorRuc}</p>}
        </div>

        {/* Datos del recibo */}
        <div className="p-6 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Recibido de</p>
              <p className="text-gray-800 font-semibold">{r.razon_social || "-"}</p>
              {r.ruc && <p className="text-gray-500 text-xs mt-0.5">RUC: {r.ruc}</p>}
            </div>
            <div className={`bg-gray-50 rounded-lg p-3 ${esReciboMultiple ? "col-span-2" : ""}`}>
              <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">
                {esReciboMultiple ? `Facturas pagadas (${lineasFacturas.length})` : "Factura"}
              </p>
              {esReciboMultiple ? (
                <ul className="space-y-1.5 mt-1 max-h-64 overflow-y-auto pr-1">
                  {lineasFacturas.map((f, i) => (
                    <li key={i} className="flex justify-between gap-2 text-sm">
                      <span className="text-gray-800 font-mono font-semibold">{f.factura_numero || "-"}</span>
                      <span className="text-gray-600 shrink-0">{fmt(f.monto, f.moneda)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-800 font-mono font-semibold">{lineasFacturas[0]?.factura_numero || r.factura_numero || "-"}</p>
              )}
              <p className="text-gray-400 text-xs mt-1.5">{r.fecha_pago || "-"}</p>
            </div>
          </div>

          {/* Monto destacado */}
          <div className="rounded-xl p-5 text-center" style={{ background: `${accentColor}15`, border: `2px solid ${accentColor}40` }}>
            <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Monto recibido</p>
            <p className="text-4xl font-black" style={{ color: accentColor }}>{fmt(r.monto)}</p>
          </div>

          {cuentaEfectiva && (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm">
              <span className="text-blue-600 text-xs uppercase tracking-wide font-semibold">Cuenta bancaria</span>
              <span className="text-blue-800 font-medium flex items-center gap-1">🏦 {cuentaEfectiva}</span>
            </div>
          )}
          {r.notas && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-r text-sm text-gray-600">
              <strong className="text-yellow-800">Notas:</strong> {r.notas}
            </div>
          )}
        </div>
        <div className="px-6 pb-4 text-center">
          <p className="text-gray-400 text-xs">{emisorDireccion}{emisorTelefono ? ` · Tel: ${emisorTelefono}` : ""}{emisorEmail ? ` · ${emisorEmail}` : ""}</p>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────
//  EditPagoModal — editar un pago individual de una factura
// ─────────────────────────────────────────────────────────────
function EditPagoModal({ factura: fac, pago, cuentasDisp, onClose, onSave, fmtMonto }) {
  const [monto, setMonto] = React.useState(String(pago.monto || ""));
  const [fecha, setFecha] = React.useState(pago.fecha || "");
  const [cuentaId, setCuentaId] = React.useState(pago.cuenta_id || "");
  const [tipoCambio, setTipoCambio] = React.useState(pago.tipo_cambio ? String(pago.tipo_cambio) : "");
  const [saving, setSaving] = React.useState(false);
  const [referencia, setReferencia] = React.useState(pago.referencia_pago || "");
  const [notaPago, setNotaPago] = React.useState(pago.nota_pago || "");

  const monedaFac = fac?.moneda || "PYG";
  // Cuentas de la empresa de la factura
  const cuentasFac = cuentasDisp.filter(c => c.logo_tipo === fac?.logo_tipo);
  const cuentaSeleccionada = cuentasFac.find(c => c.id === cuentaId);
  const mismatch = cuentaSeleccionada && cuentaSeleccionada.moneda !== monedaFac;

  const montoNum = parseFloat(monto) || 0;
  const tcNum = parseFloat(tipoCambio) || 0;

  const handleSave = async () => {
    if (!monto || parseFloat(monto) <= 0) { alert("Monto inválido"); return; }
    setSaving(true);
    const cambios = {
      monto: parseFloat(monto),
      fecha,
      cuenta_id: cuentaId || null,
      tipo_cambio: tipoCambio ? parseFloat(tipoCambio) : null,
      referencia_pago: referencia,
      nota_pago: notaPago,
    };
    await onSave(cambios);
    setSaving(false);
  };

  const inp = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-800";
  const lbl = "block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1";

  return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="font-bold text-gray-800 text-base">Editar pago</h2>
            <p className="text-xs text-gray-400 mt-0.5">Factura {fac?.numero} · {fac?.razon_social}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Monto */}
          <div>
            <label className={lbl}>Monto ({monedaFac})</label>
            <MoneyInput className={inp} value={monto}
              onChange={value => setMonto(value)} min="0" step="any" moneda={monedaFac || "PYG"}
              placeholder="0" />
          </div>

          {/* Fecha */}
          <div>
            <label className={lbl}>Fecha de pago</label>
            <DateInput className={inp} value={fecha} onChange={e => setFecha(e.target.value)} />
          </div>

          {/* Cuenta bancaria */}
          <div>
            <label className={lbl}>Cuenta bancaria</label>
            <select className={inp} value={cuentaId} onChange={e => { setCuentaId(e.target.value); setTipoCambio(""); }}>
              <option value="">— Sin especificar —</option>
              {renderCuentasPorMoneda(cuentasFac, fac?.logo_tipo)}
            </select>
            {cuentaSeleccionada && (
              <p className="mt-1 text-xs text-gray-400">
                Moneda cuenta: <strong>{cuentaSeleccionada.moneda}</strong>
                {!mismatch && " · misma moneda que la factura"}
              </p>
            )}
          </div>

          {/* Tipo de cambio (solo si hay mismatch de moneda) */}
          {mismatch && (
            <div>
              <label className={lbl}>Tipo de cambio ({cuentaSeleccionada.moneda} → {monedaFac})</label>
              <input type="number" className={inp} value={tipoCambio}
                onChange={e => setTipoCambio(e.target.value)}
                min="0" step="any" placeholder="Ej: 7500" />
              {tcNum > 0 && montoNum > 0 && (() => {
                // ANTES esto siempre dividía, asumiendo que el monto (en
                // monedaFac) se convertía a la moneda de la cuenta. Es
                // correcto cuando la factura está en guaraníes y la cuenta es
                // extranjera, pero está al revés si la factura está en
                // moneda extranjera y la cuenta es en guaraníes — ahí hay
                // que MULTIPLICAR, no dividir.
                const cuentaEsPyg = cuentaSeleccionada.moneda === "PYG";
                const equivalente = cuentaEsPyg ? montoNum * tcNum : montoNum / tcNum;
                const simbolo = cuentaEsPyg
                  ? `₲ ${Math.round(equivalente).toLocaleString("es-PY")}`
                  : `${cuentaSeleccionada.moneda} ${equivalente.toFixed(2)}`;
                const operador = cuentaEsPyg ? "×" : "÷";
                return (
                  <p className="mt-1 text-xs text-emerald-600 font-medium">
                    {fmtMonto(montoNum, monedaFac)} {operador} {tcNum} = {simbolo}
                  </p>
                );
              })()}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">N° de depósito / comprobante</label>
            <input type="text" maxLength={100} value={referencia} onChange={e => setReferencia(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nota</label>
            <textarea rows={2} maxLength={500} value={notaPago} onChange={e => setNotaPago(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 resize-none" />
          </div>

          {/* Info recibo */}
          {pago.recibo_numero && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
              📄 Recibo <strong>{pago.recibo_numero}</strong> se actualizará automáticamente
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg shadow transition-all">
            <Save className="w-4 h-4" />
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  IngresosFormModal — crear / editar un ingreso varios
// ─────────────────────────────────────────────────────────────
const CATEGORIAS_INGRESO = [
  "Transferencia", "Efectivo", "Cheque", "Préstamo", "Cobro deuda",
  "Devolución", "Subsidio", "Otro",
];

function IngresosFormModal({ ingreso, cuentasDisp, activeLogoTipo, empresas, canCrearVale, token, API, onClose, onSaved }) {
  const isEdit = !!ingreso;
  const [form, setForm] = React.useState({
    descripcion: ingreso?.descripcion || "",
    categoria:   ingreso?.categoria || "Transferencia",
    fecha:       ingreso?.fecha || new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10),
    monto:       ingreso?.monto ? String(ingreso.monto) : "",
    moneda:      ingreso?.moneda || "PYG",
    tipo_cambio: ingreso?.tipo_cambio ? String(ingreso.tipo_cambio) : "",
    cuenta_id:   ingreso?.cuenta_id || "",
    notas:       ingreso?.notas || "",
    es_vale:     false,
    cliente:     null, // { id, nombre, ruc }
  });
  const [saving, setSaving] = React.useState(false);
  const [clienteQuery, setClienteQuery] = React.useState("");
  const [clienteOpen, setClienteOpen] = React.useState(false);
  const clienteRef = React.useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  React.useEffect(() => {
    const handler = (e) => { if (clienteRef.current && !clienteRef.current.contains(e.target)) setClienteOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const clientesFiltrados = React.useMemo(() => {
    const lista = empresas || [];
    if (!clienteQuery.trim()) return lista.slice(0, 12);
    const q = clienteQuery.toLowerCase();
    return lista.filter(e =>
      (e.nombre || "").toLowerCase().includes(q) ||
      (e.razon_social || "").toLowerCase().includes(q) ||
      (e.ruc || "").toLowerCase().includes(q)
    ).slice(0, 12);
  }, [empresas, clienteQuery]);

  // Cuentas disponibles para el logo activo
  const cuentasFiltradas = cuentasDisp.filter(c =>
    c.logo_tipo === (ingreso?.logo_tipo || activeLogoTipo)
  );

  // Auto-seleccionar cuenta predeterminada de la moneda al cambiar moneda
  React.useEffect(() => {
    if (!form.cuenta_id) {
      const def = cuentasFiltradas.find(c => c.moneda === form.moneda && c.es_predeterminada)
               || cuentasFiltradas.find(c => c.moneda === form.moneda);
      if (def) set("cuenta_id", def.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.moneda]);

  const cuentaSeleccionada = cuentasFiltradas.find(c => c.id === form.cuenta_id);
  const mismatch = cuentaSeleccionada && cuentaSeleccionada.moneda !== form.moneda;
  const montoNum = parseFloat(form.monto) || 0;
  const tcNum = parseFloat(form.tipo_cambio) || 0;

  const handleSave = async () => {
    if (!form.descripcion.trim()) { alert("La descripción es requerida"); return; }
    if (!form.monto || parseFloat(form.monto) <= 0) { alert("El monto debe ser mayor a 0"); return; }
    if (form.es_vale && !form.cliente?.id) { alert("Seleccioná el cliente para cargar su vale"); return; }
    setSaving(true);
    try {
      const body = {
        descripcion: form.descripcion.trim(),
        categoria:   form.categoria,
        fecha:       form.fecha,
        monto:       parseFloat(form.monto),
        moneda:      form.moneda,
        tipo_cambio: form.tipo_cambio ? parseFloat(form.tipo_cambio) : null,
        cuenta_id:   form.cuenta_id || null,
        cuenta_nombre: cuentaSeleccionada?.nombre || null,
        notas:       form.notas || null,
        logo_tipo:   ingreso?.logo_tipo || activeLogoTipo,
        es_vale:       !isEdit ? !!form.es_vale : undefined,
        cliente_id:    !isEdit && form.es_vale ? form.cliente.id : undefined,
        cliente_nombre: !isEdit && form.es_vale ? (form.cliente.nombre || form.cliente.razon_social || "") : undefined,
        cliente_ruc:   !isEdit && form.es_vale ? (form.cliente.ruc || null) : undefined,
      };
      const url = isEdit
        ? `${API}/admin/ingresos-varios/${ingreso.id}`
        : `${API}/admin/ingresos-varios`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error al guardar"); }
      onSaved();
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  const inp = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white text-gray-800";
  const lbl = "block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1";

  return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="font-bold text-gray-800 text-base">
              {isEdit ? "Editar ingreso" : "Nuevo ingreso"}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">Ingresos varios / sin factura</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Descripción */}
          <div>
            <label className={lbl}>Descripción *</label>
            <input className={inp} value={form.descripcion}
              onChange={e => set("descripcion", e.target.value)}
              placeholder="Ej: Cobro deuda anterior EDB..." />
          </div>

          {/* Categoría */}
          {!form.es_vale && (
            <div>
              <label className={lbl}>Categoría</label>
              <select className={inp} value={form.categoria} onChange={e => set("categoria", e.target.value)}>
                {CATEGORIAS_INGRESO.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}

          {/* Vale (crédito de cliente) — solo al crear, requiere permiso granular */}
          {!isEdit && canCrearVale && (
            <div className="border border-violet-200 bg-violet-50 rounded-lg p-3 space-y-3">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" className="w-4 h-4 accent-violet-600"
                  checked={form.es_vale}
                  onChange={e => { set("es_vale", e.target.checked); if (!e.target.checked) set("cliente", null); }} />
                <span className="text-sm font-semibold text-violet-800 flex items-center gap-1.5">
                  <Wallet className="w-4 h-4" /> Cargar como Vale (crédito de cliente)
                </span>
              </label>
              <p className="text-xs text-violet-600 -mt-1">
                El cliente deja este monto a crédito para usarlo luego en una compra.
              </p>
              {form.es_vale && (
                <div ref={clienteRef} className="relative">
                  <label className={lbl}>Cliente *</label>
                  {form.cliente?.id ? (
                    <div className="flex items-center gap-2 bg-white border border-violet-300 rounded-lg px-3 py-2 min-h-[38px]">
                      <div className="flex-1 min-w-0">
                        <span className="text-gray-800 text-sm font-medium truncate block">
                          {form.cliente.nombre || form.cliente.razon_social}
                        </span>
                        {form.cliente.ruc && <span className="text-gray-400 text-xs">RUC: {form.cliente.ruc}</span>}
                      </div>
                      <button type="button" onClick={() => set("cliente", null)} className="text-gray-400 hover:text-gray-700 shrink-0 transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                        <input
                          value={clienteQuery}
                          onChange={e => { setClienteQuery(e.target.value); setClienteOpen(true); }}
                          onFocus={() => setClienteOpen(true)}
                          placeholder="Buscar cliente por nombre o RUC..."
                          className="w-full bg-white border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-gray-800 text-sm focus:outline-none focus:border-violet-400 placeholder:text-gray-400"
                        />
                      </div>
                      {clienteOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                          {clientesFiltrados.length > 0 ? (
                            <ul className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                              {clientesFiltrados.map(e => (
                                <li key={e.id}
                                  onMouseDown={() => { set("cliente", e); setClienteQuery(""); setClienteOpen(false); }}
                                  className="px-3 py-2.5 hover:bg-gray-50 cursor-pointer flex items-center justify-between">
                                  <span className="text-gray-800 text-sm">{e.nombre || e.razon_social}</span>
                                  {e.ruc && <span className="text-gray-400 text-xs ml-2 shrink-0">RUC: {e.ruc}</span>}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className="px-3 py-2 text-gray-400 text-xs">Sin resultados{clienteQuery.trim() ? ` para "${clienteQuery.trim()}"` : ""}</div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Fecha */}
          <div>
            <label className={lbl}>Fecha</label>
            <DateInput className={inp} value={form.fecha} onChange={e => set("fecha", e.target.value)} />
          </div>

          {/* Moneda + Monto */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={lbl}>Moneda</label>
              <select className={inp} value={form.moneda} onChange={e => { set("moneda", e.target.value); set("tipo_cambio", ""); set("cuenta_id", ""); }}>
                <option value="PYG">₲ PYG</option>
                <option value="USD">$ USD</option>
                <option value="BRL">R$ BRL</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className={lbl}>Monto *</label>
              <MoneyInput className={inp} value={form.monto}
                onChange={value => set("monto", value)}
                moneda={form.moneda || "PYG"}
                min="0" step="any" placeholder="0" />
            </div>
          </div>

          {/* Cuenta bancaria */}
          <div>
            <label className={lbl}>Cuenta bancaria donde ingresó</label>
            <select className={inp} value={form.cuenta_id} onChange={e => set("cuenta_id", e.target.value)}>
              <option value="">— Sin especificar —</option>
              {renderCuentasPorMoneda(cuentasFiltradas, ingreso?.logo_tipo || activeLogoTipo)}
            </select>
            {cuentaSeleccionada && (
              <p className="mt-1 text-xs text-gray-400">
                Cuenta en <strong>{cuentaSeleccionada.moneda}</strong>
                {!mismatch ? " · misma moneda" : " · ⚠ moneda diferente al ingreso"}
              </p>
            )}
          </div>

          {/* Tipo de cambio si hay mismatch */}
          {mismatch && (
            <div>
              <label className={lbl}>Tipo de cambio ({form.moneda} → {cuentaSeleccionada.moneda})</label>
              <input type="number" className={inp} value={form.tipo_cambio}
                onChange={e => set("tipo_cambio", e.target.value)}
                min="0" step="any" placeholder="Ej: 7500" />
              {tcNum > 0 && montoNum > 0 && (
                <p className="mt-1 text-xs text-emerald-600 font-medium">
                  {form.moneda} {montoNum.toLocaleString()} ÷ {tcNum} = {cuentaSeleccionada.moneda} {(montoNum / tcNum).toFixed(2)}
                </p>
              )}
            </div>
          )}

          {/* Notas */}
          <div>
            <label className={lbl}>Notas</label>
            <textarea className={inp + " resize-none min-h-[60px]"} value={form.notas}
              onChange={e => set("notas", e.target.value)}
              placeholder="Observaciones opcionales..." />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg shadow transition-all">
            <Save className="w-4 h-4" />
            {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear ingreso"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NotaCreditoFormModal({ nota, facturas, productos = [], cuentasDisp = [], activeLogoTipo, token, API, onClose, onSaved }) {
  const isEdit = !!nota;
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState({
    numero: nota?.numero || "",
    punto_expedicion: nota?.punto_expedicion || "",
    fecha: nota?.fecha || new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10),
    factura_id: nota?.factura_id || "",
    tipo_motivo: nota?.tipo_motivo || "descuento",
    motivo_detalle: nota?.motivo_detalle || "",
    monto: nota?.monto || "",
    moneda: nota?.moneda || "PYG",
    tipo_cambio: nota?.tipo_cambio || "",
    notas: nota?.notas || "",
    estado: nota?.estado || "emitida",
    cuenta_id: nota?.cuenta_id || "",
    cuenta_nombre: nota?.cuenta_nombre || "",
    items_devueltos: nota?.items_devueltos || [],
    tipo_cobro: nota?.tipo_cobro || "saldo_favor",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const factura = facturas.find(f => f.id === form.factura_id);
  const facturaEsPendiente = factura && ["pendiente", "parcial"].includes(factura.estado);
  const facturaEsPagada = factura && factura.estado === "pagada";

  // El listado de facturas (prop `facturas`) viene sin el campo `conceptos` (el backend
  // lo recorta ahí para aligerar el listado) — para poder armar los ítems a devolver
  // necesitamos el detalle completo, que se carga por demanda vía GET /admin/facturas/{id}.
  const [facturaConceptos, setFacturaConceptos] = React.useState(null);
  const [loadingConceptos, setLoadingConceptos] = React.useState(false);
  // Cuando el ítem de la factura no trae variante_id (ej: se vendió por PDV antes
  // de esta corrección, o el producto no tenía variantes en ese momento), permitimos
  // elegir manualmente cuál variante se está devolviendo, para descontar el stock correcto.
  const [varianteOverrides, setVarianteOverrides] = React.useState({}); // { [idx]: { variante_id, variante_texto } }

  // Escaneo de código de barras para ubicar rápido cuál ítem de la factura
  // se está devolviendo (no agrega productos nuevos fuera de la factura,
  // solo busca y suma +1 a la cantidad a devolver del ítem que coincida).
  const [ncBarcodeInput, setNcBarcodeInput] = React.useState("");
  const [ncBarcodeLoading, setNcBarcodeLoading] = React.useState(false);
  const [ncHighlightIdx, setNcHighlightIdx] = React.useState(null);
  const ncItemRefs = React.useRef({});

  const handleNcBarcodeSubmit = async () => {
    const code = (ncBarcodeInput || "").trim();
    if (!code) return;
    const items = facturaConceptos || [];
    if (items.length === 0) { setNcBarcodeInput(""); return; }

    let prod = productos.find(p => (p.codigo_barras || "").trim() === code || (p.sku || "").trim() === code);
    if (!prod && API && token) {
      try {
        setNcBarcodeLoading(true);
        const q = new URLSearchParams();
        if (logoTipoEfectivo) q.set("logo_tipo", logoTipoEfectivo);
        const res = await fetch(`${API}/admin/productos/by-barcode/${encodeURIComponent(code)}?${q}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) prod = await res.json();
      } catch (e) { /* se trata como no encontrado abajo */ }
      finally { setNcBarcodeLoading(false); }
    }
    if (!prod) { toast.error("No se encontró ningún producto con ese código"); setNcBarcodeInput(""); return; }

    const candidatos = items
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => item.producto_id === prod.id);

    if (candidatos.length === 0) {
      toast.error(`"${prod.nombre || prod.sku || code}" no está entre los ítems de esta factura`);
      setNcBarcodeInput("");
      return;
    }

    // Si el producto aparece en más de una línea (ej. variantes distintas),
    // priorizamos la primera que todavía no llegó a su cantidad máxima.
    const objetivo = candidatos.find(({ item: it, idx: i }) => {
      const devItem = form.items_devueltos.find(d => d._idx === i);
      const cantDev = Number(devItem?.cantidad) || 0;
      return cantDev < Number(it.cantidad || 0);
    }) || candidatos[0];

    const { item, idx } = objetivo;
    const devItem = form.items_devueltos.find(d => d._idx === idx);
    const cantDevActual = Number(devItem?.cantidad) || 0;
    const nuevaCant = Math.min(cantDevActual + 1, Number(item.cantidad || 0));

    if (nuevaCant === cantDevActual) {
      toast.error(`Ya se cargó la cantidad máxima de "${item.descripcion}" (${item.cantidad})`);
    } else {
      const cantidadItem = Number(item.cantidad || 1) || 1;
      const subtotalItem = item.subtotal ?? item.monto ?? ((Number(item.precio_unitario || 0) || 0) * cantidadItem);
      const pu = cantidadItem > 0 ? (Number(subtotalItem || 0) / cantidadItem) : 0;
      const override = varianteOverrides[idx];
      const varianteIdFinal = override?.variante_id ?? item.variante_id ?? null;
      const varianteTextoFinal = override?.variante_texto ?? item.variante_texto ?? null;
      const newItems = form.items_devueltos.filter(d => d._idx !== idx);
      newItems.push({ _idx: idx, producto_id: item.producto_id || null, producto_sku: item.producto_sku || null, variante_id: varianteIdFinal, variante_texto: varianteTextoFinal, descripcion: item.descripcion, cantidad: nuevaCant, precio_unitario: pu, subtotal: nuevaCant * pu, iva_tipo: item.iva_tipo || "10" });
      const total = newItems.reduce((s, d) => s + (d.subtotal || 0), 0);
      setForm(f => ({ ...f, items_devueltos: newItems, monto: total > 0 ? String(total) : f.monto }));
    }

    setNcHighlightIdx(idx);
    setTimeout(() => setNcHighlightIdx(cur => (cur === idx ? null : cur)), 1500);
    const node = ncItemRefs.current[idx];
    if (node && node.scrollIntoView) node.scrollIntoView({ behavior: "smooth", block: "nearest" });
    setNcBarcodeInput("");
  };

  React.useEffect(() => {
    if (!form.factura_id) { setFacturaConceptos(null); return; }
    let cancelado = false;
    setLoadingConceptos(true);
    fetch(`${API}/admin/facturas/${form.factura_id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelado) setFacturaConceptos(data?.conceptos || []); })
      .catch(() => { if (!cancelado) setFacturaConceptos([]); })
      .finally(() => { if (!cancelado) setLoadingConceptos(false); });
    return () => { cancelado = true; };
  }, [form.factura_id, API, token]);

  // Saldo disponible de la factura para esta nota (descuenta notas ya aplicadas)
  const notasYaAplicadas = (factura?.notas_credito || []).filter(n => n.id !== nota?.id);
  const yaAcreditadoFac = notasYaAplicadas.reduce((s, n) => s + (n.monto || 0), 0);
  const saldoDisponibleFac = factura ? Math.max((factura.monto || 0) - yaAcreditadoFac, 0) : null;

  // ── Timbrado (opcional) — solo si el punto de la factura ya tiene un rango
  // de Nota de Crédito configurado (ver Ventas > Configuración > Timbrado).
  // Si ningún punto de la empresa lo tiene configurado, la Nota de Crédito
  // sigue funcionando exactamente igual que siempre: número 100% libre.
  const logoTipoEfectivo = factura?.logo_tipo || nota?.logo_tipo || activeLogoTipo;
  const [timbradoConfigNC, setTimbradoConfigNC] = React.useState(null);
  const [loadingNumeroNC, setLoadingNumeroNC] = React.useState(false);
  React.useEffect(() => {
    if (!logoTipoEfectivo || !token) { setTimbradoConfigNC(null); return; }
    let cancelado = false;
    fetch(`${API}/admin/timbrado-vigente/${logoTipoEfectivo}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelado) setTimbradoConfigNC(data?.tiene_config ? data : null); })
      .catch(() => { if (!cancelado) setTimbradoConfigNC(null); });
    return () => { cancelado = true; };
  }, [logoTipoEfectivo, token, API]);

  const puntosConRangoNC = (timbradoConfigNC?.puntos_expedicion || []).filter(p => p.timbrado_activo?.rango_nota_credito);
  const tieneRangoNC = puntosConRangoNC.length > 0;
  const selectedPuntoNC = puntosConRangoNC.find(p => p.codigo === form.punto_expedicion) || null;
  const timbradoVigenteNC = !!selectedPuntoNC?.vigente;
  const esAutoNumericoNC = selectedPuntoNC?.modo_numeracion === "automatico";

  // Preseleccionar el primer punto con rango de NC configurado (solo en alta nueva)
  React.useEffect(() => {
    if (isEdit || !tieneRangoNC || form.punto_expedicion) return;
    setForm(f => ({ ...f, punto_expedicion: puntosConRangoNC[0].codigo }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tieneRangoNC]);

  const fetchSiguienteNumeroNC = async (punto) => {
    if (!logoTipoEfectivo || !punto) return;
    setLoadingNumeroNC(true);
    try {
      const res = await fetch(`${API}/admin/siguiente-numero-nota-credito/${logoTipoEfectivo}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ punto_expedicion: punto }),
      });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({ ...prev, numero: data.numero, punto_expedicion: punto }));
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo obtener el siguiente número");
      }
    } catch (e) { toast.error("Error al obtener número automático"); }
    finally { setLoadingNumeroNC(false); }
  };

  React.useEffect(() => {
    if (!factura || isEdit) return;
    setForm(f => ({
      ...f,
      factura_id: factura.id,
      moneda: factura.moneda || f.moneda,
      tipo_cambio: factura.tipo_cambio || f.tipo_cambio,
      monto: f.monto || factura.monto || "",
    }));
  }, [factura, isEdit]);

  // Boleta (sin factura legal): la devolución es una NC interna NCB-xxxxxx, sin timbrado ni IVA.
  const facturaVinculadaNC = factura || (facturas || []).find(f => f.id === form.factura_id) || null;
  const esBoletaNC = !!facturaVinculadaNC?.sin_factura;

  const handleSave = async () => {
    if (!esBoletaNC && !form.numero.trim()) { alert("El número es requerido"); return; }
    if (!form.factura_id) { alert("Debés vincular una factura de venta"); return; }
    if (!form.monto || Number(form.monto) <= 0) { alert("El monto debe ser mayor a cero"); return; }
    setSaving(true);
    try {
      const motivoLabel = form.tipo_motivo === "devolucion" ? "Devolución" : "Descuento";
      const motivoTexto = form.motivo_detalle ? `${motivoLabel} — ${form.motivo_detalle}` : motivoLabel;
      const body = {
        numero: esBoletaNC ? (form.numero.trim() || "NCB-AUTO") : form.numero.trim(),
        sin_factura: esBoletaNC,
        punto_expedicion: (!esBoletaNC && tieneRangoNC && !isEdit) ? (form.punto_expedicion || null) : null,
        fecha: form.fecha,
        factura_id: factura?.id || form.factura_id || null,
        factura_numero: factura?.numero || nota?.factura_numero || null,
        empresa_id: factura?.empresa_id || nota?.empresa_id || null,
        razon_social: factura?.razon_social || factura?.empresa_nombre || nota?.razon_social || null,
        ruc: factura?.ruc || nota?.ruc || null,
        motivo: motivoTexto,
        tipo_motivo: form.tipo_motivo,
        motivo_detalle: form.motivo_detalle || null,
        items_devueltos: form.tipo_motivo === "devolucion" ? form.items_devueltos : [],
        monto: Number(form.monto),
        moneda: form.moneda,
        tipo_cambio: form.tipo_cambio ? Number(form.tipo_cambio) : null,
        logo_tipo: nota?.logo_tipo || factura?.logo_tipo || activeLogoTipo,
        estado: form.estado,
        notas: form.notas || null,
        cuenta_id: form.cuenta_id || null,
        cuenta_nombre: form.cuenta_nombre || null,
        // tipo_cobro: solo relevante cuando la factura ya estaba pagada
        tipo_cobro: facturaEsPagada ? (form.tipo_cobro || "saldo_favor") : null,
      };
      const url = isEdit ? `${API}/admin/notas-credito/${nota.id}` : `${API}/admin/notas-credito`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error al guardar"); }
      onSaved();
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  const inp = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 bg-white text-gray-800";
  const lbl = "block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1";

  return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="font-bold text-gray-800 text-base">{isEdit ? "Editar nota de crédito" : "Nueva nota de crédito"}</h2>
            <p className="text-xs text-gray-400 mt-0.5">Ajuste o devolución sobre factura emitida</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {/* Timbrado (opcional) — solo si algún punto de esta empresa ya tiene
              un rango de Nota de Crédito configurado (Ventas > Configuración >
              Timbrado). Si no, no se muestra nada y el número sigue libre. */}
          {esBoletaNC && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              Factura vinculada es una <b>Boleta</b>: se emitirá una nota de crédito interna (NCB), sin timbrado ni IVA.
            </div>
          )}
          {!isEdit && !esBoletaNC && tieneRangoNC && (
            <div className="space-y-2">
              {puntosConRangoNC.length > 1 && (
                <div>
                  <label className={lbl}>Punto de expedición</label>
                  <select className={inp} value={form.punto_expedicion}
                    onChange={e => setForm(f => ({ ...f, punto_expedicion: e.target.value, numero: "" }))}>
                    {puntosConRangoNC.map(p => (
                      <option key={p.codigo} value={p.codigo}>Punto {p.codigo}{p.descripcion ? ` — ${p.descripcion}` : ""}</option>
                    ))}
                  </select>
                </div>
              )}
              {selectedPuntoNC && (
                <div className={`rounded-lg border px-3 py-2 flex items-start gap-2 text-xs ${timbradoVigenteNC ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-600"}`}>
                  <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    {timbradoVigenteNC ? "Timbrado vigente" : "⚠️ Timbrado vencido en este punto"} — N° {selectedPuntoNC.timbrado_activo?.nro_timbrado}
                    {selectedPuntoNC.timbrado_activo?.tipo_emision !== "electronico" && selectedPuntoNC.timbrado_activo?.fecha_vigencia && ` · Vigencia: ${selectedPuntoNC.timbrado_activo.fecha_vigencia}`}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Número *</label>
              {esBoletaNC ? (
                <input className={inp + " bg-gray-50 text-gray-500"} value={isEdit ? (form.numero || "") : ""} readOnly placeholder="NCB-xxxxxx (automático)" />
              ) : !isEdit && tieneRangoNC && esAutoNumericoNC ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input className={inp + " flex-1 bg-gray-50 text-gray-500"} value={form.numero || ""} readOnly placeholder="Número auto-generado" />
                    <button type="button" disabled={loadingNumeroNC || !timbradoVigenteNC}
                      onClick={() => fetchSiguienteNumeroNC(form.punto_expedicion || puntosConRangoNC[0]?.codigo)}
                      className="px-3 py-2 bg-rose-600 text-white text-xs rounded-lg hover:bg-rose-700 disabled:opacity-50 flex items-center gap-1 whitespace-nowrap">
                      {loadingNumeroNC ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Generar N°"}
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-400">El número se genera automáticamente y reserva el correlativo.</p>
                </div>
              ) : (
                <input className={inp} value={form.numero} onChange={e => set("numero", e.target.value)} placeholder="NC-001-001..." />
              )}
            </div>
            <div>
              <label className={lbl}>Fecha *</label>
              <DateInput className={inp} value={form.fecha} onChange={e => set("fecha", e.target.value)} />
            </div>
          </div>
          <div>
            <label className={lbl}>Factura vinculada *</label>
            <select
              className={inp + (!form.factura_id ? " border-red-400" : "")}
              value={form.factura_id}
              onChange={e => {
                const fac = facturas.find(x => x.id === e.target.value);
                const notasExist = (fac?.notas_credito || []);
                const acreditado = notasExist.reduce((s, n) => s + (n.monto || 0), 0);
                const disponible = fac ? Math.max((fac.monto || 0) - acreditado, 0) : null;
                setForm(f => ({
                  ...f,
                  factura_id: e.target.value,
                  moneda: fac?.moneda || f.moneda,
                  tipo_cambio: fac?.tipo_cambio || f.tipo_cambio,
                  monto: disponible != null ? String(disponible) : f.monto,
                  items_devueltos: [],
                  tipo_cobro: "saldo_favor",
                }));
              }}
            >
              <option value="">— Seleccioná una factura —</option>
              {facturas.map(f => (
                <option key={f.id} value={f.id}>{f.numero} · {f.razon_social || f.empresa_nombre || "Cliente"} · {fmtMonto(f.monto, f.moneda)}</option>
              ))}
            </select>
            {facturas.length === 0 && <p className="text-xs text-amber-600 mt-1">No hay facturas cargadas.</p>}
            {form.factura_id && (() => { const fac = facturas.find(x => x.id === form.factura_id); return fac ? <p className="text-xs text-gray-500 mt-1">Cliente: {fac.razon_social || fac.empresa_nombre} · Factura N° {fac.numero}</p> : null; })()}
          </div>

          {/* Saldo disponible: alerta si ya hay notas aplicadas */}
          {factura && yaAcreditadoFac > 0 && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <span className="text-amber-500 text-base mt-0.5">⚠</span>
              <div className="text-xs text-amber-700 space-y-0.5">
                <p><strong>Esta factura ya tiene notas de crédito aplicadas.</strong></p>
                <p>Ya acreditado: <strong>{parseFloat(yaAcreditadoFac).toLocaleString("es-PY")} {factura.moneda || "PYG"}</strong> · Disponible para esta nota: <strong>{parseFloat(saldoDisponibleFac).toLocaleString("es-PY")} {factura.moneda || "PYG"}</strong></p>
              </div>
            </div>
          )}

          {/* Banner: factura pendiente → ajuste automático en plan de cuentas (cobrar) */}
          {facturaEsPendiente && (
            <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <span className="text-blue-500 text-base mt-0.5">ℹ</span>
              <p className="text-xs text-blue-700">
                Esta factura está <strong>pendiente de cobro</strong>. La nota de crédito reducirá el saldo a cobrar en el plan de cuentas (Cobrar). No se genera movimiento de caja.
              </p>
            </div>
          )}

          {/* Selector tipo_cobro: solo cuando la factura ya fue pagada */}
          {facturaEsPagada && (
            <div>
              <label className={lbl}>¿Cómo se gestiona el crédito? *</label>
              <select className={inp} value={form.tipo_cobro} onChange={e => {
                set("tipo_cobro", e.target.value);
                if (e.target.value === "saldo_favor") { set("cuenta_id", ""); set("cuenta_nombre", ""); }
              }}>
                <option value="saldo_favor">Saldo a favor del cliente</option>
                <option value="reembolso">Reembolso al cliente</option>
              </select>
              {form.tipo_cobro === "saldo_favor" && (
                <p className="text-xs text-emerald-600 mt-1">El monto quedará disponible como crédito para aplicar en futuras facturas del cliente.</p>
              )}
              {form.tipo_cobro === "reembolso" && (
                <p className="text-xs text-amber-600 mt-1">Se registra el reembolso. Acordate de registrar el egreso de caja o transferencia correspondiente.</p>
              )}
            </div>
          )}

          <div>
            <label className={lbl}>Motivo *</label>
            <select className={inp} value={form.tipo_motivo} onChange={e => { set("tipo_motivo", e.target.value); set("items_devueltos", []); }}>
              <option value="descuento">Descuento</option>
              <option value="devolucion">Devolución</option>
            </select>
          </div>
          <div>
            <label className={lbl}>Detalle (opcional)</label>
            <input className={inp} value={form.motivo_detalle} onChange={e => set("motivo_detalle", e.target.value)} placeholder="Descripción adicional..." />
          </div>
          {form.tipo_motivo === "devolucion" && form.factura_id && (() => {
            if (loadingConceptos) return (
              <p className="text-xs text-gray-400">Cargando ítems de la factura...</p>
            );
            const items = facturaConceptos || [];
            if (items.length === 0) return (
              <p className="text-xs text-amber-600">Esta factura no tiene ítems detallados. Ingresá el monto manualmente.</p>
            );
            return (
              <div className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ítems a devolver</p>
                <div className="flex items-center gap-2">
                  <input
                    value={ncBarcodeInput}
                    onChange={e => setNcBarcodeInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleNcBarcodeSubmit(); } }}
                    placeholder="Escanear código de barras..."
                    disabled={ncBarcodeLoading}
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-rose-500 bg-white"
                  />
                  <button
                    type="button"
                    onClick={handleNcBarcodeSubmit}
                    disabled={ncBarcodeLoading || !ncBarcodeInput.trim()}
                    className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-medium rounded"
                  >
                    {ncBarcodeLoading ? "..." : "Buscar"}
                  </button>
                </div>
                {items.map((item, idx) => {
                  const devItem = form.items_devueltos.find(d => d._idx === idx) || { _idx: idx, cantidad: 0 };
                  const cantDev = Number(devItem.cantidad) || 0;
                  const override = varianteOverrides[idx];
                  const varianteTextoActual = override?.variante_texto ?? item.variante_texto;
                  // Producto vinculado con variantes activas — si la factura no trae variante_id
                  // (ej: venta antigua o cargada por PDV antes de esta corrección), permitimos
                  // elegir manualmente para no descontar el stock de la variante equivocada.
                  const prodVinculado = item.producto_id ? productos.find(p => p.id === item.producto_id) : null;
                  const variantesActivasItem = prodVinculado?.variantes_habilitadas
                    ? (prodVinculado.variantes || []).filter(v => v.activo !== false)
                    : [];
                  const necesitaElegirVariante = !item.variante_id && !override && variantesActivasItem.length > 1;
                  const skuMostrado = prodVinculado?.sku || item.producto_sku || null;
                  const codigoBarrasMostrado = prodVinculado?.codigo_barras || null;
                  return (
                    <div
                      key={idx}
                      ref={el => { ncItemRefs.current[idx] = el; }}
                      className={`flex items-center gap-2 text-sm rounded-lg px-1.5 py-1 -mx-1.5 transition-colors ${ncHighlightIdx === idx ? "bg-rose-100 ring-1 ring-rose-300" : ""}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-700 whitespace-normal break-words">
                          {item.descripcion}
                          {varianteTextoActual ? ` · ${varianteTextoActual}` : ""}
                        </div>
                        {(skuMostrado || codigoBarrasMostrado) && (
                          <div className="text-[10px] text-gray-400 flex flex-wrap gap-x-2">
                            {skuMostrado && <span>SKU: {skuMostrado}</span>}
                            {codigoBarrasMostrado && <span>Cód. barras: {codigoBarrasMostrado}</span>}
                          </div>
                        )}
                        {!item.producto_id && (
                          <div className="text-amber-600 text-[10px]">⚠ Sin producto vinculado al catálogo — no ajustará el stock</div>
                        )}
                        {necesitaElegirVariante && (
                          <select
                            value=""
                            onChange={e => {
                              const v = variantesActivasItem.find(x => x.id === e.target.value);
                              if (!v) return;
                              const ov = { variante_id: v.id, variante_texto: v.atributos_texto || "" };
                              setVarianteOverrides(prev => ({ ...prev, [idx]: ov }));
                              // Si ya había cantidad cargada para este ítem, actualizamos también
                              // la variante del renglón ya agregado a items_devueltos.
                              setForm(f => ({
                                ...f,
                                items_devueltos: f.items_devueltos.map(d => d._idx === idx
                                  ? { ...d, variante_id: ov.variante_id, variante_texto: ov.variante_texto }
                                  : d),
                              }));
                            }}
                            className="mt-1 w-full text-[11px] border border-amber-300 bg-amber-50 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-amber-500"
                          >
                            <option value="" disabled>⚠ Elegí qué variante se devuelve...</option>
                            {variantesActivasItem.map(v => (
                              <option key={v.id} value={v.id}>{v.atributos_texto || v.id} (stock: {v.stock_actual ?? 0})</option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div className="text-gray-400 text-xs whitespace-nowrap">Máx: {item.cantidad}</div>
                      <input
                        type="number" min="0" max={item.cantidad} step="any"
                        value={cantDev || ""}
                        placeholder="0"
                        onChange={e => {
                          const val = Math.min(parseFloat(e.target.value) || 0, item.cantidad);
                          const cantidadItem = Number(item.cantidad || 1) || 1;
                          const subtotalItem = item.subtotal ?? item.monto ?? ((Number(item.precio_unitario || 0) || 0) * cantidadItem);
                          const pu = cantidadItem > 0 ? (Number(subtotalItem || 0) / cantidadItem) : 0;
                          const varianteIdFinal = override?.variante_id ?? item.variante_id ?? null;
                          const varianteTextoFinal = override?.variante_texto ?? item.variante_texto ?? null;
                          const newItems = form.items_devueltos.filter(d => d._idx !== idx);
                          if (val > 0) newItems.push({ _idx: idx, producto_id: item.producto_id || null, producto_sku: item.producto_sku || null, variante_id: varianteIdFinal, variante_texto: varianteTextoFinal, descripcion: item.descripcion, cantidad: val, precio_unitario: pu, subtotal: val * pu, iva_tipo: item.iva_tipo || "10" });
                          const total = newItems.reduce((s, d) => s + (d.subtotal || 0), 0);
                          setForm(f => ({ ...f, items_devueltos: newItems, monto: total > 0 ? String(total) : f.monto }));
                        }}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-xs text-right focus:outline-none focus:ring-1 focus:ring-rose-500"
                      />
                    </div>
                  );
                })}
                {form.items_devueltos.length > 0 && (
                  <p className="text-xs text-emerald-600 text-right pt-1 font-medium">
                    Total calculado: {parseFloat(form.monto || 0).toLocaleString("es-PY")} {form.moneda}
                  </p>
                )}
              </div>
            );
          })()}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={lbl}>Moneda</label>
              <select className={inp} value={form.moneda} onChange={e => set("moneda", e.target.value)}>
                <option value="PYG">PYG</option>
                <option value="USD">USD</option>
                <option value="BRL">BRL</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className={lbl}>Monto *{saldoDisponibleFac != null ? <span className="text-gray-400 font-normal ml-1">(máx: {parseFloat(saldoDisponibleFac).toLocaleString("es-PY")})</span> : ""}</label>
              <MoneyInput min="0" max={saldoDisponibleFac != null ? saldoDisponibleFac : undefined} step="any" className={inp} value={form.monto} moneda={form.moneda || "PYG"} onChange={value => set("monto", value)} />
            </div>
          </div>
          {form.moneda !== "PYG" && (
            <div>
              <label className={lbl}>Tipo de cambio</label>
              <input type="number" min="0" step="any" className={inp} value={form.tipo_cambio} onChange={e => set("tipo_cambio", e.target.value)} />
            </div>
          )}
          <div>
            <label className={lbl}>Estado</label>
            <select className={inp} value={form.estado} onChange={e => set("estado", e.target.value)}>
              <option value="emitida">Emitida</option>
              <option value="anulada">Anulada</option>
            </select>
          </div>
          {/* Cuenta bancaria: solo visible cuando hay movimiento real de plata */}
          {form.tipo_cobro !== "saldo_favor" && (
            <div>
              <label className={lbl}>Cuenta bancaria</label>
              <select className={inp} value={form.cuenta_id} onChange={e => {
                const c = cuentasDisp.find(x => x.id === e.target.value);
                set("cuenta_id", e.target.value);
                set("cuenta_nombre", c?.nombre || "");
              }}>
                <option value="">— Sin especificar —</option>
                {renderCuentasPorMoneda(cuentasDisp, factura?.logo_tipo || activeLogoTipo)}
              </select>
            </div>
          )}
          <div>
            <label className={lbl}>Notas</label>
            <textarea className={inp + " resize-none min-h-[70px]"} value={form.notas} onChange={e => set("notas", e.target.value)} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg shadow">
            <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// NotaCreditoDocModal — vista visual + impresión de nota de crédito
// ═══════════════════════════════════════════════════════════════
function NotaCreditoDocModal({ nota: n, activeEmpresaPropia = null, onClose, fmtMonto }) {
  React.useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const accentColor = n.logo_tipo === "jar" ? "#dc2626" : n.logo_tipo === "arandu" ? "#2563eb" : "#1d4ed8";
  const emisorNombre = n.emisor_razon_social || activeEmpresaPropia?.razon_social || (n.logo_tipo === "jar" ? "JAR Informática" : n.logo_tipo === "arandu" ? "Arandu Informática" : "AranduJAR Informática");
  const emisorNombreFantasia = n.emisor_nombre_fantasia || activeEmpresaPropia?.nombre_fantasia || "";
  const emisorFantasiaHtml = (colorNombre, colorSub) => emisorNombreFantasia
    ? `<div style="font-weight:800;font-size:13px;color:${colorNombre};line-height:1.25">${emisorNombreFantasia}</div><div style="font-size:9px;color:${colorSub};margin-top:1px">${emisorNombre}${emisorRuc ? ` · RUC: ${emisorRuc}` : ""}</div>`
    : `<div style="font-weight:700;color:${colorNombre}">${emisorNombre}${emisorRuc ? ` · RUC: ${emisorRuc}` : ""}</div>`;
  const emisorRuc = n.emisor_ruc || activeEmpresaPropia?.ruc || "";
  const emisorDireccion = n.emisor_direccion || activeEmpresaPropia?.direccion || "";
  const emisorTelefono = n.emisor_telefono || activeEmpresaPropia?.telefono || "";
  const emisorEmail = n.emisor_email || activeEmpresaPropia?.email || "";
  const emisorLogoUrl = n.emisor_logo_url || activeEmpresaPropia?.logo_url || null;
  const emisorRubrosDinet = n.emisor_rubros_dinet || activeEmpresaPropia?.rubros_dinet || "";
  const fmt = (v, moneda) => fmtMonto(v, moneda || n.moneda);
  // Las Boletas (comprobante interno "sin_factura") nunca tienen timbrado —
  // se identifican porque su numeración siempre arranca con "BOL-" (ver
  // _generar_numero_boleta en facturas.py). Para esos casos mostramos un aviso
  // en vez de dejar la caja de documento vacía sin explicación.
  const notaEsSobreBoleta = !!(n.factura_numero && String(n.factura_numero).startsWith("BOL-"));

  const tipoCobroLabel = n.tipo_cobro === "saldo_favor"
    ? "Saldo a favor del cliente"
    : n.tipo_cobro === "reembolso"
    ? "Reembolso al cliente"
    : "Ajuste en plan de cuentas";

  const motivoLabel = n.tipo_motivo === "devolucion" ? "Devolución" : "Descuento";

  // ── Impresión A4 — mismo formato/plantilla que la Factura de venta ─────────
  // (header con logo+rubro, caja de documento, sección de cliente tipo
  // formulario, tabla de ítems con columnas Exentas/5%/10%, liquidación de
  // IVA + "Son:" en letras, y TOTAL A PAGAR en el pie de la tabla).
  const handlePrint = () => {
    const w = window.open("", "_blank");
    if (!w) { alert("Permita ventanas emergentes para imprimir."); return; }
    const docLogo = svgDocumentHeaderLogoHtml(n.logo_tipo, emisorLogoUrl);
    const moneda = n.moneda || "PYG";
    const fmtA4 = (v) => {
      if (v == null) return "-";
      if (moneda === "PYG") return `₲ ${Math.round(v).toLocaleString("es-PY")}`;
      return `${moneda} ${Number(v).toLocaleString("es-PY", { minimumFractionDigits: 2 })}`;
    };

    // Ítems: si hay devolución detallada, uno por ítem (clasificado por iva_tipo,
    // igual que Factura); si es una nota sin ítems (ej. descuento general), una
    // sola fila con el monto total, clasificada como 10% (tasa general, mismo
    // default que usa el resto del sistema cuando no hay dato de IVA por ítem).
    const itemsBase = (n.items_devueltos && n.items_devueltos.length > 0)
      ? n.items_devueltos.map(item => ({
          cantidad: item.cantidad || 1,
          descripcion: item.descripcion || "Ítem",
          variante_texto: item.variante_texto || "",
          precio_unitario: item.precio_unitario || 0,
          subtotal: item.subtotal ?? ((item.precio_unitario || 0) * (item.cantidad || 1)),
          iva_tipo: item.iva_tipo || "10",
        }))
      : [{
          cantidad: 1,
          descripcion: `${motivoLabel}${n.motivo_detalle ? ` — ${n.motivo_detalle}` : ""}`,
          variante_texto: "",
          precio_unitario: n.monto || 0,
          subtotal: n.monto || 0,
          iva_tipo: "10",
        }];

    let subExenta = 0, subGrav5 = 0, subGrav10 = 0;
    itemsBase.forEach(it => {
      if (it.iva_tipo === "exenta") subExenta += it.subtotal;
      else if (it.iva_tipo === "5") subGrav5 += it.subtotal;
      else subGrav10 += it.subtotal;
    });
    const iva5 = Math.round(subGrav5 / 21);
    const iva10 = Math.round(subGrav10 / 11);
    const base5 = subGrav5 - iva5;
    const base10 = subGrav10 - iva10;
    const totalIva = iva5 + iva10;
    const totalDoc = n.monto || 0;

    const itemRows = itemsBase.map((it, i) => {
      const colExenta = it.iva_tipo === "exenta" ? fmtA4(it.subtotal) : "";
      const col5 = it.iva_tipo === "5" ? fmtA4(it.subtotal) : "";
      const col10 = (it.iva_tipo !== "exenta" && it.iva_tipo !== "5") ? fmtA4(it.subtotal) : "";
      return `<tr style="background:${i % 2 === 0 ? "#fff" : "#fafafa"}">
        <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:center;font-size:12px">${it.cantidad}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px">${it.descripcion}${it.variante_texto ? `<div style="font-size:9px;color:#7c3aed;margin-top:2px">Variante: ${it.variante_texto}</div>` : ""}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:right;font-size:12px">${fmtA4(it.precio_unitario)}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:right;font-size:12px">${colExenta}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:right;font-size:12px">${col5}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;text-align:right;font-size:12px;font-weight:600">${col10}</td>
      </tr>`;
    }).join("");

    // Monto en letras — "Son: ..."
    const numToLetras = (num0) => {
      const num = Math.round(Math.abs(num0 || 0));
      if (num === 0) return "Cero";
      const un = ["", "un", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve", "diez", "once", "doce", "trece", "catorce", "quince", "dieciséis", "diecisiete", "dieciocho", "diecinueve"];
      const dec = ["", "", "veinte", "treinta", "cuarenta", "cincuenta", "sesenta", "setenta", "ochenta", "noventa"];
      const cen = ["", "cien", "doscientos", "trescientos", "cuatrocientos", "quinientos", "seiscientos", "setecientos", "ochocientos", "novecientos"];
      const g = (x) => {
        if (x < 20) return un[x];
        const d = Math.floor(x / 10), u = x % 10;
        if (x < 100) return dec[d] + (u ? " y " + un[u] : "");
        const c2 = Math.floor(x / 100), r = x % 100;
        return (c2 === 1 && r > 0 ? "ciento " : cen[c2] + (r ? " " : "")) + (r ? g(r) : "");
      };
      const mil = (x) => x === 0 ? "" : x === 1 ? "un mil " : g(x) + " mil ";
      const m = Math.floor(num / 1000000), rest = num % 1000000;
      const k = Math.floor(rest / 1000), u2 = rest % 1000;
      let r = (m > 0 ? (m === 1 ? "un millón " : g(m) + " millones ") : "") + mil(k) + (u2 > 0 ? g(u2) : "");
      return r.trim().charAt(0).toUpperCase() + r.trim().slice(1);
    };
    const montoLetras = moneda === "PYG" ? numToLetras(totalDoc) + " guaraníes" : numToLetras(totalDoc) + " " + moneda;

    // Caja de documento — mismo estilo que la caja de timbrado de Factura. La
    // Nota de Crédito no tiene timbrado propio en el sistema, así que se
    // reutiliza el del PUNTO de la factura vinculada (mismo RUC, misma
    // resolución de SET habilitando ambos tipos de documento) — ver
    // backend/routes/notas_credito.py::_enriquecer_timbrado_de_facturas.
    const tf = n.timbrado_factura || null;
    const docBox = notaEsSobreBoleta && !tf ? `
      <div style="border:2px solid #7c3aed;border-radius:6px;padding:10px 16px;text-align:center;min-width:200px">
        <div style="font-size:18px;font-weight:900;color:#7c3aed">NOTA DE CRÉDITO</div>
        <div style="font-size:14px;font-weight:700;color:#7c3aed;margin-top:2px">${n.numero || "—"}</div>
        ${emisorRuc ? `<div style="font-size:10px;color:#4c1d95;font-weight:700;margin-top:2px">RUC: ${emisorRuc}</div>` : ""}
        <div style="font-size:9.5px;color:#9ca3af;margin-top:4px">Sobre comprobante interno (${n.factura_numero}) · no fiscal</div>
      </div>` : `
      <div style="border:2px solid #374151;border-radius:6px;padding:8px 12px;text-align:center;min-width:220px">
        ${tf ? `
        <div style="font-size:9.5px;color:#374151;font-weight:700;text-transform:uppercase;letter-spacing:0.3px">
          TIMBRADO N°: <span style="font-size:13px;color:#111827;font-weight:800">${tf.nro_timbrado || "—"}</span>
        </div>
        <div style="font-size:9px;color:#6b7280;margin-top:2px">Establecimiento: ${tf.establecimiento || "001"} &nbsp;·&nbsp; Punto de expedición: ${tf.punto_expedicion || "—"}</div>
        ${tf.tipo_emision === "electronico" ? `<div style="font-size:9px;color:#6b7280">Sin vencimiento (electrónico)</div>` : tf.fecha_vigencia ? `<div style="font-size:9px;color:#6b7280">Vigencia: ${tf.fecha_vigencia}${tf.vigente === false ? " (vencido)" : ""}</div>` : ""}
        <div style="margin-top:6px;border-top:1px solid #d1d5db;padding-top:6px">` : ""}
        <div style="font-size:18px;font-weight:900;color:#111827;letter-spacing:1px">NOTA DE CRÉDITO</div>
        <div style="font-size:14px;font-weight:800;color:${accentColor};letter-spacing:0.5px">${n.numero || "—"}</div>
        ${emisorRuc ? `<div style="font-size:10px;color:#374151;font-weight:700;margin-top:2px">RUC: ${emisorRuc}</div>` : ""}
        <div style="font-size:9.5px;color:#6b7280;font-weight:700;letter-spacing:1px;margin-top:4px">ORIGINAL</div>
        ${tf?.tipo_emision === "autoimpresor" && tf?.habilitacion_autoimpresor ? `
        <div style="font-size:8.5px;color:#374151;font-weight:600;margin-top:4px;border-top:1px dashed #d1d5db;padding-top:4px;line-height:1.3">
          HABILITACIÓN DE AUTOIMPRESOR N°: ${tf.habilitacion_autoimpresor}
        </div>` : ""}
        ${tf ? `</div>` : ""}
      </div>`;

    const viaStyleCss = `
      .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid ${accentColor};padding-bottom:12px;margin-bottom:10px;gap:16px}
      .company-block{min-width:250px;max-width:330px;flex:0 0 330px}
      .company-info{font-size:10.5px;color:#475569;line-height:1.55;margin-top:6px}
      .rubro-block{flex:1;min-width:180px;padding-top:58px;font-size:10px;color:#64748b;line-height:1.35;font-style:italic;word-break:break-word}
      .client-section{border:1px solid #d1d5db;border-radius:4px;padding:8px 10px;margin-bottom:10px;font-size:11.5px}
      .cl-row{display:flex;gap:0;margin-bottom:3px}
      .cl-label{color:#6b7280;font-size:10px;font-weight:700;text-transform:uppercase;width:130px;flex-shrink:0}
      .cl-val{color:#111827;font-weight:600;border-bottom:1px dotted #d1d5db;flex:1}
      .cl-cond{display:flex;align-items:center;gap:12px;margin-top:6px;padding-top:6px;border-top:1px solid #e5e7eb;font-size:10.5px}
      table.items{width:100%;border-collapse:collapse;font-size:11.5px;margin-bottom:0}
      table.items thead tr{background:${accentColor};color:#fff}
      table.items thead th{padding:6px 8px;font-size:10px;font-weight:700;text-transform:uppercase;border:1px solid rgba(255,255,255,0.15)}
      table.items tfoot td{border:1px solid #d1d5db;padding:5px 8px;font-size:11.5px}
      .sub-row{background:#f8fafc}
      .iva-row{background:#f0fdf4}
      .total-row{background:${accentColor}12;font-weight:800;font-size:13px}
      .footer-note{margin-top:14px;font-size:9.5px;color:#94a3b8;text-align:center;border-top:1px dashed #e2e8f0;padding-top:8px}
    `;

    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Nota de Crédito ${n.numero}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Segoe UI',Arial,sans-serif;color:#111827;padding:20px 24px;max-width:860px;margin:0 auto;background:#fff;position:relative;font-size:12px}
        @media print{body{padding:10px 14px}@page{margin:0.8cm}}
        ${viaStyleCss}
      </style></head><body>
        <!-- CABECERA: logo izq, caja de documento der -->
        <div class="header">
          <div class="company-block">
            ${docLogo}
            <div class="company-info">
              ${emisorFantasiaHtml("#334155", "#64748b")}
              <div>${emisorDireccion}</div>
              <div>${emisorTelefono ? `Tel: ${emisorTelefono}` : ""}${emisorTelefono && emisorEmail ? " &nbsp;|&nbsp; " : ""}${emisorEmail || ""}</div>
            </div>
          </div>
          ${emisorRubrosDinet ? `<div class="rubro-block"><span style="font-weight:700;color:#475569;font-style:normal">Rubro:</span> ${emisorRubrosDinet}</div>` : `<div class="rubro-block"></div>`}
          ${docBox}
        </div>

        <!-- DATOS DEL CLIENTE -->
        <div class="client-section">
          <div class="cl-row">
            <span class="cl-label">RUC</span>
            <span class="cl-val">${n.ruc || "&nbsp;"}</span>
            <span class="cl-label" style="margin-left:16px">Fecha</span>
            <span class="cl-val" style="max-width:110px">${n.fecha || "&nbsp;"}</span>
          </div>
          <div class="cl-row">
            <span class="cl-label">Nombre / Razón Social</span>
            <span class="cl-val">${n.razon_social || "&nbsp;"}</span>
          </div>
          <div class="cl-row">
            <span class="cl-label">Dirección</span>
            <span class="cl-val">&nbsp;</span>
          </div>
          <div class="cl-cond">
            <span><strong>Comprobante de Venta Asociado:</strong> ${notaEsSobreBoleta ? "BOLETA" : "FACTURA"} N° ${n.factura_numero || "—"}</span>
            ${n.factura_fecha ? `<span><strong>Fecha de Factura:</strong> ${n.factura_fecha}</span>` : ""}
          </div>
          <div class="cl-cond" style="border-top:none;padding-top:0;margin-top:2px">
            <span><strong>Motivo:</strong> ${motivoLabel}${n.motivo_detalle ? ` — ${n.motivo_detalle}` : ""}</span>
            <span style="margin-left:auto"><strong>Gestión del crédito:</strong> ${tipoCobroLabel}</span>
          </div>
          ${n.cuenta_nombre ? `<div class="cl-cond" style="border-top:none;padding-top:0;margin-top:2px"><span>🏦 Cuenta: ${n.cuenta_nombre}</span></div>` : ""}
        </div>

        <!-- TABLA DE ITEMS -->
        <table class="items">
          <thead>
            <tr>
              <th style="width:5%;text-align:center">Cant.</th>
              <th style="width:50%;text-align:left">Descripción</th>
              <th style="width:15%;text-align:right">Precio Unit.</th>
              <th style="width:10%;text-align:right">Exentas</th>
              <th style="width:10%;text-align:right">5%</th>
              <th style="width:10%;text-align:right">10%</th>
            </tr>
          </thead>
          <tbody>${itemRows}</tbody>
          <tfoot>
            <tr class="sub-row">
              <td colspan="3" style="text-align:right;font-weight:700">SUBTOTAL</td>
              <td style="text-align:right">${subExenta > 0 ? fmtA4(subExenta) : ""}</td>
              <td style="text-align:right">${subGrav5 > 0 ? fmtA4(subGrav5) : ""}</td>
              <td style="text-align:right;font-weight:700">${subGrav10 > 0 ? fmtA4(subGrav10) : ""}</td>
            </tr>
            <tr class="iva-row">
              <td colspan="2" style="font-size:10px;color:#374151;font-weight:600">LIQUIDACIÓN DEL IVA</td>
              <td style="text-align:right;font-size:10.5px;color:#374151">5% de ${fmtA4(base5)}:</td>
              <td></td>
              <td style="text-align:right;font-size:10.5px">${fmtA4(iva5)}</td>
              <td></td>
            </tr>
            <tr class="iva-row">
              <td colspan="2"></td>
              <td style="text-align:right;font-size:10.5px;color:#374151">10% de ${fmtA4(base10)}:</td>
              <td></td>
              <td></td>
              <td style="text-align:right;font-size:10.5px">${fmtA4(iva10)}</td>
            </tr>
            <tr class="sub-row">
              <td colspan="4" style="font-size:10px;color:#374151"><strong>Son:</strong> ${montoLetras}</td>
              <td style="text-align:right;font-weight:700;font-size:10.5px">TOTAL IVA</td>
              <td style="text-align:right;font-weight:700;font-size:10.5px">${fmtA4(totalIva)}</td>
            </tr>
            <tr class="total-row">
              <td colspan="4" style="font-size:10px;color:#6b7280"></td>
              <td style="text-align:right;font-size:12px">TOTAL A PAGAR</td>
              <td style="text-align:right;font-size:14px;color:${accentColor}">${fmtA4(totalDoc)}</td>
            </tr>
          </tfoot>
        </table>

        ${n.moneda !== "PYG" && n.tipo_cambio ? `<div style="margin-top:8px;font-size:10.5px;color:#6b7280;text-align:right">Tipo de cambio: ₲ ${Number(n.tipo_cambio).toLocaleString("es-PY")} / ${n.moneda}</div>` : ""}

        ${n.notas ? `<div style="margin-top:10px;border:1px solid #e2e8f0;border-radius:6px;padding:10px"><span style="color:#6b7280;font-size:11px;text-transform:uppercase;letter-spacing:.5px">Observaciones: </span><span style="font-size:12px">${n.notas}</span></div>` : ""}
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl relative" onClick={e => e.stopPropagation()}>
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <span className="text-gray-600 font-medium text-sm">Nota de crédito</span>
          <div className="flex gap-2">
            <button onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs rounded-lg transition-all">
              <Printer className="w-3.5 h-3.5" /> Imprimir
            </button>
            <button onClick={onClose}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs rounded-lg transition-all">
              Cerrar
            </button>
          </div>
        </div>

        {/* Header del documento */}
        <div className="p-6 text-center border-b-4" style={{ borderColor: accentColor }}>
          <div className="mb-3 flex justify-center">
            {n.logo_tipo === "arandu" && <LogoArandu />}
            {n.logo_tipo === "jar" && <LogoJar />}
            {(!n.logo_tipo || n.logo_tipo === "arandujar") && <LogoAranduJarDoc />}
          </div>
          <h2 className="text-xl font-black text-gray-800 tracking-wider">NOTA DE CRÉDITO</h2>
          <p className="text-3xl font-black mt-1" style={{ color: accentColor }}>N° {n.numero}</p>
          {emisorRuc && <p className="text-gray-600 text-sm font-semibold mt-1">RUC: {emisorRuc}</p>}
          <p className="text-gray-400 text-xs mt-1">{n.fecha || ""}</p>
        </div>

        {/* Timbrado info — reutilizado de la factura vinculada */}
        {n.timbrado_factura?.nro_timbrado && (
          <div className="px-6 pt-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5 flex items-center gap-2 text-xs text-emerald-700">
              <Shield className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                <strong>Timbrado N° {n.timbrado_factura.nro_timbrado}</strong>
                <span className="ml-2 text-emerald-500">Est. {n.timbrado_factura.establecimiento || "001"}</span>
                <span className="ml-2 text-emerald-500">Punto {n.timbrado_factura.punto_expedicion}</span>
                {n.timbrado_factura.tipo_emision === "electronico"
                  ? <span className="ml-2 text-emerald-500">Sin vencimiento (electrónico)</span>
                  : n.timbrado_factura.fecha_vigencia && (
                    <span className="ml-2 text-emerald-500">
                      Vigencia: {n.timbrado_factura.fecha_vigencia}{n.timbrado_factura.vigente === false ? " (vencido)" : ""}
                    </span>
                  )}
              </span>
            </div>
          </div>
        )}

        {/* Datos */}
        <div className="p-6 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3 col-span-2">
              <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Cliente</p>
              <p className="text-gray-800 font-semibold">{n.razon_social || "—"}</p>
              {n.ruc && <p className="text-gray-500 text-xs mt-0.5">RUC: {n.ruc}</p>}
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Factura vinculada</p>
              <p className="text-gray-800 font-mono font-semibold">{n.factura_numero || "—"}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Tipo</p>
              <p className="text-gray-800 font-medium">{n.tipo_motivo === "devolucion" ? "Devolución" : "Descuento"}</p>
              {n.motivo_detalle && <p className="text-gray-500 text-xs mt-0.5">{n.motivo_detalle}</p>}
            </div>
          </div>

          {/* Tipo cobro */}
          <div className="rounded-lg px-3 py-2 text-sm flex items-center justify-between"
            style={{ background: `${accentColor}10`, border: `1px solid ${accentColor}30` }}>
            <span className="text-gray-500 text-xs uppercase tracking-wide">Gestión del crédito</span>
            <span className="font-medium text-gray-700">{tipoCobroLabel}</span>
          </div>

          {/* Ítems devueltos */}
          {n.items_devueltos && n.items_devueltos.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                <p className="text-gray-500 text-xs uppercase tracking-wide font-semibold">Ítems devueltos</p>
              </div>
              <div className="divide-y divide-gray-100">
                {n.items_devueltos.map((item, idx) => (
                  <div key={idx} className="px-3 py-2 flex justify-between text-sm">
                    <span className="text-gray-700">
                      {item.descripcion} × {item.cantidad}
                      {item.variante_texto && <span className="block text-violet-600 text-xs">Variante: {item.variante_texto}</span>}
                    </span>
                    <span className="text-gray-600 font-medium">{fmt((item.precio_unitario || 0) * (item.cantidad || 1))}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Monto destacado */}
          <div className="rounded-xl p-5 text-center" style={{ background: `${accentColor}15`, border: `2px solid ${accentColor}40` }}>
            <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Monto del crédito</p>
            <p className="text-4xl font-black" style={{ color: accentColor }}>{fmt(n.monto)}</p>
            {n.moneda !== "PYG" && n.tipo_cambio && (
              <p className="text-gray-400 text-xs mt-1">₲ {Number(n.tipo_cambio).toLocaleString("es-PY")} / {n.moneda}</p>
            )}
          </div>

          {n.notas && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm">
              <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Observaciones</p>
              <p className="text-gray-700">{n.notas}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
